import { ASSISTANT_EN, ASSISTANT_SV, ASSISTANT_DE } from "./assistant-strings";
import { DESIGN_PROFILE_EN, DESIGN_PROFILE_SV, DESIGN_PROFILE_DE } from "./design-profile-strings";
import { PROFILE_DIAGNOSTICS_EN, PROFILE_DIAGNOSTICS_SV, PROFILE_DIAGNOSTICS_DE } from "./design-profile-diagnostic-strings";
import { FUSE_BOARD_EN, FUSE_BOARD_SV, FUSE_BOARD_DE } from "./fuse-board-strings";
import type { Locale } from "./locales";
import { resolveDiagnosticLabels } from "./diagnostic-label";

const EN = {
  ...ASSISTANT_EN,
  ...DESIGN_PROFILE_EN,
  ...PROFILE_DIAGNOSTICS_EN,
  ...FUSE_BOARD_EN,
  "palette.missing.continue_cta": "Use a placeholder or request this model",
  "palette.missing.continue_body": "Keep mapping with an editable placeholder, or request the exact model.",
  "palette.missing.model_label": "Brand and model",
  "palette.missing.category": "Component category",
  "palette.missing.all_categories": "Suggested types",
  "palette.missing.template": "Placeholder connection type",
  "palette.missing.placeholder_warning": "Connections follow a generic template. Electrical ratings are unknown. Enter specs and check terminals against your manual before relying on calculations.",
  "palette.missing.email_label": "Email for follow-up (optional)",
  "palette.missing.source_label": "Manual or product link (optional)",
  "palette.missing.request_too": "Also request this exact model",
  "palette.missing.request_sent": "Request received. We’ll review the model and its documentation.",
  "palette.missing.placeholder_added": "Placeholder added. Select it to edit its specifications and connections.",
  "palette.missing.failed": "Your request was not sent. You can retry or send it through your email app.",
  "palette.missing.email_fallback": "Open email draft",
  "palette.missing.add_and_request": "Add placeholder + send request",
  "palette.missing.add_placeholder": "Add editable placeholder",
  "palette.missing.retry_request": "Retry request",
  "palette.missing.send_only": "Send request only",
  "palette.missing.continue_mapping": "Continue mapping",
  "inspector.legacy_microwave.explanation": "This older component estimates microwave consumption at the battery. Use an AC microwave with L, N and PE terminals for an AC circuit.",
  "inspector.legacy_microwave.replace": "Use {voltage} V AC microwave",
  "inspector.legacy_microwave.review": "Existing L/N wires stay connected. Check input watts against the appliance rating plate and connect PE as required.",
  "inspector.placeholder.title": "Placeholder — specifications unverified",
  "inspector.placeholder.warning": "This requested model uses generic connections. Check its manual and enter its electrical ratings and terminal details before relying on calculations.",
  "inspector.placeholder.available": "A matching model is now in the catalog. Confirm the exact variant before replacing this placeholder.",
  "inspector.placeholder.replace": "Use {name}",
  "inspector.placeholder.replace_hint": "Wires stay connected. Catalog specifications replace placeholder edits; review the terminals after swapping.",
  "diag.requested-component-unverified.title": "Component placeholders need verification",
  "diag.requested-component-unverified.message": "{count} requested components still use placeholder connections and unverified specifications.",
  "diag.requested-component-unverified.fix": "Check each model’s manual and edit its specifications and terminals, or replace it with the exact catalog part.",

  "health.reviewed_item": "Reviewed: {title}",
  "diag.monitor-power-unverified.detail_message_missing": "The drawing does not establish a complete DC supply from Vbatt+ to the negative supply connection of {label}. AUX is optional and does not replace Vbatt+.",
  "diag.monitor-power-unverified.detail_message_voltage": "{label} needs a 6.5–70 V DC supply. The connected source voltage is missing or outside that range; verify Vbatt+ against the manufacturer’s supply limits.",
  "diag.monitor-power-unverified.detail_message_protection": "The Vbatt+ supply for {label} has a path without declared protection of 1 A or less. Victron supplies this lead with a 1 A slow-blow fuse.",
  "diag.monitor-power-unverified.detail_fix": "Check the Vbatt+ lead, paired negative return and manufacturer-specified fused lead or temperature-sensor accessory. Verify the installed fuse and supply voltage.",
  "diag.charge-exceeds-acceptance.detail_message_specified": "The drawing or catalog declares {accept} A for charging. This value alone does not establish whether it is a maximum, recommendation or planning threshold. Sources can supply up to {charge} A; verify the exact battery and BMS specifications.",
  "diag.charge-exceeds-acceptance.detail_fix_specified": "Confirm what the declared current represents. Respect actual maximum ratings and manufacturer recommendations when configuring simultaneous chargers and alternator regulators.",
  "diag.charge-exceeds-acceptance.detail_message": "Declared charging sources can supply up to {charge} A to {ah} Ah of bank, above the estimated {limit}C limit (~{accept} A). Verify regulation, simultaneous operation and the manufacturer's maximum charge current.",
  "diag.charge-exceeds-acceptance.detail_fix_lead": "Verify the manufacturer's limit and charging regulation; excessive current can overheat lead-acid batteries.",
  "diag.charge-exceeds-acceptance.detail_fix_lfp": "Verify the battery and BMS maximum charge currents; the chemistry estimate does not establish the installed pack's limit.",
  "diag.charger-cumulative-vdrop.detail_message_manufacturer": "The complete charger-to-battery circuit has a conservative {drop} V design drop. The declared SmartSolar model limits battery-cable loss to {allowance}%. This nominal-voltage calculation includes both legs and any series-bank links; it is not a measurement of charging conditions.",
  "diag.charger-cumulative-vdrop.detail_message_target": "The complete charging-source-to-battery circuit has a conservative {drop} V design drop, above the selected {allowance}% planning target. This target is not a manufacturer limit.",
  "diag.charger-cumulative-vdrop.detail_message_verify": "The complete charging-source-to-battery circuit estimates {drop} V against a {allowance}% design allowance. Current or source sharing needs verification; summed conductor bounds do not predict simultaneous actual drop.",
  "diag.charger-cumulative-vdrop.detail_fix": "Check the full positive and return circuit, charging current, bank voltage and connection losses. Increase conductor sizes or shorten the route where needed; verify voltage at charging-source and battery terminals under full charge current.",

  "diag.source-current-allocation-unverified.detail_message": "Current bounds on {count} cables depend on which source supplies or receives power. Sizing can assign the full combined current to one branch; this is not a prediction of actual sharing or proof of an overload.",
  "diag.source-current-allocation-unverified.detail_fix": "Verify source selection, parallel-current sharing and battery limits. Check each branch conductor, terminal and fuse against the resulting currents and applicable starting duty.",
  "diag.fuse-protection-unverified.detail_message_topology": "The source connections exceed the supported analysis size, so the source pairs and their fault currents are unresolved. Verify the {count} declared protection locations, installed fuse products, maximum DC voltage and any required series coordination.",
  "diag.fuse-protection-unverified.detail_message_topology_placement": "The source connections exceed the supported analysis size, so the source pairs and their fault currents are unresolved. Verify the {count} declared protection locations, installed fuse products, maximum DC voltage and any required series coordination. Equal cable and device ratings do not establish whether they describe one fuse or separate inline fuses; placement is unresolved.",
  "diag.fuse-protection-unverified.detail_message_unknown": "Fault current is unknown for this source pair. {count} protection locations are declared, but installed fuse products, their DC voltage and interrupt ratings, and any required series coordination have not been verified.",
  "diag.fuse-protection-unverified.detail_message_series": "Individual battery declarations do not establish this series bank’s fault current. {count} protection locations are declared; installed fuse products, maximum DC voltage and any required series coordination remain unverified.",
  "diag.fuse-protection-unverified.detail_message_estimated": "The {faultA} A fault-current figure includes AI estimates. For {count} declared protection locations, installed fuse products, maximum DC voltage and any required series coordination remain unverified. This is not a confirmed protection rating.",
  "diag.fuse-protection-unverified.detail_message_declared": "The declared source fault current is {faultA} A; it is not a verified maximum at each fuse. For {count} declared protection locations, installed fuse products, maximum DC voltage and any required series coordination remain unverified.",
  "diag.fuse-protection-unverified.detail_fix": "Obtain source or bank fault-current data with its conditions and maximum operating voltage. Verify each installed fuse’s exact product and DC interrupt rating. Confirm fuse locations and duplicate cable/device labels. If relying on upstream protection, obtain manufacturer data for the exact series combination.",
  "diag.fuse-protection-unverified.detail_message_unknown_placement": "Fault current is unknown for this source pair. {count} protection locations are declared, but installed fuse products, their DC voltage and interrupt ratings, and any required series coordination have not been verified. Equal cable and device ratings do not establish whether they describe one fuse or separate inline fuses; placement is unresolved.",
  "diag.fuse-protection-unverified.detail_message_series_placement": "Individual battery declarations do not establish this series bank’s fault current. {count} protection locations are declared; installed fuse products, maximum DC voltage and any required series coordination remain unverified. Equal cable and device ratings do not establish whether they describe one fuse or separate inline fuses; placement is unresolved.",
  "diag.fuse-protection-unverified.detail_message_estimated_placement": "The {faultA} A fault-current figure includes AI estimates. For {count} declared protection locations, installed fuse products, maximum DC voltage and any required series coordination remain unverified. This is not a confirmed protection rating. Equal cable and device ratings do not establish whether they describe one fuse or separate inline fuses; placement is unresolved.",
  "diag.fuse-protection-unverified.detail_message_declared_placement": "The declared source fault current is {faultA} A; it is not a verified maximum at each fuse. For {count} declared protection locations, installed fuse products, maximum DC voltage and any required series coordination remain unverified. Equal cable and device ratings do not establish whether they describe one fuse or separate inline fuses; placement is unresolved.",
  "diag.fuse-identity-conflict.detail_message": "The connected cable and device fields declare {ratings} A. They cannot be combined into one fuse identity. The drawing does not establish whether this is a stale duplicate or a separate inline fuse.",
  "diag.fuse-identity-conflict.detail_fix": "Check the installed fuse locations, products and ratings. Correct stale cable or device values, or model separate inline fuses with their own holders.",
  "diag.fuse-too-far-from-battery.detail_message": "The longest modeled run from this battery bank to its first fuse is {distanceMm} mm. The default ABYC E-11 limit is 175 mm from the positive post. Installation exceptions for additional protective sheathing or enclosure are not modeled. A short before the fuse cannot be cleared by that downstream fuse. Check all highlighted feeds against the actual installation.",
  "diag.fuse-too-far-from-battery.detail_fix": "Confirm the actual unfused cable lengths and protection locations. Mount appropriately rated protection close to each battery feed; use a battery-terminal fuse or an inline holder as appropriate for the installation.",
  "diag.battery-missing-terminal-fuse.detail_message": "The positive cable leaving this battery exceeds the 175 mm ABYC E-11 protection window with no terminal fuse on the post. A short circuit on this unprotected span can overheat the conductor; the available fault current depends on the actual battery and installation.",
  "diag.battery-missing-terminal-fuse.detail_fix": "Set a battery fuse in the inspector (MRBF or Class T sized for the bank), or wire the existing fuse onto the post so the unprotected conductor is under 175 mm.",
  "diag.bms-control-unverified.detail_message": "Verify the battery sensing and shutdown controls for {count} external BMS installation(s). Power wiring alone cannot establish a functioning protection chain.",
  "diag.bms-control-unverified.detail_fix": "Check every battery data lead, required VE.Bus/GX connection, charge/load control and compatible disconnect device against the manufacturer manual. A signal wire or backup contactor alone does not verify shutdown.",
  "diag.pv-isolation-missing.detail_message": "{label} remains connected to a solar array when the reviewed PV disconnects are opened. Its manufacturer calls for PV disconnection.",
  "diag.pv-isolation-missing.detail_fix": "Provide a suitable DC PV disconnect on the array conductors and remove bypass feeds. Confirm the required poles and voltage/current ratings in the installation manual.",
  "diag.pv-isolation-unverified.detail_message": "Crimp cannot verify a suitable means of disconnecting every PV conductor supplying {label}.",
  "diag.pv-isolation-unverified.detail_fix": "Check the controller and disconnect manuals, show the power connections, and confirm the disconnect is rated for this array. A fuse or remote on/off control alone does not verify isolation.",
  "diag.inverter-dc-isolation-unverified.detail_message": "Crimp cannot verify a DC disconnect that separates {label} from every connected DC supply.",
  "diag.inverter-dc-isolation-unverified.detail_fix": "Check the inverter manual and provide a suitable means of DC disconnection for every supply path. A shared upstream disconnect may suffice; operating controls and fuse ratings alone do not verify the service procedure.",
  "diag.missing-ground.detail_message": "The drawn path from {terminal} on {label} does not reach a modeled grounding reference. A wire ending on a floating terminal does not establish a fault-current path.",
  "diag.missing-ground.detail_fix": "Trace and connect the complete protective-earth or chassis bonding path to the installation's grounding point. Verify conductor size and permitted bonding locations against the equipment manual.",
  "diag.ac-onto-dc-rail.detail_message": "The drawn wiring connects an AC line or neutral terminal to a DC power rail, directly or through connected posts. This can create a shock or fire hazard; DC fuses do not establish AC fault protection. Custom component terminals are checked using their declared internal connections, which may need correction to match the equipment.",
  "diag.ac-onto-dc-rail.detail_fix": "Disconnect the indicated AC-to-DC path in the drawing. Keep AC line and neutral on their intended AC terminals and verify custom panel connections against its manual. Protective-earth and source-specific neutral bonding must follow the equipment instructions; do not remove required protective earth.",
  "diag.unfused-charger-output.detail_message": "{label}'s {terminal} output can reach a battery+ without a fuse on the path. A short on this cable will dump the battery's full short-circuit current straight through it — ABYC E-11 §11.10.1.1 requires a fuse within 7 in (178 mm) of the battery on every conductor connected to it.",
  "diag.unfused-charger-output.detail_fix": "Add a fuse close to the battery on this conductor (MRBF or class-T for high-current sources).",
  "diag.charger-return-floating.detail_message": "{label}'s positive output is wired but its return ({negLabels}) isn't connected. Without a path back to the battery negative, the charger cannot deliver any current — the house bank will never see charge.",
  "diag.charger-return-floating.detail_message_dead_end": "{label}'s return ({negLabels}) is wired, but nothing it lands on reaches a battery negative. Without a path back to the battery negative, the charger cannot deliver any current — the house bank will never see charge.",
  "diag.charger-return-floating.detail_fix": "Run a cable from {label}'s {negLabels} to the negative bus that ties back to the destination battery's −.",
  "diag.source-return-floating.detail_message": "{label} has a positive connection to a battery bank, but the drawing does not establish a return to that bank. Its charging current cannot be verified.",
  "diag.source-return-floating.detail_fix": "Check the source's negative or case-ground connection and the complete path back to the same battery bank. Declare internal connections explicitly; a cable ending at an unrelated terminal does not complete the circuit.",
  "diag.manufacturer-breaker-unverified.detail_message": "The manufacturer specifies a {rating} A circuit breaker for this model. The drawing does not establish the required breaker or its trip curve; a generic fuse with the same ampere rating is not automatically equivalent.",
  "diag.manufacturer-breaker-unverified.detail_fix": "Confirm the exact model and manufacturer breaker, its trip curve, source placement and conductor protection.",
  "diag.manufacturer-breaker-rating.detail_message": "The drawn supply path has {rating} A protection without the specified {required} A manufacturer breaker rating.",
  "diag.manufacturer-breaker-rating.detail_fix": "Retain feeder protection where needed and verify the manufacturer-specified breaker on every supply path.",
  "diag.manufacturer-supplied-lead-protection.detail_message": "The manufacturer supplies {label} with a {mm2} mm² lead and lists {ampacity} A for that conductor. Its modeled {rating} A upstream fuse exceeds this supplied-lead limit. A larger extension cable does not increase the supplied lead's rating.",
  "diag.manufacturer-supplied-lead-protection.detail_fix": "Protect the supplied lead at its source according to the manufacturer's conductor table. Verify the installed lead and fuse; do not treat this as a terminal-contact or total-panel rating.",
  "diag.cranking-vdrop-unverified.detail_message": "The starting design estimate is {pct}% voltage drop across the drawn feed and return, above the planner’s conservative starting target.",
  "diag.cranking-vdrop-unverified.detail_fix": "Verify starting-current data, cable lengths and installed sizes against the fitted engine’s manufacturer instructions.",
  "health.details": "Details",
  "health.mark_reviewed": "Mark as reviewed",
  "health.undo_review": "Undo review",
  "health.review_note": "Checked outside Crimp? Mark as reviewed after checking.",
  "health.reviewed_note": "Marked as reviewed, not verified by Crimp. Wiring or rating changes reopen this check.",
  "health.reviewed_count": "Reviewed ({count})",
  "health.no_unreviewed": "No unreviewed issues. Reviewed checks remain below.",
  "diag.current-model-unverified.reason.parallel-paths": "{label}: Crimp cannot resolve parallel current paths. Check the load on each branch.",
  "diag.current-model-unverified.reason.source-choice-limit": "{label}: Too many source combinations for Crimp to calculate. Check cable sizes independently.",
  "diag.current-model-unverified.reason.source-route-limit": "{label}: This circuit exceeds Crimp’s route-analysis limit. Check cable sizes independently.",
  "diag.current-model-unverified.reason.source-range-limit": "{label}: Crimp cannot resolve this battery arrangement. Check bank voltage and cable sizes independently.",
  "diag.current-model-unverified.reason.shared-port-rating": "{label}: Outputs share a current rating. Check the limit for each output.",
  "diag.current-model-unverified.reason.converter-current-coupling": "{label}: Crimp cannot yet size cables with this converter’s input current limit. Check currents and cable sizes independently.",
  "diag.current-model-unverified.reason.unsupported-component-category": "{label}: Crimp cannot model this component’s current. Check its category and operating-current specification.",
  "diag.current-model-unverified.reason.ambiguous-current-role": "{label}: The input/output current roles are unclear. Check terminal directions and current specifications.",
  "diag.current-model-unverified.reason.unsupported-pv-array": "{label}: Crimp cannot resolve this solar array. Check panel ratings and series/parallel wiring.",
  "diag.current-model-unverified.reason.restricted-device-use": "{label}: This connection violates a device use restriction. Current estimates are provisional; verify the wiring before use.",
  "diag.battery-protect-reverse-use.title": "BatteryProtect reverse-current risk",
  "diag.battery-protect-reverse-use.message": "{label}: This wiring can send current from OUT to IN. Victron prohibits reverse current because it can damage the device.",
  "diag.battery-protect-reverse-use.fix": "Wire the intended source to IN and the receiving load or battery to OUT. Keep charging and discharging paths separate. Displayed current is a conservative estimate, not proof of safe reverse blocking.",
  "diag.battery-protect-model-unverified.title": "BatteryProtect model needs verification",
  "diag.battery-protect-model-unverified.message": "{label}: Edited electrical data no longer matches the reviewed BatteryProtect model. Direction and current results are unverified.",
  "diag.battery-protect-model-unverified.fix": "Restore the catalog model or check the complete wiring and operating limits against its manual.",
  "diag.combiner-manual-state.title": "Select the combiner contact state",
  "diag.combiner-manual-state.message": "{label}: Simulation uses your Isolated or Combined selection and starts isolated. Automatic voltage sensing, delays and external controls are not simulated.",
  "diag.combiner-manual-state.fix": "Compare both contact states in Simulate. Design cable sizing retains the possible combined-bank current.",
  "diag.alpha-source-model.title": "Alpha75 source assumptions",
  "diag.alpha-source-model.message": "{label}: Solar has priority within the shared 75 A output limit. Simulation then allocates AC before vehicle DC; actual AC/DC sharing and DC voltage/ignition triggers are not modeled.",
  "diag.alpha-source-model.fix": "On assumes the connected vehicle input is enabled. Check trigger settings separately; start-battery charging and recovery are not simulated. Both GROUND points represent the same common terminal and preserve older drawings.",
  "diag.current-model-unverified.reason.one-way-device": "{label}: Charging through this isolator is not yet modeled. Check currents and cable sizes independently.",
  "diag.current-model-unverified.reason.unmodeled-branch": "{label}: No complete load model reaches this branch. Check the supply, return and operating-current specifications.",

  "diag.battery-isolator-model-unverified.title": "Battery isolator calculations need verification",
  "diag.battery-isolator-model-unverified.message": "The isolator keeps its battery outputs separate. Charging through this one-way device is not yet modeled, so affected cable currents and sizes are provisional and its charging contribution is excluded from the energy budget.",
  "diag.battery-isolator-model-unverified.fix": "Verify the isolator input/output circuits, cable sizes, protection and charging performance against the source and isolator manufacturer instructions.",
  "diag.battery-short-circuit.title": "Battery terminals are shorted together",
  "diag.battery-short-circuit.message": "The drawn conductor paths connect positive to negative across {count} battery cells or batteries. A shared monitor terminal is an electrical connection too; a fuse does not make this wiring safe.",
  "diag.battery-short-circuit.fix": "Remove the shorting path before energizing. Connect each cell or bank voltage tap to its own manufacturer-specified sensing input; do not bond different series levels onto one terminal.",
  "diag.load-current-unverified.title": "Load current needs verification",
  "diag.load-current-unverified.message": "{count} connected DC loads have no operating-current specification. Wire and fuse calculations and power/runtime estimates omit their demand and are incomplete.",
  "diag.load-current-unverified.fix": "Enter the manufacturer’s operating current, then recalculate. For loads that support the constant-power model, verified wattage is another option. Terminal and fuse ratings do not establish operating current.",
  "cable.source_allocation_sizing": "These sizes use upper bounds across possible source routes. Actual current sharing is not established; verify allocation and protection before relying on a branch-current figure.",
  "cable.current_bound": "Source-routing current bound",
  "cable.current_estimate": "Provisional current estimate",
  "cable.starting_current": "Brief starting current",
  "cable.sizing_starting_duty": "Sized for sustained load and brief starting duty at {pct}% V-drop.",
  "diag.source-current-allocation-unverified.title": "Check current sharing",
  "diag.source-current-allocation-unverified.message": "Sizing for {count} cables assumes conservative current sharing. Actual sharing between sources is unknown.",
  "diag.source-current-allocation-unverified.fix": "Check source settings and each branch’s cable, terminal and fuse ratings.",
  "cable.provisional_sizing": "The current paths or component ratings are unresolved. Treat these wire and fuse sizes as provisional until the drawing is verified.",
  "diag.fuse-protection-unverified.message_topology": "Crimp cannot verify the installed fuses from this drawing.",
  "diag.fuse-protection-unverified.message_topology_placement": "Crimp cannot verify the installed fuses from this drawing.",

  "diag.current-model-unverified.title": "Cable sizing needs checking",
  "diag.current-model-unverified.message": "Crimp cannot reliably calculate current for {count} connected components. The highlighted cable sizes are provisional.",
  "diag.current-model-unverified.fix": "Open Details for the affected components and what needs checking.",
  "diag.fuse-protection-unverified.title": "Check fuse suitability: {label}",
  "diag.fuse-protection-unverified.message_unknown": "Crimp cannot verify the installed fuses from this drawing.",
  "diag.fuse-protection-unverified.message_series": "Crimp cannot verify the installed fuses from this drawing.",
  "diag.fuse-protection-unverified.message_estimated": "Crimp cannot verify the installed fuses from this drawing.",
  "diag.fuse-protection-unverified.message_declared": "Crimp cannot verify the installed fuses from this drawing.",
  "diag.fuse-protection-unverified.fix": "Check their voltage and short-circuit ratings against the battery and fuse datasheets.",
  "diag.fuse-protection-unverified.message_unknown_placement": "Crimp cannot verify the installed fuses from this drawing.",
  "diag.fuse-protection-unverified.message_series_placement": "Crimp cannot verify the installed fuses from this drawing.",
  "diag.fuse-protection-unverified.message_estimated_placement": "Crimp cannot verify the installed fuses from this drawing.",
  "diag.fuse-protection-unverified.message_declared_placement": "Crimp cannot verify the installed fuses from this drawing.",
  "diag.fuse-identity-conflict.title": "Conflicting fuse ratings: {label}",
  "diag.fuse-identity-conflict.message": "The cable and component specify different ratings: {ratings} A.",
  "diag.fuse-identity-conflict.fix": "Correct duplicate values, or show separate fuses in separate holders.",

  // ── Topbar ─────────────────────────────────────────────
  "topbar.beta": "Beta",
  "topbar.undo": "Undo",
  "topbar.redo": "Redo",
  "topbar.undo_title": "Undo (⌘Z)",
  "topbar.redo_title": "Redo (⇧⌘Z)",
  "topbar.edit": "Edit",
  "topbar.view": "View",
  "topbar.simulate": "Simulate",
  "topbar.simulate_title":
    "Run the system live — toggle component states and watch cable amps update",
  "topbar.mode_aria": "App mode",
  "topbar.share": "Share",
  "topbar.share_title": "Share this project",
  "topbar.fuses_show": "Show fuse markers on cables",
  "topbar.fuses_hide": "Hide fuse markers on cables",
  "topbar.fuses_aria": "Toggle fuse markers",
  "topbar.cable_size_show": "Show cable size on cables",
  "topbar.cable_size_hide": "Hide cable size on cables",
  "topbar.cable_size_aria": "Toggle cable size",
  "topbar.cable_length_show": "Show cable length on cables",
  "topbar.cable_length_hide": "Hide cable length on cables",
  "topbar.cable_length_aria": "Toggle cable length",
  "topbar.annotations_aria": "Canvas annotations",

  // ── Project menu ───────────────────────────────────────
  "project_menu.title": "Project menu",
  "project_menu.name": "Project name",
  "project_menu.rename": "Rename project",
  "project_menu.placeholder": "Untitled system",
  "project_menu.new": "New project",
  "project_menu.open": "Open project…",
  "project_menu.open_external": "Open external…",
  "project_menu.save_now": "Save now",
  "project_menu.save": "Save",
  "project_menu.save_dots": "Save…",
  "project_menu.auto_save": "Auto-save",
  "project_menu.save_a_copy": "Save a copy…",
  "project_menu.duplicate": "Duplicate",
  "project_menu.import": "Open from file…",
  "project_menu.export": "Download as .12v.json",
  "project_menu.export_pdf": "Export to PDF…",
  "project_menu.export_image": "Export diagram as PNG",
  "pdf.title_suffix": "Device list",
  "pdf.untitled": "Untitled system",
  "pdf.totals.devices": "devices",
  "pdf.totals.cables": "cables",
  "pdf.col.name": "Device",
  "pdf.col.part": "Manufacturer / Model",
  "pdf.col.specs": "Specs",
  "pdf.col.zone": "Zone",
  "pdf.spec.draw": "Draw {a} A",
  "pdf.spec.out": "Out {a} A",
  "pdf.spec.surge": "Surge {a} A",
  "pdf.empty": "No devices on the canvas yet.",
  "pdf.attribution": "Designed in {app}. Open the live schematic:",
  "project_menu.blueprint": "Printable blueprint…",
  "project_menu.role_badge_title": "You are viewing this project via a {role} share link.",
  "project_menu.role.viewer": "viewer",
  "project_menu.role.commenter": "commenter",
  "project_menu.role.editor": "editor",
  "project_menu.viewer_cta": "Viewing read-only",
  "project_menu.viewer_cta_action": "Edit a copy",
  "project_menu.viewer_cta_title": "This is a read-only share link. Click to save your own editable copy.",
  "project_menu.save_state.saving": "Saving",
  "project_menu.save_state.saved": "Saved",
  "project_menu.save_state.dirty": "Unsaved",
  "project_menu.save_state.error": "Save failed",
  "project_menu.save_state.local": "Saved on this device",
  "project_menu.save_state.cloud": "Saved in the cloud",
  "project_menu.save_state.syncing": "Syncing",
  "project_menu.save_state.conflict": "Save conflict",
  "project_menu.local_detail":
    "This project only exists in this browser. It won't follow you to other devices and can disappear if site data is cleared.",
  "project_menu.cloud_detail": "This project is saved locally and in the cloud.",
  "project_menu.cloud_sync": "Automatic cloud sync",

  // ── Paywall ───────────────────────────────────────────
  "cloud_nudge.label": "Cloud backup",
  "cloud_nudge.badge": "Free with an account",
  "cloud_nudge.title": "Back up this project in the cloud",
  "cloud_nudge.body":
    "It’s already saved on this device. Sign in for free to sync your projects and edit them on any device.",
  "cloud_nudge.dismiss": "Not now",
  "cloud_nudge.cta": "Back up to cloud",
  "cloud_nudge.close": "Close the cloud backup suggestion",
  // The free equipment count is the served offer arm's, never a literal:
  // interpolate {limit}. Included credits and the cloud-project cap come from
  // the billing config. Crimp Lifetime is the product sold; the `.pass`
  // variants are dormant and read only while the server sells the Pass instead.
  "paywall.label": "Crimp Lifetime",
  "paywall.title": "Unlock any size system, exports and the Blueprint",
  "paywall.title.export": "Export this project",
  "paywall.title.blueprint": "Print an installation blueprint",
  "paywall.title.equipment_limit": "Keep building past {limit} components",
  "paywall.title.ai_credits": "AI credits come with Crimp Lifetime",
  "paywall.title.ai_credits.pass": "AI credits come with the Pass",
  "paywall.title.project_limit": "Save more projects to the cloud",
  "paywall.title.expired": "Your Pass has ended",
  "paywall.description":
    "Cloud saving and sharing stay free. Crimp Lifetime lets you {action} — pay once, yours for the lifetime of the product.",
  "paywall.description.pass":
    "Cloud saving and sharing stay free. The Crimp Pass lets you {action} for 12 months.",
  "paywall.description.equipment":
    "Free projects support up to {limit} equipment components. Crimp Lifetime removes the limit from every project, for the lifetime of the product.",
  "paywall.description.equipment.pass":
    "Free projects support up to {limit} equipment components. The Crimp Pass removes the limit from every project for 12 months.",
  "paywall.description.project_limit":
    "This project did not get a cloud slot, but it stays saved on this device. Crimp Lifetime unlocks any size system, exports and the Blueprint, with up to {limit} cloud projects.",
  "paywall.description.expired":
    "Your projects stay readable and every file you exported stays yours. Renew to keep building any size system, exporting and printing the Blueprint for another 12 months.",
  "paywall.trigger.export": "export it",
  "paywall.trigger.blueprint": "print the blueprint",
  "paywall.trigger.equipment": "keep adding equipment",
  "paywall.trigger.ai_credits": "keep using the assistant",
  "paywall.trigger.project_limit": "keep more projects in the cloud",
  "paywall.trigger.pro": "unlock everything",
  "paywall.not_now": "Not now",
  "paywall.buy": "Get Crimp Lifetime — {price}",
  "paywall.buy.pass": "Get a Pass — {price}",
  "paywall.buy.pack": "Buy once — {price}",
  "paywall.renew": "Renew · {price}",
  "paywall.sign_in": "Sign in to continue",
  "paywall.auth_reason.equipment":
    "Sign in or create an account so your purchase is on your account, then keep building.",
  "paywall.local_title": "Cloud saving stays free",
  "paywall.local_body":
    "You can keep editing and syncing every project for free. Crimp Lifetime is only needed for more than {limit} equipment components, exports and the full Blueprint.",
  "paywall.local_body.pass":
    "You can keep editing and syncing every project for free. The Pass is only needed for more than {limit} equipment components, exports and the full Blueprint.",
  "paywall.local_title.equipment": "Your current drawing is unchanged",
  "paywall.local_body.equipment":
    "Cables, fuse holders, fuse blocks and busbars are always free and never count toward the {limit}-component equipment limit.",
  "paywall.local_title.project_limit": "Your project is safe on this device",
  "paywall.local_body.project_limit":
    "It is saved here and keeps autosaving. Nothing was lost; it just did not reach the cloud.",
  "paywall.included": "Also included with Crimp Lifetime",
  "paywall.included.pass": "Also included with the Pass",
  "paywall.feature.export": "PDF and PNG exports",
  "paywall.feature.blueprint": "Printable blueprint and cable schedule",
  "paywall.feature.equipment": "Any size system in every project",
  "paywall.feature.credits": "{credits} AI credits for the assistant",
  "paywall.feature.cloud": "Up to {limit} projects in the cloud",
  "paywall.intro_note": "Pay once · yours for the lifetime of the product · 30-day money-back guarantee.",
  "paywall.intro_note.pass": "One payment · 12 months · no automatic renewal · 30-day money-back guarantee.",
  "paywall.tax_note":
    "Paddle calculates and handles applicable VAT or sales tax. The final total is shown before payment.",
  "paywall.legal_label": "Purchase terms and policies",
  "paywall.legal_intro": "Review before buying:",
  "paywall.terms": "Terms of use",
  "paywall.refunds": "Refund policy",
  "paywall.privacy": "Privacy",
  "paywall.unavailable":
    "Purchases are temporarily unavailable. Your local project is still safe on this device.",
  // Cloud-project cap copy. The Professional offer lifts the cloud limit.
  "project_limit.label": "Cloud projects",
  "project_limit.title": "You have {limit} cloud projects",
  "project_limit.saved_locally":
    "This project is saved on this device and keeps autosaving here. Nothing was lost; it just did not get a cloud slot.",
  "project_limit.free_slot": "Deleting a cloud project you no longer need frees a slot.",
  "project_limit.pro":
    "Crimp Professional includes unlimited cloud projects and tools for client work.",
  "project_limit.request": "See Professional",
  "project_limit.thanks": "Your project is saved.",
  "project_limit.thanks_body": "Your project stays on this device. Nothing was charged.",
  "project_limit.not_now": "Not now",
  "project_limit.done": "Done",
  "conflict.label": "Save conflict",
  "conflict.title": "This project changed somewhere else",
  "conflict.body": "Choose which work to keep. Crimp won't overwrite either version silently.",
  "conflict.detail": "Reload the newer saved version, or preserve what is currently on your canvas as a separate local project.",
  "conflict.reload": "Load newer version",
  "conflict.keep_copy": "Keep mine as a copy",
  "conflict.copy_suffix": "(conflict copy)",

  // ── Account menu ───────────────────────────────────────
  "account.sign_in": "Sign in",
  "account.sign_in_title": "Sign in",
  "account.sign_out": "Sign out",
  "labs.title": "Feature flags",
  "labs.label": "Admin tools",
  "labs.description": "Try features in this browser. Your choices are saved here and apply only while an authorized account is signed in.",
  "labs.access_hint": "Available to admins and the owner's account. Other users keep the default settings.",
  "account.aria": "Account",
  "account.signed_in_as": "Signed in as",
  "account.ai_usage": "{used} of {limit} free AI generations used",
  "account.language": "Language",
  "account.units": "Units",
  "preferences.title": "Preferences",
  "view.title": "View",
  "settings.title": "Settings",
  "preferences.show": "Show",
  "preferences.show_fuses": "Fuse markers",
  "preferences.show_cable_size": "Cable size",
  "preferences.show_cable_length": "Cable length",
  "preferences.show_cable_vdrop": "Voltage drop",
  "preferences.show_cable_label": "Cable labels",
  "preferences.cable_hover_details": "Cable details on hover",
  "preferences.show_product_images": "Product images",
  "preferences.auto_size_target": "Auto-size target",
  "preferences.auto_size_target.3pct": "≤3 % drop",
  "preferences.auto_size_target.3pct.hint": "ABYC E-11",
  "preferences.auto_size_target.5pct": "≤5 % drop",
  "preferences.auto_size_target.5pct.hint": "NEC 210.19",
  "preferences.auto_size_target.help":
    "Fallback for circuits Crimp can't classify. ABYC E-11.14.2.6 sets the rest per circuit: 3 % for panel feeders, bilge pumps, electronics and navigation lights, 10 % for general lighting. Override per cable in the inspector.",
  "units.metric": "Metric",
  "units.imperial": "Imperial",

  // ── Confirmations / prompts ────────────────────────────
  "topbar.confirm_discard": "Discard current system and start a new one?",
  "topbar.save_copy_prompt": "Save copy as:",
  "topbar.copy_suffix": "(copy)",
  "topbar.untitled": "Untitled",
  "topbar.auto_saved": "Auto saved",
  "topbar.save_failed": "Save failed: {message}",
  "topbar.duplicate_failed": "Duplicate failed: {message}",
  "topbar.open_failed": "Open failed: {message}",
  "topbar.unknown_error": "unknown error",

  // ── Auth-required reasons ──────────────────────────────
  "auth_reason.save": "Sign in to save your project to the cloud.",
  "auth_reason.save_as": "Sign in to save a copy.",

  // ── Share-link view (acquisition affordance) ───────────
  "share_view.headline": "Someone shared this wiring diagram with you.",
  "share_view.what_is_crimp":
    "Crimp is a 12V planner — it sizes every cable and fuse from the diagram.",
  "share_view.save_copy": "Save a copy",
  "share_view.save_copy_title": "Save an editable copy to your own account",
  "share_view.save_copy_confirm": "Save copy",
  "share_view.start_build": "Start your own build",
  "share_view.start_build_title": "Open a fresh canvas and plan your own system",
  "auth_reason.share": "Sign in to share your project.",

  // ── Import errors (formatImportError) ──────────────────
  "import_error.future_version":
    "This file was created with a newer version of Crimp (file v{fileVersion}, app supports v{supportedVersion}). Update the app to open it.",
  "import_error.not_system_shape": "This file isn't a valid Crimp system.",
  "import_error.parse": "Couldn't read the file — it isn't valid JSON.",
  "import_error.generic": "Couldn't open the file: {message}",
  "import_error.cloud_future_version_reload":
    "This project was saved with a newer version of Crimp. Reload to update?",

  // ── Palette ────────────────────────────────────────────
  "palette.search": "Search components",
  "palette.search_loading": "Searching components…",
  "palette.search_failed": "Search could not load.",
  "palette.search_retry": "Try again",
  "palette.clear_search": "Clear search",
  "palette.layout": "Layout",
  "palette.zone": "Zone",
  "palette.zone_sub": "Group components by physical area",
  "palette.custom": "Custom component",
  "palette.custom_sub": "Build your own from scratch",
  "palette.describe": "Describe a part",
  "palette.describe_sub": "Name it and we'll fill in the specs",
  "palette.show_more": "Show {count} more",
  "palette.show_less": "Show less",
  "palette.missing.no_match_for": "No match for",
  "palette.missing.try_generic":
    "Or drop a generic and edit its specs in the inspector:",
  "palette.missing.cta_specific": "Request it",
  "palette.missing.cta_any": "Request a component",
  "palette.missing.body": "Brand and model help us find a datasheet.",
  "palette.missing.placeholder": "e.g. Mercury 9.9 outboard",
  "palette.missing.send": "Send request",
  "palette.missing.close": "Close",
  "palette.missing.sending": "Sending",
  "palette.missing.thanks": "Got it. We'll dig up a datasheet.",
  "palette.cat.battery": "Batteries",
  "palette.cat.charger": "Chargers & DC-DC",
  "palette.cat.solar": "Solar",
  "palette.cat.source": "Sources",
  "palette.cat.engine": "Engines",
  "palette.cat.distribution": "Distribution & Fuses",
  "palette.cat.load": "Loads",
  "palette.cat.monitor": "Monitoring",
  "palette.library": "My library",
  "palette.library_empty":
    "Save a custom component here to reuse it across diagrams.",
  "palette.library_remove": "Remove from library",
  "palette.limit.label": "Free equipment",
  "palette.limit.count": "{count} of {limit}",
  "palette.limit.free_items": "Wires, fuses and busbars never count.",

  // ── Describe-a-part modal ──────────────────────────────
  "ai_component.title": "Describe a part",
  "ai_component.options": "Part menu",
  "ai_component.prompt_label": "Component name or description",
  "ai_component.subtitle":
    "Name a real component and we'll fill in its nameplate specs. Wire sizes and fuse ratings stay ours to calculate.",
  "ai_component.placeholder": "Victron Orion-Tr Smart 12/12-30",
  "ai_component.examples": "Try: {examples}",
  "ai_component.generate": "Fill in the specs",
  "ai_component.generating": "Reading the nameplate…",
  "ai_component.cancel": "Cancel",
  "ai_component.restart": "Describe another",
  "ai_component.add": "Add to canvas",
  "ai_component.chemistry": "Chemistry",
  "ai_component.duty_hours": "Hours / day",
  "ai_component.always_on": "Always on",
  "ai_component.specs": "Specs",
  "ai_component.terminals": "Terminals",
  "ai_component.terminal_maxamp_note":
    "Terminal ratings follow these specs. Edit them in the inspector.",
  "ai_component.no_specs":
    "No specs returned. Add them in the inspector.",
  "ai_component.estimated": "estimated",
  "ai_component.estimated_hint":
    "Dotted values are estimates. Check them against the datasheet.",
  "ai_component.assumptions": "Assumptions",
  "ai_component.error.implausible":
    "The specs kept contradicting each other, so nothing was added. Naming the manufacturer and model usually fixes it.",
  "ai_component.error.unavailable":
    "Couldn't reach the assistant. Try again in a moment.",

  // ── Describe-your-system modal ─────────────────────────
  "ai_system.title": "Describe your system",
  "agent_review.home": "Crimp home",
  "agent_review.view_only": "View only",
  "agent_review.edit_copy": "Edit a copy",
  "agent_review.schematic": "Draft schematic",
  "agent_review.canvas_title": "{name} — view-only Crimp schematic",
  "agent_review.copy_hint": "Viewing does not save a project. Choose Edit a copy to change the parts or cables.",
  "agent_review.limits": "Planning checks, not installation approval. Confirm equipment, cable routes and manufacturer requirements.",
  "agent_review.finding_details": "Show all findings ({count})",
  "agent_review.assumptions": "Assumptions ({count})",
  "agent_review.no_assumptions": "No assumptions were supplied with this draft. Confirm any missing installation facts before relying on the plan.",
  "agent_draft.title": "Review your agent’s draft",
  "agent_draft.subtitle": "Check the parts, lengths and findings, then open a new local project. Your existing project is kept.",
  "agent_draft.import": "Create new local project",
  "agent_draft.importing": "Saving and opening…",
  "agent_draft.invalid": "This draft link is invalid, incompatible or exceeds the beta limits. Ask your agent to create a fresh link with no more than 10 equipment components.",
  "agent_draft.error": "The current project could not be safely saved or changed while importing. Resolve any save conflict and try again. Your current project has not been replaced.",
  "agent_draft.counts": "{equipment} equipment components · {cables} cables",
  "agent_draft.parts": "Parts to confirm",
  "agent_draft.runs": "Cable runs — one-way lengths",
  "agent_draft.estimated": "estimated — confirm",
  "agent_draft.findings": "Current checks",
  "agent_draft.finding_counts": "{critical} critical · {warning} warnings · {info} information",
  "agent_draft.provisional": "{count} cables have provisional sizing.",
  "agent_draft.no_findings": "These checks reported no findings. This does not establish that the installation is safe or compliant.",
  "agent_draft.limits": "Planning draft: confirm actual equipment, cable routes and manufacturer requirements. Suggested wire and fuse sizes are not installation approval. Assumptions remain in the project’s component notes.",
  "ai_system.beta_badge": "Beta",
  "ai_system.subtitle":
    "Say what you've got and we'll draw it. The parts come from the catalog; every wire size, fuse and voltage drop is calculated here, not guessed.",
  "ai_system.placeholder":
    "A Sprinter with two 100 Ah lithium batteries, 400 W of solar and a 2000 W inverter",
  "ai_system.generate": "Draw the system",
  "ai_system.generating": "Drawing the system…",
  "ai_system.busy_note":
    "Parts first, then the wiring. This usually takes about half a minute — longer for a big system.",
  "ai_system.busy_long":
    "Still drawing — a big system can take over a minute. Hang tight.",
  "ai_system.cancel": "Cancel",
  "ai_system.restart": "Describe another",
  "ai_system.add": "Open on canvas",
  "ai_system.engine_note":
    "Wire sizes and fuse ratings are never written by the assistant — they're computed from the finished diagram against ABYC E-11.",
  "ai_system.assumed_runs": "Run lengths we assumed",
  "ai_system.runs_decisive": "Lengths that decide a wire size",
  "ai_system.runs_minor":
    "{count} shorter runs were assumed too. None of them change a wire size.",
  "ai_system.runs_none_decisive":
    "{count} run lengths were assumed. None of them change a wire size at this length.",
  "ai_system.run_and_return": "+ return",
  "ai_system.show_all": "Show all",
  "ai_system.show_less": "Show less",
  "ai_system.picked_parts": "{count} parts we picked for you",
  "ai_system.picked_parts_hint":
    "Click any part on the canvas to swap it — the cables stay wired to it.",
  "ai_system.corrections": "Changes we made",
  "ai_system.stat_cables": "Cables",
  "ai_system.units": "Units",
  "ai_system.count_collapsed": "1 ×, not {requested}",
  "ai_system.count_collapsed_note":
    "A bus bar, fuse block or switch is one part. Wiring two in parallel would feed every circuit behind it twice, so we used one.",
  "ai_system.block_upsized": "bigger — {circuits} circuits",
  "ai_system.block_split": "{units} ×, {circuits} circuits",
  "ai_system.block_relieved_note":
    "More branch circuits were wired onto a fuse block than it has slots. A slot is one fuse and two circuits can't share it, so we used a bigger block — or a second one fed from the same place.",
  "ai_system.zone_distances": "How far apart are these?",
  "ai_system.zone_distances_hint":
    "Change one and every run across it moves with it. Single cables are still editable on the canvas.",
  "ai_system.assumptions_count": "{count} notes from the assistant",
  "ai_system.tip_verify":
    "Get a second opinion before you cut anything — an installer, a marine electrician, or the build forum you already read.",
  "ai_system.tip_numbers":
    "The assistant only picks parts and connections. Every wire size, fuse and voltage drop is computed here from the finished diagram.",
  "ai_system.tip_zones":
    "Your system arrives grouped into areas. Correct how far apart two of them are and every run between them is re-measured.",
  "ai_system.tip_swap":
    "Anything the assistant guessed is marked on the canvas. Click a part to swap it — the cables stay wired to it.",
  "ai_system.estimated": "assumed",
  "ai_system.estimated_hint":
    "Anything dotted is our guess, not your description. Correct it here and the diagram redraws.",
  "ai_system.assumptions": "Notes",
  "ai_system.confirm_replace":
    "This replaces what's on the canvas. Undo brings it back. Continue?",
  "ai_system.error.unbuildable":
    "The system kept coming back unwireable, so nothing was drawn. Naming the batteries, charger and main loads usually fixes it.",
  "ai_system.error.unavailable":
    "Couldn't reach the assistant. Try again in a moment.",
  "ai_system.photo_add": "Add a photo of your sketch",
  "ai_system.photo_remove": "Remove photo",
  "ai_system.photo_alt": "The photo you attached",
  "ai_system.photo_failed":
    "Couldn't read that image. A JPEG or PNG usually works.",
  "ai_system.photo_note":
    "A hand-drawn sketch or a photo of the real thing both work. Wire sizes and fuse ratings written on it are ignored — those get calculated here.",

  // ── Change-my-system mode ──────────────────────────────
  "ai_system.edit_title": "Change your system",
  "ai_system.edit_subtitle":
    "Say what to add, remove or rewire. Everything else on your canvas stays exactly as it is.",
  "ai_system.edit_placeholder":
    "Add a 200 W panel on its own MPPT, and a second fridge on the fuse block",
  "ai_system.edit_generate": "Plan the change",
  "ai_system.edit_generating": "Working out the change…",
  "ai_system.edit_busy_note":
    "Reading your system, then working out what has to change. This usually takes half a minute.",
  "ai_system.edit_apply": "Apply to canvas",
  "ai_system.edit_restart": "Change something else",
  "ai_system.edit_untouched_note":
    "Only what's listed here changes. Positions, lengths, wire sizes and fuses you set yourself are left alone, and undo puts it all back.",
  "ai_system.edit_added": "Parts added",
  "ai_system.edit_removed": "Parts removed",
  "ai_system.edit_removed_cables":
    "{count} cables went with them, and their wiring is gone too.",
  "ai_system.edit_unwired": "{count} cables removed",
  "ai_system.edit_new_runs": "Run lengths for the new cables",
  "ai_system.edit_stat_added": "Added",
  "ai_system.edit_stat_cables": "New cables",
  "ai_system.edit_stat_removed": "Removed",
  "ai_system.edit_nothing":
    "Nothing was changed — the note below says why. Your canvas is untouched.",
  "ai_system.edit_switch_to_new": "Start a new system instead",
  "ai_system.edit_switch_to_edit": "Change what's on the canvas instead",
  "ai_system.edit_replaces_note":
    "This draws a new system and replaces what's on the canvas.",
  "ai_system.edit_too_big":
    "This system is too big for the assistant to change. Add the part from the palette instead.",
  "ai_system.edit_error_unbuildable":
    "The change kept coming back unwireable, so nothing was applied. Naming the part and where it should connect usually fixes it.",
  "canvas.empty.subtitle":
    "Say what you've got and we'll draw it, then size every cable.",
  "canvas.empty.placeholder":
    "Two 100 Ah lithium batteries and 400 W of solar",
  "canvas.empty.palette_hint": "or drag a part in from the palette",
  "canvas.empty.manual_title": "Start your system",
  "canvas.empty.manual_subtitle":
    "Drag a part in from the palette, then drag between two terminals to wire them. Every cable is sized as you go.",
  "canvas.empty.manual_touch":
    "Tap + to add your first part.",
  "canvas.empty.read_only": "This blank diagram is read-only.",
  "starter.next_step.title": "Make this starter yours",
  "starter.next_step.body": "Set the cable length for your installation, then review the cable result. Edits save automatically on this device.",
  "starter.next_step.action": "Set your actual cable length",
  "starter.next_step.dismiss": "Later",
  "canvas.generated.placed":
    "Your system is on the canvas. Everything here is yours to edit.",
  "canvas.generated.lengths_one": "Check one run length",
  "canvas.generated.lengths_many": "Check {count} run lengths",
  "canvas.generated.lengths_why": "— the length is what picked the wire.",
  "canvas.generated.parts_one": "Swap one part",
  "canvas.generated.parts_many": "Swap {count} parts",
  "canvas.generated.parts_why":
    "— we chose them; every cable stays wired when you change one.",
  "canvas.generated.dismiss": "Dismiss",
  "mobile.ai.readonly_note":
    "You can keep editing on your phone after the diagram is created.",
  "mobile.welcome.manual_title": "12 V and 24 V wiring, sized properly",
  "mobile.welcome.manual_subtitle":
    "Build from a catalog of real parts and size every cable and fuse against ABYC E-11, right on your phone.",
  "mobile.welcome.start_manual": "Start a new diagram",
  "mobile.welcome.demo_cta": "Browse an example diagram",
  "mobile.welcome.demo_unavailable": "Example unavailable right now",
  "mobile.welcome.demo_note":
    "Read-only — pan, zoom and tap any part or cable to see its numbers.",
  "mobile.menu.title": "Menu",
  "mobile.menu.open": "Open menu",
  "mobile.menu.close": "Close menu",
  "mobile.menu.back": "Back to menu",
  "mobile.menu.project": "Project",
  "mobile.menu.privacy": "Privacy",
  "mobile.menu.advertising_cookies": "Advertising cookies",
  "mobile.menu.manage_subscription": "Manage subscription",
  "mobile.palette.title": "Add a part",
  "mobile.palette.open": "Add a part",
  "mobile.palette.add": "Add",
  "mobile.palette.close": "Close parts",
  "mobile.details.title": "Selection details",
  "mobile.details.close": "Close details",
  "mobile.details.open": "Open details",
  "mobile.details.label": "Details",
  "mobile.junction.branch": "Add cable branch",
  "mobile.share.title": "Share",
  "mobile.share.publish_title": "Save and share this diagram",
  "mobile.share.publishing": "Saving…",
  "mobile.share.copied": "Copied",
  "mobile.share.failed": "Couldn't share",
  "mobile.share.sign_in_reason":
    "Sign in to save this diagram and get a link you can send.",
  "mobile.projects.menu": "Cloud projects",
  "mobile.projects.label": "Cloud projects",
  "mobile.projects.title": "Your schematics, on the go",
  "mobile.projects.description":
    "Open a synced cloud project to view and edit it here. Changes save automatically.",
  "mobile.projects.sign_in": "Sign in to view cloud projects",
  "mobile.projects.sign_in_reason":
    "Sign in to view your cloud projects on this phone.",
  "mobile.projects.close": "Close",
  "mobile.projects.loading": "Loading cloud projects…",
  "mobile.projects.list_error": "Couldn't load your cloud projects.",
  "mobile.projects.open_error": "Couldn't open that project. Try again.",
  "mobile.projects.retry": "Try again",
  "mobile.projects.empty_title": "No cloud projects yet",
  "mobile.projects.empty_body":
    "Save a project to the cloud in Crimp and it will appear here on every device.",
  "mobile.projects.current_aria": "Refresh {name} from the cloud",
  "mobile.projects.open_aria": "Open {name}",
  "mobile.projects.viewing": "Viewing",
  "mobile.projects.read_only": "Read-only",
  "mobile.projects.editable": "Editable",
  "mobile.projects.opening": "Opening…",
  "mobile.projects.updated": "Updated {date}",
  "mobile.storage_error.title": "Editing is unavailable",
  "mobile.storage_error.body":
    "Crimp couldn't open storage on this device. Reload before editing so your work isn't lost.",
  "starter.describe_title": "Describe your system",
  "starter.describe_body":
    "Tell us what you've got in a sentence. We'll draw it and size every cable.",
  "starter.describe_cta": "Describe it",
  "tools.describe_system": "Describe your system…",
  "tools.change_system": "Change your system…",

  // ── Welcome modal ──────────────────────────────────────
  "welcome.eyebrow": "Welcome",
  "welcome.title": "Welcome to Crimp",
  "welcome.body":
    "Drag components onto the canvas, connect them with cables, and Crimp suggests wire sizes and fuse ratings for each run.",
  "welcome.terms_prefix": "By choosing Agree you accept the ",
  "welcome.terms_link": "terms of use",
  "welcome.terms_mid": " and acknowledge the ",
  "welcome.privacy_link": "privacy policy",
  "welcome.terms_suffix": ".",
  "welcome.cta": "Agree and start",

  // ── Starter / seed picker ──────────────────────────────
  "starter.label": "Start a project",
  "starter.new_title": "New project",
  "starter.new_description": "How would you like to start?",
  "starter.examples": "Use an example",
  "starter.assistant_body": "Describe your system and let the Assistant create it.",
  "starter.back": "Back",
  "starter.circuits.title": "Circuit examples",
  "starter.circuits.description": "Editable connection examples. Open System health to see checks and remaining assumptions. Verify actual equipment, cable routes and protection before installation.",
  "starter.circuit.ac0.name": "120 V AC branches and GFCI",
  "starter.circuit.ac0.description": "Shore main protection, three branch circuits and an earthed GFCI outlet.",
  "starter.circuit.ac1.name": "230 V AC branches and sockets",
  "starter.circuit.ac1.description": "Shore leakage protection, three branch circuits and earthed sockets.",
  "starter.circuit.ac2.name": "GFCI LINE, LOAD and socket",
  "starter.circuit.ac2.description": "Supply terminals, protected downstream outlet and local plug connections.",
  "starter.circuit.ac3.name": "120 V AC source selector",
  "starter.circuit.ac3.description": "Manual shore, inverter and generator selection with separate line and neutral poles.",
  "starter.circuit.ac4.name": "230 V AC source selector",
  "starter.circuit.ac4.description": "Three protected source boundaries and continuous protective earth.",
  "starter.circuit.spdt.name": "SPDT center-off switch",
  "starter.circuit.spdt.description": "One common contact selects either of two separately returned loads.",
  "starter.circuit.dpdt.name": "DPDT center-off switch",
  "starter.circuit.dpdt.description": "Two independent poles switch the positive and return paths together.",
  "starter.circuit.momentary.name": "Momentary push button",
  "starter.circuit.momentary.description": "Compare held and released contact states in Simulate.",
  "starter.circuit.relay.name": "Five-pin changeover relay",
  "starter.circuit.relay.description": "Separate coil wiring, normally closed and normally open loads. Select relay state manually.",
  "starter.circuit.pv.name": "Three-string PV combiner",
  "starter.circuit.pv.description": "Separate gPV string fuses, positive and negative buses, isolator and MPPT.",
  "starter.title": "Start from an example",
  "starter.description":
    "Choose an example, then make it yours.",
  "starter.blank": "Start blank",
  "starter.stat_system": "System",
  "starter.stat_bank": "Bank",
  "starter.stat_solar": "Solar",
  "starter.stat_parts": "Parts",
  "seed.lithium_boat.name": "Sailboat — lithium refit",
  "seed.lithium_boat.desc":
    "Older boat converted to LiFePO4: alternator charges a lithium house bank through a DC-DC, keeping the original start battery.",
  "seed.lead_boat.name": "Classic boat — lead-acid",
  "seed.lead_boat.desc":
    "Traditional setup: start and AGM house banks combined by an ACR off the alternator, topped up by a shore charger.",
  "seed.van.name": "Camper van — solar + DC-DC",
  "seed.van.desc":
    "Typical van build: roof solar via MPPT and a DC-DC from the vehicle battery feed a lithium bank, fridge, lights and a small inverter.",
  "seed.advanced.name": "Liveaboard — inverter + solar",
  "seed.advanced.desc":
    "Advanced build: a MultiPlus inverter/charger on shore power, a big solar array and an alternator DC-DC feed a 300 Ah bank.",

  // ── Auth modal ─────────────────────────────────────────
  "auth.label": "Account",
  "auth.title_signin": "Sign in to Crimp",
  "auth.title_signup": "Create your account",
  "auth.unconfigured_title": "Cloud sync isn't configured",
  "auth.unconfigured_body":
    "Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local, then reload.",
  "auth.ok": "OK",
  "auth.cancel": "Cancel",
  "auth.send_magic_link": "Send magic link",
  "auth.create_account": "Create account",
  "auth.sign_in": "Sign in",
  "auth.tab_magic": "Magic link",
  "auth.tab_password": "Password",
  "auth.tab_signup": "Sign up",
  "auth.email": "Email",
  "auth.email_placeholder": "you@example.com",
  "auth.password": "Password",
  "auth.or": "or",
  "auth.continue_google": "Continue with Google",
  "auth.oauth_failed": "Could not start sign-in. Try again.",
  "auth.check_inbox_signin": "Check your inbox for the sign-in link.",
  "auth.check_inbox_confirm": "Check your inbox to confirm your email.",

  // ── AI generation limits + end-of-allowance screens ────
  // Never hardcode an allowance number: `claim_ai_generation` owns them. Interpolate.
  "ai.wall.welcome_title": "Let’s build it together",
  "ai.wall.welcome_body": "Create a free account for a little AI help with parts and your system.",
  "ai.wall.anonymous": "Create a free account or sign in to use your free AI credits.",
  "ai.paywall.title": "That's today's free designs",
  "ai.paywall.subtitle": "{used} of {limit} used. They free up again as the older ones roll off.",
  "ai.paywall.subtitle_plain": "They free up again as the older ones roll off.",
  "ai.paywall.licence": "Crimp Lifetime · pay once",
  "ai.paywall.price": "{price} {currency}",
  "ai.paywall.body":
    "This is where a licence would normally come in. It isn't on sale — Crimp is free while we work out how many people want one. Would you have paid for this?",
  "ai.paywall.free_note":
    "Nothing is charged and nothing is locked either way. Your designs come back on their own.",
  "ai.paywall.interested": "I'd pay for this",
  "ai.paywall.decline": "Not for me",
  "ai.paywall.thanks":
    "Noted — thank you. Nothing to pay, and nothing changes about your account; you just made the case for building it.",
  "ai.paywall.done": "Close",
  "ai.usage.remaining": "{remaining} of {limit} free designs left",
  "ai.auto_add.title": "Auto add",
  "ai.auto_add.on": "On",
  "ai.auto_add.loading": "Checking auto add…",
  "ai.auto_add.enable": "Enable auto add",
  "ai.auto_add.subscription": "Available with an active monthly subscription.",
  "ai.auto_add.unavailable": "Auto add is temporarily unavailable.",
  "ai.auto_add.threshold": "Add {credits} credits when fewer than {threshold} remain.",
  "ai.auto_add.monthly_limit": "Monthly limit",
  "ai.auto_add.consent": "Charge {price} per top-up, up to {limit}/month including tax, using your subscription payment method. Turn off anytime.",
  "ai.auto_add.pending": "A top-up is processing. Your draft is saved.",
  "ai.auto_add.pending_disable": "The current top-up may still complete.",
  "ai.auto_add.price_changed": "Price changed. Save to continue.",
  "ai.auto_add.limit_reached": "Monthly auto-add limit reached. You can still buy credits.",
  "ai.auto_add.saving": "Saving…",
  "ai.auto_add.save": "Save",
  "ai.auto_add.saved": "Saved.",
  "ai.auto_add.error": "Couldn’t update auto add. Try again.",
  "ai.credits.title": "Add credits",
  "ai.credits.menu": "Credits",
  "ai.credits.remaining": "{remaining} credits",
  "ai.credits.purchased": "Purchased credits",
  "ai.credits.remaining_one": "{remaining} credit",
  "ai.credits.shared": "One balance for the assistant and component or system generation.",
  "ai.credits.balance": "{remaining} credits available",
  "ai.credits.pack": "{credits} credits",
  "ai.credits.costs": "AI requests are charged by usage.",
  "ai.credits.terms": "One-time purchase. Purchased credits never expire.",
  "ai.credits.included_first": "Credits that come with your plan are used first; credits you buy never expire.",
  // Shown to a signed-in caller without a licence: the wallet is for holders,
  // so the only action is Crimp Lifetime itself. Nothing here is a balance
  // check. The Pass variants are dormant (sold only when the server says so);
  // the "ended" pair acknowledges a Pass that lapsed inside its renew window.
  "ai.credits.lifetime_title": "AI credits come with Crimp Lifetime",
  "ai.credits.lifetime_body": "Crimp Lifetime includes {credits} AI credits, along with any size system, exports and the Blueprint. Pay once — it is yours for the lifetime of the product — and top up whenever you need more.",
  "ai.credits.get_lifetime": "Get Crimp Lifetime",
  "ai.credits.pass_ended_title": "Your Pass has ended",
  "ai.credits.pass_ended_body": "Its included credits ended with it. Crimp Lifetime brings {credits} AI credits back, along with any size system, exports and the Blueprint — pay once, yours for the lifetime of the product.",
  "ai.credits.pass_title": "AI credits come with a Pass",
  "ai.credits.pass_body": "The Crimp Pass includes {credits} AI credits for 12 months, along with any size system, exports and the Blueprint. One payment, no automatic renewal.",
  "ai.credits.get_pass": "Get a Pass",
  // The Canada pilot's version: its members are sold the monthly subscription.
  "ai.credits.subscription_title": "AI credits come with a subscription",
  "ai.credits.subscription_body": "The monthly subscription includes {credits} AI credits each month, along with any size system, exports and the Blueprint. Cancel any time.",
  "ai.credits.subscribe": "Subscribe",
  "ai.credits.taxes": "Taxes shown at checkout.",
  "ai.credits.details": "How credits work",
  "ai.credits.failed_requests": "Technical failures use no credits.",
  "ai.credits.buy": "Buy credits",
  "ai.credits.opening": "Opening checkout…",
  "ai.credits.checkout_open": "Complete your purchase in checkout.",
  "ai.credits.ready": "Credits added. You’re ready to go.",
  "ai.credits.continue": "Continue",
  "ai.credits.waiting": "Payment received. Updating your balance…",
  "ai.credits.error": "Checkout couldn’t open. Please try again.",
  "ai.credits.balance_error": "Couldn’t refresh your balance. Try again.",
  "ai.credits.unavailable": "Credit purchases are temporarily unavailable.",
  "ai.credits.retry": "Try again",
  "ai.credits.refresh": "Refresh balance",
  "ai.credits.generation": "{remaining} credits left. Up to {max} credits per request",
  "ai.credits.free": "Free AI credits with an account.",
  "ai.credits.sign_in": "Sign in for free AI credits",
  "ai.credits.first_free": "Free AI credits with an account",
  "ai.credits.usage": "Pay by usage · Up to {max} credits per request",
  "ai.denied.wall": "Sign in to add AI credits.",
  "ai.denied.quota": "Add AI credits to generate another component or system.",
  "ai.denied.rate_limited": "That's a lot of designs at once. Give it a minute and try again.",
  "ai.denied.capped":
    "The design tool is busy right now. Everything else on this page still works.",
  "ai.denied.turnstile": "We couldn't verify that request. Reload the page and try again.",
  "ai.denied.invalid":
    "Describe the system in a sentence or two — that description was empty or too long.",

  // ── Share modal ────────────────────────────────────────
  "share.label": "Share",
  "share.title": "Share this project",
  "share.description":
    "Mint a link per access tier. Anyone with the link can use it — keep editor links private.",
  "share.done": "Done",
  "share.save_first": "Save the project first to enable sharing.",
  "share.loading": "Loading…",
  "share.role.viewer": "Viewer",
  "share.role.viewer_sub": "Can view, can't edit or comment.",
  "share.role.commenter": "Commenter",
  "share.role.commenter_sub": "Can view and leave comments.",
  "share.role.editor": "Editor",
  "share.role.editor_sub": "Can view, comment, and edit the canvas.",
  "share.create_link": "Create {label} link",
  "share.copy": "Copy link",
  "share.copied": "Copied",
  "share.show_qr": "Show QR code",
  "share.hide_qr": "Hide QR code",
  "share.regenerate": "Regenerate link (invalidates the old one)",
  "share.revoke": "Revoke link",
  "share.qr_print_blurb":
    "Print this QR and keep it in the boat — anyone who scans it can view the wiring diagram.",
  "share.qr_scan_caption": "Scan to view the wiring diagram",
  "share.print": "Print",
  "share.regenerate_short": "Regenerate",
  "share.revoke_short": "Revoke",
  "share.access.label": "Direct access links",
  "share.access.sub":
    "Mint a private link for someone specific. These aren't listed in the gallery.",

  // ── Open for feedback (in share modal) ─────────────────
  "share.publish.section": "Feedback",
  "share.publish.heading": "Get a second pair of eyes",
  "share.publish.subtitle":
    "Open the build to feedback, then drop the link in your sailing group, your subreddit, or wherever your community already is. Strangers can leave comments pinned to specific cables — a good way to catch something before you crimp it.",
  "share.publish.toggle_on": "Open for feedback — anyone with the link can comment",
  "share.publish.toggle_off": "Private — only people with a share link can see it",
  "share.publish.segment_private": "Private",
  "share.publish.segment_public": "Open for feedback",
  "share.publish.views_label": "views · last 7 days",
  "share.publish.views_direct": "direct",
  "share.publish.views_empty":
    "Comments will show up on the public page, anchored to the cable or component they're about.",
  "share.publish.forks_link": "{forks} of your design",
  "share.publish.forks_singular": "fork",
  "share.publish.forks_plural": "forks",
  "share.publish.public_link": "Link to share",
  "share.publish.description_label": "What you'd like reviewed (optional)",
  "share.publish.description_placeholder":
    "e.g. Second-guessing the windlass leg — is 35 mm² over 4 m enough at the 90 A inrush?",
  "share.publish.unpublish": "Unpublish",
  "share.publish.publish": "Publish",
  "share.publish.saving": "Saving…",
  "share.publish.anonymous_label": "Publish anonymously",
  "share.publish.anonymous_hint":
    "Hide your name. The build shows up as Anonymous and the share card drops the byline.",

  // ── Community gallery ──────────────────────────────────
  "topbar.community": "Community",
  "topbar.community_title": "Browse projects published by other users",
  "community.label": "Community",
  "community.eyebrow_vol": "Vol. 01",
  "community.count_label": "builds",
  "community.title": "Builds in the wild",
  "community.description":
    "Wiring diagrams other Crimp users have made public. Open one to read the layout, or fork it as a starting point for your own.",
  "community.close": "Close",
  "community.loading": "Loading…",
  "community.empty":
    "No public projects yet. Publish yours from the Share menu and be the first.",
  "community.empty_headline": "The dock is quiet.",
  "community.empty_subline":
    "No builds have been published yet. Publish yours from the Share menu and be the first one in.",
  "community.empty_filtered":
    "No builds match this filter yet. Try a different boat type.",
  "community.owner_title": "Builds by {name}",
  "community.owner_description":
    "Public schematics {name} has shared with the community. Open one to read, or fork it as a starting point.",
  "community.error": "Couldn't load community projects: {message}",
  "community.fork_count": "{count} forks",
  "community.comment_count": "{count} comments",
  "community.published_on": "Published {date}",
  "community.open": "Open",
  "community.fork_of": "Forked from another project",
  "community.by": "by {name}",
  "community.sort.label": "Sort",
  "community.sort.aria": "Sort builds",
  "community.sort.newest": "Newest",
  "community.sort.likes": "Most loved",
  "community.sort.forks": "Most forked",
  "community.sort.comments": "Most discussed",
  "community.like.title_set": "Like this build",
  "community.like.title_unset": "Unlike",
  "community.activity_title": "Recent comment activity ({date})",
  "community.filter.label": "Filter",
  "community.filter.aria": "Filter by boat type",
  "community.filter.all": "All",

  // ── Public-view banner (when viewing a public project) ─
  "public_banner.label": "Public — read only",
  "public_banner.fork": "Fork to edit",
  "public_banner.fork_title": "Save a copy of this project to your account",
  "public_banner.sign_in_to_fork": "Sign in to fork",
  "public_banner.forking": "Forking…",
  "public_banner.fork_failed": "Fork failed: {message}",
  "public_banner.fork_name_prompt": "Name your fork:",
  "public_banner.fork_suffix": "(fork)",
  "public_banner.back_to_community": "Browse community",
  "public_banner.download_image": "Download image",
  "public_banner.download_image_title":
    "Save this diagram as a PNG — great for posting in groups that prefer image attachments.",
  "public_banner.like_aria": "Like this build",
  "public_banner.like_title": "Like this build",
  "public_banner.unlike_title": "Remove your like",
  "public_banner.share": "Share",
  "public_banner.share_title": "Share this build to a group or forum",
  "public_banner.forked_from": "from",
  "public_banner.forked_from_title": "Open the original design by {name}",

  "auth_reason.fork": "Sign in to fork this project.",
  "auth_reason.like": "Sign in to like this build.",

  // ── Lineage modal ──────────────────────────────────────
  "lineage.label": "Lineage",
  "lineage.title": "Built off your design",
  "lineage.description":
    "Public projects that started as a fork of this build. Open one to see how it diverged.",
  "lineage.close": "Close",
  "lineage.loading": "Loading…",
  "lineage.parent_label": "Forked from",
  "lineage.children_label": "Forks of this build",
  "lineage.children_empty":
    "No public forks yet. When someone forks this design and publishes it, you'll see them here.",
  "lineage.count": "{count} total",

  // ── Share composer ─────────────────────────────────────
  "share_composer.label": "Share",
  "share_composer.title": "Share this build",
  "share_composer.description":
    "A ready-to-paste post for forums, Facebook groups, or Reddit — plus the canonical link and a clean image.",
  "share_composer.close": "Close",
  "share_composer.copied": "Copied",
  "share_composer.copy_text": "Copy",
  "share_composer.post_text_label": "Post text",
  "share_composer.downloading": "Preparing…",
  "share_composer.action_image": "Image",
  "share_composer.action_image_sub": "Download the schematic as a PNG.",
  "share_composer.action_link": "Link",
  "share_composer.action_link_sub": "Copy the public URL.",
  "share_composer.action_text": "Post text",
  "share_composer.action_text_sub": "Title, author, link — ready to paste.",
  "share_composer.mod_hint":
    "Tip — most boating and van groups prefer image attachments to external links. Post the image natively, then drop the link in a comment.",
  "share_composer.compose_on": "Compose on",

  // ── Project picker modal ───────────────────────────────
  "picker.label": "Projects",
  "picker.title": "Your projects",
  "picker.description": "Open a saved project, rename, duplicate, or delete.",
  "picker.close": "Close",
  "picker.new_project": "New project",
  "picker.empty": "No saved projects yet. Click {strong} to start one.",
  "picker.empty_strong": "New project",
  "picker.open_badge": "open",
  "picker.delete_confirm": 'Delete "{name}"? This cannot be undone.',
  "picker.rename": "Rename",
  "picker.duplicate": "Duplicate",
  "picker.delete": "Delete",
  "picker.local_heading": "On this device",
  "picker.cloud_heading": "In the cloud",
  "picker.local_empty": "No local projects yet.",
  "picker.cloud_sign_in": "Sign in to see cloud projects.",
  // Shown to a Lifetime or Pass holder, whose cloud projects are capped; the
  // numbers are the server's (`cloud_project_usage()`), never a client count.
  "picker.cloud_usage": "{used} of {limit} cloud projects",
  "picker.cloud_usage_full": "Every slot is in use. Deleting a cloud project you no longer need frees one.",

  // ── Admin project picker (cross-user) ──────────────────
  "admin_picker.label": "All projects",
  "admin_picker.title": "All projects (admin)",
  "admin_picker.description":
    "Browse projects across all users. Opens read-only — saves still require ownership.",
  "admin_picker.search_placeholder": "Search by name, owner, or email…",
  "admin_picker.empty": "No projects exist yet.",
  "admin_picker.public_badge": "public",
  "admin_picker.owner_unknown": "Unknown owner",

  // ── Cable length modal ─────────────────────────────────
  "cable_modal.eyebrow": "New cable",
  "cable_modal.distance_label": "Distance from {from} to {to}",
  "cable_modal.zone_a": "Zone A",
  "cable_modal.zone_b": "Zone B",
  "cable_modal.distance_placeholder": "e.g. 3",
  "cable_modal.metres": "metres",
  "cable_modal.feet": "feet",
  "cable_modal.distance_saved":
    "Saved for these zones — cable defaults to distance + {slack} slack.",
  "cable_modal.distance_will_save":
    "We'll save this for future cables between these zones. Cable length defaults to distance + {slack} slack.",
  "cable_modal.length_label": "Cable length (one-way)",
  "cable_modal.auto_size_cable": "Auto-size cable",
  "cable_modal.pick_manually": "Pick the size manually.",
  "cable_modal.add_fuse": "Add fuse",
  "cable_modal.negative_no_fuse": "Negative path — no fuse.",
  "cable_modal.fuse_to_slot": "Sets the slot's fuse rating.",
  "cable_modal.inline_fuse_holder": "{rating} A inline fuse holder.",
  "cable_modal.skip_fuse": "Skip fuse on this cable.",
  "cable_modal.wire": "Wire",
  "cable_modal.fuse": "Fuse",
  "cable_modal.cable_amps": "Cable amps",
  "cable_modal.estimate": "{amp} est.",
  "cable_modal.no_fuse": "no fuse",
  "cable_modal.no_known_load": "no known load",
  "cable_modal.no_load_no_fuse":
    "No known load on this run — added without a fuse.",
  "cable_modal.vdrop": "V-drop",
  "cable_modal.cancel": "Cancel",
  "cable_modal.add_cable_fuse": "Add cable + fuse",
  "cable_modal.add_cable": "Add cable",
  "cable_modal.fuse_explainer_negative":
    "Negative / ground conductor — fuses protect the positive side only (ABYC).",
  "cable_modal.fuse_explainer_slot":
    "Cable connects to a fuse block — the slot's fuse rating will be set.",
  "cable_modal.fuse_explainer_inline":
    "The fuse rating will be carried by the cable itself.",

  // ── Inspector — system health ──────────────────────────
  "health.system": "System",
  "health.empty_title": "Drop in a battery to begin",
  "health.empty_body":
    "As you wire up the system, this panel flags sizing problems, fusing gaps, and other issues worth a second look.",
  "health.empty_demo_lead": "New here?",
  "health.empty_demo_button": "View a demo project",
  "health.severity.critical": "Critical",
  "health.severity.warning": "Warning",
  "health.severity.info": "Info",
  "health.severity_chip_title": "{count} {label}",
  "health.energy_budget": "Energy budget",
  "health.battery_usable": "Battery (usable)",
  "health.reserve_hint": "Reserve {pct}%",
  "health.daily_use": "Daily use",
  "health.daily_harvest": "Daily harvest",
  "health.harvest_hint": "Solar {solar} · Alt {alternator} · Shore {shore} Wh",
  "health.net_daily": "Net daily",
  "health.net_now": "Net power now",
  "health.autonomy": "Autonomy (from full)",
  "health.autonomy_hint":
    "At today's daily consumption, how long until reserve floor.",
  "health.time_to_empty": "Time to empty",
  "health.time_to_empty_hint":
    "From current SoC down to reserve, if the current state holds.",
  "health.time_to_full": "Time to full",
  "health.time_to_full_hint":
    "From current SoC up to full, if the current state holds.",
  "health.issues_all": "Issues & suggestions",
  "health.issues_filter": "{label}s",
  "health.fix_all": "Fix all ({count})",
  "health.fix_all_title": "Apply {count} one-click fixes",
  "health.healthy": "Nothing to flag — system looks healthy.",
  "health.no_at_severity": "No issues at this severity.",
  "health.top_consumers": "Top consumers",
  "health.simulate_nudge_eyebrow": "Scenario",
  "health.simulate_nudge_body": "Tweak loads, sun, and runtime to see how the system holds up.",
  "health.simulate_nudge_cta": "Open Simulate",
  "inspector.tab_edit": "Edit",
  "inspector.tabs_aria": "Inspector mode",
  "simulate.scenario": "Scenario",
  "simulate.reset": "Reset",
  "simulate.load_on": "On",
  "simulate.load_off": "Off",
  "simulate.reset_scenario_title": "Reset scenario to defaults",
  "simulate.sun_hours": "Peak sun",
  "simulate.engine_hours": "Engine running",
  "simulate.shore_hours": "Shore power",
  "simulate.reserve_floor": "Reserve floor",
  "simulate.preset_default": "Default",
  "simulate.preset_custom": "Custom",
  "simulate.no_saved_scenarios": "No saved scenarios yet",
  "simulate.delete_saved": "Delete",
  "simulate.save_current": "Save current as…",
  "simulate.save_placeholder": "e.g. Anchored weekend",
  "simulate.reset_to_default": "Reset to default",
  "simulate.show_all": "Show all {count}",
  "simulate.clear_overrides": "Clear ({count})",
  "simulate.clear_overrides_title": "Clear {count} per-load overrides",
  "simulate.harvest_none": "none on canvas",
  "simulate.solar_conditions": "Solar conditions",
  "simulate.solar_sunny": "Sunny",
  "simulate.solar_cloudy": "Cloudy",
  "simulate.solar_off": "Dark",
  "simulate.solar_mixed": "Mixed",
  "simulate.close_sheet_aria": "Close scenario sheet",
  "simulate.stop": "Stop simulation",
  "simulate.batteries": "Batteries",
  "simulate.show_on_canvas": "Show on canvas",
  "simulate.combiner.combined": "Combined",
  "simulate.combiner.isolated": "Isolated",
  "simulate.combiner.help": "Manual contact state; automatic sensing and control signals are not simulated.",
  "simulate.orion.on": "Auto",
  "simulate.orion.forced": "Forced",
  "simulate.orion.off": "Off",
  "simulate.orion.status_running": "Engine detected",
  "simulate.orion.status_waiting": "Waiting for engine",
  "simulate.orion.status_unresolved": "Engine connection unverified",
  "simulate.orion.status_forced": "Engine detection disabled",
  "simulate.ac.supplied": "AC source on",
  "simulate.ac.off": "No active AC source",
  "simulate.ac.unverified": "AC supply unverified",
  "simulate.ac.nominal": "Nominal {voltage} V",
  "simulate.ac.help": "Source and contact state only; voltage, current and protective trips are not simulated.",
  "simulate.ac.main": "Main",
  "simulate.ac.branch": "Branch {number}",
  "simulate.ac.on_label": "On",
  "simulate.ac.off_label": "Off",
  "simulate.ac.unknown_label": "Unknown",
  "simulate.ac.input": "Input: {status}",
  "simulate.ac.output": "Output: {status}",
  "simulate.orion.help": "Auto follows an alternator connected to both input rails. Voltage thresholds, delays, external control signals and input voltage lock-out are not simulated.",
  "simulate.orion.forced_help": "Simulates engine shutdown detection disabled. Charging can draw from the starter battery while the engine is off.",
  "health.fix_prefix": "Fix · ",
  "health.show_on_canvas": "Show on canvas →",
  "health.tip_drop_component":
    "Drag a component from the palette onto the canvas to start.",
  "health.tip_wire":
    "Drag from one terminal to another to wire components together.",
  "health.tip_zone_intro":
    "Drop a {strong} from the palette to group components by physical area — cable lengths are derived from zone-to-zone distance.",
  "health.tip_zone_strong": "Zone",
  "health.tip_shortcuts":
    "{undo} undo · {fit} fit view · {del} remove selected",

  // ── Inspector — component detail ───────────────────────
  "rv_panel.main": "Main L1 + L2",
  "rv_panel.branches": "Branches per leg",
  "rv_panel.resize_help": "Disconnect the last branches before reducing the count. Existing connections keep their circuit numbers.",
  "rv_panel.switch_help": "Highlighted = closed contact. Main switches both hot legs.",
  "rv_panel.limits": "Manual contacts only. Automatic trips, leakage protection, phase balance and AC load flow are not simulated. Bus terminals are after the main; connect loads to branch outputs.",
  "inspector.custom": "Custom",
  "inspector.custom_label": "Custom label",
  "inspector.swap_part": "Part",
  "inspector.swap_part_hint":
    "Swap this for another part. Every cable stays wired to it.",
  "inspector.swap_part_estimated":
    "We picked this part for you. Swap it for what you actually have — every cable stays wired to it.",
  "inspector.specs": "Specs",
  "inspector.reset_specs": "Reset",
  "inspector.reset_specs_title": "Revert to catalog defaults",
  "inspector.customize": "Customize",
  "inspector.customize_title":
    "Edit terminals and specs. Reversible — use Reset to restore catalog defaults.",
  "inspector.custom_callout.title_from": "Custom copy of {name}",
  "inspector.custom_callout.title_scratch": "Custom component",
  "inspector.custom_callout.body_from":
    "Edits to specs, terminals, and dimensions stay on this instance — other copies of {name} are unchanged.",
  "inspector.custom_callout.body_scratch":
    "Edit specs, terminals, and dimensions to match the part you have.",
  "inspector.custom_callout.revert": "Revert to catalog",
  "inspector.reset_field": "Reset to catalog",
  "inspector.identity": "Component",
  "inspector.identity.name": "Name",
  "inspector.identity.name_placeholder": "e.g. MD11C engine block",
  "inspector.identity.category": "Category",
  "inspector.identity.width": "Width",
  "inspector.identity.height": "Height",
  "inspector.identity.photo": "Photo",
  "inspector.identity.photo_add": "Add a photo",
  "inspector.identity.photo_remove": "Remove",
  "inspector.identity.photo_failed": "Upload failed — try again.",
  "inspector.identity.photo_signin": "Sign in to add a photo to this component.",
  "inspector.save_to_library": "Save to library",
  "inspector.save_to_library_title":
    "Save this custom component to your personal library on this device — it will appear in the palette so you can reuse it.",
  "inspector.saved_to_library": "Saved",
  "inspector.terminal.add": "Add terminal",
  "inspector.terminal.default_label": "T",
  "inspector.terminal.delete_title": "Remove terminal",
  "inspector.terminal.delete_blocked":
    "Disconnect {count} cable(s) first to remove this terminal.",
  "inspector.terminal.label_placeholder": "Label",
  "inspector.terminal.label_for": "Label for terminal {terminal}",
  "inspector.terminal.polarity": "Polarity",
  "inspector.terminal.direction": "Direction",
  "inspector.terminal.side": "Side",
  "inspector.terminal.max_amp": "Terminal limit (A)",
  "inspector.terminal.rating_help": "Terminal current limits (A), separate from device draw and battery capacity (Ah).",
  "inspector.terminal.offset": "Position",
  "inspector.terminal.color": "Color",
  "inspector.terminal.polarity.positive": "Positive",
  "inspector.terminal.polarity.negative": "Negative",
  "inspector.terminal.polarity.ground": "Ground",
  "inspector.terminal.polarity.signal": "Signal",
  "inspector.terminal.polarity.neutral": "Neutral",
  "inspector.terminal.direction.input": "Input",
  "inspector.terminal.direction.output": "Output",
  "inspector.terminal.direction.bidi": "Both",
  "inspector.terminal.side.left": "Left",
  "inspector.terminal.side.right": "Right",
  "inspector.terminal.side.top": "Top",
  "inspector.terminal.side.bottom": "Bottom",
  "inspector.terminal.expand": "Show terminal details",
  "inspector.terminal.collapse": "Hide terminal details",
  "inspector.advanced": "Advanced",
  "inspector.spec.capacity": "Capacity",
  "inspector.spec.voltage": "Voltage",
  "inspector.spec.vmp": "Vmp",
  "inspector.spec.vmp_hint":
    "Vmp — voltage at the panel's max-power point. Different from system voltage (V): a 12 V panel typically has Vmp ≈ 18 V.",
  "inspector.spec.power": "Power",
  "inspector.spec.output": "Output",
  "inspector.spec.imp": "Imp",
  "inspector.spec.imp_hint":
    "Imp — current at the max-power point. Imp ≈ Power ÷ Vmp (e.g. 100 W ÷ 18 V ≈ 5.6 A).",
  "inspector.spec.continuous_draw": "Continuous draw",
  "inspector.spec.peak_surge": "Peak surge",
  "inspector.spec.peak_sun_hours": "Peak sun hours",
  "inspector.spec.typical_charge": "Typical charge",
  "inspector.solar_blurb":
    'A "12 V" solar panel actually produces around 18 V at its max-power point ({vmp}). The current at that point is {imp}, not Power ÷ 12 V — that\'s why a 100 W panel gives ~5.6 A, not 8.3 A. The MPPT charger converts Vmp down to battery voltage.',
  "inspector.load_mode": "Load mode",
  "inspector.energy_use": "Energy use",
  "inspector.always_on": "Always on (24/7)",
  "inspector.hours_per_day": "Hours / day",
  "inspector.daily_energy": "Daily energy",
  "inspector.draw_when_on": "Draw when on",
  "inspector.outputs": "Outputs (fuse slots)",
  "inspector.terminals": "Terminals",
  "inspector.fuse": "Fuse",
  "inspector.fuse_rating": "Rating",
  "inspector.breaker.title": "Circuit breaker",
  "inspector.breaker.setup": "Set up as circuit breaker",
  "inspector.breaker.input": "Supply-side terminal",
  "inspector.breaker.rating": "Breaker rating (A)",
  "inspector.breaker.voltage": "Maximum DC voltage (V, optional)",
  "inspector.breaker.voltage_short": "Maximum voltage",
  "inspector.breaker.hint": "Use the installed rating. Tripping is controlled manually in Simulate.",
  "inspector.electrical.apply": "Apply settings",
  "inspector.electrical.edit": "Edit settings",
  "inspector.dc_supply.title": "DC power supply",
  "inspector.dc_supply.setup": "Set up as DC power supply",
  "inspector.dc_supply.input": "Nominal input (V)",
  "inspector.dc_supply.output": "Output voltage (V)",
  "inspector.dc_supply.current": "Output limit (A)",
  "inspector.dc_supply.efficiency": "Efficiency (%, optional)",
  "inspector.dc_supply.hint": "Enter the actual output setting. Blank efficiency uses a 90% estimate.",
  "inspector.dc_supply.range": "Output range (V, optional)",
  "inspector.dc_supply.range_display": "Output range",
  "inspector.dc_supply.range_min": "Min (V)",
  "inspector.dc_supply.range_max": "Max (V)",
  "inspector.dc_supply.range_hint": "Enter both limits and keep output voltage in range.",
  "inspector.custom_fuse.setup": "Set up as DC fuse",
  "inspector.custom_fuse.hint": "Enter the installed fuse’s type and rating. Terminal ampacity is not a fuse rating.",
  "inspector.custom_fuse.family": "Fuse type",
  "inspector.custom_fuse.rating": "Fuse rating (A)",
  "inspector.custom_fuse.apply": "Apply fuse settings",
  "inspector.custom_fuse.title": "Fuse protection",
  "inspector.custom_fuse.unconfigured": "If this component is a fuse, set its type and rating so Crimp can check protection.",
  "inspector.custom_fuse.cancel": "Cancel",
  "inspector.custom_dc_fuse_block.title": "DC fuse block",
  "inspector.custom_dc_fuse_block.setup": "Set up as DC fuse block",
  "inspector.custom_dc_fuse_block.connections": "Connect {input} to {count} fused outputs.",
  "inspector.custom_dc_fuse_block.ratings": "Set each installed fuse below.",
  "inspector.custom_dc_fuse_block.slot_rating": "Fuse rating for {label}",
  "inspector.shunt.bank": "Connected battery bank",
  "inspector.shunt.current": "Simulated current",
  "inspector.shunt.discharging": "{amps} discharging",
  "inspector.shunt.charging": "{amps} charging",
  "inspector.shunt.idle": "0 A",
  "inspector.shunt.unavailable": "Unavailable",
  "inspector.shunt.simulate_hint": "Switch to Simulate to see current.",
  "inspector.shunt.estimate": "Estimated current through this shunt.",
  "inspector.battery_fuse.title": "Battery-terminal fuse",
  "inspector.battery_fuse.blurb":
    "MRBF / MEGA / ANL / Class T bolted directly onto the battery+ post. Every cable leaving the post inherits this rating, and ABYC E-11 §11.10 (fuse within 175 mm) is satisfied automatically.",
  "inspector.battery_fuse.rating_label": "Rating",
  "inspector.battery_fuse.rating_none": "No fuse on terminal",
  "inspector.battery_fuse.type_label": "Type (for AIC check)",
  "inspector.battery_fuse.type_unknown": "Unknown",
  "inspector.rotate": "Rotate 90°",
  "inspector.rotate_title": "Rotate body 90° clockwise",
  "inspector.remove_from_zone": "Remove from zone",
  "inspector.remove_from_zone_title":
    "Remove this component from its zone (it stays where it is on the canvas)",
  "inspector.delete_component": "Delete component",

  // ── Inspector — zone detail ────────────────────────────
  "zone.eyebrow": "Zone",
  "zone.fallback_label": "Zone",
  "zone.label_field": "Label",
  "zone.label_placeholder": "e.g. Engine bay",
  "zone.engine_bay": "Engine-bay zone",
  "zone.engine_bay_hint":
    "Cables routed entirely inside this zone are sized for 50 °C ambient (ABYC E-11 Table V, 105 °C wire derated to 0.82).",
  "zone.distances": "Distance to zones",
  "zone.no_other": "No other zones yet.",
  "zone.distances_hint":
    "Sets the length of new cables between the two zones — and moves the ones already running between them by the same difference.",
  "zone.delete": "Delete zone",
  "zone.size": "{w} × {h} px",

  // ── Inspector — junction detail ────────────────────────
  "junction.eyebrow": "Wire junction",
  "junction.title": "Splice point",
  "junction.body":
    "A junction is a wire splice — any number of cables can meet here. Drag another terminal onto it to add another cable.",
  "junction.connections": "Connections",
  "junction.cables_connected": "Cables connected",
  "junction.delete": "Delete junction",
  "junction.convert_to_busbar": "Convert to busbar",
  "junction.convert_pos": "+ Bus",
  "junction.convert_pos_title": "Replace this junction with a positive busbar.",
  "junction.convert_neg": "− Bus",
  "junction.convert_neg_title": "Replace this junction with a negative busbar.",
  "junction.convert_ground": "Ground",
  "junction.convert_ground_title": "Replace this junction with a chassis-ground bonding bar.",
  "junction.convert_fusebox": "Fusebox",
  "junction.convert_fusebox_title":
    "Replace this junction with a fusebox — a positive busbar that shows each output's fuse rating inline at its stud.",
  "junction.convert_mixed_warning":
    "This junction connects to both + and − cables. Disconnect one polarity before converting it to a busbar.",
  "inspector.busbar.title": "Busbar",
  "inspector.busbar.studs": "Studs",
  "inspector.busbar.fewer": "Remove a stud",
  "inspector.busbar.more": "Add a stud",
  "inspector.busbar.add_output": "Add an output",
  "inspector.busbar.convert_to_fusebox": "Convert to fusebox",
  "inspector.busbar.convert_to_fusebox_title":
    "Render this busbar as a fusebox — each connected cable's fuse rating is shown inline at its stud.",
  "inspector.busbar.convert_to_busbar": "Convert to busbar",
  "inspector.busbar.convert_to_busbar_title":
    "Render as a plain busbar without inline fuse pills.",
  "inspector.busbar.convert_to_junction": "Convert to junction",
  "inspector.busbar.convert_to_junction_title":
    "Collapse this busbar into a wire junction. All cables will reconnect at a single point.",

  // ── Inspector — cable detail ───────────────────────────
  "cable.eyebrow": "Cable",
  "cable.label": "Label",
  "cable.label_placeholder": "e.g. C-12, stbd house +",
  "cable.label_help":
    "Free-text tag rendered on the cable — useful for matching the diagram to physical wires in the install.",
  "cable.label_color": "Tag colour",
  "cable.label_color.kraft": "Kraft",
  "cable.label_color.white": "White",
  "cable.label_color.yellow": "Yellow",
  "cable.label_color.red": "Red",
  "cable.label_color.blue": "Blue",
  "cable.label_color.green": "Green",
  "cable.label_color.slate": "Slate",
  "cable.length": "Length",
  "cable.calculated": "Calculated",
  "cable.peak_current": "Peak current",
  "cable.continuous_current": "Continuous current",
  "cable.nominal_voltage": "Nominal voltage",
  "cable.voltage_drop": "Voltage drop",
  "cable.wire_size": "Wire size",
  "cable.wire_ampacity": "Wire ampacity",
  "cable.suggested_fuse": "Suggested fuse",
  "cable.sizing_cranking":
    "Sized for {amp} brief starting duty, with a 2% voltage-drop target (cranking circuit).",
  "cable.sizing_general": "Sized for {amp} continuous at {pct}% V-drop.",
  "cable.sizing_general_at_v":
    "Sized for {amp} continuous at {volts} V, {pct}% V-drop budget.",
  "cable.sizing_ac":
    "Ampacity check uses the {amp} AC terminal rating. Verify circuit rating, installation and protection.",
  "cable.sizing_ac_unknown": "AC rating is missing. Verify circuit current, cable size and protection.",
  "cable.ac_rating": "AC rating used",
  "cable.ac_rating_missing": "Not specified",
  "cable.ac_voltage_drop_unavailable": "Not calculated for AC",
  "cable.ac_protection_label": "AC protection",
  "cable.ac_protection": "Verify AC circuit protection",
  "cable.no_fuse_recommendation": "None yet — no known load on this run",
  "cable.signal_fuse_unverified": "Signal wiring needs component-specific protection. Choose a manual rating from the manufacturer's instructions; wire ampacity alone does not establish it.",
  "cable.starting_fuse_unverified": "Starting-current data is missing or zero for a connected starter. Enter a documented fuse rating.",
  "cable.choose_fuse_rating": "Choose a rating from the component manual",
  "cable.needs_review": "Needs review",
  "cable.terminal_limit_hint": "The automatic fuse exceeds the terminal’s {rating} A rating.",
  "cable.smartshunt_fuse_hint": "SmartShunt Vbatt+ uses the manufacturer's 1 A slow-blow supply lead. Confirm the supplied lead or specified temperature-sensor accessory.",
  "cable.manufacturer_breaker_hint": "Verify the manufacturer's {rating} A motor breaker and its trip curve before using this rating.",
  "cable.add_inline_fuse_optional": "Battery-terminal protection: {rating} A. A separate branch device would use {load} A; verify whether the component requires one.",
  "cable.suggested_fuse_opt": "Suggested ({rating} A)",
  "cable.protect_to_wire":
    "No known load on this run — a fuse here is sized to the wire ({rating} A).",
  "cable.warn_undersized": "⚠ Wire is undersized for the estimated current.",
  "cable.warn_vdrop":
    "⚠ Voltage drop exceeds this circuit's {pct}% budget — consider a thicker cable or shorter run.",
  "cable.warn_fuse_trips":
    "⚠ Fuse will trip — components on this run draw {load} A continuous through a {rating} A fuse.",
  "cable.warn_wire_underrated_load":
    "⚠ Wire too thin for downstream load — run carries {load} A continuous, ampacity is {ampacity} A.",
  "cable.upgrade_to": "Upgrade to {mm2} mm² · AWG {awg}",
  "cable.worst_case": "Worst case across simulate states",
  "cable.continuous": "Continuous",
  "cable.suggested_wire": "Suggested wire",
  "cable.triggered_by": "Triggered by — {scenarios}",
  "cable.add_fuse": "Add fuse",
  "cable.add_inline_fuse": "Add inline fuse",
  "cable.add_inline_fuse_hint":
    "Battery MRBF ({rating} A) is too large to protect this wire alone. Add a {load} A inline fuse near the load.",
  "cable.fuse_from_slot":
    "Fuse rating comes from the fuse-block slot at this cable's endpoint.",
  "cable.protected_by": "Cable is protected by a {rating} A fuse near the source.",
  "cable.mirrors_battery_mrbf":
    "This {rating} A rating matches the battery MRBF — they're the same fuse, not two. Drop it to {load} A to actually protect this wire.",
  "cable.protect_with": "Protect this cable with the recommended {rating} A fuse.",
  "cable.toggle_negative": "Negative/ground cables are never fused (ABYC).",
  "cable.toggle_slot":
    "This cable's fuse is governed by the connected fuse-block slot.",
  "cable.series_link": "Intentional series link",
  "cable.series_link.on_description":
    "Marked as a series jumper (12 V → 24 V batteries, or panels in series). Polarity check is silenced for this cable; all other diagnostics still apply.",
  "cable.series_link.off_description":
    "Use only when stacking like cells (two 12 V batteries → 24 V bank, or solar panels strung for higher Voc into an MPPT). Anywhere else, + → − is still a short.",
  "cable.manual_override": "Manual override",
  "cable.wire_size_label": "Wire size",
  "cable.fuse_rating_label": "Fuse rating",
  "cable.fuse_family_recommendation_unavailable": "No automatic rating fits the selected fuse family and calculated requirement. Verify the protection design and choose a suitable device or family.",
  "cable.fuse_type_label": "Fuse type",
  "cable.vdrop_class_label": "Voltage-drop class",
  "cable.vdrop_class.auto": "Auto ({pct} % drop)",
  "cable.vdrop_class.auto_plain": "Auto",
  "cable.vdrop_class.critical": "Critical (3 % drop)",
  "cable.vdrop_class.non_critical": "Non-critical (10 % drop)",
  "cable.vdrop_class.help":
    "ABYC E-11.14.2.6: 3 % on panel feeders, bilge pumps, electronics and navigation lights, 10 % on general lighting. Set it on any cable of a run to reclassify the whole circuit.",
  "cable.auto_wire_opt": "Auto ({mm2} mm²)",
  "cable.auto_fuse_opt": "Auto ({rating} A)",
  "cable.auto_fuse_type_opt": "Auto",
  "cable.auto_fuse_type_opt_inherited": "Auto ({type})",
  "cable.reset_layout": "Re-route",
  "cable.reset_layout_title": "Re-route this cable cleanly (R)",
  "cable.delete": "Delete cable",

  // ── Inspector — multi detail ───────────────────────────
  "multi.eyebrow": "Multiple selection",
  "multi.items": "{count} items",
  "multi.components": "Components",
  "multi.cables": "Cables",
  "multi.add_fuse": "Add fuse",
  "multi.add_fuse_each": "Add fuse to each cable",
  "multi.protected_one": "{count} cable protected with a recommended fuse.",
  "multi.protected_many": "{count} cables protected with a recommended fuse.",
  "multi.protect_one":
    "Protect {count} cable with the recommended fuse rating.",
  "multi.protect_many":
    "Protect {count} cables with the recommended fuse rating.",
  "multi.delete": "Delete selection",

  // ── Comments ───────────────────────────────────────────
  "comments.title": "Comments",
  "comments.empty": "No comments yet.",
  "comments.read_only":
    "You're viewing read-only. Ask the owner for a comment or edit link.",
  "comments.your_name": "Your name",
  "comments.placeholder_node": "Ask about this component or zone…",
  "comments.placeholder_cable": "Ask about this cable…",
  "comments.post": "Post",
  "comments.resolve": "Resolve",
  "comments.name_required": "Add your name so others know who commented.",
  "comments.close": "Close",
  "comments.popover_aria": "Comments",
  "comments.popover_title_node": "Notes on this component",
  "comments.popover_title_cable": "Notes on this run",
  "comments.popover_first_node":
    "First note on this component? Ask a question or share what you'd change.",
  "comments.popover_first_cable":
    "First note on this run? Question a fuse choice, a wire size, the route.",
  "comments.popover_signin_to_post":
    "Sign in to leave a note pinned to this part of the build.",
  "comments.popover_kbd_hint": "⌘+↵ to post",
  "comments.resolved_toggle": "{count} {label}",
  "comments.resolved_singular": "resolved note",
  "comments.resolved_plural": "resolved notes",
  "comments.resolved_date": "resolved {date}",
  "comments.pin": "Pin this note",
  "comments.unpin": "Unpin",
  "comments.pinned_eyebrow": "Pinned by author",

  // ── Feedback button ────────────────────────────────────
  "feedback.btn": "Feedback",
  "feedback.btn_title":
    "Send feedback — report a bug, share an idea, or anything else",
  "feedback.type.bug": "Bug",
  "feedback.type.idea": "Idea",
  "feedback.type.other": "Contact creator",
  "feedback.type_aria": "Feedback type",
  "feedback.placeholder.bug": "What went wrong?",
  "feedback.placeholder.idea": "What's missing? What would you like to see?",
  "feedback.placeholder.other": "Write a message to the creator…",
  "feedback.heading.bug": "Report a bug",
  "feedback.heading.idea": "Share an idea",
  "feedback.heading.other": "Contact the creator",
  "feedback.picker.heading": "What's on your mind?",
  "feedback.picker.bug_desc": "We'll attach your system so we can reproduce it",
  "feedback.picker.idea_desc": "Sent straight to the team so we can plan it",
  "feedback.picker.other_desc": "Private email to the developer",
  "feedback.attached_blurb":
    "A session replay and your current system are attached automatically.",
  "feedback.other_blurb":
    "Sends privately to the developer. You won't get an automatic reply — leave your email below if you'd like one.",
  "feedback.contact.email_placeholder": "Email (optional)",
  "feedback.contact.name_placeholder": "Name (optional)",
  "feedback.contact.optional_blurb":
    "Leave your email if you'd like a reply when this is resolved.",
  "feedback.region_banner": "Drag to select an area",
  "feedback.esc_cancel": "to cancel",
  "feedback.pick_banner": "Click an item to attach it",
  "feedback.remove_screenshot": "Remove screenshot",
  "feedback.remove_item": "Remove item",
  "feedback.select_item": "Select item",
  "feedback.add_screenshot": "Add a screenshot",
  "feedback.cancel": "Cancel",
  "feedback.send": "Send",
  "feedback.sending": "Sending…",
  "feedback.fallback_component": "Component",
  "feedback.fallback_zone": "Zone",
  "feedback.fallback_junction": "Junction",
  "feedback.cable_label": "Cable: {from} → {to}",

  // ── Canvas (minimap) ───────────────────────────────────
  "canvas.minimap_aria": "System minimap",

  // ── Diagnostics — fallback labels ──────────────────────
  "diag.label.zone": "Zone",
  "diag.label.junction": "Junction",
  "diag.label.component": "Component",
  "diag.label.load": "Load",

  // ── Diagnostics — AC, controls and PV circuits ─────────
  "diag.control-coil-voltage-mismatch.fix": "Use the specified coil supply or a relay with the correct nominal coil voltage. Check both coil conductors and the control circuit.",
  "diag.control-coil-voltage-mismatch.message": "{label} pins 85 and 86 can reach the same declared {actual} V supply. This is outside the reviewed {rated} V nominal coil class. Operating-voltage tolerance and transients still require manufacturer verification.",
  "diag.control-coil-voltage-mismatch.title": "Relay coil supply voltage differs",
  "diag.control-contact-voltage-unverified.fix": "Use contacts rated for the actual supply voltage and switched current. Verify every intended position and the manufacturer's installation limits.",
  "diag.control-contact-voltage-unverified.message": "{label} is connected to a declared {actual} V DC supply. The reviewed contact rating is {rated} V DC. This source is outside the reviewed nominal class or maximum voltage.",
  "diag.control-contact-voltage-unverified.title": "Control contact voltage needs verification",
  "diag.control-state-coverage-unverified.fix": "Review all permitted combined contact positions. No additional warning does not mean the unexamined combinations have been verified.",
  "diag.control-state-coverage-unverified.message": "This drawing exceeds the 64-combination limit for checking control contact positions together. Alternate-state fault checks are incomplete.",
  "diag.control-state-coverage-unverified.title": "Control switching combinations need review",
  "diag.ac-source-neutral-shared.title": "AC sources share a neutral path",
  "diag.ac-source-neutral-shared.message": "Neutral conductors from different AC sources are connected. The drawing does not establish whether their neutral switching and earth bonds permit this arrangement.",
  "diag.ac-source-neutral-shared.fix": "Check source isolation and neutral switching in every supply mode against the source and transfer-switch instructions.",
  "diag.control-contact-overload.title": "Selected position overloads a contact or cable",
  "diag.control-contact-overload.message": "{label}, position {position}: the complete circuit carries {current} A through a contact or attached cable rated for {maxCurrent} A. This position is checked even when it is inactive in the current view.",
  "diag.control-contact-overload.fix": "Use a suitable contactor or switch and correctly rated cable. Check actual continuous, starting and inductive load requirements.",
  "diag.ac-conductor-mismatch.title": "AC conductors are crossed or joined",
  "diag.ac-conductor-mismatch.message": "A wired or selectable path joins line to neutral, protective earth or another supply phase.",
  "diag.ac-conductor-mismatch.fix": "Trace the highlighted path and correct the L, N and PE connections before energizing it.",
  "diag.ac-sources-paralleled.title": "AC source outputs are connected together",
  "diag.ac-sources-paralleled.message": "A wired or selectable line path joins multiple AC outputs. The drawing does not establish synchronization or approved parallel operation.",
  "diag.ac-sources-paralleled.fix": "Use the intended source selector and remove bypass paths, or document a manufacturer-approved parallel system before connection.",
  "diag.ac-source-pair-mismatch.title": "AC line and neutral come from different sources",
  "diag.ac-source-pair-mismatch.message": "The L and N conductors supplying {label} do not reach the same AC source port.",
  "diag.ac-source-pair-mismatch.fix": "Keep each supply's line and neutral together through the intended source selector and protection devices.",
  "diag.ac-circuit-incomplete.title": "AC supply conductors are incomplete",
  "diag.ac-circuit-incomplete.message": "The L and N paths supplying {label} do not form a complete connection to an AC source.",
  "diag.ac-circuit-incomplete.fix": "Complete the L and N paths to the same source. Protective earth cannot replace neutral.",
  "diag.ac-earth-port-ambiguous.title": "Cannot identify the protective-earth terminal",
  "diag.ac-earth-port-ambiguous.message": "The drawing does not identify which protective-earth terminal on {label} serves the AC circuit with neutral {port}.",
  "diag.ac-earth-port-ambiguous.fix": "Check the equipment wiring diagram and identify the protective-earth terminal for this AC circuit.",
  "diag.ac-source-earth-missing.title": "Protective earth does not reach the AC source",
  "diag.ac-source-earth-missing.message": "The drawing shows no continuous protective-earth path from {label} ({loadTerminal}) to its AC source, {source} ({sourceTerminal}).",
  "diag.ac-source-earth-missing.fix": "Restore the source PE connection through the grounding system. A connection to DC negative alone does not prove shore-earth continuity.",
  "diag.ac-protection-missing.title": "AC breaker protection is missing or bypassed",
  "diag.ac-protection-missing.message": "A source can supply {label} without passing through a recognized AC overcurrent-protection path.",
  "diag.ac-protection-missing.fix": "Add or verify the correct AC breaker and remove bypass wiring. Inline DC fuse values and GFCI devices do not establish AC overcurrent protection.",
  "diag.ac-protection-exceeds-rating.title": "AC protection exceeds the equipment rating",
  "diag.ac-protection-exceeds-rating.message": "{label} is rated {rating} A, but a supply path has no recognized breaker at or below that rating.",
  "diag.ac-protection-exceeds-rating.fix": "Check the installed equipment and breaker ratings, including every alternative supply path.",
  "diag.ac-load-exceeds-rating.title": "Declared AC loads exceed a circuit rating",
  "diag.ac-load-exceeds-rating.message": "The connected input watts imply at least {current} A at nominal voltage, above the {rating} A circuit or equipment rating.",
  "diag.ac-load-exceeds-rating.fix": "Reduce simultaneous loads or revise the rated circuit. Power factor and inrush can increase current beyond this lower bound.",
  "diag.ac-voltage-mismatch.title": "AC nominal voltages do not match",
  "diag.ac-voltage-mismatch.message": "{label} declares {expected} V but a connected or selectable source declares {actual} V.",
  "diag.ac-voltage-mismatch.fix": "Verify the actual equipment input range and source configuration before connection.",
  "diag.ac-neutral-earth-link-unverified.title": "Verify the drawn neutral-to-earth link",
  "diag.ac-neutral-earth-link-unverified.message": "A cable joins AC neutral to protective earth. Its correct location depends on the supply source, transfer arrangement and internal bonding relays.",
  "diag.ac-neutral-earth-link-unverified.fix": "Check the link against the manufacturer instructions in every supply mode. Do not add or remove a neutral-earth bond by assumption.",
  "diag.ac-profile-unverified.title": "Edited AC equipment needs verification",
  "diag.ac-profile-unverified.message": "Electrical edits to {label} no longer match the catalog protection and connection profile.",
  "diag.ac-profile-unverified.fix": "Verify the edited contacts, terminals and ratings against the installed equipment. The original profile's protection cannot be assumed.",
  "diag.ac-protection-reversed.title": "AC protective device is supplied from its load side",
  "diag.ac-protection-reversed.message": "A source reaches a protected output of {label} while its LINE input has no independent source feed.",
  "diag.ac-protection-reversed.fix": "Connect incoming supply to LINE and downstream wiring to the intended LOAD or socket terminals, following the device instructions.",
  "diag.ac-leakage-bypassed.title": "AC leakage protection has a bypass path",
  "diag.ac-leakage-bypassed.message": "Line or neutral can reach a protected output of {label} without passing through its protective contacts.",
  "diag.ac-leakage-bypassed.fix": "Trace both L and N and remove the unintended bypass. Keep the protective-earth path continuous.",
  "diag.ac-leakage-unverified.title": "Verify AC leakage protection",
  "diag.ac-leakage-unverified.message": "The supply paths to {label} do not establish recognized leakage protection on both L and N.",
  "diag.ac-leakage-unverified.fix": "Verify the required RCD, ELCI or GFCI type, sensitivity and placement for this installation. A plain circuit breaker does not establish leakage protection.",
  "diag.ac-state-coverage-unverified.title": "AC switching combinations need review",
  "diag.ac-state-coverage-unverified.message": "This drawing has more switching combinations than the AC diagram check can examine completely.",
  "diag.ac-state-coverage-unverified.fix": "Review every permitted source-selection combination and interlock. The absence of another finding does not verify the unexamined combinations.",
  "diag.ac-design-unverified.title": "AC installation checks remain",
  "diag.ac-design-unverified.message": "The drawing checks conductor connections and declared ratings; it does not verify complete AC protection or installation safety.",
  "diag.ac-design-unverified.fix": "Verify installed ratings, cable installation, fault current, breaker coordination, leakage protection and neutral-earth bonding in every supply mode. AC power factor, inrush and automatic trips are not simulated.",
  "diag.control-relay-manual.title": "Relay operation is selected manually",
  "diag.control-relay-manual.message": "{label}: contact position is selected manually and does not follow the coil wiring. Coil consumption, actuation and switching transients are not calculated. Sizing for the connected DC circuit remains provisional; automatic wire and fuse quantities are withheld.",
  "diag.control-relay-manual.fix": "Check the coil circuit and every intended contact position against the relay and load instructions. Verify shared feeds, returns, all branches and their protection.",
  "diag.control-coil-incomplete.title": "Relay coil connection is incomplete",
  "diag.control-coil-incomplete.message": "{label} has only one of its two coil pins connected. The drawn control circuit cannot establish coil operation.",
  "diag.control-coil-incomplete.fix": "Draw both coil supply and return. Confirm voltage, polarity and control protection from the relay instructions.",
  "diag.control-contact-bypass.title": "External wiring bypasses a control contact",
  "diag.control-contact-bypass.message": "{label} has a conducting path from its common terminal to a switched terminal outside the component. Opening this contact may not disconnect that path.",
  "diag.control-contact-bypass.fix": "Inspect the highlighted circuit and remove unintended jumpers or alternate feeds.",
  "diag.control-model-unverified.title": "Edited control topology needs review",
  "diag.control-model-unverified.message": "{label} no longer matches the reviewed contact layout. Its retained catalog ID does not establish contact isolation or operation. Sizing for the connected DC circuit remains provisional; automatic wire and fuse quantities are withheld.",
  "diag.control-model-unverified.fix": "Verify every contact, coil connection and switch position in the edited definition, then check the connected DC circuit, shared feeds, returns and protection for every intended position.",
  "diag.control-state-sizing-unverified.title": "Check every switch position",
  "diag.control-state-sizing-unverified.message": "{label}: current estimates follow one selected contact position and do not establish an envelope across all positions. Sizing for the connected DC circuit remains provisional; automatic wire and fuse quantities are withheld. Momentary spring return is not timed.",
  "diag.control-state-sizing-unverified.fix": "Check the entire connected DC circuit in each intended position, including the held position. Verify shared feeds, returns, all branches and their protection.",
  "diag.pv-string-protection-missing.title": "PV string protection is not shown",
  "diag.pv-string-protection-missing.message": "{label}, string {string}: a path from the solar panel reaches the combiner without a declared individual gPV fuse. A shared output fuse does not protect each string independently.",
  "diag.pv-string-protection-missing.fix": "Verify required string protection against module Isc, maximum series-fuse rating and parallel-string backfeed; draw the required individual protective device.",
  "diag.pv-string-protection-bypass.title": "Wiring bypasses the PV string fuse",
  "diag.pv-string-protection-bypass.message": "{label}, string {string}: a parallel conducting path remains around the declared string fuse.",
  "diag.pv-string-protection-bypass.fix": "Remove the unintended bypass and keep each required string fuse in series before the common positive bus.",
  "diag.pv-string-fuse-unrated.title": "PV fuse choice is incomplete or conflicting",
  "diag.pv-string-fuse-unrated.message": "{label}, string {string}: the holder has no usable matching gPV rating, exceeds its 30 A rating, or conflicts with an attached fuse declaration.",
  "diag.pv-string-fuse-unrated.fix": "Specify a compatible gPV fuse separately. Check its rating against the module and holder; resolve conflicting declarations.",
  "diag.pv-combiner-rating-unverified.title": "PV combiner ratings need verification",
  "diag.pv-combiner-rating-unverified.message": "{label} is a generic copper layout. Enclosure, bus and connector voltage/current ratings, module Isc, cold Voc and exact fuse coordination are not verified.",
  "diag.pv-combiner-rating-unverified.fix": "Select the actual combiner and protective products; verify their limits for this array. Fuse holders are not load-break switches.",
  "diag.pv-string-return-missing.title": "PV string return is missing",
  "diag.pv-string-return-missing.message": "{label}, string {string}: the positive input is wired but the matching negative input is not.",
  "diag.pv-string-return-missing.fix": "Draw the complete positive and negative string circuit with the correct polarity.",
  "diag.pv-combiner-model-unverified.title": "Edited PV combiner topology needs review",
  "diag.pv-combiner-model-unverified.message": "{label} no longer matches the reviewed separate positive and negative bus layout.",
  "diag.pv-combiner-model-unverified.fix": "Review the edited terminals and internal links before relying on array or protection calculations.",
  "diag.pv-holder-voltage-exceeded.title": "PV string exceeds the fuse holder voltage rating",
  "diag.pv-holder-voltage-exceeded.message": "{label}, string {string}: the drawn series string has {voltage} V declared STC Voc, exceeding the holder’s {maxVoltage} V DC rating even before cold correction.",
  "diag.pv-holder-voltage-exceeded.fix": "Select appropriately rated components and verify the maximum cold-corrected string voltage.",

  // ── Diagnostics — topology ─────────────────────────────
  "diag.unfused-positive-run.title": "Unfused positive run to {label}",
  "diag.unfused-positive-run.message":
    "{label} is wired to a battery+ without a fuse on the path. A short here can melt cables before anything trips.",
  "diag.unfused-positive-run.fix":
    "Add a fuse close to the battery on the positive conductor.",

  "diag.fuse-too-far-from-battery.title": "Check fuse distance: {label}",
  "diag.fuse-too-far-from-battery.message": "The drawing shows {distanceMm} mm before the first fuse. Crimp’s default check uses 175 mm; installation exceptions are not modeled.",
  "diag.fuse-too-far-from-battery.fix": "Confirm the actual fuse position and unfused cable length.",

  "diag.battery-missing-terminal-fuse.title": "Check battery protection: {label}",
  "diag.battery-missing-terminal-fuse.message": "The drawing shows a battery feed longer than 175 mm without a fuse on the positive post.",
  "diag.battery-missing-terminal-fuse.fix": "Check the unfused length and add suitable protection close to the battery.",

  "diag.fuse-aic-inadequate.title":
    "{type} fuse interrupt rating is below the modeled fault current",
  "diag.fuse-aic-inadequate.message":
    "The model estimates ~{faultA} A of battery fault current; this {type} fuse has a modeled interrupt rating of {aic} A. A fuse exposed to more fault current than its interrupt rating may fail to interrupt safely. Confirm the actual battery fault current and the fuse manufacturer's rating at your system voltage.",
  "diag.fuse-aic-inadequate.fix":
    "Use a fuse with adequate DC interrupt capacity at the system voltage and place it as close as practicable to the positive battery terminal. Verify the exact product rating; interrupt capacity can decrease as voltage rises.",
  "diag.fuse-aic-inadequate.title_covered":
    "Verify {type} fuse coordination with upstream protection",
  "diag.fuse-aic-inadequate.message_covered":
    "The model estimates ~{faultA} A of bank fault current, above this {type} fuse's {aic} A interrupt rating. Upstream protection was found on every modeled supply path; one selected device is a {upstreamRating} A {upstreamType} with a modeled interrupt rating of {upstreamAic} A. This does not verify the installed fuse ratings, actual fault current, or a tested series combination. Coordination and let-through energy still need manufacturer verification.",
  "diag.fuse-aic-inadequate.fix_covered":
    "Confirm the actual bank fault current and all installed fuse ratings. Check manufacturer data for the exact upstream {upstreamType} and downstream device combination at your system voltage, or use downstream protection with adequate interrupt capacity on its own.",

  "diag.fuse-rating-unavailable.title":
    "Verify {type} fuse rating: {rating} A",
  "diag.fuse-rating-unavailable.message_below":
    "The declared {rating} A rating is outside Crimp’s reference range for {type} ({min}–{max} A). Verify the exact device and its compatibility with this circuit.",
  "diag.fuse-rating-unavailable.message_above":
    "The declared {rating} A rating is outside Crimp’s reference range for {type} ({min}–{max} A). Verify the exact device and its compatibility with this circuit.",
  "diag.fuse-rating-unavailable.fix_below":
    "Select a verified device or family suitable for this circuit. Do not increase the rating without checking conductor ampacity and equipment protection.",
  "diag.fuse-rating-unavailable.fix_above":
    "Select a verified device or family suitable for this circuit. Do not increase the rating without checking conductor ampacity and equipment protection.",

  "diag.fuse-type-unspecified.title":
    "{count} fused runs don't say which type of fuse",
  "diag.fuse-type-unspecified.message":
    "This bank can deliver ~{faultA} A into a short. At that level the fuse *type* decides whether it opens or welds shut — the weakest common family interrupts only {weakestAic} A. These runs carry a rating but no type, so the interrupt-capacity and availability checks can't run on them.",
  "diag.fuse-type-unspecified.fix":
    "Pick a family per run: Class T or MRBF bolted at the battery, ANL or MEGA inline, and a blade block for small branches behind an adequate main.",

  "diag.battery-chemistry-unknown.title": "{label} has no chemistry set",
  "diag.battery-chemistry-unknown.message":
    "{label} declares neither a chemistry nor a short-circuit current, so its fault current and charge acceptance are unknown — not zero. Set the chemistry for charge-acceptance checks. Fault-current verification separately needs source or bank data; chemistry alone cannot supply it.",
  "diag.battery-chemistry-unknown.fix":
    "Give {label} a chemistry — or a declared short-circuit current and maximum charge current — in its definition.",

  "diag.fix.add_fuse_near_battery": "Add fuse near battery",
  "diag.fix.upgrade_fuse_to": "Upgrade fuse to {rating} A",
  "diag.fix.upgrade_wire_to": "Upgrade to {mm2} mm²",
  "diag.fix.delete_duplicate_one": "Delete duplicate",
  "diag.fix.delete_duplicate_many": "Delete {count} duplicates",

  "diag.load-floating.title": "{label} has no power path",
  "diag.load-floating.message":
    "No path from {label} to either battery terminal is shown in the drawing.",
  "diag.load-floating.fix":
    "Show the actual positive supply and return to the same battery, including installed fuses.",

  "diag.load-via-floating-converter.title":
    "{label} depends on {upstream}, which isn't wired to a battery",
  "diag.load-via-floating-converter.message":
    "{label} is connected to {upstream}, but {upstream} has no path to the battery — so {label} can't power on through it yet.",
  "diag.load-via-floating-converter.fix":
    "Wire {upstream}'s input side to a fused battery+ and the matching battery− bus.",

  "diag.load-no-positive.title": "{label} has no positive supply",
  "diag.load-no-positive.message":
    "{label}'s negative is wired but no path reaches a battery+.",
  "diag.load-no-positive.fix":
    "Wire the load's positive through a fuse to the battery's positive bus.",

  "diag.load-no-return.title": "{label} has no return path",
  "diag.load-no-return.message":
    "The supply reaches {label}, but its return path to that source is missing from the drawing.",
  "diag.load-no-return.ground_message": "{label} reaches a ground symbol, but that network has no return to the source supplying the load.",
  "diag.load-no-return.ground_fix": "Ground symbols connect to each other. Show the actual return from this network to the matching supply’s return terminal.",
  "diag.load-no-return.fix":
    "Show the load’s actual return path to the matching supply’s return terminal.",

  "diag.battery-bms-missing.title": "{label} needs an external BMS",
  "diag.battery-bms-missing.message": "This battery model requires a compatible external BMS. No compatible controller is connected to its battery supply in this drawing.",
  "diag.battery-bms-missing.fix": "Add the manufacturer-approved BMS and connect its required battery-side supply and battery data chain.",
  "diag.battery-bms-incompatible.title": "Battery and BMS are incompatible",
  "diag.battery-bms-incompatible.message": "{bms} is connected to {label}, but its declared battery compatibility excludes this model.",
  "diag.battery-bms-incompatible.fix": "Use the BMS specified for this battery family; Smart and NG controllers are not interchangeable.",
  "diag.bms-disconnect-bypassed.title": "BMS disconnect is bypassed",
  "diag.bms-disconnect-bypassed.message": "A conductor path connects the battery and system posts around {label}, so its built-in disconnect cannot isolate that path.",
  "diag.bms-disconnect-bypassed.fix": "Remove the bypass and route the protected system through the designated BMS system port.",
  "diag.bms-control-unverified.title": "Check BMS shutdown controls",
  "diag.bms-control-unverified.message": "Power wiring alone does not show whether the BMS can stop charging and disconnect loads.",
  "diag.bms-control-unverified.fix": "Check the sensing leads and shutdown controls against the BMS manual.",

  // BMS, service isolation and AC protection checks.
  "diag.ac-source-neutral-floating.title": "AC source neutral is floating",
  "diag.ac-source-neutral-floating.message": "{source} has an unbonded neutral in its documented factory configuration and feeds {label}; no source-side neutral/PE connection is represented. Earth-leakage protection needs installation-specific verification.",
  "diag.ac-source-neutral-floating.fix": "Have the source, neutral switching, PE continuity and leakage protection checked together against the manufacturer instructions in every supply mode. Do not add a neutral/earth bond by assumption.",
  "diag.ac-neutral-bond-unverified.title": "Verify AC neutral and earth bonding",
  "diag.ac-neutral-bond-unverified.message": "The drawing cannot verify the neutral/PE arrangement for {label} supplied by {source}, including internal bonds and source transfer states.",
  "diag.ac-neutral-bond-unverified.fix": "Have the source, neutral switching, PE continuity and leakage protection checked together against the manufacturer instructions in every supply mode. Do not add a neutral/earth bond by assumption.",
  "diag.pv-isolation-missing.title": "Solar disconnection is missing",
  "diag.pv-isolation-missing.message": "{label} remains connected to the panels when the modeled solar disconnects are opened.",
  "diag.pv-isolation-missing.fix": "Add a suitable DC solar disconnect and remove any bypass connections.",
  "diag.pv-isolation-unverified.title": "Check solar disconnection",
  "diag.pv-isolation-unverified.message": "Crimp cannot verify how all solar supply conductors to {label} are disconnected.",
  "diag.pv-isolation-unverified.fix": "Check the controller manual and the disconnect’s wiring and ratings.",
  "diag.inverter-dc-isolation-unverified.title": "Check inverter disconnection",
  "diag.inverter-dc-isolation-unverified.message": "Crimp cannot confirm that {label} can be disconnected from every DC supply.",
  "diag.inverter-dc-isolation-unverified.fix": "Verify the disconnection procedure in the inverter manual.",
  "diag.ac-neutral-bond-unverified.message_source": "The line and neutral feeding {label} cannot be traced to one modeled AC source. The source, neutral/PE arrangement and leakage protection remain unverified.",
  "diag.ac-neutral-bond-unverified.message_relay": "{source} uses an internal grounding relay. Its configuration, protective-earth continuity and behavior with shore/generator transfer are not represented, so {label} protection remains unverified.",

  "diag.missing-ground.title": "Grounding path missing: {label}",
  "diag.missing-ground.message": "{terminal} does not reach a grounding reference in the drawing.",
  "diag.missing-ground.fix": "Complete the grounding path according to the equipment manual.",

  "diag.parallel-battery-imbalance.title":
    "Parallel batteries: asymmetric cable lengths",
  "diag.parallel-battery-imbalance.message":
    "{labels} share a positive bus but their cables differ by more than 20% ({min} m vs {max} m). The shorter run will hog charge and discharge current.",
  "diag.parallel-battery-imbalance.fix":
    "Match the cable runs from each battery to the bus, or use the diagonal-bus (Smartgauge / 'method 2') wiring.",

  "diag.parallel-battery-chemistry-mismatch.title":
    "Parallel batteries: mixed chemistries",
  "diag.parallel-battery-chemistry-mismatch.message":
    "{labels} are on the same bus but use different chemistries (e.g. LiFePO4 and lead-acid). The chemistries idle at different resting voltages and follow different charge/discharge curves, so one bank dumps current into the other every time the system rests — wearing out the lead bank fast and exposing the LFP cells to charge profiles they aren't designed for.",
  "diag.parallel-battery-chemistry-mismatch.fix":
    "Don't parallel LiFePO4 with lead-acid in the same bank. Separate them onto isolated buses tied through a DC-DC charger or battery combiner.",

  "diag.reversed-polarity.title": "{aLabel} + bonded to {bLabel} −",
  "diag.reversed-polarity.message":
    "This cable wires a positive terminal directly to a negative or ground terminal. Energising it is a dead short — cables vaporise before any fuse can react.",
  "diag.reversed-polarity.fix":
    "Disconnect this cable. Re-check the polarity of both ends before reconnecting. If this is an intentional series jumper (12 V batteries → 24 V bank, or panels in series for higher Voc), mark the cable as a series link to silence this warning.",
  "diag.reversed-polarity.fix-action": "Mark as series link",

  "diag.ac-onto-dc-rail.title": "AC connected to DC: {acLabel} → {dcLabel}",
  "diag.ac-onto-dc-rail.message": "The drawing connects an AC live conductor to a DC power connection. This can cause shock or fire.",
  "diag.ac-onto-dc-rail.fix": "Remove the AC-to-DC connection. Check custom terminal definitions; keep required protective earth connected.",

  "diag.charge-on-crank-terminal.title": "{farLabel} is on the engine's cranking terminal",
  "diag.charge-on-crank-terminal.message":
    "This cable feeds {farLabel} from the engine's Starter terminal, which carries cranking current. Charging devices belong on the alternator's Alt B+ output — on the cranking terminal the cable is sized for the starter surge and won't show alternator current when the engine is running.",
  "diag.charge-on-crank-terminal.fix":
    "Move the cable to the engine's Alt B+ output terminal.",
  "diag.charge-on-crank-terminal.fix-action": "Move to Alt B+",

  "diag.suspicious-series-link.title":
    "Series link from {aLabel} to {bLabel} looks wrong",
  "diag.suspicious-series-link.message":
    "This cable is marked as a series link but the endpoints aren't both batteries or both solar panels. Series jumpers only make physical sense when stacking like cells (12 V → 24 V battery banks, panels strung for higher Voc into an MPPT). Anywhere else, + → − is still a short.",
  "diag.suspicious-series-link.fix":
    "Unmark this cable as a series link and re-check the polarity of both ends.",
  "diag.suspicious-series-link.fix-action": "Unmark series link",

  "diag.voltage-mismatch.title":
    "{label} is rated for {declared} but sits on a {actual} V rail",
  "diag.voltage-mismatch.message":
    "This component declares it works on {declared} only, but the cable lands on a {actual} V domain. Powering a 12 V device from 48 V will destroy it instantly; powering a 48 V device from 12 V won't crank. Route it through a step-up/step-down converter on the correct rail.",
  "diag.voltage-mismatch.fix":
    "Move the component onto the matching rail, or insert a DC-DC converter between the two voltages.",

  "diag.cross-voltage-bond.title":
    "{fromLabel} ({fromV} V) wired directly to {toLabel} ({toV} V)",
  "diag.cross-voltage-bond.message":
    "This cable bonds two different voltage domains together with no converter between them. Every device on the lower-voltage side will see the higher voltage — typically destroying it the moment the system energises.",
  "diag.cross-voltage-bond.fix":
    "Delete the cable. To couple the two rails for charging, insert a DC-DC charger of the right ratio between them.",

  "diag.high-voltage-rail.title":
    "Battery rail at {actual} V exceeds the 60 V DC touch-safe limit",
  "diag.high-voltage-rail.message":
    "This rail's nominal voltage ({actual} V) is above the IEC 60364 / ABYC extra-low-voltage ceiling of {limit} V DC. Standard tinned marine cable and most distribution blocks are not rated for hot-touch above that — exposed conductors become a shock hazard, and the install needs special insulation, isolation, and lockout to be legal. If you genuinely meant to build a high-voltage bank, this is a heads-up; if you accidentally stacked too many cells in series, drop some.",
  "diag.high-voltage-rail.fix":
    "Either reduce the series count so the bank stays at or below 60 V (4× 12 V LFP → 51.2 V is the typical 48 V design point), or treat this as an HV install and use HV-rated cable, insulation, and disconnects throughout.",

  "diag.mismatched-series-stack.title":
    "Series-stacked batteries have different voltages ({fromV} V + {toV} V)",
  "diag.mismatched-series-stack.message":
    "This series link bonds a {fromV} V battery to a {toV} V battery. Stacked cells must have matched nominal voltages — the smaller cell will discharge into the larger one every cycle, sag below its safe floor, and eventually go into permanent reversal. ABYC E-11 §11.10 and every battery manufacturer require matched cells in any series string.",
  "diag.mismatched-series-stack.fix":
    "Replace one of the batteries so both have the same nominal voltage, or remove the series link and treat each bank as a separate domain (with its own DC-DC if you need to bridge them).",

  "diag.custom-component-implausible-amps.surge-below-draw.title":
    "{label}: peak surge is below its continuous draw",
  "diag.custom-component-implausible-amps.surge-below-draw.message":
    "{label} declares {ampSurge} A surge against {ampDraw} A continuous. Surge is the brief inrush on top of the continuous draw, so it can't be the smaller of the two — one of the fields is holding the wrong value. As it stands the surge figure changes nothing: fuse and overload checks use the higher number, which is {ampDraw} A.",
  "diag.custom-component-implausible-amps.surge-below-draw.fix":
    "Check the part's datasheet, then put its running current in Continuous draw and its start-up peak in Peak surge. If the two got swapped, swap them back.",

  "diag.custom-component-implausible-amps.engine-surge.title":
    "{label}: {ampSurge} A surge against only {ampDraw} A continuous",
  "diag.custom-component-implausible-amps.engine-surge.message":
    "For an engine, Continuous draw is what the starter pulls while it is actually cranking and Peak surge is the brief inrush on top of that — real engines sit within about 2× of each other, this one is {ratio}×. {ampDraw} A looks like an ignition or run current, which leaves nothing on this component describing the sustained cranking current — and cranking current is what sizes your starter cable.",
  "diag.custom-component-implausible-amps.engine-surge.fix":
    "Put the engine's cranking current in Continuous draw — small marine diesels crank at roughly 100–250 A — and leave Peak surge for the initial inrush.",

  "diag.custom-component-ai-estimated.title":
    "{count} components use AI-estimated specs",
  "diag.custom-component-ai-estimated.message":
    "{fieldCount} specs are AI estimates. They affect wire sizes, fuse ratings and the energy budget.",
  "diag.custom-component-ai-estimated.fix":
    "Use Fix to find manufacturer documentation and check the estimated specs. Unverified values stay marked.",

  "diag.ai-estimated-lengths.title_one":
    "One assumed run length decides a wire size",
  "diag.ai-estimated-lengths.title_many":
    "{count} assumed run lengths decide a wire size",
  "diag.ai-estimated-lengths.message_one":
    "The assistant chose {total} run lengths because your description didn't give them. On this one, the length is what picked the wire — get it wrong and the cable is undersized. The rest are short enough that no plausible error changes anything.",
  "diag.ai-estimated-lengths.message_many":
    "The assistant chose {total} run lengths because your description didn't give them. On these {count}, the length is what picked the wire — get one wrong and that cable is undersized. The rest are short enough that no plausible error changes anything.",
  "diag.ai-estimated-lengths.fix_one":
    "Measure this run on the boat and type the real length. Follow the route the cable will actually take, not the straight line.",
  "diag.ai-estimated-lengths.fix_many":
    "Measure these runs on the boat and type the real lengths. Follow the route each cable will actually take, not the straight line.",
  "diag.ai-estimated-parts.title_one": "One part was picked for you",
  "diag.ai-estimated-parts.title_many": "{count} parts were picked for you",
  "diag.ai-estimated-parts.message_one":
    "You didn't name this one, so the assistant chose a catalogued part that fits. The specs are the catalog's, not the assistant's — but they describe the part it picked, which may not be the one you're buying.",
  "diag.ai-estimated-parts.message_many":
    "You didn't name these, so the assistant chose catalogued parts that fit. The specs are the catalog's, not the assistant's — but they describe the parts it picked, which may not be the ones you're buying.",
  "diag.ai-estimated-parts.fix_one":
    "Select it and use Part to swap it for what you actually have. Every cable stays wired to it.",
  "diag.ai-estimated-parts.fix_many":
    "Select each one and use Part to swap it for what you actually have. Every cable stays wired to it.",

  "diag.dc-breaker-exceeds-wire.title": "{rating} A breaker exceeds cable capacity",
  "diag.dc-breaker-exceeds-wire.message": "The {mm2} mm² cable is rated for {ampacity} A.",
  "diag.dc-breaker-exceeds-wire.fix": "Increase the conductor size or select suitable lower-rated protection.",
  "diag.dc-breaker-unverified.title": "Check breaker protection: {label}",
  "diag.dc-breaker-unverified.message": "Fault interruption and maximum operating voltage remain unverified.",
  "diag.dc-breaker-unverified.fix": "Check the exact breaker’s DC interrupt rating against battery fault current and maximum voltage. Confirm source placement and trip behavior.",
  "diag.dc-breaker-reversed.title": "Reverse the breaker connections: {label}",
  "diag.dc-breaker-reversed.message": "The battery reaches the load side of the breaker.",
  "diag.dc-breaker-reversed.fix": "Connect battery positive to BAT LINE and the motor positive to AUX LOAD.",
  "diag.dc-breaker-voltage.title": "Breaker voltage rating exceeded: {label}",
  "diag.dc-breaker-voltage.message": "The {voltage} V bank exceeds the declared {maximum} V DC limit.",
  "diag.dc-breaker-voltage.fix": "Use a breaker rated for the full maximum operating voltage.",
  "diag.dc-breaker-interrupt-capacity.title": "Breaker interrupt rating exceeded: {label}",
  "diag.dc-breaker-interrupt-capacity.message": "Declared battery fault current is {fault} A; the breaker is rated to interrupt {rating} A.",
  "diag.dc-breaker-interrupt-capacity.fix": "Choose protection with sufficient DC interrupt capacity for this source.",
  "diag.fuse-exceeds-wire.title": "{rating} A fuse exceeds cable capacity",
  "diag.fuse-exceeds-wire.message": "The cable's planning ampacity is {ampacity} A, below the {rating} A fuse. Verify the conductor rating and overload protection.",
  "diag.fuse-exceeds-wire.fix": "Use a suitable smaller fuse or increase the cable size.",

  "diag.lynx-fuse-sum-exceeds-rating.title":
    "Lynx branch fuses total {sum} A — above the {limit} A limit",
  "diag.lynx-fuse-sum-exceeds-rating.message":
    "The circuit fuses connected to this Lynx chain total {sum} A, but {module} limits the whole chain to {limit} A. Victron requires the fuse total to stay at or below the lowest-rated Lynx module.",
  "diag.lynx-fuse-sum-exceeds-rating.fix":
    "Reduce the branch fuse ratings or split circuits across independently fed distribution chains until the total stays within the module limit.",

  "diag.inline-fuse-needed.title":
    "Inline fuse needed — battery MRBF ({rating} A) won't protect {mm2} mm² wire",
  "diag.inline-fuse-needed.message":
    "The battery's {rating} A terminal fuse exceeds this cable's continuous ampacity of {ampacity} A. Add branch protection at the source end of the smaller conductor.",
  "diag.inline-fuse-needed.fix":
    "Add an inline fuse sized for the load at the source end of this smaller conductor, or upsize the wire to carry the MRBF rating.",

  "diag.duplicate-cable.title": "{count} cables between the same terminals",
  "diag.duplicate-cable.message":
    "Two or more cables connect the exact same pair of terminals. Almost always a leftover from editing — the duplicates won't carry extra current and just clutter the canvas.",
  "diag.duplicate-cable.fix": "Delete the duplicate cables.",

  "diag.mismatched-cable-lengths.title":
    "Cables between {aLabel} and {bLabel} have different lengths",
  "diag.mismatched-cable-lengths.message":
    "Multiple cables run between {aLabel} and {bLabel} with lengths {min} m and {max} m. Cables connecting the same pair of components usually share a route, so a difference this large is normally a data-entry slip — and the longer length is the one voltage-drop math should use.",
  "diag.mismatched-cable-lengths.fix":
    "Set both cables to the actual run length. If they really do take different routes, leave them as-is.",

  "diag.unfused-charger-output.title": "Unfused {terminal} cable: {label}",
  "diag.unfused-charger-output.message": "The {terminal} cable reaches battery positive without a fuse. A short could overheat the cable.",
  "diag.unfused-charger-output.fix": "Add suitable protection close to the battery on this conductor.",

  "diag.unfused-source-output.title": "Unfused positive output from {label}",
  "diag.unfused-source-output.message":
    "{label}'s positive output can reach a battery+ without a fuse on the path. A short on this cable will dump the battery's full short-circuit current straight through it — ABYC E-11 §11.10.1.1 requires a fuse within 7 in (178 mm) of the battery on every conductor connected to it.",
  "diag.unfused-source-output.fix":
    "Add a fuse close to the battery on this conductor (MRBF or class-T for high-current sources).",

  "diag.unfused-fuse-block-feed.title": "Unfused feed to {label}",
  "diag.unfused-fuse-block-feed.message":
    "The cable feeding {label}'s input from the battery has no fuse on its path. Per-slot fuses on the block protect each downstream branch but NOT the feed cable itself — if it chafes against chassis anywhere on the run, the battery's full short-circuit current dumps with nothing to stop it (ABYC E-11 §11.10.1.1).",
  "diag.unfused-fuse-block-feed.fix":
    "Add a main fuse (MRBF or class-T) within 7 in (178 mm) of the battery on the feed cable.",

  "diag.charger-input-output-loop.title":
    "{chargerLabel} input and output share {battLabel}",
  "diag.charger-input-output-loop.message":
    "{chargerLabel}'s positive output and a positive input both reach {battLabel}. The charger is trying to charge the same bank whose current feeds it — it will refuse to operate or just cycle losses through itself. The output (BATT+) belongs on a separate auxiliary/house battery.",
  "diag.charger-input-output-loop.fix":
    "Move {chargerLabel}'s BATT+ output to the auxiliary/house battery, not the source bank.",

  "diag.charger-output-on-cranking-bus.title":
    "{chargerLabel} output is on the cranking circuit",
  "diag.charger-output-on-cranking-bus.message":
    "{chargerLabel}'s BATT+ output reaches {engineLabel}'s starter/alternator lug — the start-battery bus. A DC-DC charger's output should land on a separate auxiliary/house battery, not the same lug its input draws from. As wired, the charger is trying to top up the bank it's powered by.",
  "diag.charger-output-on-cranking-bus.fix":
    "Move {chargerLabel}'s BATT+ output to a dedicated house battery. Keep its alternator/start-side input on the engine lug.",

  "diag.dcdc-fed-without-battery-buffer.title":
    "{chargerLabel} is fed directly by {altLabel} with no battery on the bus",
  "diag.dcdc-fed-without-battery-buffer.message":
    "{chargerLabel}'s positive input reaches {altLabel} without a battery on the same bus. A DC-DC's near-zero input impedance has nothing to clamp it, and the alternator regulator has nothing to absorb load-dump transients — expect a fried regulator or DC-DC the first time the engine is started without the start battery on-line.",
  "diag.dcdc-fed-without-battery-buffer.fix":
    "Wire the alternator B+ to the start battery first, then take {chargerLabel}'s input off the start-battery positive (or a busbar that the start battery is on).",

  "diag.fuse-block-slot-unrated.title_one":
    "{label}: slot {slots} has no fuse rating",
  "diag.fuse-block-slot-unrated.title_many":
    "{label}: slots {slots} have no fuse rating",
  "diag.fuse-block-slot-unrated.message_one":
    "{slots} on {label} is carrying a load but has no fuse rating. Until a rating is picked the slot offers no over-current protection — the downstream cable can melt before anything trips.",
  "diag.fuse-block-slot-unrated.message_many":
    "{slots} on {label} are carrying a load but have no fuse rating. Until a rating is picked the slots offer no over-current protection — the downstream cables can melt before anything trips.",
  "diag.fuse-block-slot-unrated.fix":
    "Open the fuse block in the inspector and assign a fuse rating to each used slot, sized to the smallest cable downstream.",

  "diag.charger-return-floating.title": "Return path missing: {label}",
  "diag.charger-return-floating.message": "The drawing has no complete return from {negLabels} to the destination battery bank.",
  "diag.charger-return-floating.message_dead_end": "The drawing has no complete return from {negLabels} to the destination battery bank.",
  "diag.charger-return-floating.fix": "Connect {negLabels} to the destination bank’s negative bus.",

  "diag.source-return-floating.title": "Return path missing: {label}",
  "diag.source-return-floating.message": "The positive supply is connected, but no return to the same battery bank is established.",
  "diag.source-return-floating.fix": "Check the negative or case-ground connection back to that bank.",

  "diag.monitor-power-unverified.title": "Check monitor power: {label}",
  "diag.monitor-power-unverified.message_missing": "The drawing lacks a complete supply through Vbatt+ and the negative connection. AUX does not replace Vbatt+.",
  "diag.monitor-power-unverified.message_voltage": "{label} needs 6.5–70 V DC. The supply voltage is unknown or outside this range.",
  "diag.monitor-power-unverified.message_protection": "Vbatt+ has a supply path without a declared fuse of 1 A or less. Check the supplied fused lead.",
  "diag.monitor-power-unverified.fix": "Check the supply, return and fused lead against the monitor manual.",
  "diag.shunt-bypass.title": "Current bypasses {label}",
  "diag.shunt-bypass.message": "{equipment} has a return to the monitored battery that does not cross {label}. The monitor can miss this charge or discharge current and report an incorrect state of charge.",
  "diag.shunt-bypass.fix": "For whole-bank monitoring, move these returns to the {terminal} side of {label} and remove any parallel return around the shunt.",

  "diag.sense-terminal-as-bus.title":
    "{label}: {terminal} is a sense lead, not a bus",
  "diag.sense-terminal-as-bus.message":
    "{count} cables land on {label}'s {terminal}. A sense terminal is rated {maxAmp} A and only measures voltage — it cannot carry or distribute current, so whatever is routed through it is unsized and unprotected.",
  "diag.sense-terminal-as-bus.message_bonds":
    "{label}'s {terminal} is the only link between {batteries}. Those banks are tied together through a {maxAmp} A sense wire, which will carry whatever equalising current they disagree by — unfused and never sized for it.",
  "diag.sense-terminal-as-bus.fix":
    "Give the sense terminal one lead to the battery it measures (fused 1 A at the post) and move every other cable onto a bus bar or the battery post.",

  "diag.terminal-overloaded.title_provisional": "Check terminal current: {label} {terminal}",
  "diag.terminal-overloaded.message_provisional": "Estimated current is {load} A against a {maxAmp} A rating. The incomplete model cannot confirm an overload.",
  "diag.terminal-overloaded.fix_verify": "Complete the circuit and check operating-current specifications, then compare with the terminal rating.",
  "diag.terminal-overloaded.title": "Terminal rating exceeded: {label} {terminal}",
  "diag.terminal-overloaded.message": "Calculated continuous current is {load} A, above the terminal’s {maxAmp} A rating. The connection may overheat.",
  "diag.terminal-overloaded.fix": "Use a component rated for at least {load} A or reduce the load.",

  "diag.fuse-exceeds-terminal.title": "Fuse too large for {label}: {terminal}",
  "diag.fuse-exceeds-terminal.message": "This connection is rated {maxAmp} A but protected by a {fuse} A fuse.",
  "diag.fuse-exceeds-terminal.fix": "Use protection at {maxAmp} A or less, or a component rated for the larger fuse.",

  "diag.cable-dangling-terminal.title": "Cable has a missing terminal",
  "diag.cable-dangling-terminal.message": "{label} has no {terminal} terminal. This cable cannot complete a circuit.",
  "diag.cable-dangling-terminal.fix": "Reconnect it to an existing terminal, restore the terminal or remove the cable.",
  "diag.cable-dangling-terminal.title_missing_node": "Cable has a missing component",
  "diag.cable-dangling-terminal.message_missing_node": "One end of this cable is missing. It cannot complete a circuit.",
  "diag.cable-dangling-terminal.fix_missing_node": "Reconnect the loose end or remove this cable.",
  "diag.cable-dangling-terminal.action_remove": "Remove broken cable",

  "diag.pv-input-incomplete.title": "Incomplete PV circuit at {label}",
  "diag.pv-input-incomplete.message": "The connected PV input on {label} has no complete positive and return path through a solar array. A missing connection or incorrect polarity prevents solar charging.",
  "diag.pv-input-incomplete.fix": "Complete the positive and return connections between the array and this PV input, including any series jumpers.",
  "diag.pv-string-fuse-exceeds-module.title": "PV string fuse {fuse} A exceeds module limit {max} A",
  "diag.pv-string-fuse-exceeds-module.message": "This string has a {fuse} A fuse, but {label} allows a maximum series fuse of {max} A. The fuse does not meet the module’s specified overcurrent protection limit.",
  "diag.pv-string-fuse-exceeds-module.fix": "Use a PV-rated string fuse of {max} A or less, also checking the required operating-current rating. Keep individual string protection separate from the shared combiner trunk.",

  "diag.mppt-over-voltage.title":
    "{label} PV input exceeds {max} V max",
  "diag.mppt-over-voltage.message":
    "The longest series string into {label} reaches {voc} V Voc at STC across {panels} panels, climbing to {vocCold} V at −25 °C operating temperature (NEC 690.7 ×1.20) — above the controller's {max} V max PV input. Exceeding the specified input limit can damage the controller; verify cold-weather Voc against the actual panel data and installation temperature.",
  "diag.mppt-over-voltage.fix":
    "Re-wire the panels in parallel instead of series, drop a panel from the string, or pick an MPPT with a higher PV-voltage rating (cold-Voc ≤ {max} V).",

  // ── Diagnostics — sizing ───────────────────────────────
  "diag.combined-battery-discharge.title": "Connected demand exceeds battery discharge ratings",
  "diag.combined-battery-discharge.message": "Known connected demand is at least {draw} A, above the {limit} A bound from declared battery discharge ratings.",
  "diag.combined-battery-discharge.fix": "Reduce simultaneous demand or verify and increase the battery bank’s discharge capability.",
  "diag.combined-battery-discharge.detail_message": "This compares {count} known consuming devices on complete battery circuits, using their simultaneous sustained design demand. It excludes starting surge and does not subtract optional charging. Inferred DC-DC input requires complete circuits and declared voltages; without declared efficiency it uses a loss-free lower bound. Demand across a series string counts at each required section; midpoint loads add to the batteries supplying them. Parallel ratings form an aggregate bound without assuming equal or capacity-based sharing.",
  "diag.combined-battery-discharge.detail_fix": "Check the installed battery and BMS continuous discharge ratings and operating conditions. Unknown loads, incomplete circuits and unspecified ratings are not certified by this check. Verify current sharing and manufacturer approval before adding parallel batteries. This is a design calculation, not measured current.",

  "diag.inverter-exceeds-battery-c-rate.title_unverified": "Verify battery discharge capability for {label}",
  "diag.inverter-exceeds-battery-c-rate.message_unverified": "One or more battery discharge ratings are unavailable, so Crimp cannot confirm the bank’s continuous capability for {label}. Its modeled demand is up to {draw} A.",
  "diag.inverter-exceeds-battery-c-rate.fix_unverified": "Verify the connected batteries’ continuous discharge ratings, any BMS limits and wiring paths before choosing equipment.",

  "diag.inverter-exceeds-battery-c-rate.title":
    "{label} exceeds the declared battery discharge rating",
  "diag.inverter-exceeds-battery-c-rate.message":
    "{label} has a modeled demand up to {draw} A, above the bank's declared continuous discharge limit of {bank} A. Verify the installed battery and any BMS ratings.",
  "diag.inverter-exceeds-battery-c-rate.fix":
    "Verify the connected battery paths and ratings. Reduce demand or use equipment with adequate discharge capability; follow manufacturer approval for parallel batteries.",

  "diag.dcdc-overloads-alternator.title": "Check alternator load: {pct}%",
  "diag.dcdc-overloads-alternator.message": "DC-DC demand is {draw} A against the alternator’s {rated} A rating. Available output at idle may be lower.",
  "diag.dcdc-overloads-alternator.fix": "Check the alternator’s continuous output at idle and set the DC-DC limit accordingly.",

  "diag.charge-exceeds-acceptance.title_recommended": "Check recommended charge current",
  "diag.charge-exceeds-acceptance.message_recommended": "Sources can supply up to {charge} A, above this battery model’s recommended {accept} A charging current.",
  "diag.charge-exceeds-acceptance.fix_recommended": "Review charger settings against the recommendation and the battery and BMS limits.",
  "diag.charge-exceeds-acceptance.detail_message_recommended": "The manufacturer recommends {accept} A for this catalog battery model. This is a recommendation, not its maximum charge-current rating. Sources can supply up to {charge} A; actual current depends on settings and simultaneous use.",
  "diag.charge-exceeds-acceptance.detail_fix_recommended": "Verify the installed battery model, maximum charge current, BMS limits and operating conditions. Preserve the recommended charging guidance when configuring chargers and alternator regulators.",
  "diag.charge-exceeds-acceptance.title_planning": "Verify the charge planning threshold",
  "diag.charge-exceeds-acceptance.message_planning": "Sources can supply up to {charge} A, above Crimp’s {accept} A planning threshold for this battery.",
  "diag.charge-exceeds-acceptance.fix_planning": "Check the exact battery and BMS data before choosing charging settings.",
  "diag.charge-exceeds-acceptance.detail_message_planning": "Crimp retains {accept} A as a conservative planning threshold for this catalog battery. It is not a verified manufacturer maximum or recommendation. Sources can supply up to {charge} A; confirm the actual equipment limits.",
  "diag.charge-exceeds-acceptance.detail_fix_planning": "Identify the installed model and revision, then check its charge-current rating, BMS limits and operating conditions. A planning threshold does not establish the permitted charging current.",
  "diag.charge-exceeds-acceptance.title": "Check battery charge limit",
  "diag.charge-exceeds-acceptance.title_current": "{charge} A charging capacity: check battery limit",
  "diag.charge-exceeds-acceptance.message_specified": "Sources can supply up to {charge} A; this drawing uses a {accept} A charge threshold. Verify the declaration and charger settings.",
  "diag.charge-exceeds-acceptance.fix_specified": "Keep charging within verified battery and BMS limits; check the declared threshold.",
  "diag.charge-exceeds-acceptance.message": "Sources can supply up to {charge} A, above the estimated battery limit of {accept} A. Check the actual battery specification.",
  "diag.charge-exceeds-acceptance.fix_lead": "Check the manufacturer’s maximum charge current and adjust charger settings.",
  "diag.charge-exceeds-acceptance.fix_lfp": "Check battery and BMS charge limits, then adjust charger settings.",

  "diag.manufacturer-breaker-unverified.title": "Check the specified breaker: {label}",
  "diag.manufacturer-breaker-unverified.message": "The manufacturer specifies a {rating} A breaker. A fuse with the same rating may not be equivalent.",
  "diag.manufacturer-breaker-unverified.fix": "Confirm the exact breaker and trip curve in the manual.",
  "diag.manufacturer-breaker-rating.title": "Check breaker rating: {label}",
  "diag.manufacturer-breaker-rating.message": "The drawing shows {rating} A protection; the manufacturer specifies a {required} A breaker.",
  "diag.manufacturer-breaker-rating.fix": "Check the breaker on every supply path. Keep required feeder protection.",
  "diag.manufacturer-supplied-lead-protection.title": "Check supplied cable protection: {label}",
  "diag.manufacturer-supplied-lead-protection.message": "The supplied {mm2} mm² cable is rated {ampacity} A, below the drawn {rating} A fuse.",
  "diag.manufacturer-supplied-lead-protection.fix": "Protect the supplied cable according to the manual. A thicker extension does not increase its rating.",
  "cable.manufacturer_fridge_fuse_hint": "The CRX manual specifies {rating} A at the connected supply voltage. Confirm the exact fridge variant and fuse type.",
  "cable.manufacturer_wire_ampacity_hint": "The selected wire cannot support the manufacturer fuse rating. Verify the wire and protection before choosing a fuse.",
  "cable.manufacturer_installation_unverified": "Verify the manufacturer’s wiring requirements. This wire size remains provisional.",
  "diag.manufacturer-installation-unverified.title": "{label}: verify manufacturer wiring",
  "diag.manufacturer-installation-unverified.message": "The manufacturer wire requirement is not established for this circuit.",
  "diag.manufacturer-installation-unverified.identity": "This part retains a CRX catalog identifier but its original model identity is not established. Verify its own installation manual before using numerical wire or fuse advice.",
  "diag.manufacturer-installation-unverified.route": "The complete source-to-fridge feed and return length is unresolved or has alternative paths. Verify the manufacturer wire requirement.",
  "diag.manufacturer-installation-unverified.voltage": "The fridge supply voltage is unknown or outside the supported 12/24 V classes. Verify its installation requirements.",
  "diag.manufacturer-installation-unverified.24v-chart": "The 24 V installation requires the manufacturer cable chart. The 7.5 A fuse advice does not establish the required wire size.",
  "diag.manufacturer-installation-unverified.length": "The complete 12 V feed or return exceeds the supported 8 m chart. Verify the wire size with the manufacturer.",
  "diag.manufacturer-wire-minimum.ip43": "The selected {mm2} mm² cable is below {label}'s published minimum of {minimumLabel}.",
  "diag.manufacturer-installation-unverified.ip43": "This IP43 installation is outside the reviewed cable guidance. Automatic wire and fuse advice remains provisional.",
  "diag.manufacturer-installation-unverified.ip43-identity": "The edited charger data does not establish the reviewed IP43 model and output connections. No manufacturer cable minimum is inferred from its copied catalog identity.",
  "diag.manufacturer-installation-unverified.ip43-route": "A complete matched 12 V battery route and declared source voltages are not established. Branches, series banks or additional paths prevent verification of the manufacturer cable table.",
  "diag.manufacturer-installation-unverified.ip43-length": "The longer one-way lead exceeds the published cable table: 3 m for the 12/50 or 4.5 m for the 12/30 main output. A larger cable alone does not verify this installation.",
  "diag.manufacturer-installation-unverified.ip43-terminal": "The cable directly at a charger main terminal exceeds its published 16 mm² capacity. Verify an approved termination; do not force an oversized conductor into the terminal.",
  "diag.manufacturer-installation-unverified.ip43-fix": "Confirm the exact charger version, complete positive and negative lead lengths, terminal capacity and cable installation conditions against its manual.",
  "diag.manufacturer-installation-unverified.parallel": "The manual specifies parallel DC cables. Crimp cannot verify this arrangement; automatic wire and fuse advice remains provisional.",
  "diag.manufacturer-installation-unverified.parallel-fix": "Check both DC leads against the exact model’s installation table.",
  "diag.manufacturer-installation-unverified.fix": "Confirm the exact CRX variant, supply voltage and complete cable lengths against its installation manual.",
  "diag.manufacturer-fuse-minimum.title": "{label}: fuse below manufacturer requirement",
  "diag.manufacturer-fuse-minimum.message": "A declared {rating} A supply fuse is below the documented {minimum} A requirement for {label}.",
  "diag.manufacturer-fuse-minimum.fix": "Check the exact model manual and selected wire before changing protection. A higher rating alone does not establish suitable fuse type or interrupt capacity.",
  "diag.manufacturer-wire-minimum.title": "{label}: cable below manufacturer minimum",
  "diag.manufacturer-wire-minimum.message": "This {mm2} mm² lead is below the {minimum} mm² installation minimum for {label}.",
  "diag.manufacturer-wire-minimum.fix": "Use at least the manufacturer minimum and check the complete run's ampacity and voltage drop.",
  "diag.manufacturer-fuse-range.title": "{label}: fuse outside manufacturer range",
  "diag.manufacturer-fuse-range.message": "The {rating} A fuse is outside the {min}–{max} A battery-fuse range specified for {label}.",
  "diag.manufacturer-fuse-range.fix": "Fit a fuse within the manufacturer's range and verify that the conductor can carry its rating.",

  "diag.charger-cumulative-vdrop.title": "{label}: {pct}% charging voltage drop",
  "diag.charger-cumulative-vdrop.title_verify": "Check charging voltage drop: {label}",
  "diag.charger-cumulative-vdrop.message_manufacturer": "Estimated circuit loss is {drop} V. This SmartSolar model specifies at most {allowance}% battery-cable loss.",
  "diag.charger-cumulative-vdrop.message_target": "Estimated circuit loss is {drop} V, above the {allowance}% planning target. This is not a manufacturer limit.",
  "diag.charger-cumulative-vdrop.message_verify": "The circuit estimate is {drop} V against a {allowance}% allowance. Current sharing is unverified, so actual loss may differ.",
  "diag.charger-cumulative-vdrop.fix": "Check current and cable lengths. Increase cable sizes or shorten the route where needed.",
  "diag.cranking-vdrop-unverified.title": "Check starting cable size",
  "diag.cranking-vdrop-unverified.message": "Estimated starting voltage drop is {pct}%, above Crimp’s conservative planning target.",
  "diag.cranking-vdrop-unverified.fix": "Check starting current, cable lengths and sizes against the engine manual.",

  "diag.pv-cumulative-vdrop.title": "{label}: {pct}% solar circuit voltage drop",
  "diag.pv-cumulative-vdrop.message": "The complete solar feed and return lose {drop} V at design current, exceeding the selected {allowance}% planning target.",
  "diag.pv-cumulative-vdrop.fix": "Shorten the solar cable run or increase conductor sizes; check the panel and controller installation manuals.",
  "diag.cumulative-vdrop.title": "{label}: {pct}% design drop battery → load",
  "diag.cumulative-vdrop.message":
    "The summed design voltage drop for {label} is {drop} V, exceeding the circuit's {allowance}% allowance. Per-conductor current maxima need not occur simultaneously; this is a sizing calculation, not a prediction of actual voltage loss.",
  "diag.cumulative-vdrop.title_lower_bound": "{label}: at least {pct}% design drop battery → load",
  "diag.cumulative-vdrop.message_lower_bound":
    "The resolved portion of the design voltage-drop calculation for {label} totals {drop} V, already above the {allowance}% allowance. Uncertain conductor contributions are omitted, so the full design sum is at least this value. Per-conductor maxima need not occur simultaneously; this is not a prediction of actual voltage loss.",
  "diag.cumulative-vdrop.title_provisional": "{label}: verify estimated {pct}% design drop",
  "diag.cumulative-vdrop.message_provisional":
    "The provisional design-drop estimate for {label} is {drop} V, above the {allowance}% allowance. Unverified current values contribute to this estimate, so it establishes neither actual voltage loss nor a guaranteed lower bound.",
  "diag.cumulative-vdrop.fix_provisional":
    "Verify the complete supply and return circuit, device input/output ratings and current sharing. Recalculate the voltage drop before relying on an automatic wire size.",
  "diag.cumulative-vdrop.fix":
    "Move the fuse and load closer to the battery, or step the trunk run up one wire size.",
  "diag.cumulative-vdrop.group_title": "Design voltage drop battery → load",
  "diag.cumulative-vdrop.short_title": "{label}: {pct}%",
  "diag.cumulative-vdrop.short_title_lower_bound": "{label}: at least {pct}%",
  "diag.cumulative-vdrop.short_title_provisional": "{label}: verify estimated {pct}%",
  "health.group_count": "{count} loads",
  "health.group_expand": "Show {count}",
  "health.group_collapse": "Hide",

  "diag.fuse-trips-under-load.title":
    "{rating} A fuse will trip — run carries {load} A",
  "diag.fuse-trips-under-load.message":
    "Components on this run draw {load} A continuous, but this cable is fused at {rating} A. Adding a high-current device downstream (a starter, an inverter…) raises every upstream fuse on its path.",
  "diag.fuse-trips-under-load.fix":
    "Step the fuse up to at least {target} A and verify the wire's ampacity supports it.",
  "diag.fuse-trips-under-load.title_verify":
    "Verify {rating} A fuse against {load} A design demand",
  "diag.fuse-trips-under-load.message_starting":
    "The modeled {load} A includes brief starting current. This does not establish whether the {rating} A fuse will open during starting. Check the load duration and the installed fuse's time-current curve.",
  "diag.fuse-trips-under-load.message_provisional":
    "The {load} A current estimate is provisional. It does not establish actual sustained current or a definite trip of the {rating} A fuse. Verify the circuit, current sharing and load duration before changing protection.",
  "diag.fuse-trips-under-load.fix_verify":
    "Check the actual load duration, the installed fuse's time-current curve and the conductor's ampacity. Confirm the required rating and fuse location before replacing it.",

  "diag.wire-undersized-for-load.title":
    "Wire too thin — needs ≥ {required} A ampacity, has {ampacity} A",
  "diag.wire-undersized-for-load.title_provisional":
    "Verify wire size — current estimate is incomplete",
  "diag.wire-undersized-for-load.message_provisional":
    "The provisional {load} A estimate suggests {required} A of design ampacity, above this wire's {ampacity} A rating. The current calculation is incomplete and is not a verified upper bound. Confirm current paths, load duration and installation conditions before choosing a conductor or protection rating.",
  "diag.wire-undersized-for-load.fix_verify":
    "Resolve the incomplete current model and verify conductor ampacity, circuit voltage drop and overcurrent protection. An automatic size change cannot establish that this circuit is adequately rated.",
  "diag.wire-undersized-for-load.message":
    "The modeled {load} A load requires {required} A of design ampacity, including the applicable margin for sustained loads. Brief starter current is treated separately from sustained current. This {mm2} mm² wire is rated {ampacity} A, below that requirement. Check the conductor size, installation conditions and overcurrent protection.",
  "diag.wire-undersized-for-load.fix":
    "Upgrade to a wire with ≥ {required} A ampacity, or add a fuse rated ≤ {ampacity} A.",
  "diag.wire-undersized-for-load.fix_wire":
    "Choose a conductor rated for at least {required} A under the installation conditions. Verify voltage drop across the complete circuit.",
  "diag.wire-undersized-for-load.fix_manual":
    "No automatic wire choice meets the design requirement. Reduce or split the load, or verify a suitable conductor arrangement with at least {required} A ampacity and acceptable circuit voltage drop.",

  // ── Diagnostics — energy ───────────────────────────────
  "diag.no-battery.title": "No battery in the system",
  "diag.no-battery.message":
    "Add a battery so we can estimate runtime, autonomy, and charging time.",
  "diag.battery-capacity-unknown.title": "Battery capacity is missing",
  "diag.battery-capacity-unknown.message":
    "Battery symbols are present, but their definitions do not establish usable capacity. Runtime, autonomy and charging time cannot be estimated from this data.",
  "diag.battery-capacity-unknown.fix":
    "Enter each real battery's nominal capacity in Ah. Represent a converter output as a converter, rather than giving an equivalent source a fictitious battery capacity.",

  "diag.no-charging.title": "No charging source",
  "diag.no-charging.message":
    "The active loads draw ~{watts} W with no charging source. At this draw, the battery reaches its reserve in {hours} hours.",
  "diag.no-charging.fix":
    "Add a solar panel + MPPT, a DC-DC charger, or a shore-power charger.",

  // ── Tools menu ─────────────────────────────────────────
  "tools.title": "Tools",
  "tools.bom": "Bill of materials",
  "tools.auto_layout": "Auto-layout selection",
  "tools.redraw_cables": "Redraw all cables",
  "tools.freeze_build": "Freeze build…",
  "tools.unfreeze_build": "Unfreeze build…",
  "tools.section.build_state": "Build state",
  "tools.section.build_state_hint":
    "Pin auto-sized wires and fuses so later edits don't quietly change a finalised build.",

  // ── Freeze / unfreeze build ────────────────────────────
  "freeze_build.freeze_title": "Freeze build",
  "freeze_build.unfreeze_title": "Unfreeze build",
  "freeze_build.freeze_description":
    "Pin every cable's current auto-sized wire and fuse so later edits don't quietly change them. Use this when you're committing to a build for purchase or installation.",
  "freeze_build.unfreeze_description":
    "Release every cable's wire size so wires resume tracking the live auto recommendation as you edit.",
  "freeze_build.freeze_bullet_wire":
    "Each cable's wire size is locked to the value it shows right now.",
  "freeze_build.freeze_bullet_fuse":
    "Cables that already have a fuse are refreshed to the current recommended rating. Unfused cables (negatives, cranking circuits) stay unfused.",
  "freeze_build.freeze_bullet_undo":
    "Reversible with Undo, or with the Unfreeze build command.",
  "freeze_build.unfreeze_bullet_wire":
    "Every cable's wire-size override is cleared. Wires return to live auto-sizing.",
  "freeze_build.unfreeze_bullet_fuse":
    "Fuse ratings are left untouched — unfreezing won't remove protection from any cable.",
  "freeze_build.unfreeze_bullet_undo": "Reversible with Undo.",
  "freeze_build.preview_label": "What will change",
  "freeze_build.preview_wires": "{count} cable(s) will have their wire size locked.",
  "freeze_build.preview_fuses":
    "{count} fuse rating(s) will be refreshed to the current recommendation.",
  "freeze_build.preview_cleared":
    "{count} cable(s) will be unlocked and return to auto-sizing.",
  "freeze_build.already_frozen":
    "Every cable already matches its current auto recommendation — nothing to change.",
  "freeze_build.nothing_to_unfreeze":
    "No cables have a locked wire size — nothing to unfreeze.",
  "freeze_build.no_cables": "There are no cables in this project yet.",
  "freeze_build.cancel": "Cancel",
  "freeze_build.freeze_confirm": "Freeze build",
  "freeze_build.unfreeze_confirm": "Unfreeze",

  // ── Printable blueprint ────────────────────────────────
  "blueprint.label": "Crimp Lifetime",
  "blueprint.title": "Printable blueprint",
  "blueprint.description":
    "A print-ready electrical drawing with cable references, one-way lengths, conductor sizes, assigned fuses and a cable schedule.",
  "blueprint.print_hint": "A3 landscape · Print or save as PDF in your browser",
  "blueprint.close": "Close",
  "blueprint.print": "Print / save PDF",
  "blueprint.preview.label": "Free preview",
  "blueprint.preview.title": "Unlock the full blueprint",
  "blueprint.preview.description":
    "The complete schematic and first {count} rows of each table are included. Get Crimp Lifetime to see every row, then print or save as PDF.",
  "blueprint.preview.cta": "Get the full blueprint",
  "blueprint.preview.footer_hint":
    "Preview · full schematic and sample table rows",
  "blueprint.preview.more_rows": "{count} more with Crimp Lifetime",
  "blueprint.sheet.schematic": "Electrical schematic",
  "blueprint.sheet.schedule": "Cable schedule",
  "blueprint.sheet.bom": "Bill of materials",
  "blueprint.empty": "Add components and cables to create a blueprint.",
  "blueprint.no_cables": "No cables to list.",
  "blueprint.legend.cable": "Cable reference",
  "blueprint.legend.schedule": "Full cable specifications are listed on sheet 2",
  "blueprint.legend.length": "One-way length",
  "blueprint.legend.wire": "Conductor size",
  "blueprint.legend.fuse": "Assigned fuse",
  "blueprint.verify_badge": "VERIFY BEFORE INSTALLATION",
  "blueprint.cable_schedule": "Cable schedule",
  "blueprint.warnings.title": "Items to verify",
  "blueprint.warning.invalid_length": "{refs}: length is missing or invalid.",
  "blueprint.warning.unresolved_endpoint":
    "{refs}: an endpoint or terminal could not be resolved.",
  "blueprint.warning.unresolved_sizing":
    "{refs}: load could not be resolved; verify the conductor size.",
  "blueprint.warning.unprotected_positive":
    "{refs}: positive conductor has no assigned fuse.",
  "blueprint.warning.duplicate_split_protection":
    "{refs}: the same assigned fuse appears on both sides of a splice; verify whether one or two physical fuses are intended.",
  "blueprint.warning.duplicate_user_reference":
    "{refs}: the cable label “{label}” is duplicated; unique print suffixes were added.",
  "blueprint.notes.title": "Installation notes",
  "blueprint.notes.length":
    "Lengths are one-way project values; no cutting or slack allowance is included.",
  "blueprint.notes.fuse":
    "Fuse values show assigned protection only. Fuse type, holder, and physical location are not modeled.",
  "blueprint.notes.generated_refs":
    "Generated cable references may change when the topology changes. Add your own cable label for a permanent installation ID.",
  "blueprint.bom.title": "Bill of materials",
  "blueprint.bom.polarity": "Polarity",
  "blueprint.polarity.positive": "Positive",
  "blueprint.polarity.negative": "Negative / return",
  "blueprint.polarity.signal": "Signal",
  "blueprint.polarity.unknown": "Unknown",
  "blueprint.bom.cable_summary":
    "{count} cables · {length} total recorded length",
  "blueprint.bom.unresolved":
    "Cables excluded from procurement groups because sizing is unresolved: {count}",
  "blueprint.bom.fuse_ambiguous":
    "Physical quantity is ambiguous across {refs}; verify before ordering.",
  "blueprint.document_type": "ELECTRICAL BLUEPRINT",
  "blueprint.generated": "Generated",
  "blueprint.units": "Units",
  "blueprint.size_target": "Auto-size drop target",
  "blueprint.col.ref": "Ref",
  "blueprint.col.from": "From",
  "blueprint.col.to": "To",
  "blueprint.col.length": "Length",
  "blueprint.col.conductor": "Conductor",
  "blueprint.col.current": "Design current",
  "blueprint.col.fuse": "Assigned fuse",
  "blueprint.col.vdrop": "Voltage drop",
  "blueprint.col.notes": "Notes",
  "blueprint.generated_ref": "Generated reference",
  "blueprint.value.verify": "VERIFY",
  "blueprint.value.manual": "manual",
  "blueprint.value.continuous": "continuous",
  "blueprint.value.peak": "peak",
  "blueprint.value.unfused": "No assigned fuse",
  "blueprint.value.suggested": "Suggested: {rating} A",

  "professional_feature.title": "Professional feature",
  "professional_feature.description": "Bill of materials and JSON export are included in Professional.",
  "professional_feature.action": "See Professional",

  // ── Bill of materials ──────────────────────────────────
  "bom.label": "Tools",
  "bom.title": "Bill of materials",
  "bom.unverified_cables": "{count} cables need sizing verification. Automatic sizes for these cables are omitted; any manually assigned sizes are listed as declarations to verify.",
  "bom.description":
    "Everything in your system, rolled up — components, cable lengths by size, and fuse ratings.",
  "bom.empty":
    "Drop components on the canvas and wire them up to see a bill of materials here.",
  "bom.section.components": "Components",
  "bom.section.cables": "Cables",
  "bom.section.fuses": "Fuses",
  "bom.col.item": "Item",
  "bom.col.qty": "Qty",
  "bom.col.size": "Size",
  "bom.col.length": "Total length",
  "bom.col.count": "Count",
  "bom.col.rating": "Rating",
  "bom.cables.summary": "{count} cables · {length} total",
  "bom.fuses.none": "No fuses on the cables yet.",
  "bom.cables.none": "No cables yet.",
  "bom.blueprint_hint":
    "Need the schematic and cable schedule too? Use Project → Printable blueprint.",
  "bom.copy": "Copy",
  "bom.copied": "Copied",
  "bom.copy_failed": "Couldn’t copy",
  "bom.print": "Print / save PDF",
  "bom.close": "Close",

  // ── Component settings (installer-set options on a part) ─────────────
  "inspector.settings.section": "Settings",
  "inspector.charge_limit.label": "Charge current limit",
  "inspector.charge_limit.hint": "Simulation limit. Cable sizing uses the rated {amps} A output.",
  "inspector.charge_limit.reset": "Use rated output",
  "inspector.charge_limit.range": "Enter a value from 0 to {amps} A.",
  "setting.orion.jumperDisabled.label": "Jumper disabled (off when battery switch is Combined)",
  "setting.orion.jumperDisabled.help":
    "Set when the Orion's remote on/off input is jumpered through a Blue Sea 6011 or similar dual-circuit switch's auxiliary contact, disabling the DC-DC whenever the switch is in COMBINE. Suppresses the input/output-loop warning that would otherwise fire because both sides see the same battery in that position.",
} as const;

export type TranslationKey = keyof typeof EN;

const SV: Record<TranslationKey, string> = {
  ...DESIGN_PROFILE_SV,
  ...PROFILE_DIAGNOSTICS_SV,
  ...FUSE_BOARD_SV,
  ...ASSISTANT_SV,
  "palette.missing.continue_cta": "Använd en platshållare eller önska modellen",
  "palette.missing.continue_body": "Fortsätt rita med en redigerbar platshållare eller be oss lägga till den exakta modellen.",
  "palette.missing.model_label": "Märke och modell",
  "palette.missing.category": "Komponentkategori",
  "palette.missing.all_categories": "Föreslagna typer",
  "palette.missing.template": "Anslutningstyp för platshållaren",
  "palette.missing.placeholder_warning": "Anslutningarna följer en generell mall. Elektriska märkdata är okända. Fyll i värden och kontrollera terminalerna mot manualen innan du förlitar dig på beräkningarna.",
  "palette.missing.email_label": "E-post för återkoppling (valfritt)",
  "palette.missing.source_label": "Länk till manual eller produkt (valfritt)",
  "palette.missing.request_too": "Be oss även lägga till den exakta modellen",
  "palette.missing.request_sent": "Förfrågan mottagen. Vi granskar modellen och dess dokumentation.",
  "palette.missing.placeholder_added": "Platshållaren har lagts till. Markera den för att redigera specifikationer och anslutningar.",
  "palette.missing.failed": "Din förfrågan skickades inte. Försök igen eller skicka den via ditt e-postprogram.",
  "palette.missing.email_fallback": "Öppna e-postutkast",
  "palette.missing.add_and_request": "Lägg till platshållare + skicka förfrågan",
  "palette.missing.add_placeholder": "Lägg till redigerbar platshållare",
  "palette.missing.retry_request": "Försök skicka igen",
  "palette.missing.send_only": "Skicka enbart förfrågan",
  "palette.missing.continue_mapping": "Fortsätt rita",
  "inspector.legacy_microwave.explanation": "Den här äldre komponenten uppskattar mikrovågsugnens förbrukning vid batteriet. Använd en AC-mikrovågsugn med L, N och PE för en AC-krets.",
  "inspector.legacy_microwave.replace": "Använd {voltage} V AC-mikrovågsugn",
  "inspector.legacy_microwave.review": "Befintliga L/N-kablar förblir anslutna. Kontrollera ingångseffekten på apparatens märkskylt och anslut PE enligt kraven.",
  "inspector.placeholder.title": "Platshållare — ej verifierade specifikationer",
  "inspector.placeholder.warning": "Den önskade modellen använder generella anslutningar. Kontrollera manualen och fyll i elektriska märkdata och terminaluppgifter innan du förlitar dig på beräkningarna.",
  "inspector.placeholder.available": "En matchande modell finns nu i katalogen. Bekräfta exakt variant innan du byter ut platshållaren.",
  "inspector.placeholder.replace": "Använd {name}",
  "inspector.placeholder.replace_hint": "Kablarna förblir anslutna. Katalogens specifikationer ersätter platshållarens ändringar. Kontrollera terminalerna efter bytet.",
  "diag.requested-component-unverified.title": "Komponentplatshållare behöver verifieras",
  "diag.requested-component-unverified.message": "{count} önskade komponenter använder fortfarande platshållaranslutningar och ej verifierade specifikationer.",
  "diag.requested-component-unverified.fix": "Kontrollera varje modells manual och redigera specifikationer och terminaler, eller ersätt den med exakt rätt katalogkomponent.",

  "health.reviewed_item": "Kontrollerad: {title}",
  "diag.monitor-power-unverified.detail_message_missing": "Ritningen visar ingen fullständig likströmsmatning mellan Vbatt+ och minusanslutningen för {label}. AUX är valfri och ersätter inte Vbatt+.",
  "diag.monitor-power-unverified.detail_message_voltage": "{label} behöver 6,5–70 V likspänning. Matningens spänning saknas eller ligger utanför intervallet; kontrollera Vbatt+ mot tillverkarens spänningsgränser.",
  "diag.monitor-power-unverified.detail_message_protection": "Vbatt+-matningen till {label} har en väg utan angivet skydd på högst 1 A. Victron levererar ledningen med en trög säkring på 1 A.",
  "diag.monitor-power-unverified.detail_fix": "Kontrollera Vbatt+-ledningen, tillhörande minusretur och tillverkarens säkrade ledning eller temperaturgivare. Kontrollera den installerade säkringen och matningsspänningen.",
  "diag.charge-exceeds-acceptance.detail_message_specified": "Schemat eller katalogen anger {accept} A för laddning. Värdet i sig visar inte om det är ett maximum, en rekommendation eller ett planeringsvärde. Källorna kan ge upp till {charge} A; verifiera specifikationerna för exakt batteri och BMS.",
  "diag.charge-exceeds-acceptance.detail_fix_specified": "Bekräfta vad den angivna strömmen avser. Följ faktiska maximala värden och tillverkarens rekommendationer när samtidiga laddare och generatorregulatorer ställs in.",
  "diag.charge-exceeds-acceptance.detail_message": "Angivna laddkällor kan tillsammans ge upp till {charge} A till en bank på {ah} Ah, över uppskattade {limit}C (~{accept} A). Kontrollera reglering, samtidig drift och tillverkarens maximala laddström.",
  "diag.charge-exceeds-acceptance.detail_fix_lead": "Kontrollera tillverkarens gräns och laddregleringen; för hög ström kan överhetta blybatterier.",
  "diag.charge-exceeds-acceptance.detail_fix_lfp": "Kontrollera batteriets och BMS:ens maximala laddströmmar; kemiuppskattningen fastställer inte det installerade batteriets gräns.",
  "diag.charger-cumulative-vdrop.detail_message_manufacturer": "Hela kretsen mellan laddare och batteri har ett konservativt dimensionerande spänningsfall på {drop} V. Den angivna SmartSolar-modellen begränsar batterikablarnas förlust till {allowance} %. Beräkningen använder nominell spänning och omfattar båda ledarna samt eventuella serielänkar i banken; den är ingen mätning under laddning.",
  "diag.charger-cumulative-vdrop.detail_message_target": "Hela kretsen mellan laddkälla och batteri har ett konservativt dimensionerande spänningsfall på {drop} V, över valt planeringsmål på {allowance} %. Målet är ingen tillverkargräns.",
  "diag.charger-cumulative-vdrop.detail_message_verify": "Hela kretsen mellan laddkälla och batteri uppskattas ge {drop} V mot {allowance} % dimensioneringsgräns. Ström eller fördelning mellan källor måste kontrolleras; summerade ledargränser förutsäger inte samtidigt verkligt spänningsfall.",
  "diag.charger-cumulative-vdrop.detail_fix": "Kontrollera hela plus- och returkretsen, laddström, bankspänning och kontaktförluster. Öka kabelarean eller korta sträckan vid behov; mät spänningen vid laddkällans och batteriets anslutningar vid full laddström.",

  "diag.source-current-allocation-unverified.detail_message": "Strömgränserna för {count} kablar beror på vilken källa som levererar eller tar emot effekt. Dimensioneringen kan lägga hela den sammanlagda strömmen på en gren; detta förutsäger inte den faktiska fördelningen och bevisar inte en överbelastning.",
  "diag.source-current-allocation-unverified.detail_fix": "Kontrollera val av källa, parallell strömfördelning och batterigränser. Kontrollera varje grens kabel, anslutning och säkring mot de resulterande strömmarna och eventuell startbelastning.",
  "diag.fuse-protection-unverified.detail_message_topology": "Källans anslutningar överskrider analysens storleksgräns, så källparen och deras felströmmar är oklara. Verifiera de {count} angivna skyddsplatserna, säkringsprodukterna, maximal DC-spänning och eventuell seriesamordning.",
  "diag.fuse-protection-unverified.detail_message_topology_placement": "Källans anslutningar överskrider analysens storleksgräns, så källparen och deras felströmmar är oklara. Verifiera de {count} angivna skyddsplatserna, säkringsprodukterna, maximal DC-spänning och eventuell seriesamordning. Samma märkström på kabel och enhet visar inte om det är en säkring eller separata inline-säkringar; placeringen är oklar.",
  "diag.fuse-protection-unverified.detail_message_unknown": "Felströmmen är okänd för detta källpar. {count} skyddsplatser är angivna, men säkringarnas exakta produkter, DC-spännings- och brytförmåga samt eventuell seriesamordning har inte verifierats.",
  "diag.fuse-protection-unverified.detail_message_series": "Uppgifter om enskilda batterier fastställer inte seriebankens felström. {count} skyddsplatser är angivna; säkringarnas exakta produkter, maximal DC-spänning och eventuell seriesamordning är inte verifierade.",
  "diag.fuse-protection-unverified.detail_message_estimated": "Felströmmen {faultA} A innehåller AI-uppskattningar. För {count} angivna skyddsplatser är säkringarnas exakta produkter, maximal DC-spänning och eventuell seriesamordning inte verifierade. Detta bekräftar inte skyddsförmågan.",
  "diag.fuse-protection-unverified.detail_message_declared": "Källans angivna felström är {faultA} A; det är inte ett verifierat maximum vid varje säkring. För {count} angivna skyddsplatser är säkringarnas exakta produkter, maximal DC-spänning och eventuell seriesamordning inte verifierade.",
  "diag.fuse-protection-unverified.detail_fix": "Ta fram felströmsdata för källan eller banken med förutsättningar och maximal driftspänning. Verifiera varje installerad säkrings exakta produkt och DC-brytförmåga. Bekräfta placeringar och dubbla kabel-/enhetsuppgifter. Om skyddet förlitar sig på en säkring uppströms krävs tillverkardata för den exakta seriekombinationen.",
  "diag.fuse-protection-unverified.detail_message_unknown_placement": "Felströmmen är okänd för detta källpar. {count} skyddsplatser är angivna, men säkringarnas exakta produkter, DC-spännings- och brytförmåga samt eventuell seriesamordning har inte verifierats. Samma märkström på kabel och enhet visar inte om det är en säkring eller separata inline-säkringar; placeringen är oklar.",
  "diag.fuse-protection-unverified.detail_message_series_placement": "Uppgifter om enskilda batterier fastställer inte seriebankens felström. {count} skyddsplatser är angivna; säkringarnas exakta produkter, maximal DC-spänning och eventuell seriesamordning är inte verifierade. Samma märkström på kabel och enhet visar inte om det är en säkring eller separata inline-säkringar; placeringen är oklar.",
  "diag.fuse-protection-unverified.detail_message_estimated_placement": "Felströmmen {faultA} A innehåller AI-uppskattningar. För {count} angivna skyddsplatser är säkringarnas exakta produkter, maximal DC-spänning och eventuell seriesamordning inte verifierade. Detta bekräftar inte skyddsförmågan. Samma märkström på kabel och enhet visar inte om det är en säkring eller separata inline-säkringar; placeringen är oklar.",
  "diag.fuse-protection-unverified.detail_message_declared_placement": "Källans angivna felström är {faultA} A; det är inte ett verifierat maximum vid varje säkring. För {count} angivna skyddsplatser är säkringarnas exakta produkter, maximal DC-spänning och eventuell seriesamordning inte verifierade. Samma märkström på kabel och enhet visar inte om det är en säkring eller separata inline-säkringar; placeringen är oklar.",
  "diag.fuse-identity-conflict.detail_message": "Kabel- och enhetsfälten anger {ratings} A. De kan inte kombineras till en säkringsidentitet. Ritningen visar inte om detta är en inaktuell dubblett eller en separat inline-säkring.",
  "diag.fuse-identity-conflict.detail_fix": "Kontrollera säkringarnas placeringar, produkter och märkströmmar. Rätta inaktuella kabel- eller enhetsvärden, eller modellera separata inline-säkringar med egna hållare.",
  "diag.fuse-too-far-from-battery.detail_message": "Den längsta modellerade ledningen från den här batteribanken till dess första säkring är {distanceMm} mm. ABYC E-11:s grundgräns är 175 mm från pluspolen. Installationsundantag för extra skyddshölje eller kapsling modelleras inte. En kortslutning före säkringen kan inte brytas av den nedströms säkringen. Kontrollera alla markerade matningar mot den verkliga installationen.",
  "diag.fuse-too-far-from-battery.detail_fix": "Kontrollera de faktiska osäkrade kabellängderna och skyddens placering. Montera rätt dimensionerade skydd nära varje batterimatning; använd polsäkring eller separat säkringshållare som passar installationen.",
  "diag.battery-missing-terminal-fuse.detail_message": "Pluskabeln från det här batteriet är längre än ABYC E-11:s 175 mm-gräns utan polsäkring. En kortslutning på den oskyddade sträckan kan överhetta ledaren; den tillgängliga felströmmen beror på batteriet och installationen.",
  "diag.battery-missing-terminal-fuse.detail_fix": "Sätt en batterisäkring i inspektorn (MRBF eller Class T dimensionerad för banken), eller montera den befintliga säkringen direkt på polen så att den oskyddade ledaren blir kortare än 175 mm.",
  "diag.bms-control-unverified.detail_message": "Verifiera batteriövervakning och avstängningskontroller för {count} externa BMS-installationer. Kraftkablarna ensamma visar inte att skyddskedjan fungerar.",
  "diag.bms-control-unverified.detail_fix": "Kontrollera varje batteridatakabel, nödvändig VE.Bus/GX-anslutning, ladd-/laststyrning och kompatibel brytare mot tillverkarens manual. En signalkabel eller reservkontaktor verifierar inte ensam avstängningen.",
  "diag.pv-isolation-missing.detail_message": "{label} är fortfarande ansluten till ett solcellsfält när de granskade PV-frånskiljarna öppnas. Tillverkaren kräver möjlighet att koppla bort solcellerna.",
  "diag.pv-isolation-missing.detail_fix": "Använd en lämplig DC-frånskiljare på solcellsledningarna och ta bort förbikopplingar. Kontrollera antal poler samt spännings- och strömtålighet i installationsmanualen.",
  "diag.pv-isolation-unverified.detail_message": "Crimp kan inte verifiera ett lämpligt sätt att frånskilja varje solcellsledare som matar {label}.",
  "diag.pv-isolation-unverified.detail_fix": "Kontrollera regulatorns och frånskiljarens manualer, rita kraftanslutningarna och verifiera märkdata för fältet. En säkring eller fjärrstyrd av/på-funktion verifierar inte ensam frånskiljningen.",
  "diag.inverter-dc-isolation-unverified.detail_message": "Crimp kan inte verifiera en DC-frånskiljare som skiljer {label} från alla anslutna DC-källor.",
  "diag.inverter-dc-isolation-unverified.detail_fix": "Kontrollera växelriktarens manual och ordna lämplig DC-frånskiljning för varje matningsväg. En gemensam frånskiljare kan räcka; driftreglage och säkringsvärden verifierar inte serviceförfarandet.",
  "diag.missing-ground.detail_message": "Den ritade vägen från {terminal} på {label} når ingen modellerad jordpunkt. En kabel som slutar på en flytande plint ger ingen verifierad felströmsväg.",
  "diag.missing-ground.detail_fix": "Följ och anslut hela skyddsjords- eller chassiförbindningen till installationens jordpunkt. Kontrollera ledararea och tillåtna förbindelser mot utrustningens manual.",
  "diag.ac-onto-dc-rail.detail_message": "Den ritade kopplingen förbinder en AC-fas eller nolla med en DC-skena, direkt eller via sammankopplade anslutningar. Det kan medföra risk för elstöt eller brand; DC-säkringar bevisar inte skydd mot AC-fel. Egna komponenter kontrolleras utifrån sina angivna interna kopplingar, som kan behöva rättas för att stämma med utrustningen.",
  "diag.ac-onto-dc-rail.detail_fix": "Koppla bort den markerade AC-till-DC-vägen i ritningen. Anslut fas och nolla till avsedda AC-anslutningar och kontrollera egna panelers kopplingar mot manualen. Skyddsjord och källspecifik nolljordning ska följa utrustningens anvisningar; ta inte bort nödvändig skyddsjord.",
  "diag.unfused-charger-output.detail_message": "{label}s {terminal}-utgång kan nå ett batteri+ utan säkring på vägen. En kortslutning på den här kabeln dumpar batteriets fulla kortslutningsström rakt igenom — ABYC E-11 §11.10.1.1 kräver en säkring inom 7 in (178 mm) från batteriet på varje ledare som ansluter till det.",
  "diag.unfused-charger-output.detail_fix": "Sätt en säkring nära batteriet på den här ledaren (MRBF eller class-T för högströmskällor).",
  "diag.charger-return-floating.detail_message": "{label}s plusutgång är kopplad men returen ({negLabels}) är inte ansluten. Utan väg tillbaka till batteriets minus kan laddaren inte leverera någon ström — husbanken får aldrig laddning.",
  "diag.charger-return-floating.detail_message_dead_end": "{label}s retur ({negLabels}) är kopplad, men det den landar på når aldrig ett batteriminus. Utan väg tillbaka till batteriets minus kan laddaren inte leverera någon ström — husbanken får aldrig laddning.",
  "diag.charger-return-floating.detail_fix": "Dra en kabel från {label}s {negLabels} till minusskenan som går tillbaka till destinationsbatteriets −.",
  "diag.source-return-floating.detail_message": "{label} har en plusanslutning till en batteribank, men ritningen visar ingen fullständig returväg till den banken. Laddströmmen kan inte verifieras.",
  "diag.source-return-floating.detail_fix": "Kontrollera källans minus- eller chassianslutning och hela returvägen till samma batteribank. Ange interna förbindelser uttryckligen; en kabel till en annan plint sluter inte kretsen.",
  "diag.manufacturer-breaker-unverified.detail_message": "Tillverkaren anger en automatsäkring på {rating} A för denna modell. Ritningen fastställer inte rätt automatsäkring eller utlösningskurva; en vanlig säkring med samma märkström är inte automatiskt likvärdig.",
  "diag.manufacturer-breaker-unverified.detail_fix": "Kontrollera exakt modell, tillverkarens automatsäkring, dess utlösningskurva, placering vid matningen och ledningsskydd.",
  "diag.manufacturer-breaker-rating.detail_message": "Den ritade matningsvägen har ett skydd på {rating} A utan tillverkarens angivna automatsäkringsvärde på {required} A.",
  "diag.manufacturer-breaker-rating.detail_fix": "Behåll nödvändigt matningsskydd och kontrollera tillverkarens angivna automatsäkring på varje matningsväg.",
  "diag.manufacturer-supplied-lead-protection.detail_message": "Tillverkaren levererar {label} med en ledning på {mm2} mm² och anger {ampacity} A för den ledaren. Den modellerade säkringen före ledningen är på {rating} A och överstiger denna gräns. En grövre förlängningskabel höjer inte den medföljande ledningens märkström.",
  "diag.manufacturer-supplied-lead-protection.detail_fix": "Skydda den medföljande ledningen vid matningen enligt tillverkarens ledartabell. Kontrollera den installerade ledningen och säkringen; värdet gäller inte kontaktens eller hela panelens märkström.",
  "diag.cranking-vdrop-unverified.detail_message": "Det dimensionerande spänningsfallet vid start uppskattas till {pct} % över ritad matning och retur, över planerarens konservativa startmål.",
  "diag.cranking-vdrop-unverified.detail_fix": "Kontrollera startström, kabellängder och installerade dimensioner mot den aktuella motorns tillverkaranvisningar.",
  "health.details": "Detaljer",
  "health.mark_reviewed": "Markera som kontrollerad",
  "health.undo_review": "Ångra markering",
  "health.review_note": "Kontrollerat utanför Crimp? Markera när du har kontrollerat.",
  "health.reviewed_note": "Markerad som kontrollerad, inte verifierad av Crimp. Ändrad koppling eller märkdata öppnar kontrollen igen.",
  "health.reviewed_count": "Kontrollerade ({count})",
  "health.no_unreviewed": "Inga okontrollerade problem. Kontrollerade punkter finns nedan.",
  "diag.current-model-unverified.reason.parallel-paths": "{label}: Crimp kan inte beräkna parallella strömvägar. Kontrollera belastningen i varje gren.",
  "diag.current-model-unverified.reason.source-choice-limit": "{label}: För många källkombinationer för Crimps beräkning. Kontrollera kabeldimensionerna separat.",
  "diag.current-model-unverified.reason.source-route-limit": "{label}: Kretsen överskrider Crimps analysgräns. Kontrollera kabeldimensionerna separat.",
  "diag.current-model-unverified.reason.source-range-limit": "{label}: Crimp kan inte beräkna denna batterikoppling. Kontrollera bankspänning och kabeldimensioner separat.",
  "diag.current-model-unverified.reason.shared-port-rating": "{label}: Utgångarna delar en strömgräns. Kontrollera gränsen för varje utgång.",
  "diag.current-model-unverified.reason.converter-current-coupling": "{label}: Crimp kan ännu inte dimensionera kablar med denna omvandlares ingångsströmgräns. Kontrollera strömmar och kabeldimensioner separat.",
  "diag.current-model-unverified.reason.unsupported-component-category": "{label}: Crimp kan inte modellera komponentens ström. Kontrollera kategori och angiven driftström.",
  "diag.current-model-unverified.reason.ambiguous-current-role": "{label}: Strömriktningen för in- och utgångar är oklar. Kontrollera anslutningarnas riktning och strömuppgifter.",
  "diag.current-model-unverified.reason.unsupported-pv-array": "{label}: Crimp kan inte beräkna denna solcellskoppling. Kontrollera paneldata och serie-/parallellkoppling.",
  "diag.current-model-unverified.reason.restricted-device-use": "{label}: Kopplingen bryter mot en användningsbegränsning. Strömberäkningarna är preliminära; kontrollera kopplingen före användning.",
  "diag.battery-protect-reverse-use.title": "Risk för bakström genom BatteryProtect",
  "diag.battery-protect-reverse-use.message": "{label}: Kopplingen kan leda ström från OUT till IN. Victron förbjuder bakström eftersom den kan skada enheten.",
  "diag.battery-protect-reverse-use.fix": "Anslut avsedd källa till IN och mottagande last eller batteri till OUT. Håll laddnings- och urladdningsvägar åtskilda. Visad ström är en konservativ uppskattning, inte bevis på säker spärr mot bakström.",
  "diag.battery-protect-model-unverified.title": "BatteryProtect-modellen behöver verifieras",
  "diag.battery-protect-model-unverified.message": "{label}: Ändrade eldata matchar inte längre den granskade BatteryProtect-modellen. Riktning och strömresultat är inte verifierade.",
  "diag.battery-protect-model-unverified.fix": "Återställ katalogmodellen eller kontrollera hela kopplingen och driftgränserna mot manualen.",
  "diag.combiner-manual-state.title": "Välj batterireläets kontaktläge",
  "diag.combiner-manual-state.message": "{label}: Simuleringen använder ditt val Separerade eller Sammankopplade och startar separerad. Automatisk spänningsavkänning, fördröjningar och extern styrning simuleras inte.",
  "diag.combiner-manual-state.fix": "Jämför båda kontaktlägena i Simulera. Kabeldimensioneringen behåller möjlig ström med sammankopplade banker.",
  "diag.alpha-source-model.title": "Alpha75: antaganden om strömkällor",
  "diag.alpha-source-model.message": "{label}: Sol prioriteras inom den gemensamma utgångsgränsen 75 A. Därefter tilldelar simuleringen AC före fordonets DC; verklig AC/DC-fördelning och DC-spännings-/tändningsvillkor modelleras inte.",
  "diag.alpha-source-model.fix": "På antar att ansluten fordonsingång är aktiverad. Kontrollera aktiveringsinställningar separat; startbatteriladdning och starthjälp simuleras inte. Båda GROUND-punkterna motsvarar samma gemensamma anslutning och bevarar äldre ritningar.",
  "diag.current-model-unverified.reason.one-way-device": "{label}: Laddning genom denna batteriseparator modelleras ännu inte. Kontrollera strömmar och kabeldimensioner separat.",
  "diag.current-model-unverified.reason.unmodeled-branch": "{label}: En fullständig belastningsmodell saknas för grenen. Kontrollera matning, retur och angiven driftström.",

  "diag.battery-isolator-model-unverified.title": "Beräkningar för batteriisolatorn behöver verifieras",
  "diag.battery-isolator-model-unverified.message": "Isolatorn håller batteriutgångarna åtskilda. Laddning genom denna enkelriktade enhet modelleras ännu inte, så berörda kabelströmmar och dimensioner är preliminära och dess laddning ingår inte i energibudgeten.",
  "diag.battery-isolator-model-unverified.fix": "Kontrollera isolatorns in- och utgångskretsar, kabeldimensioner, skydd och laddningsprestanda enligt tillverkarna av strömkällan och isolatorn.",
  "diag.battery-short-circuit.title": "Batteripoler är kortslutna",
  "diag.battery-short-circuit.message": "De ritade ledningsvägarna förbinder plus och minus över {count} battericeller eller batterier. En gemensam monitoranslutning leder också ström; en säkring gör inte kopplingen säker.",
  "diag.battery-short-circuit.fix": "Ta bort den kortslutande förbindelsen innan systemet spänningssätts. Anslut varje cell- eller batterispänningsmätning till en egen mätingång enligt tillverkaren; koppla inte olika serienivåer till samma terminal.",
  "diag.load-current-unverified.title": "Lastströmmen behöver verifieras",
  "diag.load-current-unverified.message": "{count} anslutna likströmslaster saknar uppgift om driftström. Deras förbrukning saknas i kabel- och säkringsberäkningar samt effekt- och drifttidsuppskattningar, som därför är ofullständiga.",
  "diag.load-current-unverified.fix": "Ange tillverkarens driftström och beräkna igen. För laster som stöder modellen för konstant effekt kan verifierad effekt också användas. Terminalers och säkringars märkström anger inte driftströmmen.",
  "cable.source_allocation_sizing": "Dimensionerna använder övre gränser för möjliga strömvägar. Den faktiska strömfördelningen är inte fastställd; kontrollera fördelning och skydd innan du använder en beräknad grenström.",
  "cable.current_bound": "Övre strömgräns för källvägar",
  "cable.current_estimate": "Preliminär strömuppskattning",
  "cable.starting_current": "Kortvarig startström",
  "cable.sizing_starting_duty": "Dimensionerad för kontinuerlig belastning och kortvarig startström vid {pct}% spänningsfall.",
  "diag.source-current-allocation-unverified.title": "Kontrollera strömfördelningen",
  "diag.source-current-allocation-unverified.message": "Dimensioneringen av {count} kablar bygger på försiktiga antaganden. Den verkliga strömfördelningen mellan källorna är okänd.",
  "diag.source-current-allocation-unverified.fix": "Kontrollera källornas inställningar samt varje grens kabel-, anslutnings- och säkringsvärden.",
  "cable.provisional_sizing": "Strömvägar eller komponentvärden är inte fastställda. Behandla kabel- och säkringsdimensionerna som preliminära tills ritningen har kontrollerats.",
  "diag.fuse-protection-unverified.message_topology": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",
  "diag.fuse-protection-unverified.message_topology_placement": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",

  "diag.current-model-unverified.title": "Kabeldimensioneringen behöver kontrolleras",
  "diag.current-model-unverified.message": "Crimp kan inte beräkna strömmen tillförlitligt för {count} anslutna komponenter. De markerade kablarnas dimensioner är preliminära.",
  "diag.current-model-unverified.fix": "Öppna Detaljer för att se berörda komponenter och vad som behöver kontrolleras.",
  "diag.fuse-protection-unverified.title": "Kontrollera säkringarna: {label}",
  "diag.fuse-protection-unverified.message_unknown": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",
  "diag.fuse-protection-unverified.message_series": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",
  "diag.fuse-protection-unverified.message_estimated": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",
  "diag.fuse-protection-unverified.message_declared": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",
  "diag.fuse-protection-unverified.fix": "Kontrollera spänningsklass och brytförmåga mot batteriets och säkringarnas datablad.",
  "diag.fuse-protection-unverified.message_unknown_placement": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",
  "diag.fuse-protection-unverified.message_series_placement": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",
  "diag.fuse-protection-unverified.message_estimated_placement": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",
  "diag.fuse-protection-unverified.message_declared_placement": "Crimp kan inte verifiera de installerade säkringarna utifrån schemat.",
  "diag.fuse-identity-conflict.title": "Motstridiga säkringsvärden: {label}",
  "diag.fuse-identity-conflict.message": "Kabeln och komponenten anger olika märkströmmar: {ratings} A.",
  "diag.fuse-identity-conflict.fix": "Rätta dubbla uppgifter eller rita separata säkringar i egna hållare.",

  // ── Topbar ─────────────────────────────────────────────
  "topbar.beta": "Beta",
  "topbar.undo": "Ångra",
  "topbar.redo": "Gör om",
  "topbar.undo_title": "Ångra (⌘Z)",
  "topbar.redo_title": "Gör om (⇧⌘Z)",
  "topbar.edit": "Redigera",
  "topbar.view": "Visa",
  "topbar.simulate": "Simulera",
  "topbar.simulate_title":
    "Kör systemet live — växla komponenters tillstånd och se kabelströmmen uppdateras",
  "topbar.mode_aria": "Appläge",
  "topbar.share": "Dela",
  "topbar.share_title": "Dela det här projektet",
  "topbar.fuses_show": "Visa säkringsmarkörer på kablar",
  "topbar.fuses_hide": "Dölj säkringsmarkörer på kablar",
  "topbar.fuses_aria": "Växla säkringsmarkörer",
  "topbar.cable_size_show": "Visa kabelarea på kablar",
  "topbar.cable_size_hide": "Dölj kabelarea på kablar",
  "topbar.cable_size_aria": "Växla kabelarea",
  "topbar.cable_length_show": "Visa kabellängd på kablar",
  "topbar.cable_length_hide": "Dölj kabellängd på kablar",
  "topbar.cable_length_aria": "Växla kabellängd",
  "topbar.annotations_aria": "Anteckningar på arbetsytan",

  // ── Project menu ───────────────────────────────────────
  "project_menu.title": "Projektmeny",
  "project_menu.name": "Projektnamn",
  "project_menu.rename": "Byt projektnamn",
  "project_menu.placeholder": "Namnlöst system",
  "project_menu.new": "Nytt projekt",
  "project_menu.open": "Öppna projekt…",
  "project_menu.open_external": "Öppna externt…",
  "project_menu.save_now": "Spara nu",
  "project_menu.save": "Spara",
  "project_menu.save_dots": "Spara…",
  "project_menu.auto_save": "Spara automatiskt",
  "project_menu.save_a_copy": "Spara en kopia…",
  "project_menu.duplicate": "Duplicera",
  "project_menu.import": "Öppna från fil…",
  "project_menu.export": "Ladda ner som .12v.json",
  "project_menu.export_pdf": "Exportera till PDF…",
  "project_menu.export_image": "Exportera schema som PNG",
  "pdf.title_suffix": "Enhetslista",
  "pdf.untitled": "Namnlöst system",
  "pdf.totals.devices": "enheter",
  "pdf.totals.cables": "kablar",
  "pdf.col.name": "Enhet",
  "pdf.col.part": "Tillverkare / modell",
  "pdf.col.specs": "Specifikationer",
  "pdf.col.zone": "Zon",
  "pdf.spec.draw": "Förbrukning {a} A",
  "pdf.spec.out": "Ut {a} A",
  "pdf.spec.surge": "Topp {a} A",
  "pdf.empty": "Inga enheter på canvasen ännu.",
  "pdf.attribution": "Ritat i {app}. Öppna det interaktiva schemat:",
  "project_menu.blueprint": "Utskriftsritning…",
  "project_menu.role_badge_title":
    "Du visar det här projektet via en {role}-delningslänk.",
  "project_menu.role.viewer": "läsare",
  "project_menu.role.commenter": "kommentator",
  "project_menu.role.editor": "redigerare",
  "project_menu.viewer_cta": "Endast läsning",
  "project_menu.viewer_cta_action": "Redigera en kopia",
  "project_menu.viewer_cta_title": "Det här är en skrivskyddad delningslänk. Klicka för att spara en egen kopia du kan redigera.",
  "project_menu.save_state.saving": "Sparar",
  "project_menu.save_state.saved": "Sparat",
  "project_menu.save_state.dirty": "Osparat",
  "project_menu.save_state.error": "Det gick inte att spara",
  "project_menu.save_state.local": "Sparat på den här enheten",
  "project_menu.save_state.cloud": "Sparat i molnet",
  "project_menu.save_state.syncing": "Synkar",
  "project_menu.save_state.conflict": "Sparningskonflikt",
  "project_menu.local_detail":
    "Projektet finns bara i den här webbläsaren. Det följer inte med till andra enheter och kan försvinna om webbplatsens data rensas.",
  "project_menu.cloud_detail": "Projektet är sparat både lokalt och i molnet.",
  "project_menu.cloud_sync": "Automatisk molnsynk",

  // ── Paywall ───────────────────────────────────────────
  "cloud_nudge.label": "Molnbackup",
  "cloud_nudge.badge": "Gratis med konto",
  "cloud_nudge.title": "Säkerhetskopiera projektet i molnet",
  "cloud_nudge.body":
    "Det är redan sparat på den här enheten. Logga in gratis för att synka dina projekt och redigera dem på valfri enhet.",
  "cloud_nudge.dismiss": "Inte nu",
  "cloud_nudge.cta": "Säkerhetskopiera i molnet",
  "cloud_nudge.close": "Stäng tipset om molnbackup",
  "paywall.label": "Crimp Lifetime",
  "paywall.title": "Lås upp system i valfri storlek, export och ritningen",
  "paywall.title.export": "Exportera det här projektet",
  "paywall.title.blueprint": "Skriv ut en installationsritning",
  "paywall.title.equipment_limit": "Fortsätt bygga efter {limit} komponenter",
  "paywall.title.ai_credits": "AI-krediter ingår i Crimp Lifetime",
  "paywall.title.ai_credits.pass": "AI-krediter ingår i Pass",
  "paywall.title.project_limit": "Spara fler projekt i molnet",
  "paywall.title.expired": "Ditt Pass har löpt ut",
  "paywall.description":
    "Molnlagring och delning förblir gratis. Med Crimp Lifetime kan du {action} — betala en gång, ditt under produktens livstid.",
  "paywall.description.pass":
    "Molnlagring och delning förblir gratis. Med Crimp Pass kan du {action} i 12 månader.",
  "paywall.description.equipment":
    "Gratisprojekt stöder upp till {limit} utrustningskomponenter. Crimp Lifetime tar bort gränsen i alla projekt, under produktens livstid.",
  "paywall.description.equipment.pass":
    "Gratisprojekt stöder upp till {limit} utrustningskomponenter. Crimp Pass tar bort gränsen i alla projekt i 12 månader.",
  "paywall.description.project_limit":
    "Projektet fick ingen molnplats, men det är sparat på den här enheten. Crimp Lifetime låser upp system i valfri storlek, export och ritningen, med upp till {limit} molnprojekt.",
  "paywall.description.expired":
    "Dina projekt går fortfarande att läsa och alla filer du exporterat är dina. Förnya för att fortsätta bygga system i valfri storlek, exportera och skriva ut ritningen i 12 månader till.",
  "paywall.trigger.export": "exportera det",
  "paywall.trigger.blueprint": "skriva ut ritningen",
  "paywall.trigger.equipment": "lägga till mer utrustning",
  "paywall.trigger.ai_credits": "fortsätta använda assistenten",
  "paywall.trigger.project_limit": "ha fler projekt i molnet",
  "paywall.trigger.pro": "låsa upp allt",
  "paywall.not_now": "Inte nu",
  "paywall.buy": "Skaffa Crimp Lifetime — {price}",
  "paywall.buy.pass": "Skaffa ett Pass — {price}",
  "paywall.buy.pack": "Engångsköp — {price}",
  "paywall.renew": "Förnya · {price}",
  "paywall.sign_in": "Logga in för att fortsätta",
  "paywall.auth_reason.equipment":
    "Logga in eller skapa ett konto så att ditt köp hamnar på ditt konto, och fortsätt bygga.",
  "paywall.local_title": "Molnlagring förblir gratis",
  "paywall.local_body":
    "Du kan fortsätta redigera och synka alla projekt gratis. Crimp Lifetime behövs bara för fler än {limit} utrustningskomponenter, export och den fullständiga ritningen.",
  "paywall.local_body.pass":
    "Du kan fortsätta redigera och synka alla projekt gratis. Pass behövs bara för fler än {limit} utrustningskomponenter, export och den fullständiga ritningen.",
  "paywall.local_title.equipment": "Din nuvarande ritning är oförändrad",
  "paywall.local_body.equipment":
    "Kablar, säkringshållare, säkringsblock och samlingsskenor är alltid gratis och räknas aldrig mot gränsen på {limit} utrustningskomponenter.",
  "paywall.local_title.project_limit": "Ditt projekt är säkert på den här enheten",
  "paywall.local_body.project_limit":
    "Det är sparat här och fortsätter att sparas automatiskt. Inget gick förlorat; det nådde bara inte molnet.",
  "paywall.included": "Ingår också i Crimp Lifetime",
  "paywall.included.pass": "Ingår också i Pass",
  "paywall.feature.export": "PDF- och PNG-export",
  "paywall.feature.blueprint": "Utskrivbar ritning och kabelschema",
  "paywall.feature.equipment": "System i valfri storlek i alla projekt",
  "paywall.feature.credits": "{credits} AI-krediter till assistenten",
  "paywall.feature.cloud": "Upp till {limit} projekt i molnet",
  "paywall.intro_note": "Betala en gång · ditt under produktens livstid · 30 dagars öppet köp.",
  "paywall.intro_note.pass": "En betalning · 12 månader · ingen automatisk förnyelse · 30 dagars öppet köp.",
  "paywall.tax_note":
    "Paddle beräknar och hanterar tillämplig moms eller skatt. Slutbeloppet visas före betalning.",
  "paywall.legal_label": "Köpvillkor och policyer",
  "paywall.legal_intro": "Läs före köp:",
  "paywall.terms": "Användarvillkor",
  "paywall.refunds": "Återbetalningar",
  "paywall.privacy": "Integritet",
  "paywall.unavailable":
    "Köp är tillfälligt otillgängliga. Ditt lokala projekt finns fortfarande kvar på den här enheten.",
  "project_limit.label": "Molnprojekt",
  "project_limit.title": "Du har {limit} molnprojekt",
  "project_limit.saved_locally":
    "Det här projektet är sparat på den här enheten och fortsätter att sparas automatiskt här. Inget gick förlorat; det fick bara ingen plats i molnet.",
  "project_limit.free_slot": "Tar du bort ett molnprojekt du inte längre behöver frigörs en plats.",
  "project_limit.pro":
    "Crimp Professional inkluderar obegränsat med molnprojekt och verktyg för kundarbete.",
  "project_limit.request": "Se Professional",
  "project_limit.thanks": "Ditt projekt är sparat.",
  "project_limit.thanks_body": "Ditt projekt finns kvar på den här enheten. Inget har debiterats.",
  "project_limit.not_now": "Inte nu",
  "project_limit.done": "Klart",
  "conflict.label": "Sparningskonflikt",
  "conflict.title": "Projektet har ändrats någon annanstans",
  "conflict.body": "Välj vilket arbete som ska behållas. Crimp skriver inte tyst över någon version.",
  "conflict.detail": "Ladda den nyare sparade versionen eller bevara det som finns på arbetsytan som ett separat lokalt projekt.",
  "conflict.reload": "Ladda den nyare versionen",
  "conflict.keep_copy": "Behåll min som kopia",
  "conflict.copy_suffix": "(konfliktkopia)",

  // ── Account menu ───────────────────────────────────────
  "account.sign_in": "Logga in",
  "account.sign_in_title": "Logga in",
  "account.sign_out": "Logga ut",
  "labs.title": "Funktionsflaggor",
  "labs.label": "Administratörsverktyg",
  "labs.description": "Prova funktioner i den här webbläsaren. Dina val sparas här och gäller bara när ett behörigt konto är inloggat.",
  "labs.access_hint": "Tillgängligt för administratörer och ägarens konto. Övriga användare behåller standardinställningarna.",
  "account.aria": "Konto",
  "account.signed_in_as": "Inloggad som",
  "account.ai_usage": "{used} av {limit} gratis AI-genereringar använda",
  "account.language": "Språk",
  "account.units": "Enheter",
  "preferences.title": "Inställningar",
  "view.title": "Vy",
  "settings.title": "Inställningar",
  "preferences.show": "Visa",
  "preferences.show_fuses": "Säkringsmarkörer",
  "preferences.show_cable_size": "Kabelarea",
  "preferences.show_cable_length": "Kabellängd",
  "preferences.show_cable_vdrop": "Spänningsfall",
  "preferences.show_cable_label": "Kabeletiketter",
  "preferences.cable_hover_details": "Kabeldetaljer vid hovring",
  "preferences.show_product_images": "Produktbilder",
  "preferences.auto_size_target": "Mål för autodimensionering",
  "preferences.auto_size_target.3pct": "≤3 % spänningsfall",
  "preferences.auto_size_target.3pct.hint": "ABYC E-11",
  "preferences.auto_size_target.5pct": "≤5 % spänningsfall",
  "preferences.auto_size_target.5pct.hint": "NEC 210.19",
  "preferences.auto_size_target.help":
    "Autodimensionerade kablar uppgraderas så att hela vägen från batteri till last håller sig under detta. 3 % följer ABYC E-11; 5 % ger tunnare kabel men större fall.",
  "units.metric": "Metriskt",
  "units.imperial": "Imperialt",

  // ── Confirmations / prompts ────────────────────────────
  "topbar.confirm_discard": "Kasta nuvarande system och börja om?",
  "topbar.save_copy_prompt": "Spara kopia som:",
  "topbar.copy_suffix": "(kopia)",
  "topbar.untitled": "Namnlöst",
  "topbar.auto_saved": "Autosparat",
  "topbar.save_failed": "Det gick inte att spara: {message}",
  "topbar.duplicate_failed": "Det gick inte att duplicera: {message}",
  "topbar.open_failed": "Det gick inte att öppna: {message}",
  "topbar.unknown_error": "okänt fel",

  // ── Auth-required reasons ──────────────────────────────
  "auth_reason.save": "Logga in för att spara ditt projekt i molnet.",
  "auth_reason.save_as": "Logga in för att spara en kopia.",

  // ── Delad länk (konverteringsyta) ──────────────────────
  "share_view.headline": "Någon har delat det här elschemat med dig.",
  "share_view.what_is_crimp":
    "Crimp är ett verktyg för 12V-system — det dimensionerar varje kabel och säkring utifrån schemat.",
  "share_view.save_copy": "Spara en kopia",
  "share_view.save_copy_title": "Spara en redigerbar kopia på ditt eget konto",
  "share_view.save_copy_confirm": "Spara kopia",
  "share_view.start_build": "Rita ditt eget",
  "share_view.start_build_title": "Öppna ett tomt canvas och planera ditt eget system",
  "auth_reason.share": "Logga in för att dela ditt projekt.",

  // ── Import errors ──────────────────────────────────────
  "import_error.future_version":
    "Filen skapades med en nyare version av Crimp (fil v{fileVersion}, appen stöder v{supportedVersion}). Uppdatera appen för att öppna den.",
  "import_error.not_system_shape": "Filen är inte ett giltigt Crimp-system.",
  "import_error.parse": "Filen kunde inte läsas — det är inte giltig JSON.",
  "import_error.generic": "Det gick inte att öppna filen: {message}",
  "import_error.cloud_future_version_reload":
    "Det här projektet sparades med en nyare version av Crimp. Vill du ladda om för att uppdatera?",

  // ── Palette ────────────────────────────────────────────
  "palette.search": "Sök komponenter",
  "palette.search_loading": "Söker komponenter…",
  "palette.search_failed": "Sökningen kunde inte laddas.",
  "palette.search_retry": "Försök igen",
  "palette.clear_search": "Rensa sökning",
  "palette.layout": "Layout",
  "palette.zone": "Zon",
  "palette.zone_sub": "Gruppera komponenter efter fysiskt område",
  "palette.custom": "Egen komponent",
  "palette.custom_sub": "Bygg från grunden",
  "palette.describe": "Beskriv en komponent",
  "palette.describe_sub": "Namnge den så fyller vi i specarna",
  "palette.show_more": "Visa {count} till",
  "palette.show_less": "Visa färre",
  "palette.missing.no_match_for": "Inga träffar för",
  "palette.missing.try_generic":
    "Eller släpp in en generisk komponent och justera specifikationerna i inspektören:",
  "palette.missing.cta_specific": "Be oss lägga till",
  "palette.missing.cta_any": "Be oss lägga till en komponent",
  "palette.missing.body": "Märke och modell hjälper oss hitta ett datablad.",
  "palette.missing.placeholder": "t.ex. Mercury 9,9 utombordare",
  "palette.missing.send": "Skicka förfrågan",
  "palette.missing.close": "Stäng",
  "palette.missing.sending": "Skickar",
  "palette.missing.thanks": "Tack — vi letar upp ett datablad.",
  "palette.cat.battery": "Batterier",
  "palette.cat.charger": "Laddare & DC-DC",
  "palette.cat.solar": "Sol",
  "palette.cat.source": "Källor",
  "palette.cat.engine": "Motorer",
  "palette.cat.distribution": "Fördelning & säkringar",
  "palette.cat.load": "Förbrukare",
  "palette.cat.monitor": "Övervakning",
  "palette.library": "Mitt bibliotek",
  "palette.library_empty":
    "Spara en egen komponent här för att återanvända den i flera scheman.",
  "palette.library_remove": "Ta bort från biblioteket",
  "palette.limit.label": "Gratis utrustning",
  "palette.limit.count": "{count} av {limit}",
  "palette.limit.free_items": "Kablar, säkringar och samlingsskenor räknas aldrig.",

  // ── Beskriv-en-komponent ───────────────────────────────
  "ai_component.title": "Beskriv en komponent",
  "ai_component.options": "Komponentmeny",
  "ai_component.prompt_label": "Komponentens namn eller beskrivning",
  "ai_component.subtitle":
    "Namnge en verklig komponent så fyller vi i uppgifterna från märkskylten. Kabelareor och säkringar räknar vi ut själva.",
  "ai_component.placeholder": "Victron Orion-Tr Smart 12/12-30",
  "ai_component.examples": "Testa: {examples}",
  "ai_component.generate": "Fyll i specarna",
  "ai_component.generating": "Läser märkskylten …",
  "ai_component.cancel": "Avbryt",
  "ai_component.restart": "Beskriv en till",
  "ai_component.add": "Lägg till på ritningen",
  "ai_component.chemistry": "Kemi",
  "ai_component.duty_hours": "Timmar / dygn",
  "ai_component.always_on": "Alltid på",
  "ai_component.specs": "Specifikationer",
  "ai_component.terminals": "Anslutningar",
  "ai_component.terminal_maxamp_note":
    "Anslutningarnas strömtålighet följer dessa värden. Ändra i inspektorn.",
  "ai_component.no_specs":
    "Inga specifikationer hittades. Lägg till dem i inspektorn.",
  "ai_component.estimated": "uppskattat",
  "ai_component.estimated_hint":
    "Prickade värden är uppskattningar. Kontrollera dem mot databladet.",
  "ai_component.assumptions": "Antaganden",
  "ai_component.error.implausible":
    "Specarna motsade varandra gång på gång, så inget lades till. Att ange tillverkare och modell brukar lösa det.",
  "ai_component.error.unavailable":
    "Kunde inte nå assistenten. Försök igen om en stund.",

  // ── Describe-your-system modal ─────────────────────────
  "ai_system.title": "Beskriv ditt system",
  "agent_review.home": "Crimps startsida",
  "agent_review.view_only": "Endast visning",
  "agent_review.edit_copy": "Redigera en kopia",
  "agent_review.schematic": "Utkast till elschema",
  "agent_review.canvas_title": "{name} — skrivskyddat elschema i Crimp",
  "agent_review.copy_hint": "Att visa schemat sparar inget projekt. Välj Redigera en kopia för att ändra delar eller kablar.",
  "agent_review.limits": "Planeringskontroller, inget installationsgodkännande. Bekräfta utrustning, kabeldragning och tillverkarens krav.",
  "agent_review.finding_details": "Visa alla anmärkningar ({count})",
  "agent_review.assumptions": "Antaganden ({count})",
  "agent_review.no_assumptions": "Inga antaganden följde med utkastet. Bekräfta saknade installationsuppgifter innan du förlitar dig på planen.",
  "agent_draft.title": "Granska agentens utkast",
  "agent_draft.subtitle": "Kontrollera delar, längder och anmärkningar och öppna sedan ett nytt lokalt projekt. Ditt befintliga projekt sparas.",
  "agent_draft.import": "Skapa nytt lokalt projekt",
  "agent_draft.importing": "Sparar och öppnar…",
  "agent_draft.invalid": "Länken är ogiltig, inkompatibel eller överskrider betans gränser. Be din agent skapa en ny länk med högst 10 utrustningskomponenter.",
  "agent_draft.error": "Det aktuella projektet kunde inte sparas säkert eller ändrades under importen. Lös eventuella sparningskonflikter och försök igen. Ditt aktuella projekt har inte ersatts.",
  "agent_draft.counts": "{equipment} utrustningskomponenter · {cables} kablar",
  "agent_draft.parts": "Delar att bekräfta",
  "agent_draft.runs": "Kabeldragningar — enkel längd",
  "agent_draft.estimated": "uppskattat — bekräfta",
  "agent_draft.findings": "Aktuella kontroller",
  "agent_draft.finding_counts": "{critical} kritiska · {warning} varningar · {info} information",
  "agent_draft.provisional": "{count} kablar har preliminär dimensionering.",
  "agent_draft.no_findings": "Kontrollerna gav inga anmärkningar. Det innebär inte att installationen är säker eller uppfyller alla krav.",
  "agent_draft.limits": "Planeringsutkast: bekräfta verklig utrustning, kabeldragning och tillverkarens krav. Föreslagna kabel- och säkringsstorlekar är inget installationsgodkännande. Antagandena sparas i projektets komponentanteckningar.",
  "ai_system.beta_badge": "Beta",
  "ai_system.subtitle":
    "Berätta vad du har så ritar vi upp det. Komponenterna kommer från katalogen; kabelarea, säkringar och spänningsfall räknas ut här — de gissas aldrig.",
  "ai_system.placeholder":
    "En Sprinter med två 100 Ah litiumbatterier, 400 W solpaneler och en växelriktare på 2000 W",
  "ai_system.generate": "Rita systemet",
  "ai_system.generating": "Ritar systemet …",
  "ai_system.busy_note":
    "Först komponenterna, sedan kablarna. Det tar oftast runt en halv minut — längre för ett stort system.",
  "ai_system.busy_long":
    "Ritar fortfarande — ett stort system kan ta över en minut. Häng kvar.",
  "ai_system.cancel": "Avbryt",
  "ai_system.restart": "Beskriv ett till",
  "ai_system.add": "Öppna på ritningen",
  "ai_system.engine_note":
    "Assistenten skriver aldrig kabelarea eller säkringsstorlek — de räknas fram ur den färdiga ritningen enligt ABYC E-11.",
  "ai_system.assumed_runs": "Kabellängder vi antog",
  "ai_system.runs_decisive": "Längder som avgör kabelarean",
  "ai_system.runs_minor":
    "{count} kortare kabeldragningar antogs också. Ingen av dem ändrar arean.",
  "ai_system.runs_none_decisive":
    "{count} kabellängder antogs. Vid de längderna ändrar ingen av dem arean.",
  "ai_system.run_and_return": "+ retur",
  "ai_system.show_all": "Visa alla",
  "ai_system.show_less": "Visa mindre",
  "ai_system.picked_parts": "{count} komponenter vi valde åt dig",
  "ai_system.picked_parts_hint":
    "Klicka på en komponent på ritningen för att byta den — kablarna sitter kvar.",
  "ai_system.corrections": "Ändringar vi gjorde",
  "ai_system.stat_cables": "Kablar",
  "ai_system.units": "Antal",
  "ai_system.count_collapsed": "1 ×, inte {requested}",
  "ai_system.count_collapsed_note":
    "En samlingsskena, säkringsbox eller huvudbrytare är en enda komponent. Två parallellt skulle mata varje krets bakom den två gånger, så vi använde en.",
  "ai_system.block_upsized": "större — {circuits} kretsar",
  "ai_system.block_split": "{units} ×, {circuits} kretsar",
  "ai_system.block_relieved_note":
    "Fler grenkretsar drogs till säkringsboxen än den har platser. En plats är en säkring och två kretsar kan inte dela på den, så vi tog en större box — eller en till, matad från samma ställe.",
  "ai_system.zone_distances": "Hur långt är det mellan dem?",
  "ai_system.zone_distances_hint":
    "Ändra ett avstånd så följer varje kabel som går däremellan med. Enskilda kablar går fortfarande att ändra på ritningen.",
  "ai_system.assumptions_count": "{count} noteringar från assistenten",
  "ai_system.tip_verify":
    "Låt någon till titta innan du kapar något — en installatör, en marinelektriker eller forumet du redan läser.",
  "ai_system.tip_numbers":
    "Assistenten väljer bara komponenter och kopplingar. Kabelarea, säkringar och spänningsfall räknas fram här ur den färdiga ritningen.",
  "ai_system.tip_zones":
    "Systemet kommer indelat i utrymmen. Rätta avståndet mellan två av dem så mäts varje kabel däremellan om.",
  "ai_system.tip_swap":
    "Allt assistenten gissat är markerat på ritningen. Klicka på en komponent för att byta den — kablarna sitter kvar.",
  "ai_system.estimated": "antaget",
  "ai_system.estimated_hint":
    "Allt som är prickat är vår gissning, inte din beskrivning. Ändra här så ritas systemet om.",
  "ai_system.assumptions": "Noteringar",
  "ai_system.confirm_replace":
    "Det här ersätter det som ligger på ritningen. Ångra tar tillbaka det. Fortsätta?",
  "ai_system.error.unbuildable":
    "Systemet gick inte att koppla ihop, så inget ritades. Att nämna batterier, laddare och de största förbrukarna brukar lösa det.",
  "ai_system.error.unavailable":
    "Kunde inte nå assistenten. Försök igen om en stund.",
  "ai_system.photo_add": "Lägg till ett foto av din skiss",
  "ai_system.photo_remove": "Ta bort fotot",
  "ai_system.photo_alt": "Fotot du bifogade",
  "ai_system.photo_failed":
    "Kunde inte läsa bilden. En JPEG eller PNG brukar fungera.",
  "ai_system.photo_note":
    "En handritad skiss eller ett foto av verkligheten fungerar lika bra. Kabelareor och säkringsstorlekar som står på bilden struntar vi i — de räknas ut här.",

  "ai_system.edit_title": "Ändra ditt system",
  "ai_system.edit_subtitle":
    "Säg vad som ska läggas till, tas bort eller kopplas om. Allt annat på ritningen står kvar precis som det är.",
  "ai_system.edit_placeholder":
    "Lägg till en 200 W-panel med egen MPPT och ett kylskåp till på säkringsplinten",
  "ai_system.edit_generate": "Planera ändringen",
  "ai_system.edit_generating": "Räknar ut ändringen …",
  "ai_system.edit_busy_note":
    "Läser ditt system och räknar ut vad som måste ändras. Det tar oftast en halv minut.",
  "ai_system.edit_apply": "Lägg in på ritningen",
  "ai_system.edit_restart": "Ändra något annat",
  "ai_system.edit_untouched_note":
    "Bara det som står här ändras. Placeringar, längder, areor och säkringar du satt själv lämnas i fred, och ångra tar tillbaka allt.",
  "ai_system.edit_added": "Komponenter som läggs till",
  "ai_system.edit_removed": "Komponenter som tas bort",
  "ai_system.edit_removed_cables":
    "{count} kablar följer med, så deras dragning försvinner också.",
  "ai_system.edit_unwired": "{count} kablar tas bort",
  "ai_system.edit_new_runs": "Längder för de nya kablarna",
  "ai_system.edit_stat_added": "Tillagt",
  "ai_system.edit_stat_cables": "Nya kablar",
  "ai_system.edit_stat_removed": "Borttaget",
  "ai_system.edit_nothing":
    "Ingenting ändrades — noteringen nedan säger varför. Din ritning är orörd.",
  "ai_system.edit_switch_to_new": "Börja om med ett nytt system i stället",
  "ai_system.edit_switch_to_edit": "Ändra det som redan finns på ritningen",
  "ai_system.edit_replaces_note":
    "Det här ritar ett nytt system och ersätter det som finns på ritningen.",
  "ai_system.edit_too_big":
    "Systemet är för stort för att assistenten ska kunna ändra det. Dra in komponenten från paletten i stället.",
  "ai_system.edit_error_unbuildable":
    "Ändringen kom tillbaka omöjlig att koppla, så ingenting lades in. Säg vilken komponent det gäller och var den ska sitta.",
  "canvas.empty.subtitle":
    "Berätta vad du har så ritar vi upp det och dimensionerar varje kabel.",
  "canvas.empty.placeholder": "Två 100 Ah litiumbatterier och 400 W solpaneler",
  "canvas.empty.palette_hint": "eller dra in en komponent från paletten",
  "canvas.empty.manual_title": "Bygg ditt system",
  "canvas.empty.manual_subtitle":
    "Dra in en komponent från paletten och dra sedan mellan två poler för att koppla ihop dem. Varje kabel dimensioneras medan du bygger.",
  "canvas.empty.manual_touch":
    "Tryck på + för att lägga till din första komponent.",
  "canvas.empty.read_only": "Det här tomma schemat är skrivskyddat.",
  "starter.next_step.title": "Anpassa startprojektet",
  "starter.next_step.body": "Ange kabellängden för din installation och granska sedan kabelresultatet. Ändringar sparas automatiskt på den här enheten.",
  "starter.next_step.action": "Ange din faktiska kabellängd",
  "starter.next_step.dismiss": "Senare",
  "canvas.generated.placed":
    "Systemet ligger på ritningen. Allt här går att ändra.",
  "canvas.generated.lengths_one": "Kolla en kabellängd",
  "canvas.generated.lengths_many": "Kolla {count} kabellängder",
  "canvas.generated.lengths_why": "— där är det längden som bestämde arean.",
  "canvas.generated.parts_one": "Byt en komponent",
  "canvas.generated.parts_many": "Byt {count} komponenter",
  "canvas.generated.parts_why":
    "— vi valde dem; alla kablar sitter kvar när du byter en.",
  "canvas.generated.dismiss": "Stäng",
  "mobile.ai.readonly_note":
    "Du kan fortsätta redigera i mobilen när schemat har skapats.",
  "mobile.welcome.manual_title": "12 V och 24 V, rätt dimensionerat",
  "mobile.welcome.manual_subtitle":
    "Bygg med riktiga komponenter ur katalogen och dimensionera varje kabel och säkring mot ABYC E-11 direkt i mobilen.",
  "mobile.welcome.start_manual": "Skapa ett nytt schema",
  "mobile.welcome.demo_cta": "Titta på ett exempelschema",
  "mobile.welcome.demo_unavailable": "Exemplet är inte tillgängligt just nu",
  "mobile.welcome.demo_note":
    "Skrivskyddat — panorera, zooma och tryck på en komponent eller kabel för att se siffrorna.",
  "mobile.menu.title": "Meny",
  "mobile.menu.open": "Öppna menyn",
  "mobile.menu.close": "Stäng menyn",
  "mobile.menu.back": "Tillbaka till menyn",
  "mobile.menu.project": "Projekt",
  "mobile.menu.privacy": "Integritet",
  "mobile.menu.advertising_cookies": "Reklamcookies",
  "mobile.menu.manage_subscription": "Hantera prenumeration",
  "mobile.palette.title": "Lägg till komponent",
  "mobile.palette.open": "Lägg till komponent",
  "mobile.palette.add": "Lägg till",
  "mobile.palette.close": "Stäng komponenter",
  "mobile.details.title": "Detaljer för markering",
  "mobile.details.close": "Stäng detaljer",
  "mobile.details.open": "Öppna detaljer",
  "mobile.details.label": "Detaljer",
  "mobile.junction.branch": "Lägg till kabelgren",
  "mobile.share.title": "Dela",
  "mobile.share.publish_title": "Spara och dela schemat",
  "mobile.share.publishing": "Sparar…",
  "mobile.share.copied": "Kopierad",
  "mobile.share.failed": "Kunde inte dela",
  "mobile.share.sign_in_reason":
    "Logga in för att spara schemat och få en länk du kan skicka vidare.",
  "mobile.projects.menu": "Molnprojekt",
  "mobile.projects.label": "Molnprojekt",
  "mobile.projects.title": "Dina scheman, var du än är",
  "mobile.projects.description":
    "Öppna ett synkat molnprojekt och visa eller redigera det här. Ändringar sparas automatiskt.",
  "mobile.projects.sign_in": "Logga in för att visa molnprojekt",
  "mobile.projects.sign_in_reason":
    "Logga in för att visa dina molnprojekt i mobilen.",
  "mobile.projects.close": "Stäng",
  "mobile.projects.loading": "Laddar molnprojekt…",
  "mobile.projects.list_error": "Det gick inte att ladda dina molnprojekt.",
  "mobile.projects.open_error": "Det gick inte att öppna projektet. Försök igen.",
  "mobile.projects.retry": "Försök igen",
  "mobile.projects.empty_title": "Inga molnprojekt ännu",
  "mobile.projects.empty_body":
    "Spara ett projekt i molnet i Crimp så visas det här på alla dina enheter.",
  "mobile.projects.current_aria": "Hämta den senaste versionen av {name} från molnet",
  "mobile.projects.open_aria": "Öppna {name}",
  "mobile.projects.viewing": "Visas",
  "mobile.projects.read_only": "Skrivskyddat",
  "mobile.projects.editable": "Redigerbart",
  "mobile.projects.opening": "Öppnar…",
  "mobile.projects.updated": "Uppdaterat {date}",
  "mobile.storage_error.title": "Redigering är inte tillgänglig",
  "mobile.storage_error.body":
    "Crimp kunde inte öppna lagringen på den här enheten. Ladda om innan du redigerar så att inget arbete går förlorat.",
  "starter.describe_title": "Beskriv ditt system",
  "starter.describe_body":
    "Berätta i en mening vad du har. Vi ritar upp det och dimensionerar varje kabel.",
  "starter.describe_cta": "Beskriv det",
  "tools.describe_system": "Beskriv ditt system …",
  "tools.change_system": "Ändra ditt system …",

  // ── Welcome modal ──────────────────────────────────────
  "welcome.eyebrow": "Välkommen",
  "welcome.title": "Välkommen till Crimp",
  "welcome.body":
    "Dra in komponenter på arbetsytan, koppla ihop dem med kablar, och Crimp föreslår kabelarea och säkringsstorlek för varje dragning.",
  "welcome.terms_prefix": "Genom att välja Godkänn accepterar du ",
  "welcome.terms_link": "användarvillkoren",
  "welcome.terms_mid": " och har tagit del av ",
  "welcome.privacy_link": "integritetspolicyn",
  "welcome.terms_suffix": ".",
  "welcome.cta": "Godkänn och börja",

  // ── Starter / seed picker ──────────────────────────────
  "starter.label": "Starta ett projekt",
  "starter.new_title": "Nytt projekt",
  "starter.new_description": "Hur vill du börja?",
  "starter.examples": "Använd ett exempel",
  "starter.assistant_body": "Beskriv ditt system och låt Assistenten skapa det.",
  "starter.back": "Tillbaka",
  "starter.circuits.title": "Kretsexempel",
  "starter.circuits.description": "Redigerbara anslutningsexempel. Öppna Systemhälsa för kontroller och kvarstående antaganden. Kontrollera verklig utrustning, kabeldragning och skydd före installation.",
  "starter.circuit.ac0.name": "120 V AC-grupper och GFCI",
  "starter.circuit.ac0.description": "Huvudskydd för landström, tre grupper och ett skyddsjordat GFCI-uttag.",
  "starter.circuit.ac1.name": "230 V AC-grupper och uttag",
  "starter.circuit.ac1.description": "Jordfelsskydd för landström, tre grupper och skyddsjordade uttag.",
  "starter.circuit.ac2.name": "GFCI: LINE, LOAD och uttag",
  "starter.circuit.ac2.description": "Matning, skyddat vidarematat uttag och anslutning till det lokala uttaget.",
  "starter.circuit.ac3.name": "120 V AC-källväljare",
  "starter.circuit.ac3.description": "Manuellt val av landström, växelriktare eller generator med separata fas- och nollpoler.",
  "starter.circuit.ac4.name": "230 V AC-källväljare",
  "starter.circuit.ac4.description": "Tre separat skyddade matningar och obruten skyddsjord.",
  "starter.circuit.spdt.name": "SPDT-omkopplare med mittläge av",
  "starter.circuit.spdt.description": "En gemensam kontakt väljer mellan två laster med separata returledare.",
  "starter.circuit.dpdt.name": "DPDT-omkopplare med mittläge av",
  "starter.circuit.dpdt.description": "Två oberoende poler växlar plus och retur tillsammans.",
  "starter.circuit.momentary.name": "Återfjädrande tryckknapp",
  "starter.circuit.momentary.description": "Jämför intryckt och släppt kontaktläge i Simulera.",
  "starter.circuit.relay.name": "Fempoligt växlande relä",
  "starter.circuit.relay.description": "Separat spole samt normalt sluten och normalt öppen last. Välj reläläge manuellt.",
  "starter.circuit.pv.name": "PV-kopplingsbox för tre strängar",
  "starter.circuit.pv.description": "Separata gPV-strängsäkringar, plus- och minusskenor, frånskiljare och MPPT.",
  "starter.title": "Börja med ett exempel",
  "starter.description":
    "Välj ett exempel och anpassa det.",
  "starter.blank": "Tomt projekt",
  "starter.stat_system": "System",
  "starter.stat_bank": "Bank",
  "starter.stat_solar": "Sol",
  "starter.stat_parts": "Delar",
  "seed.lithium_boat.name": "Segelbåt — litiumkonvertering",
  "seed.lithium_boat.desc":
    "Äldre båt konverterad till LiFePO4: generatorn laddar ett litiumhusbatteri via en DC-DC och behåller det ursprungliga startbatteriet.",
  "seed.lead_boat.name": "Klassisk båt — blybatterier",
  "seed.lead_boat.desc":
    "Traditionellt upplägg: start- och AGM-husbatterier kopplas ihop av ett skiljerelä från generatorn och fylls på av en landladdare.",
  "seed.van.name": "Husbil — sol + DC-DC",
  "seed.van.desc":
    "Typiskt husbilsbygge: taksol via MPPT och en DC-DC från fordonsbatteriet matar ett litiumbatteri, kyl, belysning och en liten växelriktare.",
  "seed.advanced.name": "Liveaboard — växelriktare + sol",
  "seed.advanced.desc":
    "Avancerat bygge: en MultiPlus växelriktare/laddare på landström, en stor solpanelsuppsättning och en DC-DC från generatorn matar ett 300 Ah-batteri.",

  // ── Auth modal ─────────────────────────────────────────
  "auth.label": "Konto",
  "auth.title_signin": "Logga in på Crimp",
  "auth.title_signup": "Skapa ditt konto",
  "auth.unconfigured_title": "Molnsynk är inte konfigurerad",
  "auth.unconfigured_body":
    "Sätt VITE_SUPABASE_URL och VITE_SUPABASE_ANON_KEY i .env.local och ladda om.",
  "auth.ok": "OK",
  "auth.cancel": "Avbryt",
  "auth.send_magic_link": "Skicka magisk länk",
  "auth.create_account": "Skapa konto",
  "auth.sign_in": "Logga in",
  "auth.tab_magic": "Magisk länk",
  "auth.tab_password": "Lösenord",
  "auth.tab_signup": "Registrera",
  "auth.email": "E-post",
  "auth.email_placeholder": "du@exempel.se",
  "auth.password": "Lösenord",
  "auth.or": "eller",
  "auth.continue_google": "Fortsätt med Google",
  "auth.oauth_failed": "Det gick inte att starta inloggningen. Försök igen.",
  "auth.check_inbox_signin":
    "Kolla din inkorg efter inloggningslänken.",
  "auth.check_inbox_confirm":
    "Kolla din inkorg för att bekräfta din e-postadress.",

  // ── AI generation limits + end-of-allowance screens ────
  "ai.wall.welcome_title": "Vi bygger det tillsammans",
  "ai.wall.welcome_body": "Skapa ett gratiskonto för lite AI-hjälp med delar och ditt system.",
  "ai.wall.anonymous": "Skapa ett gratiskonto eller logga in för att använda dina gratis AI-krediter.",
  "ai.paywall.title": "Där tog dagens gratisförslag slut",
  "ai.paywall.subtitle": "{used} av {limit} använda. De frigörs igen när de äldre faller bort.",
  "ai.paywall.subtitle_plain": "De frigörs igen när de äldre faller bort.",
  "ai.paywall.licence": "Crimp Lifetime · betala en gång",
  "ai.paywall.price": "{price} {currency}",
  "ai.paywall.body":
    "Här skulle en licens normalt komma in. Den säljs inte — Crimp är gratis medan vi tar reda på hur många som vill ha en. Skulle du ha betalat för det här?",
  "ai.paywall.free_note":
    "Inget debiteras och inget låses, oavsett vad du svarar. Dina förslag kommer tillbaka av sig själva.",
  "ai.paywall.interested": "Det skulle jag betala för",
  "ai.paywall.decline": "Inte för mig",
  "ai.paywall.thanks":
    "Noterat — tack. Inget att betala och inget ändras med ditt konto; du gjorde bara argumentet för att bygga det.",
  "ai.paywall.done": "Stäng",
  "ai.usage.remaining": "{remaining} av {limit} gratisförslag kvar",
  "ai.auto_add.title": "Automatisk påfyllning",
  "ai.auto_add.on": "På",
  "ai.auto_add.loading": "Kontrollerar automatisk påfyllning…",
  "ai.auto_add.enable": "Aktivera automatisk påfyllning",
  "ai.auto_add.subscription": "Tillgängligt med en aktiv månadsprenumeration.",
  "ai.auto_add.unavailable": "Automatisk påfyllning är inte tillgänglig just nu.",
  "ai.auto_add.threshold": "Fyll på {credits} krediter när färre än {threshold} återstår.",
  "ai.auto_add.monthly_limit": "Månadsgräns",
  "ai.auto_add.consent": "Debitera {price} per påfyllning, högst {limit}/månad inklusive skatt, med prenumerationens betalsätt. Stäng av när du vill.",
  "ai.auto_add.pending": "En påfyllning behandlas. Ditt utkast är sparat.",
  "ai.auto_add.pending_disable": "Den pågående påfyllningen kan fortfarande slutföras.",
  "ai.auto_add.price_changed": "Priset har ändrats. Spara för att fortsätta.",
  "ai.auto_add.limit_reached": "Månadsgränsen för automatisk påfyllning är nådd. Du kan fortfarande köpa krediter.",
  "ai.auto_add.saving": "Sparar…",
  "ai.auto_add.save": "Spara",
  "ai.auto_add.saved": "Sparat.",
  "ai.auto_add.error": "Kunde inte uppdatera automatisk påfyllning. Försök igen.",
  "ai.credits.title": "Fyll på krediter",
  "ai.credits.menu": "Krediter",
  "ai.credits.purchased": "Köpta krediter",
  "ai.credits.remaining": "{remaining} krediter",
  "ai.credits.remaining_one": "{remaining} kredit",
  "ai.credits.shared": "Samma saldo för assistenten och generering av komponenter och system.",
  "ai.credits.balance": "{remaining} krediter tillgängliga",
  "ai.credits.pack": "{credits} krediter",
  "ai.credits.costs": "AI-förfrågningar debiteras efter användning.",
  "ai.credits.terms": "Engångsköp. Köpta krediter går aldrig ut.",
  "ai.credits.included_first": "Krediterna som ingår i din plan används först; krediter du köper går aldrig ut.",
  "ai.credits.lifetime_title": "AI-krediter ingår i Crimp Lifetime",
  "ai.credits.lifetime_body": "Crimp Lifetime innehåller {credits} AI-krediter, tillsammans med system i valfri storlek, export och ritningen. Betala en gång — det är ditt under produktens livstid — och fyll på när du behöver fler.",
  "ai.credits.get_lifetime": "Skaffa Crimp Lifetime",
  "ai.credits.pass_ended_title": "Ditt Pass har löpt ut",
  "ai.credits.pass_ended_body": "Dess krediter följde med. Crimp Lifetime ger dig {credits} AI-krediter igen, tillsammans med system i valfri storlek, export och ritningen — betala en gång, ditt under produktens livstid.",
  "ai.credits.pass_title": "AI-krediter ingår i Pass",
  "ai.credits.pass_body": "Crimp Pass innehåller {credits} AI-krediter i 12 månader, tillsammans med system i valfri storlek, export och ritningen. En betalning, ingen automatisk förnyelse.",
  "ai.credits.get_pass": "Skaffa ett Pass",
  "ai.credits.subscription_title": "AI-krediter ingår i abonnemanget",
  "ai.credits.subscription_body": "Månadsabonnemanget innehåller {credits} AI-krediter varje månad, tillsammans med system i valfri storlek, export och ritningen. Avsluta när du vill.",
  "ai.credits.subscribe": "Prenumerera",
  "ai.credits.taxes": "Eventuell skatt visas i kassan.",
  "ai.credits.details": "Så fungerar krediter",
  "ai.credits.failed_requests": "Tekniska fel kostar inga krediter.",
  "ai.credits.buy": "Köp krediter",
  "ai.credits.opening": "Öppnar kassan…",
  "ai.credits.checkout_open": "Slutför ditt köp i kassan.",
  "ai.credits.ready": "Krediterna är påfyllda. Du kan fortsätta.",
  "ai.credits.continue": "Fortsätt",
  "ai.credits.waiting": "Betalningen är klar. Uppdaterar saldot…",
  "ai.credits.error": "Kassan kunde inte öppnas. Försök igen.",
  "ai.credits.balance_error": "Saldot kunde inte uppdateras. Försök igen.",
  "ai.credits.unavailable": "Det går inte att köpa krediter just nu.",
  "ai.credits.retry": "Försök igen",
  "ai.credits.refresh": "Uppdatera saldo",
  "ai.credits.generation": "{remaining} krediter kvar. Högst {max} krediter per förfrågan",
  "ai.credits.free": "Gratis AI-krediter med ett konto.",
  "ai.credits.sign_in": "Logga in för gratis AI-krediter",
  "ai.credits.first_free": "Gratis AI-krediter med ett konto",
  "ai.credits.usage": "Betala efter användning · Högst {max} krediter per förfrågan",
  "ai.denied.wall": "Logga in för att fylla på AI-krediter.",
  "ai.denied.quota": "Fyll på AI-krediter för att generera fler komponenter eller system.",
  "ai.denied.rate_limited":
    "Det blev många förslag på kort tid. Vänta en minut och försök igen.",
  "ai.denied.capped":
    "Designverktyget är upptaget just nu. Allt annat på sidan fungerar som vanligt.",
  "ai.denied.turnstile":
    "Vi kunde inte verifiera förfrågan. Ladda om sidan och försök igen.",
  "ai.denied.invalid":
    "Beskriv systemet i en mening eller två — beskrivningen var tom eller för lång.",

  // ── Share modal ────────────────────────────────────────
  "share.label": "Dela",
  "share.title": "Dela det här projektet",
  "share.description":
    "Skapa en länk per åtkomstnivå. Vem som helst med länken kan använda den — håll redigeringslänkar privata.",
  "share.done": "Klar",
  "share.save_first": "Spara projektet först för att kunna dela.",
  "share.loading": "Laddar…",
  "share.role.viewer": "Läsare",
  "share.role.viewer_sub": "Kan visa, men inte redigera eller kommentera.",
  "share.role.commenter": "Kommentator",
  "share.role.commenter_sub": "Kan visa och lämna kommentarer.",
  "share.role.editor": "Redigerare",
  "share.role.editor_sub": "Kan visa, kommentera och redigera arbetsytan.",
  "share.create_link": "Skapa {label}länk",
  "share.copy": "Kopiera länk",
  "share.copied": "Kopierad",
  "share.show_qr": "Visa QR-kod",
  "share.hide_qr": "Dölj QR-kod",
  "share.regenerate": "Regenerera länk (ogiltigförklarar den gamla)",
  "share.revoke": "Återkalla länk",
  "share.qr_print_blurb":
    "Skriv ut QR-koden och förvara den i båten — alla som skannar den kan se kopplingsschemat.",
  "share.qr_scan_caption": "Skanna för att se kopplingsschemat",
  "share.print": "Skriv ut",
  "share.regenerate_short": "Regenerera",
  "share.revoke_short": "Återkalla",
  "share.access.label": "Direkta åtkomstlänkar",
  "share.access.sub":
    "Skapa en privat länk till en specifik person. Visas inte i galleriet.",

  // ── Öppen för feedback (i share-modalen) ───────────────
  "share.publish.section": "Feedback",
  "share.publish.heading": "Få ett andra par ögon",
  "share.publish.subtitle":
    "Öppna bygget för feedback och släng länken i din seglargrupp, ditt subreddit, eller där din community redan finns. Folk kan lämna kommentarer fästa på specifika kablar — ett bra sätt att fånga ett misstag innan du krymper hylsan.",
  "share.publish.toggle_on": "Öppen för feedback — alla med länken kan kommentera",
  "share.publish.toggle_off": "Privat — endast personer med en delningslänk ser det",
  "share.publish.segment_private": "Privat",
  "share.publish.segment_public": "Öppen för feedback",
  "share.publish.views_label": "visningar · senaste 7 dagarna",
  "share.publish.views_direct": "direkt",
  "share.publish.views_empty":
    "Kommentarer dyker upp på den publika sidan, fästa på den kabel eller komponent de gäller.",
  "share.publish.forks_link": "{forks} av din design",
  "share.publish.forks_singular": "fork",
  "share.publish.forks_plural": "forkar",
  "share.publish.public_link": "Länk att dela",
  "share.publish.description_label": "Vad du vill ha synat (frivilligt)",
  "share.publish.description_placeholder":
    "t.ex. Tveksam kring ankarspelets matning — räcker 35 mm² över 4 m vid 90 A startström?",
  "share.publish.unpublish": "Avpublicera",
  "share.publish.publish": "Publicera",
  "share.publish.saving": "Sparar…",
  "share.publish.anonymous_label": "Publicera anonymt",
  "share.publish.anonymous_hint":
    "Dölj ditt namn. Bygget visas som Anonymous och delningskortet utelämnar bylinen.",

  // ── Community gallery ──────────────────────────────────
  "topbar.community": "Community",
  "topbar.community_title": "Bläddra bland projekt som andra publicerat",
  "community.label": "Community",
  "community.eyebrow_vol": "Vol. 01",
  "community.count_label": "bygg",
  "community.title": "Bygg i det vilda",
  "community.description":
    "Kopplingsscheman som andra Crimp-användare gjort publika. Öppna ett för att läsa upplägget, eller forka det som utgångspunkt för ditt eget.",
  "community.close": "Stäng",
  "community.loading": "Laddar…",
  "community.empty":
    "Inga publika projekt än. Publicera ditt från Dela-menyn och bli först.",
  "community.empty_headline": "Det är lugnt vid kajen.",
  "community.empty_subline":
    "Inga bygg har publicerats ännu. Publicera ditt från Dela-menyn och var först in.",
  "community.empty_filtered":
    "Inga bygg matchar det här filtret. Prova en annan båttyp.",
  "community.owner_title": "Bygg av {name}",
  "community.owner_description":
    "Publika scheman som {name} delat med community. Öppna ett för att läsa, eller forka det som utgångspunkt.",
  "community.error": "Kunde inte ladda community-projekt: {message}",
  "community.fork_count": "{count} forkar",
  "community.comment_count": "{count} kommentarer",
  "community.published_on": "Publicerad {date}",
  "community.open": "Öppna",
  "community.fork_of": "Forkat från ett annat projekt",
  "community.by": "av {name}",
  "community.sort.label": "Sortera",
  "community.sort.aria": "Sortera bygg",
  "community.sort.newest": "Senaste",
  "community.sort.likes": "Mest älskade",
  "community.sort.forks": "Mest forkade",
  "community.sort.comments": "Mest diskuterade",
  "community.like.title_set": "Gilla det här bygget",
  "community.like.title_unset": "Ogilla",
  "community.activity_title": "Aktivitet nyligen ({date})",
  "community.filter.label": "Filtrera",
  "community.filter.aria": "Filtrera på båttyp",
  "community.filter.all": "Alla",

  // ── Public-view banner ─────────────────────────────────
  "public_banner.label": "Publik — endast läsning",
  "public_banner.fork": "Forka för att redigera",
  "public_banner.fork_title": "Spara en kopia av det här projektet till ditt konto",
  "public_banner.sign_in_to_fork": "Logga in för att forka",
  "public_banner.forking": "Forkar…",
  "public_banner.fork_failed": "Forkning misslyckades: {message}",
  "public_banner.fork_name_prompt": "Namnge din fork:",
  "public_banner.fork_suffix": "(fork)",
  "public_banner.back_to_community": "Bläddra i community",
  "public_banner.download_image": "Ladda ner bild",
  "public_banner.download_image_title":
    "Spara schemat som PNG — bra för grupper som föredrar bildbilagor.",
  "public_banner.like_aria": "Gilla det här bygget",
  "public_banner.like_title": "Gilla det här bygget",
  "public_banner.unlike_title": "Ta bort din gillning",
  "public_banner.share": "Dela",
  "public_banner.share_title": "Dela bygget till en grupp eller ett forum",
  "public_banner.forked_from": "från",
  "public_banner.forked_from_title": "Öppna originalet av {name}",

  "auth_reason.fork": "Logga in för att forka det här projektet.",
  "auth_reason.like": "Logga in för att gilla det här bygget.",

  // ── Lineage modal ──────────────────────────────────────
  "lineage.label": "Härkomst",
  "lineage.title": "Byggt på din design",
  "lineage.description":
    "Publika projekt som börjat som en fork av det här bygget. Öppna ett för att se hur det utvecklades.",
  "lineage.close": "Stäng",
  "lineage.loading": "Laddar…",
  "lineage.parent_label": "Forkat från",
  "lineage.children_label": "Forkar av det här bygget",
  "lineage.children_empty":
    "Inga publika forkar än. När någon forkar din design och publicerar den dyker de upp här.",
  "lineage.count": "{count} totalt",

  // ── Share composer ─────────────────────────────────────
  "share_composer.label": "Dela",
  "share_composer.title": "Dela det här bygget",
  "share_composer.description":
    "Färdig text att klistra in i forum, Facebook-grupper eller Reddit — plus en bild och länken.",
  "share_composer.close": "Stäng",
  "share_composer.copied": "Kopierat",
  "share_composer.copy_text": "Kopiera",
  "share_composer.post_text_label": "Inläggstext",
  "share_composer.downloading": "Förbereder…",
  "share_composer.action_image": "Bild",
  "share_composer.action_image_sub": "Ladda ner schemat som PNG.",
  "share_composer.action_link": "Länk",
  "share_composer.action_link_sub": "Kopiera den publika URL:en.",
  "share_composer.action_text": "Inläggstext",
  "share_composer.action_text_sub": "Titel, författare, länk — klart att klistra in.",
  "share_composer.mod_hint":
    "Tips — de flesta båt- och husbilsgrupper föredrar bildbilagor framför externa länkar. Posta bilden direkt och släng länken i en kommentar.",
  "share_composer.compose_on": "Skriv på",

  // ── Project picker modal ───────────────────────────────
  "picker.label": "Projekt",
  "picker.title": "Dina projekt",
  "picker.description":
    "Öppna ett sparat projekt, byt namn, duplicera eller radera.",
  "picker.close": "Stäng",
  "picker.new_project": "Nytt projekt",
  "picker.empty":
    "Inga sparade projekt än. Klicka på {strong} för att börja ett.",
  "picker.empty_strong": "Nytt projekt",
  "picker.open_badge": "öppet",
  "picker.delete_confirm":
    'Radera "{name}"? Det går inte att ångra.',
  "picker.rename": "Byt namn",
  "picker.duplicate": "Duplicera",
  "picker.delete": "Radera",
  "picker.local_heading": "På den här enheten",
  "picker.cloud_heading": "I molnet",
  "picker.local_empty": "Inga lokala projekt ännu.",
  "picker.cloud_sign_in": "Logga in för att se molnprojekt.",
  "picker.cloud_usage": "{used} av {limit} molnprojekt",
  "picker.cloud_usage_full": "Alla platser används. Ta bort ett molnprojekt du inte längre behöver för att frigöra en.",

  // ── Admin project picker (cross-user) ──────────────────
  "admin_picker.label": "Alla projekt",
  "admin_picker.title": "Alla projekt (admin)",
  "admin_picker.description":
    "Bläddra bland alla användares projekt. Öppnas skrivskyddat — för att spara krävs ägarskap.",
  "admin_picker.search_placeholder": "Sök på namn, ägare eller e-post…",
  "admin_picker.empty": "Inga projekt finns ännu.",
  "admin_picker.public_badge": "publikt",
  "admin_picker.owner_unknown": "Okänd ägare",

  // ── Cable length modal ─────────────────────────────────
  "cable_modal.eyebrow": "Ny kabel",
  "cable_modal.distance_label": "Avstånd från {from} till {to}",
  "cable_modal.zone_a": "Zon A",
  "cable_modal.zone_b": "Zon B",
  "cable_modal.distance_placeholder": "t.ex. 3",
  "cable_modal.metres": "meter",
  "cable_modal.feet": "fot",
  "cable_modal.distance_saved":
    "Sparat för dessa zoner — kabeln får som standard avstånd + {slack} extra.",
  "cable_modal.distance_will_save":
    "Vi sparar det här för framtida kablar mellan dessa zoner. Kabellängd får som standard avstånd + {slack} extra.",
  "cable_modal.length_label": "Kabellängd (enkel väg)",
  "cable_modal.auto_size_cable": "Automatisk kabelarea",
  "cable_modal.pick_manually": "Välj area manuellt.",
  "cable_modal.add_fuse": "Lägg till säkring",
  "cable_modal.negative_no_fuse": "Minussida — ingen säkring.",
  "cable_modal.fuse_to_slot": "Sätter platsens säkringsvärde.",
  "cable_modal.inline_fuse_holder": "Inline-säkringshållare {rating} A.",
  "cable_modal.skip_fuse": "Hoppa över säkring på den här kabeln.",
  "cable_modal.wire": "Kabel",
  "cable_modal.fuse": "Säkring",
  "cable_modal.cable_amps": "Kabelström",
  "cable_modal.estimate": "{amp} uppskattat",
  "cable_modal.no_fuse": "ingen säkring",
  "cable_modal.no_known_load": "ingen känd last",
  "cable_modal.no_load_no_fuse":
    "Ingen känd last på kabeln — läggs till utan säkring.",
  "cable_modal.vdrop": "Spänningsfall",
  "cable_modal.cancel": "Avbryt",
  "cable_modal.add_cable_fuse": "Lägg till kabel + säkring",
  "cable_modal.add_cable": "Lägg till kabel",
  "cable_modal.fuse_explainer_negative":
    "Negativ-/jordledare — säkringar skyddar bara plussidan (ABYC).",
  "cable_modal.fuse_explainer_slot":
    "Kabeln ansluter till en säkringsplint — platsens säkringsvärde sätts.",
  "cable_modal.fuse_explainer_inline":
    "Säkringsvärdet bärs av själva kabeln.",

  // ── Inspector — system health ──────────────────────────
  "health.system": "System",
  "health.empty_title": "Släpp in ett batteri för att börja",
  "health.empty_body":
    "När du kopplar ihop systemet flaggar panelen dimensioneringsproblem, säkringsluckor och annat värt en andra titt.",
  "health.empty_demo_lead": "Ny här?",
  "health.empty_demo_button": "Visa ett demoprojekt",
  "health.severity.critical": "Kritiskt",
  "health.severity.warning": "Varning",
  "health.severity.info": "Info",
  "health.severity_chip_title": "{count} {label}",
  "health.energy_budget": "Energibudget",
  "health.battery_usable": "Batteri (användbart)",
  "health.reserve_hint": "Reserv {pct}%",
  "health.daily_use": "Daglig förbrukning",
  "health.daily_harvest": "Daglig inmatning",
  "health.harvest_hint": "Sol {solar} · Generator {alternator} · Land {shore} Wh",
  "health.net_daily": "Netto per dygn",
  "health.net_now": "Netto just nu",
  "health.autonomy": "Autonomi (från fullt)",
  "health.autonomy_hint":
    "Vid dagens förbrukning, hur länge tills reservgolvet nås.",
  "health.time_to_empty": "Tid till tomt",
  "health.time_to_empty_hint":
    "Från nuvarande SoC ner till reserven, om nuläget håller i sig.",
  "health.time_to_full": "Tid till fullt",
  "health.time_to_full_hint":
    "Från nuvarande SoC upp till fullt, om nuläget håller i sig.",
  "health.issues_all": "Problem & förslag",
  "health.issues_filter": "{label}",
  "health.fix_all": "Åtgärda alla ({count})",
  "health.fix_all_title": "Tillämpa {count} ett-klicksåtgärder",
  "health.healthy": "Inget att flagga — systemet ser friskt ut.",
  "health.no_at_severity": "Inga problem på den här allvarsnivån.",
  "health.top_consumers": "Största förbrukare",
  "health.simulate_nudge_eyebrow": "Scenario",
  "health.simulate_nudge_body": "Justera laster, sol och drifttid för att se hur systemet håller.",
  "health.simulate_nudge_cta": "Öppna Simulera",
  "inspector.tab_edit": "Redigera",
  "inspector.tabs_aria": "Inspektorläge",
  "simulate.scenario": "Scenario",
  "simulate.reset": "Återställ",
  "simulate.load_on": "På",
  "simulate.load_off": "Av",
  "simulate.reset_scenario_title": "Återställ scenariot till standardvärden",
  "simulate.sun_hours": "Solens topptimmar",
  "simulate.engine_hours": "Motorgång",
  "simulate.shore_hours": "Landström",
  "simulate.reserve_floor": "Reservgräns",
  "simulate.preset_default": "Standard",
  "simulate.preset_custom": "Anpassat",
  "simulate.no_saved_scenarios": "Inga sparade scenarier än",
  "simulate.delete_saved": "Ta bort",
  "simulate.save_current": "Spara nuvarande som…",
  "simulate.save_placeholder": "t.ex. Helg vid ankare",
  "simulate.reset_to_default": "Återställ till standard",
  "simulate.show_all": "Visa alla {count}",
  "simulate.clear_overrides": "Återställ ({count})",
  "simulate.clear_overrides_title": "Återställ {count} förbrukare till diagrammet",
  "simulate.harvest_none": "ingen i diagrammet",
  "simulate.solar_conditions": "Solförhållanden",
  "simulate.solar_sunny": "Sol",
  "simulate.solar_cloudy": "Moln",
  "simulate.solar_off": "Mörker",
  "simulate.solar_mixed": "Blandat",
  "simulate.close_sheet_aria": "Stäng scenarioruta",
  "simulate.stop": "Stoppa simulering",
  "simulate.batteries": "Batterier",
  "simulate.show_on_canvas": "Visa i diagrammet",
  "simulate.combiner.combined": "Sammankopplade",
  "simulate.combiner.isolated": "Separerade",
  "simulate.combiner.help": "Manuellt kontaktläge; automatisk avkänning och styrsignaler simuleras inte.",
  "simulate.orion.on": "Auto",
  "simulate.orion.forced": "Tvingad",
  "simulate.orion.off": "Av",
  "simulate.orion.status_running": "Motor detekterad",
  "simulate.orion.status_waiting": "Väntar på motorn",
  "simulate.orion.status_unresolved": "Motoranslutning ej verifierad",
  "simulate.orion.status_forced": "Motordetektering avstängd",
  "simulate.ac.supplied": "AC-källa på",
  "simulate.ac.off": "Ingen aktiv AC-källa",
  "simulate.ac.unverified": "AC-matning ej verifierad",
  "simulate.ac.nominal": "Nominellt {voltage} V",
  "simulate.ac.help": "Endast källans och kontakternas tillstånd; spänning, ström och skyddsutlösning simuleras inte.",
  "simulate.ac.main": "Huvudbrytare",
  "simulate.ac.branch": "Grupp {number}",
  "simulate.ac.on_label": "På",
  "simulate.ac.off_label": "Av",
  "simulate.ac.unknown_label": "Okänd",
  "simulate.ac.input": "Ingång: {status}",
  "simulate.ac.output": "Utgång: {status}",
  "simulate.orion.help": "Auto följer en generator ansluten till båda ingångsledarna. Spänningsgränser, fördröjningar, externa styrsignaler och underspänningsspärr simuleras inte.",
  "simulate.orion.forced_help": "Simulerar avstängd motordetektering. Laddningen kan dra ström från startbatteriet när motorn är avstängd.",
  "health.fix_prefix": "Åtgärd · ",
  "health.show_on_canvas": "Visa på arbetsytan →",
  "health.tip_drop_component":
    "Dra en komponent från paletten till arbetsytan för att börja.",
  "health.tip_wire":
    "Dra från en plint till en annan för att koppla ihop komponenter.",
  "health.tip_zone_intro":
    "Släpp en {strong} från paletten för att gruppera komponenter efter fysiskt område — kabellängder härleds från avstånd mellan zoner.",
  "health.tip_zone_strong": "Zon",
  "health.tip_shortcuts":
    "{undo} ångra · {fit} anpassa vy · {del} ta bort markerat",

  // ── Inspector — component detail ───────────────────────
  "rv_panel.main": "Huvudbrytare L1 + L2",
  "rv_panel.branches": "Grupper per fas",
  "rv_panel.resize_help": "Koppla bort de sista grupperna innan antalet minskas. Befintliga anslutningar behåller sina gruppnummer.",
  "rv_panel.switch_help": "Markerad = sluten kontakt. Huvudbrytaren bryter båda fasledarna.",
  "rv_panel.limits": "Endast manuella kontakter. Automatisk utlösning, jordfelsskydd, fasbalans och AC-lastflöde simuleras inte. Bussanslutningarna ligger efter huvudbrytaren; anslut laster till grupputgångarna.",
  "inspector.custom": "Egen",
  "inspector.custom_label": "Egen etikett",
  "inspector.swap_part": "Komponent",
  "inspector.swap_part_hint":
    "Byt den mot en annan komponent. Alla kablar sitter kvar.",
  "inspector.swap_part_estimated":
    "Vi valde den här åt dig. Byt till den du faktiskt har — alla kablar sitter kvar.",
  "inspector.specs": "Specifikationer",
  "inspector.reset_specs": "Återställ",
  "inspector.reset_specs_title": "Återställ till katalogvärden",
  "inspector.customize": "Anpassa",
  "inspector.customize_title":
    "Redigera terminaler och specifikationer. Återställbar — använd Återställ för att gå tillbaka till katalogen.",
  "inspector.custom_callout.title_from": "Egen kopia av {name}",
  "inspector.custom_callout.title_scratch": "Egen komponent",
  "inspector.custom_callout.body_from":
    "Ändringar i specifikationer, terminaler och mått stannar på den här instansen — andra kopior av {name} påverkas inte.",
  "inspector.custom_callout.body_scratch":
    "Redigera specifikationer, terminaler och mått så att de matchar din verkliga komponent.",
  "inspector.custom_callout.revert": "Återgå till katalog",
  "inspector.reset_field": "Återställ till katalog",
  "inspector.identity": "Komponent",
  "inspector.identity.name": "Namn",
  "inspector.identity.name_placeholder": "t.ex. MD11C motorblock",
  "inspector.identity.category": "Kategori",
  "inspector.identity.width": "Bredd",
  "inspector.identity.height": "Höjd",
  "inspector.identity.photo": "Foto",
  "inspector.identity.photo_add": "Lägg till foto",
  "inspector.identity.photo_remove": "Ta bort",
  "inspector.identity.photo_failed": "Uppladdningen misslyckades — försök igen.",
  "inspector.identity.photo_signin": "Logga in för att lägga till ett foto på komponenten.",
  "inspector.save_to_library": "Spara i biblioteket",
  "inspector.save_to_library_title":
    "Spara den här egna komponenten i ditt personliga bibliotek på den här enheten — den dyker upp i paletten för återanvändning.",
  "inspector.saved_to_library": "Sparad",
  "inspector.terminal.add": "Lägg till terminal",
  "inspector.terminal.default_label": "T",
  "inspector.terminal.delete_title": "Ta bort terminal",
  "inspector.terminal.delete_blocked":
    "Koppla bort {count} kabel/kablar först för att ta bort terminalen.",
  "inspector.terminal.label_placeholder": "Etikett",
  "inspector.terminal.label_for": "Etikett för anslutning {terminal}",
  "inspector.terminal.polarity": "Polaritet",
  "inspector.terminal.direction": "Riktning",
  "inspector.terminal.side": "Sida",
  "inspector.terminal.max_amp": "Anslutningens strömgräns (A)",
  "inspector.terminal.rating_help": "Anslutningarnas strömgränser (A), separat från egenförbrukning och batterikapacitet (Ah).",
  "inspector.terminal.offset": "Position",
  "inspector.terminal.color": "Färg",
  "inspector.terminal.polarity.positive": "Plus",
  "inspector.terminal.polarity.negative": "Minus",
  "inspector.terminal.polarity.ground": "Jord",
  "inspector.terminal.polarity.signal": "Signal",
  "inspector.terminal.polarity.neutral": "Neutral",
  "inspector.terminal.direction.input": "Ingång",
  "inspector.terminal.direction.output": "Utgång",
  "inspector.terminal.direction.bidi": "Båda",
  "inspector.terminal.side.left": "Vänster",
  "inspector.terminal.side.right": "Höger",
  "inspector.terminal.side.top": "Topp",
  "inspector.terminal.side.bottom": "Botten",
  "inspector.terminal.expand": "Visa terminaldetaljer",
  "inspector.terminal.collapse": "Dölj terminaldetaljer",
  "inspector.advanced": "Avancerat",
  "inspector.spec.capacity": "Kapacitet",
  "inspector.spec.voltage": "Spänning",
  "inspector.spec.vmp": "Vmp",
  "inspector.spec.vmp_hint":
    "Vmp — spänningen vid panelens maxeffektpunkt. Skiljer sig från systemspänningen (V): en \"12 V\"-panel har typiskt Vmp ≈ 18 V.",
  "inspector.spec.power": "Effekt",
  "inspector.spec.output": "Utgång",
  "inspector.spec.imp": "Imp",
  "inspector.spec.imp_hint":
    "Imp — strömmen vid maxeffektpunkten. Imp ≈ Effekt ÷ Vmp (t.ex. 100 W ÷ 18 V ≈ 5,6 A).",
  "inspector.spec.continuous_draw": "Kontinuerlig förbrukning",
  "inspector.spec.peak_surge": "Toppström (surge)",
  "inspector.spec.peak_sun_hours": "Soltimmar (peak)",
  "inspector.spec.typical_charge": "Typisk laddström",
  "inspector.solar_blurb":
    'En "12 V"-solpanel ger faktiskt runt 18 V vid sin maxeffektpunkt ({vmp}). Strömmen där är {imp}, inte Effekt ÷ 12 V — därför ger en 100 W-panel ~5,6 A, inte 8,3 A. MPPT-laddaren omvandlar Vmp ner till batterispänningen.',
  "inspector.load_mode": "Förbrukarläge",
  "inspector.energy_use": "Energianvändning",
  "inspector.always_on": "Alltid på (24/7)",
  "inspector.hours_per_day": "Timmar/dygn",
  "inspector.daily_energy": "Daglig energi",
  "inspector.draw_when_on": "Förbrukning när på",
  "inspector.outputs": "Utgångar (säkringsplatser)",
  "inspector.terminals": "Plintar",
  "inspector.fuse": "Säkring",
  "inspector.fuse_rating": "Märkvärde",
  "inspector.breaker.title": "Kretsbrytare",
  "inspector.breaker.setup": "Ställ in som kretsbrytare",
  "inspector.breaker.input": "Anslutning på matningssidan",
  "inspector.breaker.rating": "Brytarens märkström (A)",
  "inspector.breaker.voltage": "Högsta DC-spänning (V, valfritt)",
  "inspector.breaker.voltage_short": "Högsta spänning",
  "inspector.breaker.hint": "Använd den installerade märkströmmen. Utlösning styrs manuellt i Simulera.",
  "inspector.electrical.apply": "Spara inställningar",
  "inspector.electrical.edit": "Ändra inställningar",
  "inspector.dc_supply.title": "DC-strömförsörjning",
  "inspector.dc_supply.setup": "Ställ in som DC-strömförsörjning",
  "inspector.dc_supply.input": "Nominell inspänning (V)",
  "inspector.dc_supply.output": "Utspänning (V)",
  "inspector.dc_supply.current": "Utströmgräns (A)",
  "inspector.dc_supply.efficiency": "Verkningsgrad (%, valfritt)",
  "inspector.dc_supply.hint": "Ange den faktiska utspänningen. Tom verkningsgrad använder ett uppskattat värde på 90 %.",
  "inspector.dc_supply.range": "Utspänningsområde (V, valfritt)",
  "inspector.dc_supply.range_display": "Utspänningsområde",
  "inspector.dc_supply.range_min": "Min (V)",
  "inspector.dc_supply.range_max": "Max (V)",
  "inspector.dc_supply.range_hint": "Ange båda gränserna och håll utspänningen inom området.",
  "inspector.custom_fuse.setup": "Ställ in som DC-säkring",
  "inspector.custom_fuse.hint": "Ange den installerade säkringens typ och märkström. Anslutningens strömtålighet är inte säkringens märkström.",
  "inspector.custom_fuse.family": "Säkringstyp",
  "inspector.custom_fuse.rating": "Säkringens märkström (A)",
  "inspector.custom_fuse.apply": "Spara säkringsinställningar",
  "inspector.custom_fuse.title": "Säkringsskydd",
  "inspector.custom_fuse.unconfigured": "Om komponenten är en säkring, ange typ och märkström så att Crimp kan kontrollera skyddet.",
  "inspector.custom_fuse.cancel": "Avbryt",
  "inspector.custom_dc_fuse_block.title": "DC-säkringsblock",
  "inspector.custom_dc_fuse_block.setup": "Ställ in som DC-säkringsblock",
  "inspector.custom_dc_fuse_block.connections": "Anslut {input} till {count} säkrade utgångar.",
  "inspector.custom_dc_fuse_block.ratings": "Ange varje installerad säkring nedan.",
  "inspector.custom_dc_fuse_block.slot_rating": "Säkringens märkström för {label}",
  "inspector.shunt.bank": "Ansluten batteribank",
  "inspector.shunt.current": "Simulerad ström",
  "inspector.shunt.discharging": "{amps} urladdning",
  "inspector.shunt.charging": "{amps} laddning",
  "inspector.shunt.idle": "0 A",
  "inspector.shunt.unavailable": "Ej tillgänglig",
  "inspector.shunt.simulate_hint": "Byt till Simulera för att se strömmen.",
  "inspector.shunt.estimate": "Beräknad ström genom denna shunt.",
  "inspector.battery_fuse.title": "Säkring på batteripolen",
  "inspector.battery_fuse.blurb":
    "MRBF / MEGA / ANL / Class T monterad direkt på batteriets pluspol. Alla kablar som lämnar polen ärver detta märkvärde, och ABYC E-11 §11.10 (säkring inom 175 mm) uppfylls automatiskt.",
  "inspector.battery_fuse.rating_label": "Märkvärde",
  "inspector.battery_fuse.rating_none": "Ingen säkring på polen",
  "inspector.battery_fuse.type_label": "Typ (för AIC-kontroll)",
  "inspector.battery_fuse.type_unknown": "Okänd",
  "inspector.rotate": "Rotera 90°",
  "inspector.rotate_title": "Rotera kroppen 90° medurs",
  "inspector.remove_from_zone": "Ta bort från zon",
  "inspector.remove_from_zone_title":
    "Ta bort den här komponenten från zonen (den ligger kvar på arbetsytan)",
  "inspector.delete_component": "Radera komponent",

  // ── Inspector — zone detail ────────────────────────────
  "zone.eyebrow": "Zon",
  "zone.fallback_label": "Zon",
  "zone.label_field": "Etikett",
  "zone.label_placeholder": "t.ex. Motorrum",
  "zone.engine_bay": "Motorrumszon",
  "zone.engine_bay_hint":
    "Kablar som löper helt inom denna zon dimensioneras för 50 °C omgivning (ABYC E-11 tabell V, 105 °C-kabel derateras till 0,82).",
  "zone.distances": "Avstånd till zoner",
  "zone.no_other": "Inga andra zoner än.",
  "zone.distances_hint":
    "Bestämmer längden på nya kablar mellan zonerna — och flyttar de som redan går däremellan lika mycket.",
  "zone.delete": "Radera zon",
  "zone.size": "{w} × {h} px",

  // ── Inspector — junction detail ────────────────────────
  "junction.eyebrow": "Skarvpunkt",
  "junction.title": "Skarv",
  "junction.body":
    "En skarvpunkt är en kabelskarv — valfritt antal kablar kan mötas här. Dra en annan plint till den för att lägga till en kabel.",
  "junction.connections": "Anslutningar",
  "junction.cables_connected": "Anslutna kablar",
  "junction.delete": "Radera skarv",
  "junction.convert_to_busbar": "Gör om till skena",
  "junction.convert_pos": "+ Skena",
  "junction.convert_pos_title": "Byt ut skarven mot en plusskena.",
  "junction.convert_neg": "− Skena",
  "junction.convert_neg_title": "Byt ut skarven mot en minusskena.",
  "junction.convert_ground": "Jord",
  "junction.convert_ground_title": "Byt ut skarven mot en chassijordpunkt.",
  "junction.convert_fusebox": "Säkringsbox",
  "junction.convert_fusebox_title":
    "Byt ut skarven mot en säkringsbox — en plusskena som visar varje utgångs säkring vid sitt stift.",
  "junction.convert_mixed_warning":
    "Skarven är ansluten till både + och −-kablar. Koppla bort den ena polariteten innan du gör om den till en skena.",
  "inspector.busbar.title": "Skena",
  "inspector.busbar.studs": "Stift",
  "inspector.busbar.fewer": "Ta bort ett stift",
  "inspector.busbar.more": "Lägg till ett stift",
  "inspector.busbar.add_output": "Lägg till en utgång",
  "inspector.busbar.convert_to_fusebox": "Gör om till säkringsbox",
  "inspector.busbar.convert_to_fusebox_title":
    "Visa skenan som säkringsbox — varje ansluten kabels säkring visas vid sitt stift.",
  "inspector.busbar.convert_to_busbar": "Gör om till skena",
  "inspector.busbar.convert_to_busbar_title":
    "Visa som vanlig skena utan säkringspiller.",
  "inspector.busbar.convert_to_junction": "Gör om till skarv",
  "inspector.busbar.convert_to_junction_title":
    "Slå ihop skenan till en enkel skarv. Alla kablar samlas i en punkt.",

  // ── Inspector — cable detail ───────────────────────────
  "cable.eyebrow": "Kabel",
  "cable.label": "Etikett",
  "cable.label_placeholder": "t.ex. C-12, sb husbatt +",
  "cable.label_help":
    "Fritextsetikett som visas på kabeln — användbar för att matcha schemat mot fysiska kablar vid installation.",
  "cable.label_color": "Etikettfärg",
  "cable.label_color.kraft": "Kraftpapper",
  "cable.label_color.white": "Vit",
  "cable.label_color.yellow": "Gul",
  "cable.label_color.red": "Röd",
  "cable.label_color.blue": "Blå",
  "cable.label_color.green": "Grön",
  "cable.label_color.slate": "Grafit",
  "cable.length": "Längd",
  "cable.calculated": "Beräknat",
  "cable.peak_current": "Toppström",
  "cable.continuous_current": "Kontinuerlig ström",
  "cable.nominal_voltage": "Nominell spänning",
  "cable.voltage_drop": "Spänningsfall",
  "cable.wire_size": "Kabelarea",
  "cable.wire_ampacity": "Kabelns ampacitet",
  "cable.suggested_fuse": "Föreslagen säkring",
  "cable.sizing_cranking":
    "Dimensionerad för {amp} kortvarig startström, med ett mål på 2 % spänningsfall (startkrets).",
  "cable.sizing_general":
    "Dimensionerad för {amp} kontinuerligt vid {pct} % spänningsfall.",
  "cable.sizing_general_at_v":
    "Dimensionerad för {amp} kontinuerligt vid {volts} V, {pct} % spänningsfallsbudget.",
  "cable.sizing_ac":
    "Strömtålighetskontrollen använder AC-terminalens märkström {amp}. Kontrollera kretsens märkström, installation och skydd.",
  "cable.sizing_ac_unknown": "AC-märkström saknas. Kontrollera kretsström, kabelarea och skydd.",
  "cable.ac_rating": "Använd AC-märkström",
  "cable.ac_rating_missing": "Ej angiven",
  "cable.ac_voltage_drop_unavailable": "Beräknas inte för AC",
  "cable.ac_protection_label": "AC-skydd",
  "cable.ac_protection": "Kontrollera AC-kretsens skydd",
  "cable.no_fuse_recommendation": "Ingen ännu — ingen känd last på kabeln",
  "cable.signal_fuse_unverified": "Signalkablar behöver komponentspecifikt skydd. Välj säkringsvärde enligt tillverkarens anvisningar; kabelns strömtålighet räcker inte som underlag.",
  "cable.starting_fuse_unverified": "Startströmsdata saknas eller är noll för en ansluten startmotor. Ange en dokumenterad säkringsstorlek.",
  "cable.choose_fuse_rating": "Välj säkringsvärde enligt komponentens manual",
  "cable.needs_review": "Behöver granskas",
  "cable.terminal_limit_hint": "Den automatiskt valda säkringen överskrider anslutningens märkström på {rating} A.",
  "cable.smartshunt_fuse_hint": "SmartShunt Vbatt+ använder tillverkarens matningskabel med en trög 1 A-säkring. Kontrollera den medföljande kabeln eller angivet temperaturgivartillbehör.",
  "cable.manufacturer_breaker_hint": "Kontrollera tillverkarens {rating} A-motorskydd och dess utlösningskurva innan du använder värdet.",
  "cable.add_inline_fuse_optional": "Batteripolens skydd: {rating} A. Ett separat grenskydd skulle ha värdet {load} A; kontrollera om komponenten kräver ett sådant.",
  "cable.suggested_fuse_opt": "Föreslaget ({rating} A)",
  "cable.protect_to_wire":
    "Ingen känd last på kabeln — en säkring här dimensioneras efter ledaren ({rating} A).",
  "cable.warn_undersized":
    "⚠ Kabeln är underdimensionerad för uppskattad ström.",
  "cable.warn_vdrop":
    "⚠ Spänningsfallet överstiger kretsens {pct} % — överväg grövre kabel eller kortare dragning.",
  "cable.warn_fuse_trips":
    "⚠ Säkringen löser ut — komponenter på dragningen drar {load} A kontinuerligt genom en {rating} A-säkring.",
  "cable.warn_wire_underrated_load":
    "⚠ Kabeln är för klen för nedströmslasten — dragningen bär {load} A kontinuerligt, ampaciteten är {ampacity} A.",
  "cable.upgrade_to": "Uppgradera till {mm2} mm² · AWG {awg}",
  "cable.worst_case": "Värsta fall över simuleringstillstånd",
  "cable.continuous": "Kontinuerligt",
  "cable.suggested_wire": "Föreslagen kabel",
  "cable.triggered_by": "Utlöst av — {scenarios}",
  "cable.add_fuse": "Lägg till säkring",
  "cable.add_inline_fuse": "Lägg till inline-säkring",
  "cable.add_inline_fuse_hint":
    "Batteriets MRBF ({rating} A) är för stor för att skydda den här kabeln. Lägg en {load} A inline-säkring nära lasten.",
  "cable.fuse_from_slot":
    "Säkringsvärdet kommer från säkringsplintens plats vid kabelns ändpunkt.",
  "cable.protected_by":
    "Kabeln skyddas av en {rating} A-säkring nära källan.",
  "cable.mirrors_battery_mrbf":
    "Detta {rating} A-värde är samma som batteriets MRBF — det är en säkring, inte två. Sänk till {load} A för att verkligen skydda den här kabeln.",
  "cable.protect_with":
    "Skydda kabeln med rekommenderad {rating} A-säkring.",
  "cable.toggle_negative":
    "Negativa/jord-kablar säkras aldrig (ABYC).",
  "cable.toggle_slot":
    "Kabelns säkring styrs av den anslutna säkringsplintens plats.",
  "cable.series_link": "Avsiktlig serielänk",
  "cable.series_link.on_description":
    "Markerad som seriebrygga (12 V → 24 V-batterier, eller paneler i serie). Polaritetskontrollen tystas för denna kabel; övriga diagnoser gäller fortfarande.",
  "cable.series_link.off_description":
    "Använd bara när du staplar likadana celler (två 12 V-batterier → 24 V-bank, eller solpaneler i serie för högre Voc till MPPT). På andra ställen är + → − fortfarande en kortslutning.",
  "cable.manual_override": "Manuell åsidosättning",
  "cable.wire_size_label": "Kabelarea",
  "cable.fuse_rating_label": "Säkringsvärde",
  "cable.fuse_family_recommendation_unavailable": "Ingen automatisk märkström passar den valda säkringsfamiljen och det beräknade behovet. Verifiera skyddet och välj en lämplig säkring eller familj.",
  "cable.fuse_type_label": "Säkringstyp",
  "cable.vdrop_class_label": "Spänningsfallsklass",
  "cable.vdrop_class.auto": "Auto ({pct} % fall)",
  "cable.vdrop_class.auto_plain": "Auto",
  "cable.vdrop_class.critical": "Kritisk (3 % fall)",
  "cable.vdrop_class.non_critical": "Icke-kritisk (10 % fall)",
  "cable.vdrop_class.help":
    "ABYC E-11.14.2.6: 3 % för centralmatningar, länspumpar, elektronik och lanternor, 10 % för allmän belysning. Ställ in den på valfri kabel i en slinga så klassas hela kretsen om.",
  "cable.auto_fuse_type_opt": "Auto",
  "cable.auto_fuse_type_opt_inherited": "Auto ({type})",
  "cable.auto_wire_opt": "Auto ({mm2} mm²)",
  "cable.auto_fuse_opt": "Auto ({rating} A)",
  "cable.reset_layout": "Dra om",
  "cable.reset_layout_title": "Dra om kabeln rent (R)",
  "cable.delete": "Radera kabel",

  // ── Inspector — multi detail ───────────────────────────
  "multi.eyebrow": "Flera markerade",
  "multi.items": "{count} objekt",
  "multi.components": "Komponenter",
  "multi.cables": "Kablar",
  "multi.add_fuse": "Lägg till säkring",
  "multi.add_fuse_each": "Lägg till säkring på varje kabel",
  "multi.protected_one":
    "{count} kabel skyddad med rekommenderad säkring.",
  "multi.protected_many":
    "{count} kablar skyddade med rekommenderad säkring.",
  "multi.protect_one":
    "Skydda {count} kabel med rekommenderat säkringsvärde.",
  "multi.protect_many":
    "Skydda {count} kablar med rekommenderat säkringsvärde.",
  "multi.delete": "Radera markering",

  // ── Comments ───────────────────────────────────────────
  "comments.title": "Kommentarer",
  "comments.empty": "Inga kommentarer än.",
  "comments.read_only":
    "Du visar i läsläge. Be ägaren om en kommenterings- eller redigeringslänk.",
  "comments.your_name": "Ditt namn",
  "comments.placeholder_node": "Fråga om den här komponenten eller zonen…",
  "comments.placeholder_cable": "Fråga om den här kabeln…",
  "comments.post": "Posta",
  "comments.resolve": "Lös",
  "comments.name_required":
    "Skriv ditt namn så att andra vet vem som kommenterat.",
  "comments.close": "Stäng",
  "comments.popover_aria": "Kommentarer",
  "comments.popover_title_node": "Anteckningar om den här komponenten",
  "comments.popover_title_cable": "Anteckningar om den här ledningen",
  "comments.popover_first_node":
    "Första anteckningen på komponenten? Ställ en fråga eller dela vad du skulle ändra.",
  "comments.popover_first_cable":
    "Första anteckningen på ledningen? Ifrågasätt säkringsvalet, kabelarean, dragningen.",
  "comments.popover_signin_to_post":
    "Logga in för att lämna en anteckning som hänger på just den här delen av bygget.",
  "comments.popover_kbd_hint": "⌘+↵ skickar",
  "comments.resolved_toggle": "{count} {label}",
  "comments.resolved_singular": "löst notering",
  "comments.resolved_plural": "lösta noteringar",
  "comments.resolved_date": "löst {date}",
  "comments.pin": "Fäst den här noten",
  "comments.unpin": "Lossa",
  "comments.pinned_eyebrow": "Fäst av författaren",

  // ── Feedback button ────────────────────────────────────
  "feedback.btn": "Feedback",
  "feedback.btn_title":
    "Skicka feedback — rapportera en bugg, dela en idé eller annat",
  "feedback.type.bug": "Bugg",
  "feedback.type.idea": "Idé",
  "feedback.type.other": "Kontakta skaparen",
  "feedback.type_aria": "Feedbacktyp",
  "feedback.placeholder.bug": "Vad gick fel?",
  "feedback.placeholder.idea": "Vad saknas? Vad skulle du vilja se?",
  "feedback.placeholder.other": "Skriv ett meddelande till skaparen…",
  "feedback.heading.bug": "Rapportera en bugg",
  "feedback.heading.idea": "Dela en idé",
  "feedback.heading.other": "Kontakta skaparen",
  "feedback.picker.heading": "Vad är på gång?",
  "feedback.picker.bug_desc": "Vi bifogar ditt system så vi kan återskapa det",
  "feedback.picker.idea_desc": "Skickas direkt till teamet så vi kan planera in det",
  "feedback.picker.other_desc": "Privat e-post till utvecklaren",
  "feedback.attached_blurb":
    "En sessionsuppspelning och ditt aktuella system bifogas automatiskt.",
  "feedback.other_blurb":
    "Skickas privat till utvecklaren. Du får inget automatiskt svar — lämna din e-post nedan om du vill ha ett.",
  "feedback.contact.email_placeholder": "E-post (valfritt)",
  "feedback.contact.name_placeholder": "Namn (valfritt)",
  "feedback.contact.optional_blurb":
    "Lämna din e-post om du vill ha svar när det här är löst.",
  "feedback.region_banner": "Dra för att markera ett område",
  "feedback.esc_cancel": "för att avbryta",
  "feedback.pick_banner": "Klicka på ett objekt för att bifoga det",
  "feedback.remove_screenshot": "Ta bort skärmbild",
  "feedback.remove_item": "Ta bort objekt",
  "feedback.select_item": "Välj objekt",
  "feedback.add_screenshot": "Lägg till skärmbild",
  "feedback.cancel": "Avbryt",
  "feedback.send": "Skicka",
  "feedback.sending": "Skickar…",
  "feedback.fallback_component": "Komponent",
  "feedback.fallback_zone": "Zon",
  "feedback.fallback_junction": "Skarv",
  "feedback.cable_label": "Kabel: {from} → {to}",

  // ── Canvas (minimap) ───────────────────────────────────
  "canvas.minimap_aria": "Systemkarta",

  // ── Diagnostics — fallback labels ──────────────────────
  "diag.label.zone": "Zon",
  "diag.label.junction": "Skarv",
  "diag.label.component": "Komponent",
  "diag.label.load": "Förbrukare",

  // ── Diagnostics — AC, controls and PV circuits ─────────
  "diag.control-coil-voltage-mismatch.fix": "Använd föreskriven spolmatning eller ett relä med rätt nominell spolspänning. Kontrollera båda spolledningarna och styrkretsen.",
  "diag.control-coil-voltage-mismatch.message": "Stift 85 och 86 på {label} kan nå samma angivna källa på {actual} V. Detta ligger utanför den granskade nominella spolklassen på {rated} V. Tillåten driftspänning och transienter måste fortfarande verifieras mot tillverkaren.",
  "diag.control-coil-voltage-mismatch.title": "Reläspolens matningsspänning avviker",
  "diag.control-contact-voltage-unverified.fix": "Använd kontakter som är klassade för den faktiska matningsspänningen och strömmen som bryts. Kontrollera alla avsedda lägen och tillverkarens installationsgränser.",
  "diag.control-contact-voltage-unverified.message": "{label} är ansluten till en angiven likspänningskälla på {actual} V. Den granskade kontaktklassningen är {rated} V DC. Källan ligger utanför granskad nominell spänningsklass eller maximal spänning.",
  "diag.control-contact-voltage-unverified.title": "Manöverkontaktens spänning behöver verifieras",
  "diag.control-state-coverage-unverified.fix": "Granska alla tillåtna kombinationer av kontaktlägen. Att ytterligare varningar saknas betyder inte att de ogranskade kombinationerna har verifierats.",
  "diag.control-state-coverage-unverified.message": "Ritningen överskrider gränsen på 64 kombinationer för samtidig kontroll av kontaktlägen. Felkontrollen av alternativa lägen är ofullständig.",
  "diag.control-state-coverage-unverified.title": "Kombinationer av kontaktlägen behöver granskas",
  "diag.ac-source-neutral-shared.title": "AC-källor delar neutralledare",
  "diag.ac-source-neutral-shared.message": "Neutralledare från olika AC-källor är sammankopplade. Ritningen fastställer inte om deras neutralomkoppling och jordförbindelser tillåter detta.",
  "diag.ac-source-neutral-shared.fix": "Kontrollera källornas isolation och neutralomkoppling i varje driftläge mot anvisningarna för källor och omkopplare.",
  "diag.control-contact-overload.title": "Ett kontaktläge överbelastar kontakt eller kabel",
  "diag.control-contact-overload.message": "{label}, läge {position}: den kompletta kretsen leder {current} A genom en kontakt eller ansluten kabel med gränsen {maxCurrent} A. Läget kontrolleras även när det är inaktivt i aktuell vy.",
  "diag.control-contact-overload.fix": "Välj lämplig kontaktor eller brytare och rätt dimensionerad kabel. Kontrollera verklig kontinuerlig last, startström och induktiv last.",
  "diag.ac-conductor-mismatch.title": "AC-ledare är förväxlade eller sammankopplade",
  "diag.ac-conductor-mismatch.message": "En ansluten eller valbar väg förbinder fas med neutralledare, skyddsjord eller en annan fas.",
  "diag.ac-conductor-mismatch.fix": "Följ den markerade vägen och rätta L-, N- och PE-anslutningarna innan spänningen slås på.",
  "diag.ac-sources-paralleled.title": "AC-källornas utgångar är sammankopplade",
  "diag.ac-sources-paralleled.message": "En ansluten eller valbar fasväg förbinder flera AC-utgångar. Ritningen visar inte verifierad synkronisering eller godkänd parallelldrift.",
  "diag.ac-sources-paralleled.fix": "Använd avsedd källväljare och ta bort förbikopplingar, eller dokumentera ett av tillverkaren godkänt parallellsystem före anslutning.",
  "diag.ac-source-pair-mismatch.title": "AC-fas och neutral kommer från olika källor",
  "diag.ac-source-pair-mismatch.message": "L och N som matar {label} når inte samma AC-källport.",
  "diag.ac-source-pair-mismatch.fix": "Håll varje källas fas och neutral tillsammans genom avsedd källväljare och skyddsutrustning.",
  "diag.ac-circuit-incomplete.title": "AC-matningens ledare är ofullständiga",
  "diag.ac-circuit-incomplete.message": "L- och N-vägarna som matar {label} bildar inte en fullständig anslutning till en AC-källa.",
  "diag.ac-circuit-incomplete.fix": "Komplettera L och N till samma källa. Skyddsjord får inte ersätta neutralledaren.",
  "diag.ac-earth-port-ambiguous.title": "Skyddsjordsterminalen kan inte identifieras",
  "diag.ac-earth-port-ambiguous.message": "Ritningen visar inte vilken skyddsjordsterminal på {label} som hör till växelströmskretsen med neutralledaren {port}.",
  "diag.ac-earth-port-ambiguous.fix": "Kontrollera utrustningens kopplingsschema och ange vilken skyddsjordsterminal som hör till kretsen.",
  "diag.ac-source-earth-missing.title": "Skyddsjorden når inte AC-källan",
  "diag.ac-source-earth-missing.message": "Ritningen visar ingen obruten skyddsjordsförbindelse från {label} ({loadTerminal}) till dess växelströmskälla, {source} ({sourceTerminal}).",
  "diag.ac-source-earth-missing.fix": "Återställ källans PE-anslutning genom jordningssystemet. Enbart anslutning till DC-minus bevisar inte kontinuitet till landströmmen.",
  "diag.ac-protection-missing.title": "AC-överströmsskydd saknas eller är förbikopplat",
  "diag.ac-protection-missing.message": "En källa kan mata {label} utan att passera en identifierad väg genom AC-överströmsskydd.",
  "diag.ac-protection-missing.fix": "Lägg till eller verifiera rätt AC-brytare och ta bort förbikopplingar. DC-säkringsvärden och GFCI fastställer inte AC-överströmsskydd.",
  "diag.ac-protection-exceeds-rating.title": "AC-skyddet överstiger utrustningens märkström",
  "diag.ac-protection-exceeds-rating.message": "{label} är märkt {rating} A, men en matningsväg saknar identifierat överströmsskydd på högst detta värde.",
  "diag.ac-protection-exceeds-rating.fix": "Kontrollera installerad utrustning och skyddens märkström, inklusive varje alternativ matningsväg.",
  "diag.ac-load-exceeds-rating.title": "Angivna AC-laster överstiger kretsens märkström",
  "diag.ac-load-exceeds-rating.message": "Ansluten inmatad effekt innebär minst {current} A vid nominell spänning, över kretsens eller utrustningens {rating} A.",
  "diag.ac-load-exceeds-rating.fix": "Minska samtidiga laster eller ändra kretsens dimensionering. Effektfaktor och startström kan höja strömmen över denna undre gräns.",
  "diag.ac-voltage-mismatch.title": "AC-spänningarna stämmer inte överens",
  "diag.ac-voltage-mismatch.message": "{label} anger {expected} V, men en ansluten eller valbar källa anger {actual} V.",
  "diag.ac-voltage-mismatch.fix": "Verifiera utrustningens verkliga spänningsområde och källans inställning före anslutning.",
  "diag.ac-neutral-earth-link-unverified.title": "Verifiera den ritade neutral–jord-förbindelsen",
  "diag.ac-neutral-earth-link-unverified.message": "En kabel förbinder AC-neutral med skyddsjord. Rätt placering beror på källa, omkoppling och interna jordningsreläer.",
  "diag.ac-neutral-earth-link-unverified.fix": "Kontrollera förbindelsen mot tillverkarens anvisningar i varje driftläge. Lägg inte till eller ta bort en neutral–jord-förbindelse på antagande.",
  "diag.ac-profile-unverified.title": "Redigerad AC-utrustning behöver verifieras",
  "diag.ac-profile-unverified.message": "Elektriska ändringar i {label} stämmer inte längre med katalogens skydds- och anslutningsprofil.",
  "diag.ac-profile-unverified.fix": "Verifiera ändrade kontakter, anslutningar och märkdata mot den installerade utrustningen. Originalprofilens skydd får inte förutsättas.",
  "diag.ac-protection-reversed.title": "AC-skyddet matas från lastsidan",
  "diag.ac-protection-reversed.message": "En källa når en skyddad utgång på {label} medan LINE-ingången saknar egen matning från källa.",
  "diag.ac-protection-reversed.fix": "Anslut inkommande matning till LINE och utgående ledningar till avsedda LOAD- eller uttagsanslutningar enligt tillverkarens anvisningar.",
  "diag.ac-leakage-bypassed.title": "AC-jordfelsskyddet är förbikopplat",
  "diag.ac-leakage-bypassed.message": "Fas eller neutral kan nå en skyddad utgång på {label} utan att passera dess skyddskontakter.",
  "diag.ac-leakage-bypassed.fix": "Följ både L och N och ta bort den oavsiktliga förbikopplingen. Håll skyddsjorden obruten.",
  "diag.ac-leakage-unverified.title": "Verifiera AC-jordfelsskyddet",
  "diag.ac-leakage-unverified.message": "Matningsvägarna till {label} fastställer inte identifierat jordfelsskydd genom både L och N.",
  "diag.ac-leakage-unverified.fix": "Verifiera erforderlig RCD-, ELCI- eller GFCI-typ, känslighet och placering för installationen. En vanlig automatsäkring fastställer inte jordfelsskydd.",
  "diag.ac-state-coverage-unverified.title": "AC-omkopplingens kombinationer behöver granskas",
  "diag.ac-state-coverage-unverified.message": "Ritningen har fler omkopplingskombinationer än AC-kontrollen kan undersöka fullständigt.",
  "diag.ac-state-coverage-unverified.fix": "Granska varje tillåten källkombination och förregling. Avsaknad av andra fynd verifierar inte de kombinationer som inte undersökts.",
  "diag.ac-design-unverified.title": "AC-installationen behöver fler kontroller",
  "diag.ac-design-unverified.message": "Ritningen kontrollerar ledaranslutningar och angivna märkdata; den verifierar inte fullständigt AC-skydd eller installationssäkerhet.",
  "diag.ac-design-unverified.fix": "Verifiera installerade märkdata, kabelförläggning, felström, skyddskoordination, jordfelsskydd och neutral–jord-förbindning i varje driftläge. Effektfaktor, startström och automatisk utlösning simuleras inte.",
  "diag.control-relay-manual.title": "Reläläget väljs manuellt",
  "diag.control-relay-manual.message": "{label}: kontaktläget väljs manuellt och följer inte spolens koppling. Spolström, tillslag och kopplingstransienter beräknas inte. Dimensioneringen av den anslutna DC-kretsen är preliminär; automatiska kabel- och säkringsmängder utelämnas.",
  "diag.control-relay-manual.fix": "Kontrollera spolkretsen och varje avsett kontaktläge mot reläets och lastens instruktioner. Verifiera gemensamma matningar, returer, alla grenar och deras skydd.",
  "diag.control-coil-incomplete.title": "Reläspolens anslutning är ofullständig",
  "diag.control-coil-incomplete.message": "{label} har bara en av spolens två anslutningar inkopplad. Den ritade styrkretsen visar inte en fungerande spolmatning.",
  "diag.control-coil-incomplete.fix": "Rita både spolmatning och retur. Kontrollera spänning, polaritet och skydd enligt reläets instruktioner.",
  "diag.control-contact-bypass.title": "Extern ledning förbikopplar en kontakt",
  "diag.control-contact-bypass.message": "{label} har en ledande väg från gemensam anslutning till en kopplad anslutning utanför komponenten. Kontakten kan därför inte bryta den vägen.",
  "diag.control-contact-bypass.fix": "Granska den markerade kretsen och ta bort oavsiktliga byglar eller alternativa matningar.",
  "diag.control-model-unverified.title": "Ändrad kontaktkoppling behöver granskas",
  "diag.control-model-unverified.message": "{label} matchar inte längre det granskade kontaktschemat. Det behållna katalog-ID:t verifierar inte kontaktisolering eller funktion. Dimensioneringen av den anslutna DC-kretsen är preliminär; automatiska kabel- och säkringsmängder utelämnas.",
  "diag.control-model-unverified.fix": "Verifiera varje kontakt, spolanslutning och brytarläge i den redigerade definitionen. Kontrollera sedan den anslutna DC-kretsen, gemensamma matningar, returer och skydd i varje avsett läge.",
  "diag.control-state-sizing-unverified.title": "Kontrollera varje brytarläge",
  "diag.control-state-sizing-unverified.message": "{label}: strömuppskattningar följer ett valt kontaktläge och verifierar inte alla lägen tillsammans. Dimensioneringen av den anslutna DC-kretsen är preliminär; automatiska kabel- och säkringsmängder utelämnas. Återfjädring tidsmodelleras inte.",
  "diag.control-state-sizing-unverified.fix": "Kontrollera hela den anslutna DC-kretsen i varje avsett läge, även intryckt läge. Verifiera gemensamma matningar, returer, alla grenar och deras skydd.",
  "diag.pv-string-protection-missing.title": "Solsträngens skydd är inte utritat",
  "diag.pv-string-protection-missing.message": "{label}, sträng {string}: en väg från solpanelen når samlingsboxen utan angiven individuell gPV-säkring. En gemensam utgångssäkring skyddar inte varje sträng separat.",
  "diag.pv-string-protection-missing.fix": "Kontrollera behovet av strängskydd mot modulens Isc, högsta serissäkring och bakmatning från parallella strängar. Rita det individuella skydd som behövs.",
  "diag.pv-string-protection-bypass.title": "Ledning förbikopplar strängsäkringen",
  "diag.pv-string-protection-bypass.message": "{label}, sträng {string}: en parallell ledande väg finns runt den angivna strängsäkringen.",
  "diag.pv-string-protection-bypass.fix": "Ta bort den oavsiktliga förbikopplingen. Varje nödvändig strängsäkring ska sitta i serie före gemensam plusskena.",
  "diag.pv-string-fuse-unrated.title": "Valet av solsäkring är ofullständigt",
  "diag.pv-string-fuse-unrated.message": "{label}, sträng {string}: hållaren saknar giltigt matchande gPV-värde, överskrider 30 A eller motsägs av en ansluten säkringsuppgift.",
  "diag.pv-string-fuse-unrated.fix": "Ange en kompatibel gPV-säkring separat. Kontrollera värdet mot modul och hållare och rätta motstridiga uppgifter.",
  "diag.pv-combiner-rating-unverified.title": "Samlingsboxens märkdata behöver verifieras",
  "diag.pv-combiner-rating-unverified.message": "{label} visar en generell ledarkoppling. Märkspänning och märkström för kapsling, skenor och anslutningar samt Isc, kall Voc och exakt säkringssamordning är inte verifierade.",
  "diag.pv-combiner-rating-unverified.fix": "Välj verklig samlingsbox och skydd och kontrollera gränserna för denna solpanelgrupp. Säkringshållarna är inte lastbrytare.",
  "diag.pv-string-return-missing.title": "Solsträngens minusledning saknas",
  "diag.pv-string-return-missing.message": "{label}, sträng {string}: plusingången är ansluten men motsvarande minusingång är inte det.",
  "diag.pv-string-return-missing.fix": "Rita hela strängens plus- och minuskrets med rätt polaritet.",
  "diag.pv-combiner-model-unverified.title": "Ändrad samlingsbox behöver granskas",
  "diag.pv-combiner-model-unverified.message": "{label} motsvarar inte längre den granskade kopplingen med separata plus- och minusskenor.",
  "diag.pv-combiner-model-unverified.fix": "Granska ändrade anslutningar och interna förbindelser innan du använder beräkningar för solpaneler eller skydd.",
  "diag.pv-holder-voltage-exceeded.title": "Solsträngens spänning överskrider hållarens gräns",
  "diag.pv-holder-voltage-exceeded.message": "{label}, sträng {string}: den ritade seriekopplingen har {voltage} V angiven Voc vid STC, över hållarens {maxVoltage} V DC redan före köldkorrigering.",
  "diag.pv-holder-voltage-exceeded.fix": "Välj komponenter med rätt märkspänning och kontrollera högsta köldkorrigerade strängspänning.",

  // ── Diagnostics — topology ─────────────────────────────
  "diag.unfused-positive-run.title": "Osäkrad plusledning till {label}",
  "diag.unfused-positive-run.message":
    "{label} är kopplad till ett batteri+ utan säkring på vägen. En kortslutning här kan smälta kablar innan något löser ut.",
  "diag.unfused-positive-run.fix":
    "Sätt en säkring nära batteriet på plusledaren.",

  "diag.fuse-too-far-from-battery.title": "Kontrollera säkringsavståndet: {label}",
  "diag.fuse-too-far-from-battery.message": "Schemat visar {distanceMm} mm före första säkringen. Crimps standardkontroll använder 175 mm; installationsundantag ingår inte.",
  "diag.fuse-too-far-from-battery.fix": "Kontrollera säkringens verkliga placering och den osäkrade kabellängden.",

  "diag.battery-missing-terminal-fuse.title": "Kontrollera batteriskyddet: {label}",
  "diag.battery-missing-terminal-fuse.message": "Schemat visar en batterimatning längre än 175 mm utan säkring på pluspolen.",
  "diag.battery-missing-terminal-fuse.fix": "Kontrollera den osäkrade längden och sätt lämpligt skydd nära batteriet.",

  "diag.fuse-aic-inadequate.title":
    "{type}-säkringens brytförmåga är lägre än den modellerade felströmmen",
  "diag.fuse-aic-inadequate.message":
    "Modellen uppskattar batteriets kortslutningsström till ~{faultA} A; den här {type}-säkringen har en modellerad brytförmåga på {aic} A. Om felströmmen överstiger brytförmågan kan säkringen misslyckas med att bryta säkert. Kontrollera batteriets faktiska kortslutningsström och tillverkarens brytförmåga vid systemets spänning.",
  "diag.fuse-aic-inadequate.fix":
    "Använd en säkring med tillräcklig DC-brytförmåga vid systemspänningen och placera den så nära batteriets pluspol som praktiskt möjligt. Kontrollera den exakta produktens märkdata; brytförmågan kan minska när spänningen ökar.",
  "diag.fuse-aic-inadequate.title_covered":
    "Kontrollera samordningen mellan {type}-säkringen och uppströms skydd",
  "diag.fuse-aic-inadequate.message_covered":
    "Modellen uppskattar bankens kortslutningsström till ~{faultA} A, över den här {type}-säkringens brytförmåga på {aic} A. Uppströms skydd finns på varje modellerad matningsväg; en av enheterna är en {upstreamRating} A {upstreamType} med modellerad brytförmåga på {upstreamAic} A. Detta verifierar inte monterade säkringars märkdata, faktisk kortslutningsström eller en provad seriekombination. Samordning och genomsläppt energi måste fortfarande verifieras med tillverkarens uppgifter.",
  "diag.fuse-aic-inadequate.fix_covered":
    "Kontrollera bankens faktiska kortslutningsström och alla monterade säkringars märkdata. Kontrollera tillverkarens uppgifter för den exakta kombinationen av uppströms {upstreamType} och nedströms skydd vid systemspänningen, eller välj ett nedströms skydd med tillräcklig egen brytförmåga.",

  "diag.fuse-rating-unavailable.title":
    "Verifiera {type}-säkringens märkström: {rating} A",
  "diag.fuse-rating-unavailable.message_below":
    "Den angivna märkströmmen {rating} A ligger utanför Crimps referensintervall för {type} ({min}–{max} A). Verifiera den exakta säkringen och att den passar kretsen.",
  "diag.fuse-rating-unavailable.message_above":
    "Den angivna märkströmmen {rating} A ligger utanför Crimps referensintervall för {type} ({min}–{max} A). Verifiera den exakta säkringen och att den passar kretsen.",
  "diag.fuse-rating-unavailable.fix_below":
    "Välj en verifierad säkring eller familj som passar kretsen. Höj inte märkströmmen utan att kontrollera ledarens strömtålighet och utrustningens skydd.",
  "diag.fuse-rating-unavailable.fix_above":
    "Välj en verifierad säkring eller familj som passar kretsen. Höj inte märkströmmen utan att kontrollera ledarens strömtålighet och utrustningens skydd.",

  "diag.battery-chemistry-unknown.title": "{label} saknar kemi",
  "diag.battery-chemistry-unknown.message":
    "{label} anger varken kemi eller kortslutningsström, så dess felström och laddacceptans är okända — inte noll. Ange kemin för kontroll av laddacceptans. Verifiering av felström kräver separata käll- eller bankdata; kemin räcker inte.",
  "diag.battery-chemistry-unknown.fix":
    "Ge {label} en kemi — eller en angiven kortslutningsström och maximal laddström — i dess definition.",

  "diag.fuse-type-unspecified.title":
    "{count} säkrade ledningar saknar säkringstyp",
  "diag.fuse-type-unspecified.message":
    "Banken kan leverera ~{faultA} A i en kortslutning. Vid den nivån är det säkringens *typ* som avgör om den löser ut eller svetsas fast — den svagaste vanliga familjen bryter bara {weakestAic} A. De här ledningarna har ett värde men ingen typ, så kontrollerna av brytförmåga och tillgänglighet kan inte köras på dem.",
  "diag.fuse-type-unspecified.fix":
    "Välj familj per ledning: Class T eller MRBF på batteripolen, ANL eller MEGA inline, och en bladsäkringsplint för små avgreningar bakom en tillräcklig huvudsäkring.",

  "diag.fix.add_fuse_near_battery": "Lägg till säkring nära batteriet",
  "diag.fix.upgrade_fuse_to": "Uppgradera säkring till {rating} A",
  "diag.fix.upgrade_wire_to": "Uppgradera till {mm2} mm²",
  "diag.fix.delete_duplicate_one": "Radera dubblett",
  "diag.fix.delete_duplicate_many": "Radera {count} dubbletter",

  "diag.load-floating.title": "{label} saknar matningsväg",
  "diag.load-floating.message":
    "Ritningen visar ingen väg från {label} till någon av batteripolerna.",
  "diag.load-floating.fix":
    "Visa den faktiska plusmatningen och returen till samma batteri, inklusive installerade säkringar.",

  "diag.load-via-floating-converter.title":
    "{label} beror på {upstream}, som inte är kopplad till ett batteri",
  "diag.load-via-floating-converter.message":
    "{label} är kopplad till {upstream}, men {upstream} har ingen väg till batteriet — så {label} kan inte startas via den ännu.",
  "diag.load-via-floating-converter.fix":
    "Koppla {upstream}s ingång till ett säkrat batteri+ och motsvarande batteri−-skena.",

  "diag.load-no-positive.title": "{label} saknar plusmatning",
  "diag.load-no-positive.message":
    "{label}s minus är kopplad men ingen väg når ett batteri+.",
  "diag.load-no-positive.fix":
    "Koppla förbrukarens plus via en säkring till batteriets plusskena.",

  "diag.load-no-return.title": "{label} saknar returväg",
  "diag.load-no-return.message":
    "Matningen når {label}, men returvägen till samma källa saknas i ritningen.",
  "diag.load-no-return.ground_message": "{label} är ansluten till en jordsymbol, men nätet saknar retur till källan som matar förbrukaren.",
  "diag.load-no-return.ground_fix": "Jordsymboler är förbundna med varandra. Visa den verkliga returvägen från nätet till den matande källans returanslutning.",
  "diag.load-no-return.fix":
    "Visa förbrukarens verkliga returväg till den matande källans returanslutning.",

  "diag.battery-bms-missing.title": "{label} behöver ett externt BMS",
  "diag.battery-bms-missing.message": "Den här batterimodellen kräver ett kompatibelt externt BMS. Ingen kompatibel styrenhet är ansluten till dess batterimatning i ritningen.",
  "diag.battery-bms-missing.fix": "Lägg till tillverkarens godkända BMS och anslut dess matning på batterisidan samt batteriernas datakedja.",
  "diag.battery-bms-incompatible.title": "Batteri och BMS är inte kompatibla",
  "diag.battery-bms-incompatible.message": "{bms} är anslutet till {label}, men dess angivna batterikompatibilitet omfattar inte den här modellen.",
  "diag.battery-bms-incompatible.fix": "Använd det BMS som anges för batterifamiljen; Smart- och NG-styrenheter är inte utbytbara.",
  "diag.bms-disconnect-bypassed.title": "BMS-frånskiljningen är förbikopplad",
  "diag.bms-disconnect-bypassed.message": "En ledningsväg förbinder batteri- och systempolerna runt {label}, så dess inbyggda frånskiljare kan inte bryta den vägen.",
  "diag.bms-disconnect-bypassed.fix": "Ta bort förbikopplingen och anslut det skyddade systemet genom avsedd systemport på BMS.",
  "diag.bms-control-unverified.title": "Kontrollera BMS-systemets avstängning",
  "diag.bms-control-unverified.message": "Strömkablarna visar inte om BMS-systemet kan stoppa laddning och koppla bort förbrukare.",
  "diag.bms-control-unverified.fix": "Kontrollera mätkablar och avstängningssignaler enligt BMS-manualen.",

  // BMS, service isolation and AC protection checks.
  "diag.ac-source-neutral-floating.title": "AC-källans neutralledare är flytande",
  "diag.ac-source-neutral-floating.message": "{source} har obunden neutralledare i dokumenterat fabriksutförande och matar {label}; ingen förbindelse mellan neutralledare och skyddsjord visas på källsidan. Jordfelsskyddet behöver verifieras för installationen.",
  "diag.ac-source-neutral-floating.fix": "Låt kontrollera källa, neutralomkoppling, skyddsjordens kontinuitet och jordfelsskydd mot tillverkarens anvisningar i varje driftläge. Lägg inte till en neutral–jord-förbindelse på antagande.",
  "diag.ac-neutral-bond-unverified.title": "Verifiera AC-neutralledare och jordförbindning",
  "diag.ac-neutral-bond-unverified.message": "Ritningen kan inte verifiera neutral–jord-arrangemanget för {label} som matas av {source}, inklusive interna förbindelser och omkoppling mellan källor.",
  "diag.ac-neutral-bond-unverified.fix": "Låt kontrollera källa, neutralomkoppling, skyddsjordens kontinuitet och jordfelsskydd mot tillverkarens anvisningar i varje driftläge. Lägg inte till en neutral–jord-förbindelse på antagande.",
  "diag.pv-isolation-missing.title": "Frånkoppling av solpanelerna saknas",
  "diag.pv-isolation-missing.message": "{label} är fortfarande ansluten till panelerna när schemats solcellsbrytare öppnas.",
  "diag.pv-isolation-missing.fix": "Lägg till en lämplig DC-frånskiljare och ta bort förbikopplingar.",
  "diag.pv-isolation-unverified.title": "Kontrollera frånkoppling av solpanelerna",
  "diag.pv-isolation-unverified.message": "Crimp kan inte verifiera hur alla solcellsmatningar till {label} kopplas bort.",
  "diag.pv-isolation-unverified.fix": "Kontrollera regulatorns manual samt frånskiljarens inkoppling och märkdata.",
  "diag.inverter-dc-isolation-unverified.title": "Kontrollera frånkoppling av växelriktaren",
  "diag.inverter-dc-isolation-unverified.message": "Crimp kan inte bekräfta att {label} kan kopplas bort från alla DC-källor.",
  "diag.inverter-dc-isolation-unverified.fix": "Kontrollera hur frånkoppling ska ske enligt växelriktarens manual.",
  "diag.ac-neutral-bond-unverified.message_source": "Fas och neutralledare som matar {label} kan inte följas till en gemensam modellerad AC-källa. Källa, neutral–jord-arrangemang och jordfelsskydd är inte verifierade.",
  "diag.ac-neutral-bond-unverified.message_relay": "{source} använder ett internt jordningsrelä. Konfiguration, skyddsjordens kontinuitet och beteende vid omkoppling till landström/generator visas inte, så skyddet för {label} är inte verifierat.",

  "diag.missing-ground.title": "Jordförbindelse saknas: {label}",
  "diag.missing-ground.message": "{terminal} når ingen jordpunkt i schemat.",
  "diag.missing-ground.fix": "Komplettera jordförbindelsen enligt utrustningens manual.",

  "diag.parallel-battery-imbalance.title":
    "Parallellbatterier: asymmetriska kabellängder",
  "diag.parallel-battery-imbalance.message":
    "{labels} delar plusskena men deras kablar skiljer mer än 20 % ({min} m mot {max} m). Den kortare dragningen drar åt sig oproportionerligt mycket ladd- och urladdningsström.",
  "diag.parallel-battery-imbalance.fix":
    "Matcha kabellängderna från varje batteri till skenan, eller använd diagonalskens-koppling (Smartgauge / 'method 2').",

  "diag.parallel-battery-chemistry-mismatch.title":
    "Parallellbatterier: blandade kemier",
  "diag.parallel-battery-chemistry-mismatch.message":
    "{labels} sitter på samma skena men har olika kemier (t.ex. LiFePO4 och blybatteri). Kemierna vilar på olika spänningsnivåer och följer olika ladd-/urladdningskurvor, så ett batteri dumpar ström i det andra varje gång systemet står stilla — vilket sliter ut blybatteriet och utsätter LFP-cellerna för laddprofiler de inte är gjorda för.",
  "diag.parallel-battery-chemistry-mismatch.fix":
    "Parallellkoppla inte LiFePO4 med blybatteri i samma bank. Separera dem på isolerade skenor som kopplas via DC-DC-laddare eller batterikombinator.",

  "diag.reversed-polarity.title": "{aLabel} + bondad till {bLabel} −",
  "diag.reversed-polarity.message":
    "Den här kabeln kopplar en plusplint direkt till en minus- eller jordplint. Att slå till spänning innebär en direkt kortslutning — kablar förångas innan någon säkring hinner reagera.",
  "diag.reversed-polarity.fix":
    "Koppla bort kabeln. Kontrollera polariteten på båda ändar innan du återansluter. Om det här är en avsiktlig seriebrygga (12 V-batterier → 24 V-bank, eller paneler i serie för högre Voc) — markera kabeln som serielänk för att tysta varningen.",
  "diag.reversed-polarity.fix-action": "Markera som serielänk",

  "diag.ac-onto-dc-rail.title": "AC anslutet till DC: {acLabel} → {dcLabel}",
  "diag.ac-onto-dc-rail.message": "Schemat kopplar en strömförande AC-ledare till en DC-anslutning. Det kan orsaka elchock eller brand.",
  "diag.ac-onto-dc-rail.fix": "Ta bort AC–DC-kopplingen. Kontrollera egna anslutningsdefinitioner; behåll nödvändig skyddsjord.",

  "diag.charge-on-crank-terminal.title": "{farLabel} sitter på motorns startterminal",
  "diag.charge-on-crank-terminal.message":
    "Den här kabeln matar {farLabel} från motorns startterminal (Starter), som för startström. Laddningsenheter hör hemma på generatorns Alt B+-utgång — på startterminalen dimensioneras kabeln för startsurgen och visar ingen generatorström när motorn går.",
  "diag.charge-on-crank-terminal.fix":
    "Flytta kabeln till motorns Alt B+-utgång.",
  "diag.charge-on-crank-terminal.fix-action": "Flytta till Alt B+",

  "diag.suspicious-series-link.title":
    "Serielänk från {aLabel} till {bLabel} ser fel ut",
  "diag.suspicious-series-link.message":
    "Den här kabeln är markerad som serielänk men ändarna är inte två batterier eller två solpaneler. Seriebryggor är bara meningsfulla mellan likadana celler (12 V → 24 V-bank, paneler i serie för högre Voc till MPPT). På andra ställen är + → − fortfarande en kortslutning.",
  "diag.suspicious-series-link.fix":
    "Avmarkera serielänken och kontrollera polariteten på båda ändar.",
  "diag.suspicious-series-link.fix-action": "Avmarkera serielänk",

  "diag.voltage-mismatch.title":
    "{label} är specad för {declared} men sitter på en {actual} V-ledning",
  "diag.voltage-mismatch.message":
    "Den här komponenten anger {declared} som tillåtna spänningar, men kabeln landar på en {actual} V-domän. Att mata en 12 V-pryl från 48 V förstör den ögonblickligen; en 48 V-enhet på 12 V startar inte. Använd en step-up/step-down-omvandlare på rätt sida.",
  "diag.voltage-mismatch.fix":
    "Flytta komponenten till rätt ledning, eller sätt in en DC-DC-omvandlare mellan spänningarna.",

  "diag.cross-voltage-bond.title":
    "{fromLabel} ({fromV} V) direktkopplad till {toLabel} ({toV} V)",
  "diag.cross-voltage-bond.message":
    "Kabeln binder ihop två olika spänningsdomäner utan omvandlare emellan. Allt på lägre-spänningssidan ser den högre spänningen — vanligtvis förstörs det när systemet slås på.",
  "diag.cross-voltage-bond.fix":
    "Ta bort kabeln. För att koppla samman ledningarna för laddning, sätt in en DC-DC-laddare av rätt förhållande.",

  "diag.high-voltage-rail.title":
    "Batteriledningen vid {actual} V överstiger 60 V DC-tröskeln för säker beröring",
  "diag.high-voltage-rail.message":
    "Ledningens nominella spänning ({actual} V) ligger över IEC 60364 / ABYC:s extra-låg-spänningsgräns på {limit} V DC. Standardtennad marin kabel och de flesta distributionsplint är inte godkända för beröring över det — exponerade ledare blir en stötrisk och installationen kräver särskild isolering, isolation och låsning för att vara laglig. Om du medvetet bygger en högspänningsbank är detta en notis; om du råkat seriekoppla för många celler, ta bort några.",
  "diag.high-voltage-rail.fix":
    "Minska serieantalet så banken hamnar vid eller under 60 V (4× 12 V LFP → 51,2 V är standard 48 V-designpunkt), eller hantera detta som en HV-installation med HV-klassad kabel, isolering och frånskiljare hela vägen.",

  "diag.mismatched-series-stack.title":
    "Seriestaplade batterier har olika spänningar ({fromV} V + {toV} V)",
  "diag.mismatched-series-stack.message":
    "Den här serielänken kopplar ett {fromV} V-batteri till ett {toV} V-batteri. Staplade celler måste ha samma nominella spänning — den mindre cellen kommer att tömmas in i den större varje cykel, sjunka under sin säkra gräns och så småningom hamna i permanent omvänd polaritet. ABYC E-11 §11.10 och alla batteritillverkare kräver matchade celler i varje seriesträng.",
  "diag.mismatched-series-stack.fix":
    "Ersätt ett av batterierna så båda har samma nominella spänning, eller ta bort serielänken och behandla varje bank som en separat domän (med en egen DC-DC om de behöver brygga ihop).",

  "diag.custom-component-implausible-amps.surge-below-draw.title":
    "{label}: toppströmmen är lägre än den kontinuerliga förbrukningen",
  "diag.custom-component-implausible-amps.surge-below-draw.message":
    "{label} anger {ampSurge} A toppström mot {ampDraw} A kontinuerligt. Toppströmmen är den korta rusningen ovanpå den kontinuerliga förbrukningen och kan alltså inte vara det lägre av de två talen — ett av fälten innehåller fel värde. Som det ser ut nu gör toppvärdet ingen skillnad: säkrings- och överlastkontrollerna räknar med det högre talet, alltså {ampDraw} A.",
  "diag.custom-component-implausible-amps.surge-below-draw.fix":
    "Läs av produktbladet och lägg driftströmmen i Kontinuerlig förbrukning och startrusningen i Toppström (surge). Har de hamnat i fel fält, byt plats på dem.",

  "diag.custom-component-implausible-amps.engine-surge.title":
    "{label}: {ampSurge} A toppström mot bara {ampDraw} A kontinuerligt",
  "diag.custom-component-implausible-amps.engine-surge.message":
    "För en motor är Kontinuerlig förbrukning den ström startmotorn drar medan den faktiskt drar runt motorn, och Toppström (surge) den korta rusningen ovanpå det — riktiga motorer ligger inom ungefär 2× från varandra, den här ligger på {ratio}×. {ampDraw} A ser ut som en tändnings- eller driftström, vilket gör att ingenting på den här komponenten beskriver den uthålliga startströmmen — och det är startströmmen som dimensionerar startkabeln.",
  "diag.custom-component-implausible-amps.engine-surge.fix":
    "Lägg motorns startström i Kontinuerlig förbrukning — små marindieslar drar runt 100–250 A vid start — och låt Toppström (surge) vara den inledande rusningen.",

  "diag.custom-component-ai-estimated.title":
    "{count} komponenter har specar som assistenten uppskattat",
  "diag.custom-component-ai-estimated.message":
    "{fieldCount} värden är AI-uppskattningar. De påverkar kabelstorlekar, säkringar och energibudgeten.",
  "diag.custom-component-ai-estimated.fix":
    "Använd Åtgärda för att söka tillverkarens dokumentation och kontrollera värdena. Overifierade värden behåller sin markering.",

  "diag.ai-estimated-lengths.title_one":
    "En antagen kabellängd avgör en kabelarea",
  "diag.ai-estimated-lengths.title_many":
    "{count} antagna kabellängder avgör en kabelarea",
  "diag.ai-estimated-lengths.message_one":
    "Assistenten valde {total} kabellängder eftersom din beskrivning inte angav dem. På den här är det längden som bestämde arean — blir den fel är kabeln underdimensionerad. Resten är så korta att inget rimligt fel spelar någon roll.",
  "diag.ai-estimated-lengths.message_many":
    "Assistenten valde {total} kabellängder eftersom din beskrivning inte angav dem. På de här {count} är det längden som bestämde arean — blir en fel är den kabeln underdimensionerad. Resten är så korta att inget rimligt fel spelar någon roll.",
  "diag.ai-estimated-lengths.fix_one":
    "Mät den här dragningen i båten och skriv in den verkliga längden. Följ vägen kabeln faktiskt tar, inte fågelvägen.",
  "diag.ai-estimated-lengths.fix_many":
    "Mät de här dragningarna i båten och skriv in de verkliga längderna. Följ vägen varje kabel faktiskt tar, inte fågelvägen.",
  "diag.ai-estimated-parts.title_one": "En komponent valdes åt dig",
  "diag.ai-estimated-parts.title_many": "{count} komponenter valdes åt dig",
  "diag.ai-estimated-parts.message_one":
    "Du namngav inte den här, så assistenten valde en katalogkomponent som passar. Specarna kommer ur katalogen, inte ur assistenten — men de gäller den komponent den valde, som kanske inte är den du köper.",
  "diag.ai-estimated-parts.message_many":
    "Du namngav inte de här, så assistenten valde katalogkomponenter som passar. Specarna kommer ur katalogen, inte ur assistenten — men de gäller komponenterna den valde, som kanske inte är dem du köper.",
  "diag.ai-estimated-parts.fix_one":
    "Markera den och byt under Komponent till det du faktiskt har. Alla kablar sitter kvar.",
  "diag.ai-estimated-parts.fix_many":
    "Markera var och en och byt under Komponent till det du faktiskt har. Alla kablar sitter kvar.",

  "diag.dc-breaker-exceeds-wire.title": "{rating} A automatsäkring överstiger kabelns kapacitet",
  "diag.dc-breaker-exceeds-wire.message": "Kabeln på {mm2} mm² är klassad för {ampacity} A.",
  "diag.dc-breaker-exceeds-wire.fix": "Öka kabelarean eller välj ett lämpligt skydd med lägre märkström.",
  "diag.dc-breaker-unverified.title": "Kontrollera automatsäkringen: {label}",
  "diag.dc-breaker-unverified.message": "Brytförmåga och högsta driftspänning är inte verifierade.",
  "diag.dc-breaker-unverified.fix": "Jämför automatsäkringens DC-brytförmåga med batteriets kortslutningsström och högsta spänning. Kontrollera placering och utlösning.",
  "diag.dc-breaker-reversed.title": "Vänd brytarens anslutningar: {label}",
  "diag.dc-breaker-reversed.message": "Batteriet är anslutet till brytarens lastsida.",
  "diag.dc-breaker-reversed.fix": "Anslut batteriplus till BAT LINE och motorplus till AUX LOAD.",
  "diag.dc-breaker-voltage.title": "Brytarens spänningsgräns överskrids: {label}",
  "diag.dc-breaker-voltage.message": "Batteribankens {voltage} V överstiger den angivna DC-gränsen på {maximum} V.",
  "diag.dc-breaker-voltage.fix": "Välj en brytare som tål högsta driftspänningen.",
  "diag.dc-breaker-interrupt-capacity.title": "Brytarens brytförmåga överskrids: {label}",
  "diag.dc-breaker-interrupt-capacity.message": "Angiven kortslutningsström är {fault} A; brytarens brytförmåga är {rating} A.",
  "diag.dc-breaker-interrupt-capacity.fix": "Välj ett skydd med tillräcklig DC-brytförmåga för denna källa.",
  "diag.fuse-exceeds-wire.title": "{rating} A säkring överstiger kabelns kapacitet",
  "diag.fuse-exceeds-wire.message": "Kabelns strömgräns för dimensioneringen är {ampacity} A, under säkringens {rating} A. Kontrollera kabelns märkdata och överlastskydd.",
  "diag.fuse-exceeds-wire.fix": "Välj en lämplig mindre säkring eller öka kabeldimensionen.",

  "diag.lynx-fuse-sum-exceeds-rating.title":
    "Lynx-grenarnas säkringar summerar till {sum} A — över gränsen {limit} A",
  "diag.lynx-fuse-sum-exceeds-rating.message":
    "Säkringarna för kretsarna som är anslutna till den här Lynx-kedjan summerar till {sum} A, men {module} begränsar hela kedjan till {limit} A. Victron kräver att säkringssumman inte överstiger den lägst märkta Lynx-modulen.",
  "diag.lynx-fuse-sum-exceeds-rating.fix":
    "Minska grenssäkringarnas värden eller dela kretsarna mellan separat matade distributionskedjor tills summan håller sig inom modulgränsen.",

  "diag.inline-fuse-needed.title":
    "Inline-säkring saknas — batteriets MRBF ({rating} A) skyddar inte {mm2} mm²-kabel",
  "diag.inline-fuse-needed.message":
    "Batteriets polsäkring på {rating} A överstiger kabelns kontinuerliga ampacitet på {ampacity} A. Sätt ett grenskydd vid matningen till den klenare ledaren.",
  "diag.inline-fuse-needed.fix":
    "Sätt en inline-säkring dimensionerad för lasten vid matningen till den klenare ledaren, eller välj grövre kabel som klarar MRBF-värdet.",

  "diag.duplicate-cable.title": "{count} kablar mellan samma plintar",
  "diag.duplicate-cable.message":
    "Två eller flera kablar ansluter exakt samma plintpar. Nästan alltid en kvarleva från redigering — dubbletterna ger ingen extra strömkapacitet och skräpar bara ner arbetsytan.",
  "diag.duplicate-cable.fix": "Ta bort dubblettkablarna.",

  "diag.mismatched-cable-lengths.title":
    "Kablarna mellan {aLabel} och {bLabel} har olika längd",
  "diag.mismatched-cable-lengths.message":
    "Flera kablar går mellan {aLabel} och {bLabel} med längderna {min} m och {max} m. Kablar som förbinder samma komponentpar följer vanligtvis samma dragning, så en så stor skillnad är oftast ett inmatningsfel — och det är den längre längden som spänningsfallsberäkningen ska utgå från.",
  "diag.mismatched-cable-lengths.fix":
    "Ange den faktiska kabellängden för båda kablarna. Om de verkligen tar olika vägar — lämna dem som de är.",

  "diag.unfused-charger-output.title": "Osäkrad {terminal}-kabel: {label}",
  "diag.unfused-charger-output.message": "Kabeln från {terminal} når batteriplus utan säkring. En kortslutning kan överhetta kabeln.",
  "diag.unfused-charger-output.fix": "Lägg till lämpligt skydd nära batteriet på denna ledare.",

  "diag.unfused-source-output.title": "Osäkrad plusutgång från {label}",
  "diag.unfused-source-output.message":
    "{label}s plusutgång kan nå ett batteri+ utan säkring på vägen. En kortslutning på kabeln dumpar batteriets fulla kortslutningsström rakt igenom — ABYC E-11 §11.10.1.1 kräver en säkring inom 7 in (178 mm) från batteriet på varje ledare som ansluter till det.",
  "diag.unfused-source-output.fix":
    "Sätt en säkring nära batteriet på den här ledaren (MRBF eller class-T för högströmskällor).",

  "diag.unfused-fuse-block-feed.title": "Osäkrad matning till {label}",
  "diag.unfused-fuse-block-feed.message":
    "Kabeln som matar {label}s ingång från batteriet saknar säkring på vägen. Plats-säkringarna i centralen skyddar varje gren — men inte själva matningskabeln. Skavs den mot chassit någonstans dumpar batteriet sin fulla kortslutningsström rakt igenom (ABYC E-11 §11.10.1.1).",
  "diag.unfused-fuse-block-feed.fix":
    "Sätt en huvudsäkring (MRBF eller class-T) inom 7 in (178 mm) från batteriet på matningskabeln.",

  "diag.charger-input-output-loop.title":
    "{chargerLabel}s in- och utgång delar {battLabel}",
  "diag.charger-input-output-loop.message":
    "{chargerLabel}s plusutgång och en plusingång når båda {battLabel}. Laddaren försöker ladda samma bank vars ström matar den — den vägrar gå eller bara cirkulerar förluster genom sig själv. Utgången (BATT+) hör hemma på ett separat hjälp-/husbatteri.",
  "diag.charger-input-output-loop.fix":
    "Flytta {chargerLabel}s BATT+-utgång till hjälp-/husbatteriet, inte källbanken.",

  "diag.charger-output-on-cranking-bus.title":
    "{chargerLabel}s utgång ligger på startkretsen",
  "diag.charger-output-on-cranking-bus.message":
    "{chargerLabel}s BATT+-utgång når {engineLabel}s startmotor-/generatorlugg — startbatteriets skena. En DC-DC-laddares utgång ska landa på ett separat hjälp-/husbatteri, inte samma lugg som dess ingång drar från. Som kopplat försöker laddaren toppa upp banken som matar den.",
  "diag.charger-output-on-cranking-bus.fix":
    "Flytta {chargerLabel}s BATT+-utgång till ett dedikerat husbatteri. Behåll generator-/startsidans ingång på motorlugen.",

  "diag.dcdc-fed-without-battery-buffer.title":
    "{chargerLabel} matas direkt av {altLabel} utan batteri på skenan",
  "diag.dcdc-fed-without-battery-buffer.message":
    "{chargerLabel}s plusingång når {altLabel} utan något batteri på samma skena. DC-DC:ns nästan obegränsade ingångsström har inget batteri som håller ner spänningen, och generatorns regulator har inget som absorberar load-dump-spikar — räkna med stekt regulator eller DC-DC första gången motorn startas utan startbatteriet inkopplat.",
  "diag.dcdc-fed-without-battery-buffer.fix":
    "Koppla generatorns B+ till startbatteriet först, ta sedan {chargerLabel}s ingång från startbatteriets plus (eller en skena som startbatteriet sitter på).",

  "diag.fuse-block-slot-unrated.title_one":
    "{label}: plats {slots} har inget säkringsvärde",
  "diag.fuse-block-slot-unrated.title_many":
    "{label}: platserna {slots} har inget säkringsvärde",
  "diag.fuse-block-slot-unrated.message_one":
    "{slots} på {label} bär last men har inget säkringsvärde. Tills ett värde valts finns inget överströmsskydd — kabeln nedströms kan smälta innan något löser ut.",
  "diag.fuse-block-slot-unrated.message_many":
    "{slots} på {label} bär last men har inga säkringsvärden. Tills värden valts finns inget överströmsskydd — kablarna nedströms kan smälta innan något löser ut.",
  "diag.fuse-block-slot-unrated.fix":
    "Öppna säkringsplinten i inspektören och tilldela ett säkringsvärde till varje använd plats, dimensionerat efter den klenaste kabeln nedströms.",

  "diag.charger-return-floating.title": "Returledning saknas: {label}",
  "diag.charger-return-floating.message": "Schemat saknar en fullständig retur från {negLabels} till den bank som ska laddas.",
  "diag.charger-return-floating.message_dead_end": "Schemat saknar en fullständig retur från {negLabels} till den bank som ska laddas.",
  "diag.charger-return-floating.fix": "Anslut {negLabels} till batteribankens minusskena.",

  "diag.source-return-floating.title": "Returledning saknas: {label}",
  "diag.source-return-floating.message": "Plusmatningen är ansluten men en retur till samma batteribank saknas i schemat.",
  "diag.source-return-floating.fix": "Kontrollera minus- eller chassianslutningen tillbaka till banken.",

  "diag.monitor-power-unverified.title": "Kontrollera monitorns matning: {label}",
  "diag.monitor-power-unverified.message_missing": "Schemat saknar fullständig matning via Vbatt+ och minusanslutningen. AUX ersätter inte Vbatt+.",
  "diag.monitor-power-unverified.message_voltage": "{label} behöver 6,5–70 V DC. Matningsspänningen är okänd eller utanför intervallet.",
  "diag.monitor-power-unverified.message_protection": "Vbatt+ har en matning utan angiven säkring på högst 1 A. Kontrollera den medföljande säkrade kabeln.",
  "diag.monitor-power-unverified.fix": "Kontrollera matning, retur och säkrad kabel mot monitorns manual.",
  "diag.shunt-bypass.title": "Ström går förbi {label}",
  "diag.shunt-bypass.message": "{equipment} har en retur till det övervakade batteriet som inte passerar {label}. Monitorn kan missa denna laddnings- eller urladdningsström och visa fel laddningsnivå.",
  "diag.shunt-bypass.fix": "För övervakning av hela banken, flytta dessa returer till {terminal}-sidan av {label} och ta bort parallella returvägar förbi shunten.",

  "diag.sense-terminal-as-bus.title":
    "{label}: {terminal} är en mätledning, inte en skena",
  "diag.sense-terminal-as-bus.message":
    "{count} kablar landar på {label}s {terminal}. En mätterminal är märkt {maxAmp} A och mäter bara spänning — den kan varken bära eller fördela ström, så allt som dras genom den är odimensionerat och oskyddat.",
  "diag.sense-terminal-as-bus.message_bonds":
    "{label}s {terminal} är den enda länken mellan {batteries}. Bankerna binds ihop genom en {maxAmp} A mätledning, som får bära den utjämningsström de skiljer sig med — osäkrad och aldrig dimensionerad för det.",
  "diag.sense-terminal-as-bus.fix":
    "Ge mätterminalen en enda ledning till batteriet den mäter (säkrad 1 A vid polen) och flytta alla andra kablar till en skena eller batteripolen.",

  "diag.terminal-overloaded.title_provisional": "Kontrollera anslutningsström: {label} {terminal}",
  "diag.terminal-overloaded.message_provisional": "Uppskattad ström är {load} A mot märkströmmen {maxAmp} A. Den ofullständiga modellen kan inte bekräfta överlast.",
  "diag.terminal-overloaded.fix_verify": "Komplettera kretsen och kontrollera driftströmmen. Jämför sedan med anslutningens märkström.",
  "diag.terminal-overloaded.title": "Anslutningens märkström överskrids: {label} {terminal}",
  "diag.terminal-overloaded.message": "Beräknad kontinuerlig ström är {load} A, över anslutningens {maxAmp} A. Anslutningen kan överhettas.",
  "diag.terminal-overloaded.fix": "Använd en komponent för minst {load} A eller minska belastningen.",

  "diag.fuse-exceeds-terminal.title": "För stor säkring för {label}: {terminal}",
  "diag.fuse-exceeds-terminal.message": "Anslutningen är märkt {maxAmp} A men skyddas av en {fuse} A säkring.",
  "diag.fuse-exceeds-terminal.fix": "Använd skydd på högst {maxAmp} A eller en komponent som klarar den större säkringen.",

  "diag.cable-dangling-terminal.title": "Kabeln saknar en anslutning",
  "diag.cable-dangling-terminal.message": "{label} har ingen anslutning {terminal}. Kabeln kan inte sluta en krets.",
  "diag.cable-dangling-terminal.fix": "Anslut till en befintlig anslutning, återställ anslutningen eller ta bort kabeln.",
  "diag.cable-dangling-terminal.title_missing_node": "Kabeln saknar en komponent",
  "diag.cable-dangling-terminal.message_missing_node": "Kabelns ena ände saknas. Den kan inte sluta en krets.",
  "diag.cable-dangling-terminal.fix_missing_node": "Anslut den lösa änden eller ta bort kabeln.",
  "diag.cable-dangling-terminal.action_remove": "Ta bort trasig kabel",

  "diag.pv-input-incomplete.title": "Ofullständig solcellskrets vid {label}",
  "diag.pv-input-incomplete.message": "Den anslutna solcellsingången på {label} saknar en komplett plus- och returväg genom solpanelerna. En saknad anslutning eller fel polaritet hindrar solcellsladdning.",
  "diag.pv-input-incomplete.fix": "Slutför plus- och returanslutningarna mellan panelerna och solcellsingången, inklusive eventuella seriekopplingar.",
  "diag.pv-string-fuse-exceeds-module.title": "Solcellssträngens säkring på {fuse} A överskrider modulens gräns på {max} A",
  "diag.pv-string-fuse-exceeds-module.message": "Strängen har en säkring på {fuse} A, men {label} tillåter en maximal seriesäkring på {max} A. Säkringen uppfyller inte modulens angivna gräns för överströmsskydd.",
  "diag.pv-string-fuse-exceeds-module.fix": "Använd en strängsäkring för solceller på högst {max} A och kontrollera även den märkström som krävs vid drift. Håll strängarnas individuella skydd åtskilda från samlingsledningen.",

  "diag.mppt-over-voltage.title":
    "{label} PV-ingång överskrider max {max} V",
  "diag.mppt-over-voltage.message":
    "Längsta seriesträngen till {label} når {voc} V Voc vid STC över {panels} paneler, och stiger till {vocCold} V vid −25 °C driftstemperatur (NEC 690.7 ×1,20) — över regulatorns max-PV-ingång på {max} V. Om den angivna ingångsgränsen överskrids kan regulatorn skadas; kontrollera kall-Voc med panelens faktiska data och installationens lägsta temperatur.",
  "diag.mppt-over-voltage.fix":
    "Koppla om panelerna parallellt i stället för seriellt, ta bort en panel från strängen, eller välj en MPPT med högre PV-spänningstålighet (kall-Voc ≤ {max} V).",

  // ── Diagnostics — sizing ───────────────────────────────
  "diag.combined-battery-discharge.title": "Ansluten förbrukning överstiger batteriernas urladdningsvärden",
  "diag.combined-battery-discharge.message": "Känd ansluten förbrukning är minst {draw} A, över gränsen {limit} A från angivna urladdningsvärden.",
  "diag.combined-battery-discharge.fix": "Minska samtidig förbrukning eller verifiera och öka batteribankens urladdningsförmåga.",
  "diag.combined-battery-discharge.detail_message": "Kontrollen jämför {count} kända förbrukare med kompletta batterikretsar utifrån deras samtidiga kontinuerliga dimensioneringsström. Startström räknas inte med och möjlig laddning dras inte av. Beräknad DC-DC-ingångsström kräver kompletta kretsar och angivna spänningar; utan angiven verkningsgrad används en förlustfri undre gräns. Förbrukning över hela seriekopplingen räknas i varje nödvändig del; laster vid mittuttag tillkommer för de batterier som försörjer dem. Parallella märkströmmar bildar en total gräns utan antaganden om lika eller kapacitetsbaserad strömfördelning.",
  "diag.combined-battery-discharge.detail_fix": "Kontrollera de installerade batteriernas och BMS:ens kontinuerliga urladdningsvärden och driftvillkor. Okända laster, ofullständiga kretsar och ospecificerade värden verifieras inte av denna kontroll. Kontrollera strömfördelning och tillverkarens godkännande före parallellkoppling. Detta är en dimensioneringsberäkning, inte uppmätt ström.",

  "diag.inverter-exceeds-battery-c-rate.title_unverified": "Verifiera batteriets urladdningsförmåga för {label}",
  "diag.inverter-exceeds-battery-c-rate.message_unverified": "Ett eller flera urladdningsvärden för batterierna saknas, så Crimp kan inte bekräfta bankens kontinuerliga förmåga för {label}. Den beräknade förbrukningen är upp till {draw} A.",
  "diag.inverter-exceeds-battery-c-rate.fix_unverified": "Kontrollera de anslutna batteriernas kontinuerliga urladdningsvärden, eventuella BMS-gränser och kabelvägar innan du väljer utrustning.",

  "diag.inverter-exceeds-battery-c-rate.title":
    "{label} överstiger det angivna urladdningsvärdet för batteribanken",
  "diag.inverter-exceeds-battery-c-rate.message":
    "{label} har en beräknad förbrukning på upp till {draw} A, över bankens angivna kontinuerliga urladdningsgräns på {bank} A. Kontrollera det installerade batteriets och eventuell BMS märkdata.",
  "diag.inverter-exceeds-battery-c-rate.fix":
    "Kontrollera anslutna batterivägar och märkdata. Minska förbrukningen eller använd utrustning med tillräcklig urladdningsförmåga; följ tillverkarens godkännande för parallellkoppling.",

  "diag.dcdc-overloads-alternator.title": "Kontrollera generatorbelastningen: {pct} %",
  "diag.dcdc-overloads-alternator.message": "DC-DC-laddaren kräver {draw} A av generatorns {rated} A märkström. Tillgänglig ström vid tomgång kan vara lägre.",
  "diag.dcdc-overloads-alternator.fix": "Kontrollera generatorns kontinuerliga ström vid tomgång och ställ in DC-DC-gränsen därefter.",

  "diag.charge-exceeds-acceptance.title_recommended": "Kontrollera rekommenderad laddström",
  "diag.charge-exceeds-acceptance.message_recommended": "Källorna kan ge upp till {charge} A, över batterimodellens rekommenderade laddström på {accept} A.",
  "diag.charge-exceeds-acceptance.fix_recommended": "Kontrollera laddinställningarna mot rekommendationen och batteriets och BMS-systemets gränser.",
  "diag.charge-exceeds-acceptance.detail_message_recommended": "Tillverkaren rekommenderar {accept} A för denna batterimodell i katalogen. Det är en rekommendation, inte den maximala laddströmmen. Källorna kan ge upp till {charge} A; verklig ström beror på inställningar och samtidig användning.",
  "diag.charge-exceeds-acceptance.detail_fix_recommended": "Verifiera den installerade batterimodellen, maximal laddström, BMS-gränser och driftförhållanden. Följ även rekommenderad laddström när laddare och generatorregulatorer ställs in.",
  "diag.charge-exceeds-acceptance.title_planning": "Verifiera planeringsvärdet för laddning",
  "diag.charge-exceeds-acceptance.message_planning": "Källorna kan ge upp till {charge} A, över Crimps planeringsvärde på {accept} A för batteriet.",
  "diag.charge-exceeds-acceptance.fix_planning": "Kontrollera uppgifterna för exakt batteri och BMS innan laddinställningarna väljs.",
  "diag.charge-exceeds-acceptance.detail_message_planning": "Crimp behåller {accept} A som ett försiktigt planeringsvärde för detta katalogbatteri. Värdet är varken ett verifierat tillverkarmaximum eller en tillverkarrekommendation. Källorna kan ge upp till {charge} A; kontrollera utrustningens faktiska gränser.",
  "diag.charge-exceeds-acceptance.detail_fix_planning": "Identifiera installerad modell och revision och kontrollera dess laddström, BMS-gränser och driftförhållanden. Ett planeringsvärde fastställer inte tillåten laddström.",
  "diag.charge-exceeds-acceptance.title": "Kontrollera batteriets laddström",
  "diag.charge-exceeds-acceptance.title_current": "{charge} A laddkapacitet: kontrollera batterigränsen",
  "diag.charge-exceeds-acceptance.message_specified": "Källorna kan ge upp till {charge} A; schemat använder {accept} A som laddgräns. Verifiera uppgiften och laddinställningarna.",
  "diag.charge-exceeds-acceptance.fix_specified": "Håll laddningen inom verifierade batteri- och BMS-gränser och kontrollera den angivna gränsen.",
  "diag.charge-exceeds-acceptance.message": "Källorna kan ge upp till {charge} A, över uppskattad batterigräns på {accept} A. Kontrollera batteriets faktiska specifikation.",
  "diag.charge-exceeds-acceptance.fix_lead": "Kontrollera tillverkarens högsta laddström och justera laddarnas inställningar.",
  "diag.charge-exceeds-acceptance.fix_lfp": "Kontrollera batteriets och BMS-systemets laddgränser och justera laddarna.",

  "diag.manufacturer-breaker-unverified.title": "Kontrollera föreskriven brytare: {label}",
  "diag.manufacturer-breaker-unverified.message": "Tillverkaren anger en {rating} A automatsäkring. En smältsäkring med samma märkström behöver inte vara likvärdig.",
  "diag.manufacturer-breaker-unverified.fix": "Kontrollera exakt brytare och utlösningskurva i manualen.",
  "diag.manufacturer-breaker-rating.title": "Kontrollera brytarens märkström: {label}",
  "diag.manufacturer-breaker-rating.message": "Schemat visar {rating} A skydd; tillverkaren anger en {required} A automatsäkring.",
  "diag.manufacturer-breaker-rating.fix": "Kontrollera brytaren på varje matning. Behåll nödvändigt matningsskydd.",
  "diag.manufacturer-supplied-lead-protection.title": "Kontrollera skyddet för medföljande kabel: {label}",
  "diag.manufacturer-supplied-lead-protection.message": "Den medföljande {mm2} mm² kabeln är märkt {ampacity} A, lägre än schemats {rating} A säkring.",
  "diag.manufacturer-supplied-lead-protection.fix": "Skydda kabeln enligt manualen. En grövre förlängning höjer inte dess märkström.",
  "cable.manufacturer_fridge_fuse_hint": "CRX-manualen anger {rating} A vid den anslutna matningsspänningen. Kontrollera exakt kylskåpsvariant och säkringstyp.",
  "cable.manufacturer_wire_ampacity_hint": "Den valda kabeln klarar inte tillverkarens säkringsvärde. Kontrollera kabel och skydd innan du väljer säkring.",
  "cable.manufacturer_installation_unverified": "Kontrollera tillverkarens kabelkrav. Kabelarean är preliminär.",
  "diag.manufacturer-installation-unverified.title": "{label}: kontrollera tillverkarens kabelkrav",
  "diag.manufacturer-installation-unverified.message": "Tillverkarens kabelkrav är inte fastställt för denna krets.",
  "diag.manufacturer-installation-unverified.identity": "Delen har kvar ett CRX-katalog-id men originalmodellens identitet är inte fastställd. Kontrollera dess egen installationsmanual innan numeriska kabel- eller säkringsråd används.",
  "diag.manufacturer-installation-unverified.route": "Hela fram- och returledningen mellan källa och kylskåp är oklar eller har alternativa vägar. Kontrollera tillverkarens kabelkrav.",
  "diag.manufacturer-installation-unverified.voltage": "Kylskåpets matningsspänning är okänd eller utanför de stödda klasserna 12/24 V. Kontrollera installationskraven.",
  "diag.manufacturer-installation-unverified.24v-chart": "Installationen på 24 V kräver tillverkarens kabeldiagram. Säkringsrådet på 7,5 A fastställer inte kabelarean.",
  "diag.manufacturer-installation-unverified.length": "Hela fram- eller returledningen på 12 V överstiger diagrammets stödda 8 m. Kontrollera kabelarean med tillverkaren.",
  "diag.manufacturer-wire-minimum.ip43": "Vald kabel på {mm2} mm² underskrider det publicerade minimikravet {minimumLabel} för {label}.",
  "diag.manufacturer-installation-unverified.ip43": "Den här IP43-installationen ligger utanför granskade kabelkrav. Automatiska kabel- och säkringsråd är fortfarande preliminära.",
  "diag.manufacturer-installation-unverified.ip43-identity": "Redigerade laddardata fastställer inte den granskade IP43-modellen och dess utgångar. Inget minsta kabeltvärsnitt hämtas från enbart den kopierade katalogidentiteten.",
  "diag.manufacturer-installation-unverified.ip43-route": "En komplett krets till samma 12 V-batterikälla och dess angivna spänningar är inte fastställda. Förgreningar, seriekopplade batterier eller ytterligare vägar hindrar verifiering mot tillverkarens kabeltabell.",
  "diag.manufacturer-installation-unverified.ip43-length": "Den längre enkelriktade ledningen överskrider den publicerade kabeltabellen: 3 m för 12/50 eller 4,5 m för huvudutgången på 12/30. En grövre kabel räcker inte för att verifiera installationen.",
  "diag.manufacturer-installation-unverified.ip43-terminal": "Kabeln direkt vid en huvudanslutning på laddaren överskrider angiven kapacitet på 16 mm². Kontrollera en godkänd anslutning; pressa inte in en för grov ledare i terminalen.",
  "diag.manufacturer-installation-unverified.ip43-fix": "Kontrollera exakt laddarversion, hela plus- och minusledningarnas längder, terminalkapacitet och kabelförläggning mot manualen.",
  "diag.manufacturer-installation-unverified.parallel": "Manualen anger parallella DC-kablar. Crimp kan inte verifiera detta; automatiska kabel- och säkringsråd är preliminära.",
  "diag.manufacturer-installation-unverified.parallel-fix": "Kontrollera båda DC-ledningarna mot exakt modells installationstabell.",
  "diag.manufacturer-installation-unverified.fix": "Kontrollera exakt CRX-variant, matningsspänning och hela kabellängderna mot installationsmanualen.",
  "diag.manufacturer-fuse-minimum.title": "{label}: säkring under tillverkarens krav",
  "diag.manufacturer-fuse-minimum.message": "En angiven matningssäkring på {rating} A understiger det dokumenterade kravet på {minimum} A för {label}.",
  "diag.manufacturer-fuse-minimum.fix": "Kontrollera rätt modellmanual och vald kabel innan skyddet ändras. Ett högre värde fastställer inte rätt säkringstyp eller brytförmåga.",
  "diag.manufacturer-wire-minimum.title": "{label}: kabel under tillverkarens minimum",
  "diag.manufacturer-wire-minimum.message": "Denna kabel på {mm2} mm² understiger installationskravet på minst {minimum} mm² för {label}.",
  "diag.manufacturer-wire-minimum.fix": "Välj minst tillverkarens kabelarea och kontrollera hela sträckans strömtålighet och spänningsfall.",
  "diag.manufacturer-fuse-range.title": "{label}: säkring utanför tillverkarens intervall",
  "diag.manufacturer-fuse-range.message": "Säkringen på {rating} A ligger utanför intervallet {min}–{max} A som tillverkaren anger för batterisäkringen till {label}.",
  "diag.manufacturer-fuse-range.fix": "Välj en säkring inom tillverkarens intervall och kontrollera att kabeln tål märkströmmen.",

  "diag.charger-cumulative-vdrop.title": "{label}: {pct} % spänningsfall vid laddning",
  "diag.charger-cumulative-vdrop.title_verify": "Kontrollera laddningens spänningsfall: {label}",
  "diag.charger-cumulative-vdrop.message_manufacturer": "Beräknad förlust är {drop} V. Denna SmartSolar-modell anger högst {allowance} % förlust i batterikabeln.",
  "diag.charger-cumulative-vdrop.message_target": "Beräknad förlust är {drop} V, över planeringsmålet {allowance} %. Detta är ingen tillverkargräns.",
  "diag.charger-cumulative-vdrop.message_verify": "Kretsen beräknas tappa {drop} V mot en gräns på {allowance} %. Strömfördelningen är okänd; verkligt tapp kan skilja sig.",
  "diag.charger-cumulative-vdrop.fix": "Kontrollera ström och kabellängder. Öka dimensionerna eller korta sträckan vid behov.",
  "diag.cranking-vdrop-unverified.title": "Kontrollera startkablarnas dimension",
  "diag.cranking-vdrop-unverified.message": "Beräknat spänningsfall vid start är {pct} %, över Crimps försiktiga planeringsmål.",
  "diag.cranking-vdrop-unverified.fix": "Kontrollera startström, kabellängder och dimensioner mot motorns manual.",

  "diag.pv-cumulative-vdrop.title": "{label}: {pct} % spänningsfall i solkretsen",
  "diag.pv-cumulative-vdrop.message": "Solkretsens hela matning och retur tappar {drop} V vid dimensionerande ström, över valt planeringsmål på {allowance}%.",
  "diag.pv-cumulative-vdrop.fix": "Korta solkretsens kabeldragning eller öka ledararean; kontrollera panelens och regulatorns installationsmanualer.",
  "diag.cumulative-vdrop.title": "{label}: {pct} % dimensionerande spänningsfall batteri → förbrukare",
  "diag.cumulative-vdrop.message":
    "Det summerade dimensionerande spänningsfallet för {label} är {drop} V, över kretsens tillåtna {allowance}%. Ledarnas maximala strömmar behöver inte förekomma samtidigt; detta är en dimensioneringsberäkning, inte en prognos för faktiskt spänningsfall.",
  "diag.cumulative-vdrop.title_lower_bound": "{label}: minst {pct} % dimensionerande spänningsfall batteri → förbrukare",
  "diag.cumulative-vdrop.message_lower_bound":
    "Den beräknade delen av det dimensionerande spänningsfallet för {label} är {drop} V, redan över tillåtna {allowance}%. Osäkra ledarbidrag ingår inte, så den fullständiga dimensioneringssumman är minst detta värde. Ledarnas maximala strömmar behöver inte förekomma samtidigt; detta är inte en prognos för faktiskt spänningsfall.",
  "diag.cumulative-vdrop.title_provisional": "{label}: kontrollera uppskattat {pct} % dimensionerande spänningsfall",
  "diag.cumulative-vdrop.message_provisional":
    "Den preliminära uppskattningen av dimensionerande spänningsfall för {label} är {drop} V, över tillåtna {allowance}%. Overifierade strömvärden ingår, så uppskattningen visar varken faktiskt spänningsfall eller en garanterad undre gräns.",
  "diag.cumulative-vdrop.fix_provisional":
    "Kontrollera hela matnings- och returkretsen, apparaternas in- och utgångsvärden samt strömfördelningen. Beräkna spänningsfallet igen innan du förlitar dig på ett automatiskt kabelval.",
  "diag.cumulative-vdrop.fix":
    "Flytta säkring och förbrukare närmare batteriet, eller grovgör stamdragningen ett kabelsteg.",
  "diag.cumulative-vdrop.group_title": "Dimensionerande spänningsfall batteri → förbrukare",
  "diag.cumulative-vdrop.short_title": "{label}: {pct} %",
  "diag.cumulative-vdrop.short_title_lower_bound": "{label}: minst {pct} %",
  "diag.cumulative-vdrop.short_title_provisional": "{label}: kontrollera uppskattade {pct} %",
  "health.group_count": "{count} förbrukare",
  "health.group_expand": "Visa {count}",
  "health.group_collapse": "Dölj",

  "diag.fuse-trips-under-load.title":
    "{rating} A-säkring kommer lösa ut — sträckan bär {load} A",
  "diag.fuse-trips-under-load.message":
    "Komponenter på den här sträckan drar {load} A kontinuerligt, men kabeln är säkrad till {rating} A. Att lägga till en högströmsenhet nedströms (startmotor, växelriktare…) höjer varje uppströms säkring på dess väg.",
  "diag.fuse-trips-under-load.fix":
    "Höj säkringen till minst {target} A och kontrollera att kabelns ampacitet räcker.",
  "diag.fuse-trips-under-load.title_verify":
    "Kontrollera {rating} A-säkringen mot {load} A dimensionerande ström",
  "diag.fuse-trips-under-load.message_starting":
    "Den modellerade strömmen på {load} A innehåller kortvarig startström. Det visar inte om säkringen på {rating} A löser ut vid start. Kontrollera lastens varaktighet och den installerade säkringens tid-strömkurva.",
  "diag.fuse-trips-under-load.message_provisional":
    "Strömuppskattningen på {load} A är preliminär. Den fastställer varken faktisk varaktig ström eller att säkringen på {rating} A kommer att lösa ut. Kontrollera kretsen, strömfördelningen och lastens varaktighet innan skyddet ändras.",
  "diag.fuse-trips-under-load.fix_verify":
    "Kontrollera lastens faktiska varaktighet, den installerade säkringens tid-strömkurva och ledarens ampacitet. Bekräfta rätt märkström och placering innan säkringen byts.",

  "diag.wire-undersized-for-load.title":
    "Kabel för klen — kräver ≥ {required} A ampacitet, har {ampacity} A",
  "diag.wire-undersized-for-load.title_provisional":
    "Kontrollera kabeldimensionen — strömuppskattningen är ofullständig",
  "diag.wire-undersized-for-load.message_provisional":
    "Den preliminära uppskattningen på {load} A pekar på {required} A dimensionerande ampacitet, över kabelns märkning på {ampacity} A. Strömberäkningen är ofullständig och är ingen verifierad övre gräns. Bekräfta strömvägar, lastens varaktighet och installationsförhållanden innan ledare eller skydd väljs.",
  "diag.wire-undersized-for-load.fix_verify":
    "Komplettera strömmodellen och kontrollera ledarens ampacitet, kretsens spänningsfall och överströmsskyddet. En automatisk dimensionsändring kan inte fastställa att kretsen är korrekt dimensionerad.",
  "diag.wire-undersized-for-load.message":
    "Den modellerade lasten på {load} A kräver {required} A ledarampacitet, inklusive tillämplig marginal för varaktiga laster. Kortvarig startström behandlas separat från varaktig ström. Den här {mm2} mm²-kabeln är klassad för {ampacity} A, under kravet. Kontrollera ledararea, installationsförhållanden och överströmsskydd.",
  "diag.wire-undersized-for-load.fix":
    "Uppgradera till en kabel med ≥ {required} A ampacitet, eller sätt en säkring ≤ {ampacity} A.",
  "diag.wire-undersized-for-load.fix_wire":
    "Välj en ledare som klarar minst {required} A under installationsförhållandena. Kontrollera spänningsfallet i hela kretsen.",
  "diag.wire-undersized-for-load.fix_manual":
    "Inget automatiskt kabelval uppfyller dimensioneringskravet. Minska eller dela upp lasten, eller verifiera en lämplig ledarlösning med minst {required} A ampacitet och godtagbart spänningsfall i kretsen.",

  // ── Diagnostics — energy ───────────────────────────────
  "diag.no-battery.title": "Inget batteri i systemet",
  "diag.no-battery.message":
    "Lägg till ett batteri så vi kan uppskatta drifttid, autonomi och laddtid.",
  "diag.battery-capacity-unknown.title": "Batterikapacitet saknas",
  "diag.battery-capacity-unknown.message":
    "Batterisymboler finns, men deras definitioner anger ingen användbar kapacitet. Drifttid, autonomi och laddtid kan inte uppskattas från dessa uppgifter.",
  "diag.battery-capacity-unknown.fix":
    "Ange varje verkligt batteris nominella kapacitet i Ah. Modellera en omvandlarutgång som en omvandlare i stället för att ge en ersättningskälla en påhittad batterikapacitet.",

  "diag.no-charging.title": "Ingen laddningskälla",
  "diag.no-charging.message":
    "De aktiva förbrukarna drar ~{watts} W utan laddningskälla. Med denna förbrukning når batteriet sin reserv om {hours} timmar.",
  "diag.no-charging.fix":
    "Lägg till en solpanel + MPPT, en DC-DC-laddare eller en landströmsladdare.",

  // ── Tools menu ─────────────────────────────────────────
  "tools.title": "Verktyg",
  "tools.bom": "Materiallista",
  "tools.auto_layout": "Ordna valda komponenter",
  "tools.redraw_cables": "Rita om alla kablar",
  "tools.freeze_build": "Frys bygget…",
  "tools.unfreeze_build": "Tina upp bygget…",
  "tools.section.build_state": "Byggstatus",
  "tools.section.build_state_hint":
    "Lås automatdimensionerade areor och säkringar så att senare ändringar inte tyst påverkar ett fastställt bygge.",

  // ── Freeze / unfreeze build ────────────────────────────
  "freeze_build.freeze_title": "Frys bygget",
  "freeze_build.unfreeze_title": "Tina upp bygget",
  "freeze_build.freeze_description":
    "Lås varje kabels nuvarande automatdimensionerade area och säkring så att senare ändringar inte tyst påverkar dem. Använd när du fastställer bygget inför inköp eller installation.",
  "freeze_build.unfreeze_description":
    "Släpp varje kabels lås så att areorna åter följer den automatiska rekommendationen när du redigerar.",
  "freeze_build.freeze_bullet_wire":
    "Varje kabels area låses till värdet som visas just nu.",
  "freeze_build.freeze_bullet_fuse":
    "Kablar som redan har en säkring uppdateras till den aktuella rekommenderade märkströmmen. Osäkrade kablar (minusledare, startkretsar) förblir osäkrade.",
  "freeze_build.freeze_bullet_undo":
    "Kan ångras med Ångra eller med kommandot Tina upp bygget.",
  "freeze_build.unfreeze_bullet_wire":
    "Varje kabels area-lås tas bort. Areorna återgår till automatisk dimensionering i realtid.",
  "freeze_build.unfreeze_bullet_fuse":
    "Säkringar lämnas orörda — upptining tar inte bort skydd från någon kabel.",
  "freeze_build.unfreeze_bullet_undo": "Kan ångras med Ångra.",
  "freeze_build.preview_label": "Vad som kommer att ändras",
  "freeze_build.preview_wires": "{count} kabel/kablar får sin area låst.",
  "freeze_build.preview_fuses":
    "{count} säkring(ar) uppdateras till aktuell rekommendation.",
  "freeze_build.preview_cleared":
    "{count} kabel/kablar låses upp och återgår till automatisk dimensionering.",
  "freeze_build.already_frozen":
    "Alla kablar matchar redan sin aktuella rekommendation — inget att ändra.",
  "freeze_build.nothing_to_unfreeze":
    "Inga kablar har en låst area — inget att tina upp.",
  "freeze_build.no_cables": "Det finns inga kablar i projektet ännu.",
  "freeze_build.cancel": "Avbryt",
  "freeze_build.freeze_confirm": "Frys bygget",
  "freeze_build.unfreeze_confirm": "Tina upp",

  // ── Utskriftsritning ───────────────────────────────────
  "blueprint.label": "Crimp Lifetime",
  "blueprint.title": "Utskriftsritning",
  "blueprint.description":
    "En utskriftsklar elritning med kabelreferenser, enkelvägslängder, ledarareor, tilldelade säkringar och kabellista.",
  "blueprint.print_hint": "A3 liggande · Skriv ut eller spara som PDF i webbläsaren",
  "blueprint.close": "Stäng",
  "blueprint.print": "Skriv ut / spara PDF",
  "blueprint.preview.label": "Gratis förhandsvisning",
  "blueprint.preview.title": "Lås upp hela ritningen",
  "blueprint.preview.description":
    "Hela elschemat och de första {count} raderna i varje tabell ingår. Skaffa Crimp Lifetime för att se alla rader och skriva ut eller spara som PDF.",
  "blueprint.preview.cta": "Få hela ritningen",
  "blueprint.preview.footer_hint":
    "Förhandsvisning · hela elschemat och några tabellrader",
  "blueprint.preview.more_rows": "{count} till med Crimp Lifetime",
  "blueprint.sheet.schematic": "Elschema",
  "blueprint.sheet.schedule": "Kabellista",
  "blueprint.sheet.bom": "Materiallista",
  "blueprint.empty": "Lägg till komponenter och kablar för att skapa en ritning.",
  "blueprint.no_cables": "Det finns inga kablar att lista.",
  "blueprint.legend.cable": "Kabelreferens",
  "blueprint.legend.schedule": "Fullständiga kabelspecifikationer finns på blad 2",
  "blueprint.legend.length": "Enkelvägslängd",
  "blueprint.legend.wire": "Ledararea",
  "blueprint.legend.fuse": "Tilldelad säkring",
  "blueprint.verify_badge": "KONTROLLERA FÖRE INSTALLATION",
  "blueprint.cable_schedule": "Kabellista",
  "blueprint.warnings.title": "Punkter att kontrollera",
  "blueprint.warning.invalid_length": "{refs}: längden saknas eller är ogiltig.",
  "blueprint.warning.unresolved_endpoint":
    "{refs}: en ändpunkt eller anslutning kunde inte identifieras.",
  "blueprint.warning.unresolved_sizing":
    "{refs}: lasten kunde inte fastställas; kontrollera ledararean.",
  "blueprint.warning.unprotected_positive":
    "{refs}: plusledaren saknar tilldelad säkring.",
  "blueprint.warning.duplicate_split_protection":
    "{refs}: samma tilldelade säkring visas på båda sidor om en skarv; kontrollera om en eller två fysiska säkringar avses.",
  "blueprint.warning.duplicate_user_reference":
    "{refs}: kabelmärkningen ”{label}” används flera gånger; unika utskriftssuffix har lagts till.",
  "blueprint.notes.title": "Installationsnoteringar",
  "blueprint.notes.length":
    "Längderna är projektets enkelvägslängder; kap- och slackmån ingår inte.",
  "blueprint.notes.fuse":
    "Säkringsvärden visar endast tilldelat skydd. Säkringstyp, hållare och fysisk placering modelleras inte.",
  "blueprint.notes.generated_refs":
    "Genererade kabelreferenser kan ändras när topologin ändras. Ange en egen kabeletikett för ett permanent installations-ID.",
  "blueprint.bom.title": "Materiallista",
  "blueprint.bom.polarity": "Polaritet",
  "blueprint.polarity.positive": "Plus",
  "blueprint.polarity.negative": "Minus / retur",
  "blueprint.polarity.signal": "Signal",
  "blueprint.polarity.unknown": "Okänd",
  "blueprint.bom.cable_summary":
    "{count} kablar · {length} total registrerad längd",
  "blueprint.bom.unresolved":
    "Kablar som inte ingår i inköpsgrupperingen på grund av olöst dimensionering: {count}",
  "blueprint.bom.fuse_ambiguous":
    "Det fysiska antalet är osäkert för {refs}; kontrollera före beställning.",
  "blueprint.document_type": "ELEKTRISK INSTALLATIONSRITNING",
  "blueprint.generated": "Skapad",
  "blueprint.units": "Enheter",
  "blueprint.size_target": "Mål för spänningsfall",
  "blueprint.col.ref": "Ref",
  "blueprint.col.from": "Från",
  "blueprint.col.to": "Till",
  "blueprint.col.length": "Längd",
  "blueprint.col.conductor": "Ledare",
  "blueprint.col.current": "Dimensionerande ström",
  "blueprint.col.fuse": "Tilldelad säkring",
  "blueprint.col.vdrop": "Spänningsfall",
  "blueprint.col.notes": "Noteringar",
  "blueprint.generated_ref": "Genererad referens",
  "blueprint.value.verify": "KONTROLLERA",
  "blueprint.value.manual": "manuell",
  "blueprint.value.continuous": "kontinuerligt",
  "blueprint.value.peak": "topp",
  "blueprint.value.unfused": "Ingen tilldelad säkring",
  "blueprint.value.suggested": "Rekommenderad: {rating} A",

  "professional_feature.title": "Professional-funktion",
  "professional_feature.description": "Materiallista och JSON-export ingår i Professional.",
  "professional_feature.action": "Visa Professional",

  // ── Bill of materials ──────────────────────────────────
  "bom.label": "Verktyg",
  "bom.title": "Materiallista",
  "bom.unverified_cables": "{count} kablar behöver kontrollerad dimensionering. Automatiska dimensioner för dessa kablar utelämnas; manuellt angivna dimensioner visas som uppgifter att kontrollera.",
  "bom.description":
    "Allt i systemet sammanräknat — komponenter, kabellängder per storlek och säkringar.",
  "bom.empty":
    "Dra ut komponenter på arbetsytan och koppla ihop dem för att se en materiallista här.",
  "bom.section.components": "Komponenter",
  "bom.section.cables": "Kablar",
  "bom.section.fuses": "Säkringar",
  "bom.col.item": "Artikel",
  "bom.col.qty": "Antal",
  "bom.col.size": "Storlek",
  "bom.col.length": "Total längd",
  "bom.col.count": "Antal",
  "bom.col.rating": "Märkström",
  "bom.cables.summary": "{count} kablar · {length} totalt",
  "bom.fuses.none": "Inga säkringar på kablarna än.",
  "bom.cables.none": "Inga kablar än.",
  "bom.blueprint_hint":
    "Behöver du även elschemat och kabellistan? Välj Projekt → Utskriftsritning.",
  "bom.copy": "Kopiera",
  "bom.copied": "Kopierat",
  "bom.copy_failed": "Kunde inte kopiera",
  "bom.print": "Skriv ut / spara PDF",
  "bom.close": "Stäng",

  "inspector.settings.section": "Inställningar",
  "inspector.charge_limit.label": "Laddströmsgräns",
  "inspector.charge_limit.hint": "Gräns i simuleringen. Kabeldimensionering använder märkströmmen {amps} A.",
  "inspector.charge_limit.reset": "Använd märkström",
  "inspector.charge_limit.range": "Ange ett värde mellan 0 och {amps} A.",
  "setting.orion.jumperDisabled.label": "Bygel deaktiverad (av när batterivredet står på Combined)",
  "setting.orion.jumperDisabled.help":
    "Markera när Orions fjärr-på/av-ingång är byglad via en hjälpkontakt på Blue Sea 6011 eller liknande dubbelkretsbrytare, så att DC-DC:n stängs av när vredet står i COMBINE. Undertrycker varningen om in-/utgångsslinga som annars utlöses eftersom båda sidor då ser samma batteri.",
};

const DE: Record<TranslationKey, string> = {
  ...DESIGN_PROFILE_DE,
  ...PROFILE_DIAGNOSTICS_DE,
  ...FUSE_BOARD_DE,
  ...ASSISTANT_DE,
  "palette.missing.continue_cta": "Platzhalter verwenden oder Modell anfordern",
  "palette.missing.continue_body": "Zeichne mit einem bearbeitbaren Platzhalter weiter oder fordere das genaue Modell an.",
  "palette.missing.model_label": "Marke und Modell",
  "palette.missing.category": "Komponentenkategorie",
  "palette.missing.all_categories": "Vorgeschlagene Typen",
  "palette.missing.template": "Anschlusstyp des Platzhalters",
  "palette.missing.placeholder_warning": "Die Anschlüsse folgen einer allgemeinen Vorlage. Elektrische Nenndaten sind unbekannt. Trage die Werte ein und prüfe die Anschlüsse anhand des Handbuchs, bevor du dich auf Berechnungen verlässt.",
  "palette.missing.email_label": "E-Mail für Rückfragen (optional)",
  "palette.missing.source_label": "Link zum Handbuch oder Produkt (optional)",
  "palette.missing.request_too": "Dieses genaue Modell auch anfordern",
  "palette.missing.request_sent": "Anfrage erhalten. Wir prüfen das Modell und seine Dokumentation.",
  "palette.missing.placeholder_added": "Platzhalter hinzugefügt. Wähle ihn aus, um Spezifikationen und Anschlüsse zu bearbeiten.",
  "palette.missing.failed": "Deine Anfrage wurde nicht gesendet. Versuche es erneut oder sende sie über dein E-Mail-Programm.",
  "palette.missing.email_fallback": "E-Mail-Entwurf öffnen",
  "palette.missing.add_and_request": "Platzhalter hinzufügen + Anfrage senden",
  "palette.missing.add_placeholder": "Bearbeitbaren Platzhalter hinzufügen",
  "palette.missing.retry_request": "Anfrage erneut senden",
  "palette.missing.send_only": "Nur Anfrage senden",
  "palette.missing.continue_mapping": "Weiterzeichnen",
  "inspector.legacy_microwave.explanation": "Dieses ältere Bauteil schätzt den Mikrowellenverbrauch auf der Batterieseite. Verwende für einen AC-Stromkreis eine AC-Mikrowelle mit L, N und PE.",
  "inspector.legacy_microwave.replace": "{voltage}-V-AC-Mikrowelle verwenden",
  "inspector.legacy_microwave.review": "Vorhandene L/N-Kabel bleiben verbunden. Prüfe die Eingangsleistung am Typenschild und schließe PE wie erforderlich an.",
  "inspector.placeholder.title": "Platzhalter — Spezifikationen ungeprüft",
  "inspector.placeholder.warning": "Dieses angeforderte Modell verwendet allgemeine Anschlüsse. Prüfe das Handbuch und trage die elektrischen Nenndaten und Anschlussdetails ein, bevor du dich auf Berechnungen verlässt.",
  "inspector.placeholder.available": "Ein passendes Modell ist jetzt im Katalog. Bestätige die genaue Variante, bevor du diesen Platzhalter ersetzt.",
  "inspector.placeholder.replace": "{name} verwenden",
  "inspector.placeholder.replace_hint": "Kabel bleiben verbunden. Katalogdaten ersetzen Änderungen am Platzhalter. Prüfe die Anschlüsse nach dem Austausch.",
  "diag.requested-component-unverified.title": "Komponentenplatzhalter müssen geprüft werden",
  "diag.requested-component-unverified.message": "{count} angeforderte Komponenten verwenden noch Platzhalteranschlüsse und ungeprüfte Spezifikationen.",
  "diag.requested-component-unverified.fix": "Prüfe das Handbuch jedes Modells und bearbeite die Spezifikationen und Anschlüsse oder ersetze es durch die genaue Katalogkomponente.",

  "health.reviewed_item": "Geprüft: {title}",
  "diag.monitor-power-unverified.detail_message_missing": "Die Zeichnung weist keine vollständige Gleichstromversorgung zwischen Vbatt+ und dem Minusanschluss von {label} nach. AUX ist optional und ersetzt Vbatt+ nicht.",
  "diag.monitor-power-unverified.detail_message_voltage": "{label} benötigt eine Gleichspannungsversorgung von 6,5–70 V. Die Spannung der angeschlossenen Quelle fehlt oder liegt außerhalb dieses Bereichs; Vbatt+ anhand der Herstellergrenzen prüfen.",
  "diag.monitor-power-unverified.detail_message_protection": "Die Vbatt+-Versorgung von {label} hat einen Pfad ohne angegebenen Schutz von höchstens 1 A. Victron liefert diese Leitung mit einer trägen 1-A-Sicherung.",
  "diag.monitor-power-unverified.detail_fix": "Vbatt+-Leitung, zugehörigen Minusrückleiter und die vom Hersteller vorgesehene abgesicherte Leitung oder den Temperatursensor prüfen. Verbaute Sicherung und Versorgungsspannung kontrollieren.",
  "diag.charge-exceeds-acceptance.detail_message_specified": "Plan oder Katalog geben {accept} A für das Laden an. Der Wert allein belegt nicht, ob es ein Maximum, eine Empfehlung oder ein Planungswert ist. Quellen können bis zu {charge} A liefern; Spezifikationen der genauen Batterie und des BMS prüfen.",
  "diag.charge-exceeds-acceptance.detail_fix_specified": "Klären, was die Stromangabe bedeutet. Tatsächliche Höchstwerte und Herstellerempfehlungen beim Einstellen gleichzeitig arbeitender Ladegeräte und Lichtmaschinenregler einhalten.",
  "diag.charge-exceeds-acceptance.detail_message": "Die angegebenen Ladequellen können zusammen bis zu {charge} A an die Bank mit {ah} Ah liefern, mehr als die geschätzte Grenze von {limit}C (~{accept} A). Prüfen Sie Regelung, gleichzeitigen Betrieb und den maximalen Ladestrom des Herstellers.",
  "diag.charge-exceeds-acceptance.detail_fix_lead": "Prüfen Sie Herstellergrenze und Laderegelung; überhöhter Strom kann Blei-Säure-Batterien überhitzen.",
  "diag.charge-exceeds-acceptance.detail_fix_lfp": "Prüfen Sie die maximalen Ladeströme von Batterie und BMS; die Chemieschätzung bestimmt nicht die Grenze des eingebauten Akkus.",
  "diag.charger-cumulative-vdrop.detail_message_manufacturer": "Der vollständige Stromkreis zwischen Ladegerät und Batterie hat einen konservativen Auslegungs-Spannungsabfall von {drop} V. Das angegebene SmartSolar-Modell begrenzt Batteriekabelverluste auf {allowance} %. Die Berechnung mit Nennspannung umfasst Hin- und Rückleitung sowie Reihenschaltungen der Bank; sie ist keine Messung beim Laden.",
  "diag.charger-cumulative-vdrop.detail_message_target": "Der vollständige Stromkreis zwischen Ladequelle und Batterie hat einen konservativen Auslegungs-Spannungsabfall von {drop} V und überschreitet das gewählte Planungsziel von {allowance} %. Dieses Ziel ist keine Herstellergrenze.",
  "diag.charger-cumulative-vdrop.detail_message_verify": "Für den vollständigen Stromkreis zwischen Ladequelle und Batterie werden {drop} V bei {allowance} % Auslegungsgrenze geschätzt. Strom oder Quellenaufteilung müssen geprüft werden; summierte Leitergrenzen sagen keinen gleichzeitigen tatsächlichen Spannungsabfall voraus.",
  "diag.charger-cumulative-vdrop.detail_fix": "Vollständige Plus- und Rückleitung, Ladestrom, Bankspannung und Kontaktverluste prüfen. Bei Bedarf Leiterquerschnitte erhöhen oder Leitungen kürzen; Spannung an Ladequelle und Batterie bei vollem Ladestrom messen.",

  "diag.source-current-allocation-unverified.detail_message": "Die Stromobergrenzen an {count} Kabeln hängen davon ab, welche Quelle Leistung liefert oder aufnimmt. Die Auslegung kann einem Zweig den gesamten kombinierten Strom zuweisen; dies ist keine Vorhersage der tatsächlichen Aufteilung und kein Nachweis einer Überlast.",
  "diag.source-current-allocation-unverified.detail_fix": "Prüfen Sie Quellenauswahl, parallele Stromaufteilung und Batteriegrenzen. Prüfen Sie jeden Zweigleiter, Anschluss und jede Sicherung anhand der resultierenden Ströme und einer möglichen Startbelastung.",
  "diag.fuse-protection-unverified.detail_message_topology": "Die Quellenverbindungen überschreiten die unterstützte Analysegröße; Quellenpaare und Fehlerströme sind daher ungeklärt. Die {count} angegebenen Schutzstellen, Sicherungsprodukte, maximale DC-Spannung und eine nötige Serienkoordination prüfen.",
  "diag.fuse-protection-unverified.detail_message_topology_placement": "Die Quellenverbindungen überschreiten die unterstützte Analysegröße; Quellenpaare und Fehlerströme sind daher ungeklärt. Die {count} angegebenen Schutzstellen, Sicherungsprodukte, maximale DC-Spannung und eine nötige Serienkoordination prüfen. Gleiche Kabel- und Gerätewerte belegen nicht, ob sie eine Sicherung oder separate Leitungssicherungen beschreiben; die Position ist ungeklärt.",
  "diag.fuse-protection-unverified.detail_message_unknown": "Der Fehlerstrom dieses Quellenpaars ist unbekannt. {count} Schutzstellen sind angegeben, doch die eingebauten Sicherungsprodukte, ihre DC-Spannungs- und Ausschaltwerte sowie eine nötige Serienkoordination sind nicht verifiziert.",
  "diag.fuse-protection-unverified.detail_message_series": "Angaben zu einzelnen Batterien bestimmen nicht den Fehlerstrom der Reihenschaltung. {count} Schutzstellen sind angegeben; Sicherungsprodukte, maximale DC-Spannung und eine nötige Serienkoordination sind nicht verifiziert.",
  "diag.fuse-protection-unverified.detail_message_estimated": "Der Fehlerstrom von {faultA} A enthält KI-Schätzungen. Für {count} angegebene Schutzstellen sind Sicherungsprodukte, maximale DC-Spannung und eine nötige Serienkoordination nicht verifiziert. Dies bestätigt keine Schutzwirkung.",
  "diag.fuse-protection-unverified.detail_message_declared": "Der angegebene Quellenfehlerstrom beträgt {faultA} A; dies ist kein verifiziertes Maximum an jeder Sicherung. Für {count} angegebene Schutzstellen sind Sicherungsprodukte, maximale DC-Spannung und eine nötige Serienkoordination nicht verifiziert.",
  "diag.fuse-protection-unverified.detail_fix": "Fehlerstromdaten der Quelle oder Batteriebank einschließlich Bedingungen und maximaler Betriebsspannung beschaffen. Das genaue Produkt und DC-Ausschaltvermögen jeder eingebauten Sicherung prüfen. Positionen und doppelte Kabel-/Geräteangaben klären. Bei Schutz durch eine vorgeschaltete Sicherung sind Herstellerdaten für die genaue Serienkombination erforderlich.",
  "diag.fuse-protection-unverified.detail_message_unknown_placement": "Der Fehlerstrom dieses Quellenpaars ist unbekannt. {count} Schutzstellen sind angegeben, doch die eingebauten Sicherungsprodukte, ihre DC-Spannungs- und Ausschaltwerte sowie eine nötige Serienkoordination sind nicht verifiziert. Gleiche Kabel- und Gerätewerte belegen nicht, ob sie eine Sicherung oder separate Leitungssicherungen beschreiben; die Position ist ungeklärt.",
  "diag.fuse-protection-unverified.detail_message_series_placement": "Angaben zu einzelnen Batterien bestimmen nicht den Fehlerstrom der Reihenschaltung. {count} Schutzstellen sind angegeben; Sicherungsprodukte, maximale DC-Spannung und eine nötige Serienkoordination sind nicht verifiziert. Gleiche Kabel- und Gerätewerte belegen nicht, ob sie eine Sicherung oder separate Leitungssicherungen beschreiben; die Position ist ungeklärt.",
  "diag.fuse-protection-unverified.detail_message_estimated_placement": "Der Fehlerstrom von {faultA} A enthält KI-Schätzungen. Für {count} angegebene Schutzstellen sind Sicherungsprodukte, maximale DC-Spannung und eine nötige Serienkoordination nicht verifiziert. Dies bestätigt keine Schutzwirkung. Gleiche Kabel- und Gerätewerte belegen nicht, ob sie eine Sicherung oder separate Leitungssicherungen beschreiben; die Position ist ungeklärt.",
  "diag.fuse-protection-unverified.detail_message_declared_placement": "Der angegebene Quellenfehlerstrom beträgt {faultA} A; dies ist kein verifiziertes Maximum an jeder Sicherung. Für {count} angegebene Schutzstellen sind Sicherungsprodukte, maximale DC-Spannung und eine nötige Serienkoordination nicht verifiziert. Gleiche Kabel- und Gerätewerte belegen nicht, ob sie eine Sicherung oder separate Leitungssicherungen beschreiben; die Position ist ungeklärt.",
  "diag.fuse-identity-conflict.detail_message": "Kabel- und Gerätefelder nennen {ratings} A. Daraus lässt sich keine gemeinsame Sicherungsidentität ableiten. Die Zeichnung zeigt nicht, ob dies ein veralteter Doppeleintrag oder eine separate Leitungssicherung ist.",
  "diag.fuse-identity-conflict.detail_fix": "Positionen, Produkte und Nennströme der eingebauten Sicherungen prüfen. Veraltete Kabel- oder Gerätewerte korrigieren oder separate Leitungssicherungen mit eigenen Haltern modellieren.",
  "diag.fuse-too-far-from-battery.detail_message": "Die längste modellierte Leitung von dieser Batteriebank zur ersten Sicherung ist {distanceMm} mm lang. Die Standardgrenze nach ABYC E-11 beträgt 175 mm ab dem Pluspol. Installationsausnahmen für zusätzliche Schutzummantelung oder Einhausung werden nicht modelliert. Einen Kurzschluss vor der Sicherung kann diese nachgeschaltete Sicherung nicht abschalten. Prüfe alle markierten Zuleitungen anhand der tatsächlichen Installation.",
  "diag.fuse-too-far-from-battery.detail_fix": "Prüfe die tatsächlichen ungesicherten Leitungslängen und Schutzpositionen. Montiere passend bemessenen Schutz nahe jeder Batteriezuleitung; verwende je nach Installation eine Polsicherung oder einen separaten Sicherungshalter.",
  "diag.battery-missing-terminal-fuse.detail_message": "Das vom Pluspol abgehende Kabel überschreitet die 175-mm-Grenze nach ABYC E-11 ohne Polsicherung. Ein Kurzschluss auf diesem ungeschützten Abschnitt kann den Leiter überhitzen; der verfügbare Fehlerstrom hängt von Batterie und Installation ab.",
  "diag.battery-missing-terminal-fuse.detail_fix": "Setze im Inspector eine Batteriesicherung (MRBF oder Class T passend zur Bank) oder verlege die vorhandene Sicherung direkt an den Pol, sodass der ungeschützte Leiter unter 175 mm bleibt.",
  "diag.bms-control-unverified.detail_message": "Batterieüberwachung und Abschaltsteuerung für {count} externe BMS-Installationen prüfen. Leistungsverkabelung allein belegt keine funktionierende Schutzkette.",
  "diag.bms-control-unverified.detail_fix": "Jede Batteriedatenleitung, erforderliche VE.Bus/GX-Verbindung, Lade-/Laststeuerung und geeignete Abschaltvorrichtung anhand der Herstelleranleitung prüfen. Ein Signalkabel oder Reserve-Schütz allein bestätigt keine Abschaltung.",
  "diag.pv-isolation-missing.detail_message": "{label} bleibt mit einem Solarfeld verbunden, wenn die geprüften PV-Trenner geöffnet werden. Der Hersteller verlangt eine Möglichkeit zur PV-Trennung.",
  "diag.pv-isolation-missing.detail_fix": "Einen geeigneten DC-PV-Trenner in den Array-Leitungen vorsehen und Umgehungen entfernen. Erforderliche Pole sowie Spannungs- und Strombemessung anhand der Installationsanleitung prüfen.",
  "diag.pv-isolation-unverified.detail_message": "Crimp kann keine geeignete Trennung aller PV-Leiter verifizieren, die {label} versorgen.",
  "diag.pv-isolation-unverified.detail_fix": "Anleitungen von Regler und Trenner prüfen, Leistungsanschlüsse einzeichnen und die Eignung für dieses Array bestätigen. Eine Sicherung oder Fernsteuerung allein bestätigt keine Freischaltung.",
  "diag.inverter-dc-isolation-unverified.detail_message": "Crimp kann keinen DC-Trenner verifizieren, der {label} von allen angeschlossenen DC-Quellen trennt.",
  "diag.inverter-dc-isolation-unverified.detail_fix": "Wechselrichteranleitung prüfen und jede Zuleitung passend freischaltbar machen. Ein gemeinsamer vorgeschalteter Trenner kann genügen; Bedienelemente und Sicherungswerte allein bestätigen das Wartungsverfahren nicht.",
  "diag.missing-ground.detail_message": "Der eingezeichnete Pfad von {terminal} an {label} erreicht keinen modellierten Erdungspunkt. Ein Draht zu einer isolierten Klemme belegt keinen Fehlerstrompfad.",
  "diag.missing-ground.detail_fix": "Den gesamten Schutzleiter- oder Chassisverbindungspfad zum Erdungspunkt der Installation verfolgen und anschließen. Leiterquerschnitt und zulässige Verbindungsstellen anhand der Geräteanleitung prüfen.",
  "diag.ac-onto-dc-rail.detail_message": "Die gezeichnete Verdrahtung verbindet einen AC-Außenleiter oder Neutralleiter direkt oder über verbundene Anschlüsse mit einer DC-Schiene. Das kann Stromschlag- oder Brandgefahr verursachen; DC-Sicherungen belegen keinen Schutz gegen AC-Fehler. Eigene Komponenten werden anhand ihrer angegebenen internen Verbindungen geprüft, die gegebenenfalls an das Gerät angepasst werden müssen.",
  "diag.ac-onto-dc-rail.detail_fix": "Trenne den markierten AC-zu-DC-Pfad in der Zeichnung. Verbinde Außen- und Neutralleiter mit den vorgesehenen AC-Anschlüssen und prüfe eigene Panel-Verbindungen anhand des Handbuchs. Schutzerdung und quellenspezifische Neutralleitererdung müssen den Geräteanweisungen entsprechen; entferne keinen erforderlichen Schutzleiter.",
  "diag.unfused-charger-output.detail_message": "Der {terminal}-Ausgang von {label} kann ohne Sicherung im Pfad einen Batterie+ erreichen. Ein Kurzschluss auf diesem Kabel jagt den vollen Kurzschlussstrom der Batterie hindurch — ABYC E-11 §11.10.1.1 fordert eine Sicherung innerhalb von 7 in (178 mm) der Batterie an jedem mit ihr verbundenen Leiter.",
  "diag.unfused-charger-output.detail_fix": "Setze nahe der Batterie eine Sicherung an diesem Leiter (MRBF oder Class-T für Hochstromquellen).",
  "diag.charger-return-floating.detail_message": "Der Plus-Ausgang von {label} ist verkabelt, aber der Rückleiter ({negLabels}) ist nicht angeschlossen. Ohne Pfad zurück zum Batterie-Minus kann das Ladegerät keinen Strom liefern — die Hausbank wird nie geladen.",
  "diag.charger-return-floating.detail_message_dead_end": "Der Rückleiter von {label} ({negLabels}) ist verkabelt, aber was er erreicht, führt nie zu einem Batterie-Minus. Ohne Pfad zurück zum Batterie-Minus kann das Ladegerät keinen Strom liefern — die Hausbank wird nie geladen.",
  "diag.charger-return-floating.detail_fix": "Führe ein Kabel von {label}s {negLabels} zur Minusschiene, die zum Minus der Zielbatterie zurückführt.",
  "diag.source-return-floating.detail_message": "{label} ist mit dem Pluspol einer Batteriebank verbunden, aber die Zeichnung zeigt keine vollständige Rückleitung zu dieser Bank. Der Ladestrom lässt sich nicht bestätigen.",
  "diag.source-return-floating.detail_fix": "Prüfen Sie den Minus- oder Gehäusemasseanschluss der Quelle und die vollständige Rückleitung zur selben Batteriebank. Interne Verbindungen müssen ausdrücklich angegeben sein; ein Kabel zu einer anderen Klemme schließt den Stromkreis nicht.",
  "diag.manufacturer-breaker-unverified.detail_message": "Der Hersteller schreibt für dieses Modell einen {rating}-A-Schutzschalter vor. Die Zeichnung bestätigt weder den vorgeschriebenen Schutzschalter noch dessen Auslösekennlinie; eine gewöhnliche Sicherung mit gleichem Nennstrom ist nicht automatisch gleichwertig.",
  "diag.manufacturer-breaker-unverified.detail_fix": "Genaues Modell, Hersteller-Schutzschalter, Auslösekennlinie, Platzierung an der Einspeisung und Leitungsschutz prüfen.",
  "diag.manufacturer-breaker-rating.detail_message": "Der gezeichnete Versorgungspfad hat einen Schutz mit {rating} A ohne den vorgeschriebenen Hersteller-Schutzschalter-Nennwert von {required} A.",
  "diag.manufacturer-breaker-rating.detail_fix": "Erforderlichen Zuleitungsschutz beibehalten und den vorgeschriebenen Hersteller-Schutzschalter auf jedem Versorgungspfad prüfen.",
  "diag.manufacturer-supplied-lead-protection.detail_message": "Der Hersteller liefert {label} mit einer {mm2}-mm²-Leitung und gibt für diesen Leiter {ampacity} A an. Die modellierte vorgeschaltete {rating}-A-Sicherung überschreitet diese Leitungsgrenze. Eine stärkere Verlängerung erhöht die Belastbarkeit der mitgelieferten Leitung nicht.",
  "diag.manufacturer-supplied-lead-protection.detail_fix": "Schütze die mitgelieferte Leitung am Einspeisepunkt gemäß der Leitertabelle des Herstellers. Prüfe die verbaute Leitung und Sicherung; dieser Wert ist keine Kontakt- oder Gesamtpanel-Stromangabe.",
  "diag.cranking-vdrop-unverified.detail_message": "Der geschätzte Auslegungs-Spannungsabfall beim Start beträgt {pct} % über die gezeichnete Zuleitung und Rückleitung und liegt über dem konservativen Startziel des Planers.",
  "diag.cranking-vdrop-unverified.detail_fix": "Startstrom, Kabellängen und installierte Querschnitte anhand der Herstelleranleitung des eingebauten Motors prüfen.",
  "health.details": "Details",
  "health.mark_reviewed": "Als geprüft markieren",
  "health.undo_review": "Prüfvermerk zurücknehmen",
  "health.review_note": "Außerhalb von Crimp geprüft? Danach als geprüft markieren.",
  "health.reviewed_note": "Als geprüft markiert, nicht von Crimp verifiziert. Änderungen an Verdrahtung oder Bemessungswerten öffnen die Prüfung erneut.",
  "health.reviewed_count": "Geprüft ({count})",
  "health.no_unreviewed": "Keine ungeprüften Hinweise. Geprüfte Punkte stehen unten.",
  "diag.current-model-unverified.reason.parallel-paths": "{label}: Crimp kann parallele Strompfade nicht auflösen. Last jedes Zweigs prüfen.",
  "diag.current-model-unverified.reason.source-choice-limit": "{label}: Zu viele Quellenkombinationen für Crimp. Kabelgrößen unabhängig prüfen.",
  "diag.current-model-unverified.reason.source-route-limit": "{label}: Dieser Stromkreis überschreitet Crimps Analysegrenze. Kabelgrößen unabhängig prüfen.",
  "diag.current-model-unverified.reason.source-range-limit": "{label}: Crimp kann diese Batterieanordnung nicht auflösen. Bankspannung und Kabelgrößen unabhängig prüfen.",
  "diag.current-model-unverified.reason.shared-port-rating": "{label}: Ausgänge teilen sich einen Stromgrenzwert. Grenze jedes Ausgangs prüfen.",
  "diag.current-model-unverified.reason.converter-current-coupling": "{label}: Crimp kann Kabel mit dieser Eingangsstrombegrenzung noch nicht dimensionieren. Ströme und Kabelquerschnitte unabhängig prüfen.",
  "diag.current-model-unverified.reason.unsupported-component-category": "{label}: Crimp kann den Strom dieser Komponente nicht modellieren. Kategorie und Betriebsstrom prüfen.",
  "diag.current-model-unverified.reason.ambiguous-current-role": "{label}: Eingangs- und Ausgangsströme sind unklar. Anschlussrichtungen und Stromangaben prüfen.",
  "diag.current-model-unverified.reason.unsupported-pv-array": "{label}: Crimp kann diese Solaranordnung nicht auflösen. Moduldaten und Reihen-/Parallelschaltung prüfen.",
  "diag.current-model-unverified.reason.restricted-device-use": "{label}: Diese Verbindung verletzt eine Einsatzbeschränkung. Stromschätzungen sind vorläufig; Verdrahtung vor Verwendung prüfen.",
  "diag.battery-protect-reverse-use.title": "BatteryProtect: Rückstromgefahr",
  "diag.battery-protect-reverse-use.message": "{label}: Diese Verdrahtung kann Strom von OUT nach IN führen. Victron verbietet Rückstrom, da er das Gerät beschädigen kann.",
  "diag.battery-protect-reverse-use.fix": "Die vorgesehene Quelle an IN und den versorgten Verbraucher oder Akku an OUT anschließen. Lade- und Entladepfade trennen. Der angezeigte Strom ist eine konservative Schätzung, kein Nachweis einer sicheren Rückstromsperre.",
  "diag.battery-protect-model-unverified.title": "BatteryProtect-Modell prüfen",
  "diag.battery-protect-model-unverified.message": "{label}: Geänderte elektrische Daten entsprechen nicht mehr dem geprüften BatteryProtect-Modell. Richtung und Stromergebnisse sind ungeprüft.",
  "diag.battery-protect-model-unverified.fix": "Katalogmodell wiederherstellen oder vollständige Verdrahtung und Betriebsgrenzen anhand des Handbuchs prüfen.",
  "diag.combiner-manual-state.title": "Kontaktzustand des Batterierelais wählen",
  "diag.combiner-manual-state.message": "{label}: Die Simulation nutzt Ihre Auswahl Getrennt oder Verbunden und startet getrennt. Automatische Spannungserkennung, Verzögerungen und externe Steuerungen werden nicht simuliert.",
  "diag.combiner-manual-state.fix": "Beide Kontaktzustände im Simulationsmodus vergleichen. Die Kabelauslegung berücksichtigt weiterhin den möglichen Strom verbundener Banken.",
  "diag.alpha-source-model.title": "Alpha75: Quellenannahmen",
  "diag.alpha-source-model.message": "{label}: Solar hat Vorrang innerhalb der gemeinsamen 75-A-Ausgangsgrenze. Danach teilt die Simulation AC vor Fahrzeug-DC zu; tatsächliche AC/DC-Aufteilung und DC-Spannungs-/Zündschwellen werden nicht modelliert.",
  "diag.alpha-source-model.fix": "Ein setzt einen freigegebenen Fahrzeugeingang voraus. Auslöseeinstellungen separat prüfen; Starterbatterieladung und Starthilfe werden nicht simuliert. Beide GROUND-Punkte stehen für denselben gemeinsamen Anschluss und erhalten ältere Zeichnungen.",
  "diag.current-model-unverified.reason.one-way-device": "{label}: Laden durch diesen Batterietrenner wird noch nicht modelliert. Ströme und Kabelgrößen unabhängig prüfen.",
  "diag.current-model-unverified.reason.unmodeled-branch": "{label}: Für diesen Zweig fehlt ein vollständiges Lastmodell. Versorgung, Rückleitung und Betriebsstromangaben prüfen.",

  "diag.battery-isolator-model-unverified.title": "Berechnungen für den Batterietrenner müssen geprüft werden",
  "diag.battery-isolator-model-unverified.message": "Der Batterietrenner hält seine Batterieausgänge getrennt. Das Laden über dieses nur in einer Richtung leitende Gerät wird noch nicht modelliert. Betroffene Kabelströme und Querschnitte sind vorläufig; sein Ladebeitrag fehlt in der Energiebilanz.",
  "diag.battery-isolator-model-unverified.fix": "Prüfen Sie Eingangs- und Ausgangskreise, Kabelquerschnitte, Schutz und Ladeleistung anhand der Anweisungen der Hersteller von Stromquelle und Batterietrenner.",
  "diag.battery-short-circuit.title": "Batteriepole sind kurzgeschlossen",
  "diag.battery-short-circuit.message": "Die gezeichneten Leiter verbinden Plus und Minus von {count} Batteriezellen oder Batterien. Auch eine gemeinsam genutzte Monitorklemme ist eine elektrische Verbindung; eine Sicherung macht diese Verdrahtung nicht sicher.",
  "diag.battery-short-circuit.fix": "Entfernen Sie den Kurzschlusspfad vor dem Einschalten. Verbinden Sie jeden Spannungsabgriff mit einem eigenen Messeingang gemäß Herstellerangaben; verbinden Sie unterschiedliche Serienpotenziale nicht an derselben Klemme.",
  "diag.load-current-unverified.title": "Laststrom muss geprüft werden",
  "diag.load-current-unverified.message": "{count} angeschlossene Gleichstromverbraucher haben keine Betriebsstromangabe. Ihr Bedarf fehlt in Kabel- und Sicherungsberechnungen sowie Leistungs- und Laufzeitschätzungen; diese sind daher unvollständig.",
  "diag.load-current-unverified.fix": "Tragen Sie den Betriebsstrom des Herstellers ein und berechnen Sie erneut. Bei Verbrauchern mit Konstantleistungsmodell kann auch eine geprüfte Leistung verwendet werden. Klemmen- und Sicherungsnennströme bestimmen nicht den Betriebsstrom.",
  "cable.source_allocation_sizing": "Diese Größen verwenden Obergrenzen über mögliche Quellenpfade. Die tatsächliche Stromaufteilung steht nicht fest; prüfen Sie Aufteilung und Schutz, bevor Sie einen berechneten Zweigstrom zugrunde legen.",
  "cable.current_bound": "Stromobergrenze der Quellenpfade",
  "cable.current_estimate": "Vorläufige Stromschätzung",
  "cable.starting_current": "Kurzzeitiger Startstrom",
  "cable.sizing_starting_duty": "Ausgelegt für Dauerlast und kurzzeitigen Startstrom bei {pct}% Spannungsabfall.",
  "diag.source-current-allocation-unverified.title": "Stromaufteilung prüfen",
  "diag.source-current-allocation-unverified.message": "Die Dimensionierung von {count} Kabeln nutzt konservative Annahmen. Die tatsächliche Stromaufteilung zwischen den Quellen ist unbekannt.",
  "diag.source-current-allocation-unverified.fix": "Quelleneinstellungen sowie Kabel-, Anschluss- und Sicherungswerte jedes Zweigs prüfen.",
  "cable.provisional_sizing": "Strompfade oder Komponentenwerte sind ungeklärt. Diese Kabel- und Sicherungsgrößen sind vorläufig, bis die Zeichnung überprüft wurde.",
  "diag.fuse-protection-unverified.message_topology": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",
  "diag.fuse-protection-unverified.message_topology_placement": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",

  "diag.current-model-unverified.title": "Kabeldimensionierung prüfen",
  "diag.current-model-unverified.message": "Crimp kann den Strom für {count} verbundene Komponenten nicht zuverlässig berechnen. Die markierten Kabelgrößen sind vorläufig.",
  "diag.current-model-unverified.fix": "Details zeigt die betroffenen Komponenten und die nötigen Prüfungen.",
  "diag.fuse-protection-unverified.title": "Sicherungen prüfen: {label}",
  "diag.fuse-protection-unverified.message_unknown": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",
  "diag.fuse-protection-unverified.message_series": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",
  "diag.fuse-protection-unverified.message_estimated": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",
  "diag.fuse-protection-unverified.message_declared": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",
  "diag.fuse-protection-unverified.fix": "Spannungsfestigkeit und Abschaltvermögen anhand der Batterie- und Sicherungsdatenblätter prüfen.",
  "diag.fuse-protection-unverified.message_unknown_placement": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",
  "diag.fuse-protection-unverified.message_series_placement": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",
  "diag.fuse-protection-unverified.message_estimated_placement": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",
  "diag.fuse-protection-unverified.message_declared_placement": "Crimp kann die eingebauten Sicherungen anhand des Plans nicht verifizieren.",
  "diag.fuse-identity-conflict.title": "Widersprüchliche Sicherungswerte: {label}",
  "diag.fuse-identity-conflict.message": "Kabel und Komponente geben unterschiedliche Werte an: {ratings} A.",
  "diag.fuse-identity-conflict.fix": "Doppelte Angaben korrigieren oder getrennte Sicherungen in eigenen Haltern einzeichnen.",

  // ── Topbar ─────────────────────────────────────────────
  "topbar.beta": "Beta",
  "topbar.undo": "Rückgängig",
  "topbar.redo": "Wiederherstellen",
  "topbar.undo_title": "Rückgängig (⌘Z)",
  "topbar.redo_title": "Wiederherstellen (⇧⌘Z)",
  "topbar.edit": "Bearbeiten",
  "topbar.view": "Ansicht",
  "topbar.simulate": "Simulieren",
  "topbar.simulate_title":
    "System live betreiben — Komponentenzustände umschalten und Kabelströme live beobachten",
  "topbar.mode_aria": "App-Modus",
  "topbar.share": "Teilen",
  "topbar.share_title": "Dieses Projekt teilen",
  "topbar.fuses_show": "Sicherungsmarker an Kabeln anzeigen",
  "topbar.fuses_hide": "Sicherungsmarker an Kabeln ausblenden",
  "topbar.fuses_aria": "Sicherungsmarker umschalten",
  "topbar.cable_size_show": "Kabelquerschnitt an Kabeln anzeigen",
  "topbar.cable_size_hide": "Kabelquerschnitt an Kabeln ausblenden",
  "topbar.cable_size_aria": "Kabelquerschnitt umschalten",
  "topbar.cable_length_show": "Kabellänge an Kabeln anzeigen",
  "topbar.cable_length_hide": "Kabellänge an Kabeln ausblenden",
  "topbar.cable_length_aria": "Kabellänge umschalten",
  "topbar.annotations_aria": "Anmerkungen auf der Arbeitsfläche",

  // ── Project menu ───────────────────────────────────────
  "project_menu.title": "Projektmenü",
  "project_menu.name": "Projektname",
  "project_menu.rename": "Projekt umbenennen",
  "project_menu.placeholder": "Unbenanntes System",
  "project_menu.new": "Neues Projekt",
  "project_menu.open": "Projekt öffnen…",
  "project_menu.open_external": "Extern öffnen…",
  "project_menu.save_now": "Jetzt speichern",
  "project_menu.save": "Speichern",
  "project_menu.save_dots": "Speichern…",
  "project_menu.auto_save": "Automatisch speichern",
  "project_menu.save_a_copy": "Kopie speichern…",
  "project_menu.duplicate": "Duplizieren",
  "project_menu.import": "Aus Datei öffnen…",
  "project_menu.export": "Als .12v.json herunterladen",
  "project_menu.export_pdf": "Als PDF exportieren…",
  "project_menu.export_image": "Schaltplan als PNG exportieren",
  "pdf.title_suffix": "Geräteliste",
  "pdf.untitled": "Unbenanntes System",
  "pdf.totals.devices": "Geräte",
  "pdf.totals.cables": "Kabel",
  "pdf.col.name": "Gerät",
  "pdf.col.part": "Hersteller / Modell",
  "pdf.col.specs": "Spezifikationen",
  "pdf.col.zone": "Zone",
  "pdf.spec.draw": "Verbrauch {a} A",
  "pdf.spec.out": "Aus {a} A",
  "pdf.spec.surge": "Spitze {a} A",
  "pdf.empty": "Noch keine Geräte auf der Leinwand.",
  "pdf.attribution": "Erstellt mit {app}. Interaktives Schaltbild öffnen:",
  "project_menu.blueprint": "Druckplan…",
  "project_menu.role_badge_title":
    "Du siehst dieses Projekt über einen {role}-Freigabelink.",
  "project_menu.role.viewer": "Betrachter",
  "project_menu.role.commenter": "Kommentator",
  "project_menu.role.editor": "Bearbeiter",
  "project_menu.viewer_cta": "Nur Lesezugriff",
  "project_menu.viewer_cta_action": "Kopie bearbeiten",
  "project_menu.viewer_cta_title": "Dies ist ein schreibgeschützter Freigabelink. Klicke, um eine eigene bearbeitbare Kopie zu speichern.",
  "project_menu.save_state.saving": "Speichert",
  "project_menu.save_state.saved": "Gespeichert",
  "project_menu.save_state.dirty": "Ungespeichert",
  "project_menu.save_state.error": "Speichern fehlgeschlagen",
  "project_menu.save_state.local": "Auf diesem Gerät gespeichert",
  "project_menu.save_state.cloud": "In der Cloud gespeichert",
  "project_menu.save_state.syncing": "Wird synchronisiert",
  "project_menu.save_state.conflict": "Speicherkonflikt",
  "project_menu.local_detail":
    "Dieses Projekt existiert nur in diesem Browser. Es ist auf anderen Geräten nicht verfügbar und kann beim Löschen der Websitedaten verloren gehen.",
  "project_menu.cloud_detail": "Dieses Projekt ist lokal und in der Cloud gespeichert.",
  "project_menu.cloud_sync": "Automatische Cloud-Synchronisierung",

  // ── Paywall ───────────────────────────────────────────
  "cloud_nudge.label": "Cloud-Backup",
  "cloud_nudge.badge": "Kostenlos mit Konto",
  "cloud_nudge.title": "Dieses Projekt in der Cloud sichern",
  "cloud_nudge.body":
    "Es ist bereits auf diesem Gerät gespeichert. Melde dich kostenlos an, um deine Projekte zu synchronisieren und auf jedem Gerät zu bearbeiten.",
  "cloud_nudge.dismiss": "Nicht jetzt",
  "cloud_nudge.cta": "In der Cloud sichern",
  "cloud_nudge.close": "Hinweis zum Cloud-Backup schließen",
  "paywall.label": "Crimp Lifetime",
  "paywall.title": "Systeme jeder Größe, Exporte und den Plan freischalten",
  "paywall.title.export": "Dieses Projekt exportieren",
  "paywall.title.blueprint": "Einen Installationsplan drucken",
  "paywall.title.equipment_limit": "Mit mehr als {limit} Komponenten weiterbauen",
  "paywall.title.ai_credits": "KI-Credits gehören zu Crimp Lifetime",
  "paywall.title.ai_credits.pass": "KI-Credits gehören zum Pass",
  "paywall.title.project_limit": "Mehr Projekte in der Cloud speichern",
  "paywall.title.expired": "Dein Pass ist abgelaufen",
  "paywall.description":
    "Cloud-Speicherung und Teilen bleiben kostenlos. Mit Crimp Lifetime kannst du {action} — einmal zahlen, deins für die Lebensdauer des Produkts.",
  "paywall.description.pass":
    "Cloud-Speicherung und Teilen bleiben kostenlos. Mit dem Crimp Pass kannst du 12 Monate lang {action}.",
  "paywall.description.equipment":
    "Kostenlose Projekte unterstützen bis zu {limit} Gerätekomponenten. Crimp Lifetime hebt das Limit in allen Projekten auf, für die Lebensdauer des Produkts.",
  "paywall.description.equipment.pass":
    "Kostenlose Projekte unterstützen bis zu {limit} Gerätekomponenten. Der Crimp Pass hebt das Limit in allen Projekten für 12 Monate auf.",
  "paywall.description.project_limit":
    "Dieses Projekt hat keinen Cloud-Platz bekommen, bleibt aber auf diesem Gerät gespeichert. Crimp Lifetime schaltet Systeme jeder Größe, Exporte und den Plan frei, mit bis zu {limit} Cloud-Projekten.",
  "paywall.description.expired":
    "Deine Projekte bleiben lesbar, und jede exportierte Datei gehört weiter dir. Verlängere, um weitere 12 Monate Systeme jeder Größe zu bauen, zu exportieren und den Plan zu drucken.",
  "paywall.trigger.export": "exportieren",
  "paywall.trigger.blueprint": "den Elektroplan drucken",
  "paywall.trigger.equipment": "weitere Geräte hinzufügen",
  "paywall.trigger.ai_credits": "den Assistenten weiter nutzen",
  "paywall.trigger.project_limit": "mehr Projekte in der Cloud behalten",
  "paywall.trigger.pro": "alles freischalten",
  "paywall.not_now": "Nicht jetzt",
  "paywall.buy": "Crimp Lifetime holen — {price}",
  "paywall.buy.pass": "Pass holen — {price}",
  "paywall.buy.pack": "Einmalkauf — {price}",
  "paywall.renew": "Verlängern · {price}",
  "paywall.sign_in": "Zum Fortfahren anmelden",
  "paywall.auth_reason.equipment":
    "Melde dich an oder erstelle ein Konto, damit dein Kauf zu deinem Konto gehört, und baue weiter.",
  "paywall.local_title": "Cloud-Speicherung bleibt kostenlos",
  "paywall.local_body":
    "Du kannst alle Projekte kostenlos weiter bearbeiten und synchronisieren. Crimp Lifetime ist nur für mehr als {limit} Gerätekomponenten, Exporte und den vollständigen Installationsplan nötig.",
  "paywall.local_body.pass":
    "Du kannst alle Projekte kostenlos weiter bearbeiten und synchronisieren. Der Pass ist nur für mehr als {limit} Gerätekomponenten, Exporte und den vollständigen Installationsplan nötig.",
  "paywall.local_title.equipment": "Deine aktuelle Zeichnung ist unverändert",
  "paywall.local_body.equipment":
    "Kabel, Sicherungshalter, Sicherungsblöcke und Sammelschienen sind immer kostenlos und zählen nie zum Limit von {limit} Gerätekomponenten.",
  "paywall.local_title.project_limit": "Dein Projekt ist auf diesem Gerät sicher",
  "paywall.local_body.project_limit":
    "Es ist hier gespeichert und wird weiter automatisch gesichert. Nichts ist verloren; es hat nur die Cloud nicht erreicht.",
  "paywall.included": "Ebenfalls in Crimp Lifetime enthalten",
  "paywall.included.pass": "Ebenfalls im Pass enthalten",
  "paywall.feature.export": "PDF- und PNG-Exporte",
  "paywall.feature.blueprint": "Druckbarer Installationsplan und Kabelplan",
  "paywall.feature.equipment": "Systeme jeder Größe in jedem Projekt",
  "paywall.feature.credits": "{credits} KI-Credits für den Assistenten",
  "paywall.feature.cloud": "Bis zu {limit} Projekte in der Cloud",
  "paywall.intro_note": "Einmal zahlen · deins für die Lebensdauer des Produkts · 30 Tage Geld-zurück-Garantie.",
  "paywall.intro_note.pass": "Eine Zahlung · 12 Monate · keine automatische Verlängerung · 30 Tage Geld-zurück-Garantie.",
  "paywall.tax_note":
    "Paddle berechnet und verwaltet die anfallende Mehrwert- oder Umsatzsteuer. Der Endbetrag wird vor der Zahlung angezeigt.",
  "paywall.legal_label": "Kaufbedingungen und Richtlinien",
  "paywall.legal_intro": "Vor dem Kauf lesen:",
  "paywall.terms": "Nutzungsbedingungen",
  "paywall.refunds": "Rückerstattungen",
  "paywall.privacy": "Datenschutz",
  "paywall.unavailable":
    "Käufe sind vorübergehend nicht verfügbar. Dein lokales Projekt bleibt auf diesem Gerät erhalten.",
  "project_limit.label": "Cloud-Projekte",
  "project_limit.title": "Du hast {limit} Cloud-Projekte",
  "project_limit.saved_locally":
    "Dieses Projekt ist auf diesem Gerät gespeichert und wird hier weiter automatisch gesichert. Nichts ist verloren; es hat nur keinen Cloud-Platz bekommen.",
  "project_limit.free_slot": "Wenn du ein Cloud-Projekt löschst, das du nicht mehr brauchst, wird ein Platz frei.",
  "project_limit.pro":
    "Crimp Professional umfasst unbegrenzte Cloud-Projekte und Werkzeuge für die Arbeit mit Kunden.",
  "project_limit.request": "Professional ansehen",
  "project_limit.thanks": "Dein Projekt ist gespeichert.",
  "project_limit.thanks_body": "Dein Projekt bleibt auf diesem Gerät. Es wurde nichts berechnet.",
  "project_limit.not_now": "Nicht jetzt",
  "project_limit.done": "Fertig",
  "conflict.label": "Speicherkonflikt",
  "conflict.title": "Dieses Projekt wurde an anderer Stelle geändert",
  "conflict.body": "Wähle aus, welche Arbeit erhalten bleibt. Crimp überschreibt keine Version unbemerkt.",
  "conflict.detail": "Lade die neuere gespeicherte Version oder bewahre den aktuellen Stand als separates lokales Projekt auf.",
  "conflict.reload": "Neuere Version laden",
  "conflict.keep_copy": "Meine Version als Kopie behalten",
  "conflict.copy_suffix": "(Konfliktkopie)",

  // ── Account menu ───────────────────────────────────────
  "account.sign_in": "Anmelden",
  "account.sign_in_title": "Anmelden",
  "account.sign_out": "Abmelden",
  "labs.title": "Feature-Flags",
  "labs.label": "Admin-Werkzeuge",
  "labs.description": "Probiere Funktionen in diesem Browser aus. Deine Auswahl wird hier gespeichert und gilt nur, solange ein berechtigtes Konto angemeldet ist.",
  "labs.access_hint": "Für Administratoren und das Konto des Eigentümers verfügbar. Andere Nutzer behalten die Standardeinstellungen.",
  "account.aria": "Konto",
  "account.signed_in_as": "Angemeldet als",
  "account.ai_usage": "{used} von {limit} kostenlosen KI-Erstellungen verwendet",
  "account.language": "Sprache",
  "account.units": "Einheiten",
  "preferences.title": "Einstellungen",
  "view.title": "Ansicht",
  "settings.title": "Einstellungen",
  "preferences.show": "Anzeigen",
  "preferences.show_fuses": "Sicherungsmarker",
  "preferences.show_cable_size": "Kabelquerschnitt",
  "preferences.show_cable_length": "Kabellänge",
  "preferences.show_cable_vdrop": "Spannungsabfall",
  "preferences.show_cable_label": "Kabel-Etiketten",
  "preferences.cable_hover_details": "Kabeldetails beim Überfahren",
  "preferences.show_product_images": "Produktbilder",
  "preferences.auto_size_target": "Auto-Auslegungsziel",
  "preferences.auto_size_target.3pct": "≤3 % Abfall",
  "preferences.auto_size_target.3pct.hint": "ABYC E-11",
  "preferences.auto_size_target.5pct": "≤5 % Abfall",
  "preferences.auto_size_target.5pct.hint": "NEC 210.19",
  "preferences.auto_size_target.help":
    "Automatisch dimensionierte Leitungen werden so vergrößert, dass der gesamte Spannungsabfall von Batterie zu Verbraucher unter diesem Wert bleibt. 3 % entspricht ABYC E-11; 5 % erlaubt dünnere Leitungen mit höherem Abfall.",
  "units.metric": "Metrisch",
  "units.imperial": "Imperial",

  // ── Confirmations / prompts ────────────────────────────
  "topbar.confirm_discard": "Aktuelles System verwerfen und neu beginnen?",
  "topbar.save_copy_prompt": "Kopie speichern als:",
  "topbar.copy_suffix": "(Kopie)",
  "topbar.untitled": "Unbenannt",
  "topbar.auto_saved": "Automatisch gespeichert",
  "topbar.save_failed": "Speichern fehlgeschlagen: {message}",
  "topbar.duplicate_failed": "Duplizieren fehlgeschlagen: {message}",
  "topbar.open_failed": "Öffnen fehlgeschlagen: {message}",
  "topbar.unknown_error": "unbekannter Fehler",

  // ── Auth-required reasons ──────────────────────────────
  "auth_reason.save": "Melde dich an, um dein Projekt in der Cloud zu speichern.",
  "auth_reason.save_as": "Melde dich an, um eine Kopie zu speichern.",

  // ── Geteilter Link (Konversionsfläche) ─────────────────
  "share_view.headline": "Jemand hat dieses Schaltbild mit dir geteilt.",
  "share_view.what_is_crimp":
    "Crimp ist ein 12V-Planer — er dimensioniert jedes Kabel und jede Sicherung aus dem Schaltbild.",
  "share_view.save_copy": "Kopie speichern",
  "share_view.save_copy_title": "Eine bearbeitbare Kopie in deinem Konto speichern",
  "share_view.save_copy_confirm": "Kopie speichern",
  "share_view.start_build": "Eigenes Projekt starten",
  "share_view.start_build_title": "Eine leere Leinwand öffnen und dein eigenes System planen",
  "auth_reason.share": "Melde dich an, um dein Projekt zu teilen.",

  // ── Import errors (formatImportError) ──────────────────
  "import_error.future_version":
    "Diese Datei wurde mit einer neueren Version von Crimp erstellt (Datei v{fileVersion}, App unterstützt v{supportedVersion}). Aktualisiere die App, um sie zu öffnen.",
  "import_error.not_system_shape": "Diese Datei ist kein gültiges Crimp-System.",
  "import_error.parse": "Datei konnte nicht gelesen werden — kein gültiges JSON.",
  "import_error.generic": "Datei konnte nicht geöffnet werden: {message}",
  "import_error.cloud_future_version_reload":
    "Dieses Projekt wurde mit einer neueren Version von Crimp gespeichert. Neu laden, um zu aktualisieren?",

  // ── Palette ────────────────────────────────────────────
  "palette.search": "Komponenten suchen",
  "palette.search_loading": "Komponenten werden gesucht…",
  "palette.search_failed": "Die Suche konnte nicht geladen werden.",
  "palette.search_retry": "Erneut versuchen",
  "palette.clear_search": "Suche zurücksetzen",
  "palette.layout": "Layout",
  "palette.zone": "Zone",
  "palette.zone_sub": "Komponenten nach physischem Bereich gruppieren",
  "palette.custom": "Eigene Komponente",
  "palette.custom_sub": "Von Grund auf neu erstellen",
  "palette.describe": "Bauteil beschreiben",
  "palette.describe_sub": "Nennen Sie es, wir tragen die Daten ein",
  "palette.show_more": "{count} weitere anzeigen",
  "palette.show_less": "Weniger anzeigen",
  "palette.missing.no_match_for": "Keine Treffer für",
  "palette.missing.try_generic":
    "Oder zieh eine generische Komponente rein und passe ihre Daten im Inspector an:",
  "palette.missing.cta_specific": "Anfordern",
  "palette.missing.cta_any": "Komponente anfordern",
  "palette.missing.body": "Marke und Modell helfen uns, ein Datenblatt zu finden.",
  "palette.missing.placeholder": "z. B. Mercury 9.9 Außenborder",
  "palette.missing.send": "Anfrage senden",
  "palette.missing.close": "Schließen",
  "palette.missing.sending": "Senden",
  "palette.missing.thanks": "Danke. Wir suchen ein Datenblatt heraus.",
  "palette.cat.battery": "Batterien",
  "palette.cat.charger": "Ladegeräte & DC-DC",
  "palette.cat.solar": "Solar",
  "palette.cat.source": "Quellen",
  "palette.cat.engine": "Motoren",
  "palette.cat.distribution": "Verteilung & Sicherungen",
  "palette.cat.load": "Verbraucher",
  "palette.cat.monitor": "Überwachung",
  "palette.library": "Meine Bibliothek",
  "palette.library_empty":
    "Speichere hier eine eigene Komponente, um sie in mehreren Schemas wiederzuverwenden.",
  "palette.library_remove": "Aus Bibliothek entfernen",
  "palette.limit.label": "Kostenlose Geräte",
  "palette.limit.count": "{count} von {limit}",
  "palette.limit.free_items": "Kabel, Sicherungen und Sammelschienen zählen nie.",

  // ── Bauteil-beschreiben-Dialog ─────────────────────────
  "ai_component.title": "Bauteil beschreiben",
  "ai_component.options": "Bauteilmenü",
  "ai_component.prompt_label": "Bauteilname oder Beschreibung",
  "ai_component.subtitle":
    "Nennen Sie ein reales Bauteil, wir tragen die Typenschilddaten ein. Querschnitte und Sicherungswerte berechnen wir selbst.",
  "ai_component.placeholder": "Victron Orion-Tr Smart 12/12-30",
  "ai_component.examples": "Zum Beispiel: {examples}",
  "ai_component.generate": "Daten eintragen",
  "ai_component.generating": "Typenschild wird gelesen …",
  "ai_component.cancel": "Abbrechen",
  "ai_component.restart": "Weiteres beschreiben",
  "ai_component.add": "Auf die Zeichnung",
  "ai_component.chemistry": "Zelltyp",
  "ai_component.duty_hours": "Stunden / Tag",
  "ai_component.always_on": "Dauerbetrieb",
  "ai_component.specs": "Technische Daten",
  "ai_component.terminals": "Anschlüsse",
  "ai_component.terminal_maxamp_note":
    "Die Belastbarkeit der Anschlüsse folgt diesen Daten. Im Inspektor anpassbar.",
  "ai_component.no_specs":
    "Keine Daten erhalten. Im Inspektor ergänzen.",
  "ai_component.estimated": "geschätzt",
  "ai_component.estimated_hint":
    "Gepunktete Werte sind Schätzungen. Mit dem Datenblatt abgleichen.",
  "ai_component.assumptions": "Annahmen",
  "ai_component.error.implausible":
    "Die Daten widersprachen sich wiederholt, deshalb wurde nichts übernommen. Hersteller und Modell zu nennen hilft meist.",
  "ai_component.error.unavailable":
    "Der Assistent war nicht erreichbar. Bitte gleich noch einmal versuchen.",

  // ── Describe-your-system modal ─────────────────────────
  "ai_system.title": "Beschreibe deine Anlage",
  "agent_review.home": "Crimp-Startseite",
  "agent_review.view_only": "Nur ansehen",
  "agent_review.edit_copy": "Kopie bearbeiten",
  "agent_review.schematic": "Schaltplanentwurf",
  "agent_review.canvas_title": "{name} — schreibgeschützter Crimp-Schaltplan",
  "agent_review.copy_hint": "Beim Ansehen wird kein Projekt gespeichert. Wähle Kopie bearbeiten, um Bauteile oder Kabel zu ändern.",
  "agent_review.limits": "Planungsprüfungen, keine Installationsfreigabe. Bestätige Geräte, Kabelwege und Herstellervorgaben.",
  "agent_review.finding_details": "Alle Hinweise anzeigen ({count})",
  "agent_review.assumptions": "Annahmen ({count})",
  "agent_review.no_assumptions": "Für diesen Entwurf wurden keine Annahmen angegeben. Bestätige fehlende Installationsdaten, bevor du dich auf den Plan verlässt.",
  "agent_draft.title": "Entwurf deines Agenten prüfen",
  "agent_draft.subtitle": "Prüfe Bauteile, Längen und Hinweise und öffne dann ein neues lokales Projekt. Dein bestehendes Projekt bleibt erhalten.",
  "agent_draft.import": "Neues lokales Projekt erstellen",
  "agent_draft.importing": "Wird gespeichert und geöffnet…",
  "agent_draft.invalid": "Dieser Link ist ungültig, inkompatibel oder überschreitet die Beta-Grenzen. Bitte deinen Agenten um einen neuen Link mit höchstens 10 Gerätekomponenten.",
  "agent_draft.error": "Das aktuelle Projekt konnte nicht sicher gespeichert werden oder wurde während des Imports geändert. Löse mögliche Speicherkonflikte und versuche es erneut. Dein aktuelles Projekt wurde nicht ersetzt.",
  "agent_draft.counts": "{equipment} Gerätekomponenten · {cables} Kabel",
  "agent_draft.parts": "Zu bestätigende Bauteile",
  "agent_draft.runs": "Kabelwege — einfache Längen",
  "agent_draft.estimated": "geschätzt — bestätigen",
  "agent_draft.findings": "Aktuelle Prüfungen",
  "agent_draft.finding_counts": "{critical} kritisch · {warning} Warnungen · {info} Informationen",
  "agent_draft.provisional": "{count} Kabel haben eine vorläufige Dimensionierung.",
  "agent_draft.no_findings": "Diese Prüfungen haben keine Hinweise ergeben. Das bestätigt weder die Sicherheit noch die Normkonformität der Installation.",
  "agent_draft.limits": "Planungsentwurf: tatsächliche Geräte, Kabelwege und Herstellervorgaben bestätigen. Vorgeschlagene Kabel- und Sicherungsgrößen sind keine Installationsfreigabe. Annahmen bleiben in den Bauteilnotizen des Projekts erhalten.",
  "ai_system.beta_badge": "Beta",
  "ai_system.subtitle":
    "Sag uns, was du hast, und wir zeichnen es. Die Bauteile kommen aus dem Katalog; Querschnitt, Sicherung und Spannungsfall werden hier berechnet, nicht geraten.",
  "ai_system.placeholder":
    "Ein Sprinter mit zwei 100-Ah-Lithiumbatterien, 400 W Solar und einem 2000-W-Wechselrichter",
  "ai_system.generate": "Anlage zeichnen",
  "ai_system.generating": "Anlage wird gezeichnet …",
  "ai_system.busy_note":
    "Erst die Bauteile, dann die Verkabelung. Das dauert meist etwa eine halbe Minute — bei einer großen Anlage länger.",
  "ai_system.busy_long":
    "Wir zeichnen noch — eine große Anlage kann über eine Minute dauern. Einen Moment noch.",
  "ai_system.cancel": "Abbrechen",
  "ai_system.restart": "Noch eine beschreiben",
  "ai_system.add": "Auf der Arbeitsfläche öffnen",
  "ai_system.engine_note":
    "Querschnitte und Sicherungswerte schreibt der Assistent nie — sie werden aus der fertigen Zeichnung nach ABYC E-11 berechnet.",
  "ai_system.assumed_runs": "Angenommene Leitungslängen",
  "ai_system.runs_decisive": "Längen, die den Querschnitt bestimmen",
  "ai_system.runs_minor":
    "{count} kürzere Leitungswege wurden ebenfalls angenommen. Keiner davon ändert einen Querschnitt.",
  "ai_system.runs_none_decisive":
    "{count} Leitungslängen wurden angenommen. Bei diesen Längen ändert keine davon einen Querschnitt.",
  "ai_system.run_and_return": "+ Rückleiter",
  "ai_system.show_all": "Alle anzeigen",
  "ai_system.show_less": "Weniger anzeigen",
  "ai_system.picked_parts": "{count} Bauteile, die wir für dich gewählt haben",
  "ai_system.picked_parts_hint":
    "Klicke ein Bauteil auf der Arbeitsfläche an, um es zu tauschen — die Kabel bleiben daran.",
  "ai_system.corrections": "Was wir geändert haben",
  "ai_system.stat_cables": "Kabel",
  "ai_system.units": "Stück",
  "ai_system.count_collapsed": "1 ×, nicht {requested}",
  "ai_system.count_collapsed_note":
    "Eine Sammelschiene, ein Sicherungsblock oder ein Trennschalter ist ein einzelnes Bauteil. Zwei davon parallel würden jeden Stromkreis dahinter doppelt speisen, deshalb haben wir eins verwendet.",
  "ai_system.block_upsized": "größer — {circuits} Stromkreise",
  "ai_system.block_split": "{units} ×, {circuits} Stromkreise",
  "ai_system.block_relieved_note":
    "Es wurden mehr Abzweigstromkreise auf einen Sicherungsblock gelegt, als er Plätze hat. Ein Platz ist eine Sicherung, und zwei Stromkreise können sie sich nicht teilen — deshalb ein größerer Block, oder ein zweiter aus derselben Quelle gespeist.",
  "ai_system.zone_distances": "Wie weit liegen diese auseinander?",
  "ai_system.zone_distances_hint":
    "Änderst du einen Abstand, wandert jede Leitung dazwischen mit. Einzelne Kabel bleiben auf der Arbeitsfläche änderbar.",
  "ai_system.assumptions_count": "{count} Anmerkungen des Assistenten",
  "ai_system.tip_verify":
    "Hol dir eine zweite Meinung, bevor du etwas ablängst — ein Installateur, ein Bootselektriker oder das Forum, das du ohnehin liest.",
  "ai_system.tip_numbers":
    "Der Assistent wählt nur Bauteile und Verbindungen. Querschnitt, Sicherung und Spannungsfall werden hier aus der fertigen Zeichnung berechnet.",
  "ai_system.tip_zones":
    "Deine Anlage kommt in Bereiche gegliedert. Korrigiere den Abstand zweier Bereiche, und jede Leitung dazwischen wird neu vermessen.",
  "ai_system.tip_swap":
    "Alles Geratene ist auf der Arbeitsfläche markiert. Klick ein Bauteil an, um es zu tauschen — die Kabel bleiben daran.",
  "ai_system.estimated": "angenommen",
  "ai_system.estimated_hint":
    "Alles Gepunktete ist unsere Annahme, nicht deine Beschreibung. Korrigiere es hier, dann wird neu gezeichnet.",
  "ai_system.assumptions": "Anmerkungen",
  "ai_system.confirm_replace":
    "Das ersetzt, was auf der Arbeitsfläche liegt. Rückgängig holt es zurück. Fortfahren?",
  "ai_system.error.unbuildable":
    "Die Anlage ließ sich nicht verkabeln, deshalb wurde nichts gezeichnet. Batterien, Ladegerät und die größten Verbraucher zu nennen hilft meist.",
  "ai_system.error.unavailable":
    "Der Assistent war nicht erreichbar. Bitte gleich noch einmal versuchen.",
  "ai_system.photo_add": "Foto deiner Skizze hinzufügen",
  "ai_system.photo_remove": "Foto entfernen",
  "ai_system.photo_alt": "Das Foto, das du angehängt hast",
  "ai_system.photo_failed":
    "Das Bild konnte nicht gelesen werden. Ein JPEG oder PNG funktioniert meist.",
  "ai_system.photo_note":
    "Eine Handskizze oder ein Foto der echten Anlage funktionieren beide. Aufgeschriebene Querschnitte und Sicherungswerte ignorieren wir — die werden hier berechnet.",

  "ai_system.edit_title": "Anlage ändern",
  "ai_system.edit_subtitle":
    "Sag, was dazukommen, weg oder umgeklemmt werden soll. Alles andere auf der Zeichnung bleibt genau so, wie es ist.",
  "ai_system.edit_placeholder":
    "Ein 200-W-Panel mit eigenem MPPT dazu und einen zweiten Kühlschrank an den Sicherungsblock",
  "ai_system.edit_generate": "Änderung planen",
  "ai_system.edit_generating": "Änderung wird ausgearbeitet …",
  "ai_system.edit_busy_note":
    "Erst deine Anlage lesen, dann ausarbeiten, was sich ändern muss. Dauert meist eine halbe Minute.",
  "ai_system.edit_apply": "Auf die Zeichnung übernehmen",
  "ai_system.edit_restart": "Etwas anderes ändern",
  "ai_system.edit_untouched_note":
    "Nur was hier steht, ändert sich. Positionen, Längen, Querschnitte und Sicherungen, die du selbst gesetzt hast, bleiben unangetastet — und Rückgängig holt alles zurück.",
  "ai_system.edit_added": "Neue Bauteile",
  "ai_system.edit_removed": "Entfernte Bauteile",
  "ai_system.edit_removed_cables":
    "{count} Kabel gehen mit, ihre Verlegung ist damit auch weg.",
  "ai_system.edit_unwired": "{count} Kabel entfernt",
  "ai_system.edit_new_runs": "Längen der neuen Kabel",
  "ai_system.edit_stat_added": "Neu",
  "ai_system.edit_stat_cables": "Neue Kabel",
  "ai_system.edit_stat_removed": "Entfernt",
  "ai_system.edit_nothing":
    "Es wurde nichts geändert — warum, steht in der Notiz unten. Deine Zeichnung ist unberührt.",
  "ai_system.edit_switch_to_new": "Stattdessen eine neue Anlage zeichnen",
  "ai_system.edit_switch_to_edit": "Stattdessen die Zeichnung ändern",
  "ai_system.edit_replaces_note":
    "Das zeichnet eine neue Anlage und ersetzt, was auf der Zeichnung ist.",
  "ai_system.edit_too_big":
    "Diese Anlage ist zu groß, als dass der Assistent sie ändern könnte. Zieh das Bauteil stattdessen aus der Palette herein.",
  "ai_system.edit_error_unbuildable":
    "Die Änderung kam immer wieder unverdrahtbar zurück, deshalb wurde nichts übernommen. Nenn das Bauteil und wo es hin soll.",
  "canvas.empty.subtitle":
    "Sag uns, was du hast — wir zeichnen es und dimensionieren jedes Kabel.",
  "canvas.empty.placeholder": "Zwei 100-Ah-Lithiumbatterien und 400 W Solar",
  "canvas.empty.palette_hint": "oder zieh ein Bauteil aus der Palette herein",
  "canvas.empty.manual_title": "Bau deine Anlage",
  "canvas.empty.manual_subtitle":
    "Zieh ein Bauteil aus der Palette herein und dann von Pol zu Pol, um zu verkabeln. Jedes Kabel wird dabei dimensioniert.",
  "canvas.empty.manual_touch":
    "Tippe auf +, um dein erstes Bauteil hinzuzufügen.",
  "canvas.empty.read_only": "Dieser leere Schaltplan ist schreibgeschützt.",
  "starter.next_step.title": "Passe das Startprojekt an",
  "starter.next_step.body": "Stelle die Kabellänge für deine Installation ein und prüfe dann das Kabelergebnis. Änderungen werden automatisch auf diesem Gerät gespeichert.",
  "starter.next_step.action": "Tatsächliche Kabellänge einstellen",
  "starter.next_step.dismiss": "Später",
  "canvas.generated.placed":
    "Deine Anlage liegt auf der Arbeitsfläche. Alles hier lässt sich ändern.",
  "canvas.generated.lengths_one": "Eine Leitungslänge prüfen",
  "canvas.generated.lengths_many": "{count} Leitungslängen prüfen",
  "canvas.generated.lengths_why":
    "— dort hat die Länge den Querschnitt bestimmt.",
  "canvas.generated.parts_one": "Ein Bauteil tauschen",
  "canvas.generated.parts_many": "{count} Bauteile tauschen",
  "canvas.generated.parts_why":
    "— die haben wir gewählt; alle Kabel bleiben daran, wenn du eins änderst.",
  "canvas.generated.dismiss": "Schließen",
  "mobile.ai.readonly_note":
    "Nach dem Erstellen kannst du den Schaltplan auf dem Handy weiter bearbeiten.",
  "mobile.welcome.manual_title": "12 V und 24 V, richtig dimensioniert",
  "mobile.welcome.manual_subtitle":
    "Baue mit echten Katalogteilen und dimensioniere jedes Kabel und jede Sicherung nach ABYC E-11 direkt auf dem Handy.",
  "mobile.welcome.start_manual": "Neuen Schaltplan starten",
  "mobile.welcome.demo_cta": "Beispiel-Schaltplan ansehen",
  "mobile.welcome.demo_unavailable": "Beispiel gerade nicht verfügbar",
  "mobile.welcome.demo_note":
    "Nur lesen — verschieben, zoomen und ein Bauteil oder Kabel antippen, um die Werte zu sehen.",
  "mobile.menu.title": "Menü",
  "mobile.menu.open": "Menü öffnen",
  "mobile.menu.close": "Menü schließen",
  "mobile.menu.back": "Zurück zum Menü",
  "mobile.menu.project": "Projekt",
  "mobile.menu.privacy": "Datenschutz",
  "mobile.menu.advertising_cookies": "Werbe-Cookies",
  "mobile.menu.manage_subscription": "Abonnement verwalten",
  "mobile.palette.title": "Bauteil hinzufügen",
  "mobile.palette.open": "Bauteil hinzufügen",
  "mobile.palette.add": "Hinzufügen",
  "mobile.palette.close": "Bauteile schließen",
  "mobile.details.title": "Auswahldetails",
  "mobile.details.close": "Details schließen",
  "mobile.details.open": "Details öffnen",
  "mobile.details.label": "Details",
  "mobile.junction.branch": "Kabelabzweig hinzufügen",
  "mobile.share.title": "Teilen",
  "mobile.share.publish_title": "Schaltplan speichern und teilen",
  "mobile.share.publishing": "Wird gespeichert…",
  "mobile.share.copied": "Kopiert",
  "mobile.share.failed": "Teilen fehlgeschlagen",
  "mobile.share.sign_in_reason":
    "Melde dich an, um den Schaltplan zu speichern und einen Link zum Weitergeben zu erhalten.",
  "mobile.projects.menu": "Cloud-Projekte",
  "mobile.projects.label": "Cloud-Projekte",
  "mobile.projects.title": "Deine Schaltpläne für unterwegs",
  "mobile.projects.description":
    "Öffne hier ein synchronisiertes Cloud-Projekt zum Ansehen und Bearbeiten. Änderungen werden automatisch gespeichert.",
  "mobile.projects.sign_in": "Anmelden, um Cloud-Projekte anzusehen",
  "mobile.projects.sign_in_reason":
    "Melde dich an, um deine Cloud-Projekte auf diesem Handy anzusehen.",
  "mobile.projects.close": "Schließen",
  "mobile.projects.loading": "Cloud-Projekte werden geladen…",
  "mobile.projects.list_error": "Deine Cloud-Projekte konnten nicht geladen werden.",
  "mobile.projects.open_error": "Das Projekt konnte nicht geöffnet werden. Versuch es noch einmal.",
  "mobile.projects.retry": "Noch einmal versuchen",
  "mobile.projects.empty_title": "Noch keine Cloud-Projekte",
  "mobile.projects.empty_body":
    "Speichere ein Projekt in Crimp in der Cloud, dann erscheint es auf allen deinen Geräten.",
  "mobile.projects.current_aria": "{name} aus der Cloud aktualisieren",
  "mobile.projects.open_aria": "{name} öffnen",
  "mobile.projects.viewing": "Geöffnet",
  "mobile.projects.read_only": "Schreibgeschützt",
  "mobile.projects.editable": "Bearbeitbar",
  "mobile.projects.opening": "Wird geöffnet…",
  "mobile.projects.updated": "Aktualisiert {date}",
  "mobile.storage_error.title": "Bearbeitung ist nicht verfügbar",
  "mobile.storage_error.body":
    "Crimp konnte den Speicher auf diesem Gerät nicht öffnen. Lade die Seite vor dem Bearbeiten neu, damit deine Arbeit nicht verloren geht.",
  "starter.describe_title": "Beschreibe deine Anlage",
  "starter.describe_body":
    "Sag uns in einem Satz, was du hast. Wir zeichnen es und dimensionieren jedes Kabel.",
  "starter.describe_cta": "Beschreiben",
  "tools.describe_system": "Anlage beschreiben …",
  "tools.change_system": "Anlage ändern …",

  // ── Welcome modal ──────────────────────────────────────
  "welcome.eyebrow": "Willkommen",
  "welcome.title": "Willkommen bei Crimp",
  "welcome.body":
    "Zieh Komponenten auf die Arbeitsfläche, verbinde sie mit Kabeln, und Crimp schlägt für jede Strecke Kabelquerschnitt und Sicherungswert vor.",
  "welcome.terms_prefix": "Mit „Zustimmen“ akzeptierst du die ",
  "welcome.terms_link": "Nutzungsbedingungen",
  "welcome.terms_mid": " und nimmst die ",
  "welcome.privacy_link": "Datenschutzerklärung",
  "welcome.terms_suffix": " zur Kenntnis.",
  "welcome.cta": "Zustimmen und loslegen",

  // ── Starter / seed picker ──────────────────────────────
  "starter.label": "Projekt starten",
  "starter.new_title": "Neues Projekt",
  "starter.new_description": "Wie möchtest du beginnen?",
  "starter.examples": "Beispiel verwenden",
  "starter.assistant_body": "Beschreibe deine Anlage und lass sie vom Assistenten erstellen.",
  "starter.back": "Zurück",
  "starter.circuits.title": "Schaltungsbeispiele",
  "starter.circuits.description": "Bearbeitbare Anschlussbeispiele. Systemzustand zeigt Prüfungen und offene Annahmen. Tatsächliche Geräte, Leitungswege und Schutzmaßnahmen vor der Installation prüfen.",
  "starter.circuit.ac0.name": "120-V-AC-Stromkreise und GFCI",
  "starter.circuit.ac0.description": "Landstrom-Hauptschutz, drei Stromkreise und eine geerdete GFCI-Steckdose.",
  "starter.circuit.ac1.name": "230-V-AC-Stromkreise und Steckdosen",
  "starter.circuit.ac1.description": "Fehlerstromschutz für Landstrom, drei Stromkreise und geerdete Steckdosen.",
  "starter.circuit.ac2.name": "GFCI: LINE, LOAD und Steckdose",
  "starter.circuit.ac2.description": "Einspeisung, geschützte nachgeschaltete Steckdose und lokaler Steckeranschluss.",
  "starter.circuit.ac3.name": "120-V-AC-Quellenwahlschalter",
  "starter.circuit.ac3.description": "Manuelle Wahl zwischen Landstrom, Wechselrichter und Generator mit getrennten L- und N-Polen.",
  "starter.circuit.ac4.name": "230-V-AC-Quellenwahlschalter",
  "starter.circuit.ac4.description": "Drei separat geschützte Einspeisungen und durchgehender Schutzleiter.",
  "starter.circuit.spdt.name": "SPDT-Schalter mit Mittelstellung Aus",
  "starter.circuit.spdt.description": "Ein gemeinsamer Kontakt wählt zwischen zwei Verbrauchern mit getrennten Rückleitern.",
  "starter.circuit.dpdt.name": "DPDT-Schalter mit Mittelstellung Aus",
  "starter.circuit.dpdt.description": "Zwei unabhängige Pole schalten Plus- und Rückleiter gemeinsam um.",
  "starter.circuit.momentary.name": "Momenttaster",
  "starter.circuit.momentary.description": "Gedrückten und losgelassenen Kontaktzustand in der Simulation vergleichen.",
  "starter.circuit.relay.name": "Fünfpoliges Wechslerrelais",
  "starter.circuit.relay.description": "Separate Spule sowie Öffner- und Schließerlast. Relaiszustand manuell wählen.",
  "starter.circuit.pv.name": "PV-Sammelbox für drei Strings",
  "starter.circuit.pv.description": "Separate gPV-Stringsicherungen, Plus- und Minusschienen, Trennschalter und MPPT.",
  "starter.title": "Mit einem Beispiel beginnen",
  "starter.description":
    "Wähle ein Beispiel und passe es an.",
  "starter.blank": "Leeres Projekt",
  "starter.stat_system": "System",
  "starter.stat_bank": "Bank",
  "starter.stat_solar": "Solar",
  "starter.stat_parts": "Teile",
  "seed.lithium_boat.name": "Segelboot — Lithium-Umbau",
  "seed.lithium_boat.desc":
    "Älteres Boot auf LiFePO4 umgerüstet: Die Lichtmaschine lädt eine Lithium-Bordbatterie über einen DC-DC und behält die Starterbatterie.",
  "seed.lead_boat.name": "Klassisches Boot — Bleibatterien",
  "seed.lead_boat.desc":
    "Traditioneller Aufbau: Starter- und AGM-Bordbatterie werden über ein Trennrelais von der Lichtmaschine gekoppelt und per Landlader nachgeladen.",
  "seed.van.name": "Camper — Solar + DC-DC",
  "seed.van.desc":
    "Typischer Camper-Aufbau: Dachsolar über MPPT und ein DC-DC von der Fahrzeugbatterie speisen eine Lithium-Bank, Kühlbox, Licht und einen kleinen Wechselrichter.",
  "seed.advanced.name": "Liveaboard — Wechselrichter + Solar",
  "seed.advanced.desc":
    "Fortgeschrittener Aufbau: ein MultiPlus Wechselrichter/Lader am Landstrom, eine große Solaranlage und ein DC-DC von der Lichtmaschine speisen eine 300-Ah-Bank.",

  // ── Auth modal ─────────────────────────────────────────
  "auth.label": "Konto",
  "auth.title_signin": "Bei Crimp anmelden",
  "auth.title_signup": "Konto erstellen",
  "auth.unconfigured_title": "Cloud-Synchronisierung ist nicht konfiguriert",
  "auth.unconfigured_body":
    "Setze VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY in .env.local und lade neu.",
  "auth.ok": "OK",
  "auth.cancel": "Abbrechen",
  "auth.send_magic_link": "Magic-Link senden",
  "auth.create_account": "Konto erstellen",
  "auth.sign_in": "Anmelden",
  "auth.tab_magic": "Magic-Link",
  "auth.tab_password": "Passwort",
  "auth.tab_signup": "Registrieren",
  "auth.email": "E-Mail",
  "auth.email_placeholder": "du@beispiel.de",
  "auth.password": "Passwort",
  "auth.or": "oder",
  "auth.continue_google": "Mit Google fortfahren",
  "auth.oauth_failed": "Die Anmeldung konnte nicht gestartet werden. Versuche es erneut.",
  "auth.check_inbox_signin": "Sieh in deinem Posteingang nach dem Anmeldelink.",
  "auth.check_inbox_confirm":
    "Sieh in deinem Posteingang nach, um deine E-Mail-Adresse zu bestätigen.",

  // ── AI generation limits + end-of-allowance screens ────
  "ai.wall.welcome_title": "Lass uns gemeinsam planen",
  "ai.wall.welcome_body": "Erstelle ein kostenloses Konto für etwas KI-Unterstützung bei Bauteilen und deinem System.",
  "ai.wall.anonymous": "Erstelle ein kostenloses Konto oder melde dich an, um deine kostenlosen KI-Credits zu nutzen.",
  "ai.paywall.title": "Das waren die kostenlosen Entwürfe für heute",
  "ai.paywall.subtitle": "{used} von {limit} genutzt. Sie werden wieder frei, sobald die älteren herausfallen.",
  "ai.paywall.subtitle_plain": "Sie werden wieder frei, sobald die älteren herausfallen.",
  "ai.paywall.licence": "Crimp Lifetime · einmal zahlen",
  "ai.paywall.price": "{price} {currency}",
  "ai.paywall.body":
    "Hier käme normalerweise eine Lizenz ins Spiel. Sie ist nicht im Verkauf — Crimp ist kostenlos, während wir herausfinden, wie viele eine wollen. Hättest du dafür bezahlt?",
  "ai.paywall.free_note":
    "Es wird nichts berechnet und nichts gesperrt, egal wie du antwortest. Deine Entwürfe kommen von selbst zurück.",
  "ai.paywall.interested": "Dafür würde ich zahlen",
  "ai.paywall.decline": "Nichts für mich",
  "ai.paywall.thanks":
    "Notiert — danke. Nichts zu zahlen, und an deinem Konto ändert sich nichts; du hast nur das Argument dafür geliefert, es zu bauen.",
  "ai.paywall.done": "Schließen",
  "ai.usage.remaining": "{remaining} von {limit} kostenlosen Entwürfen übrig",
  "ai.auto_add.title": "Automatisch aufladen",
  "ai.auto_add.on": "Ein",
  "ai.auto_add.loading": "Automatisches Aufladen wird geprüft…",
  "ai.auto_add.enable": "Automatisches Aufladen aktivieren",
  "ai.auto_add.subscription": "Mit einem aktiven Monatsabo verfügbar.",
  "ai.auto_add.unavailable": "Automatisches Aufladen ist vorübergehend nicht verfügbar.",
  "ai.auto_add.threshold": "Füge {credits} Credits hinzu, wenn weniger als {threshold} übrig sind.",
  "ai.auto_add.monthly_limit": "Monatslimit",
  "ai.auto_add.consent": "Belaste deine Abo-Zahlungsmethode mit {price} pro Aufladung, höchstens {limit}/Monat inklusive Steuern. Jederzeit abschaltbar.",
  "ai.auto_add.pending": "Eine Aufladung wird verarbeitet. Dein Entwurf ist gespeichert.",
  "ai.auto_add.pending_disable": "Die laufende Aufladung kann noch abgeschlossen werden.",
  "ai.auto_add.price_changed": "Der Preis hat sich geändert. Speichere, um fortzufahren.",
  "ai.auto_add.limit_reached": "Das Monatslimit für automatisches Aufladen ist erreicht. Du kannst weiter Credits kaufen.",
  "ai.auto_add.saving": "Wird gespeichert…",
  "ai.auto_add.save": "Speichern",
  "ai.auto_add.saved": "Gespeichert.",
  "ai.auto_add.error": "Automatisches Aufladen konnte nicht aktualisiert werden. Versuche es erneut.",
  "ai.credits.title": "Credits hinzufügen",
  "ai.credits.menu": "Credits",
  "ai.credits.remaining": "{remaining} Credits",
  "ai.credits.purchased": "Gekaufte Credits",
  "ai.credits.remaining_one": "{remaining} Credit",
  "ai.credits.shared": "Ein Guthaben für den Assistenten und die Erstellung von Komponenten und Systemen.",
  "ai.credits.balance": "{remaining} Credits verfügbar",
  "ai.credits.pack": "{credits} Credits",
  "ai.credits.costs": "KI-Anfragen werden nach Nutzung berechnet.",
  "ai.credits.terms": "Einmaliger Kauf. Gekaufte Credits verfallen nie.",
  "ai.credits.included_first": "Die in deinem Plan enthaltenen Credits werden zuerst verwendet; gekaufte Credits verfallen nie.",
  "ai.credits.lifetime_title": "KI-Credits gehören zu Crimp Lifetime",
  "ai.credits.lifetime_body": "Crimp Lifetime enthält {credits} KI-Credits, dazu Systeme jeder Größe, Exporte und den Plan. Einmal zahlen — es gehört dir für die Lebensdauer des Produkts — und bei Bedarf Credits nachkaufen.",
  "ai.credits.get_lifetime": "Crimp Lifetime holen",
  "ai.credits.pass_ended_title": "Dein Pass ist abgelaufen",
  "ai.credits.pass_ended_body": "Seine Credits sind mit ihm abgelaufen. Crimp Lifetime bringt {credits} KI-Credits zurück, dazu Systeme jeder Größe, Exporte und den Plan — einmal zahlen, deins für die Lebensdauer des Produkts.",
  "ai.credits.pass_title": "KI-Credits gehören zum Pass",
  "ai.credits.pass_body": "Der Crimp Pass enthält {credits} KI-Credits für 12 Monate, dazu Systeme jeder Größe, Exporte und den Plan. Eine Zahlung, keine automatische Verlängerung.",
  "ai.credits.get_pass": "Pass holen",
  "ai.credits.subscription_title": "KI-Credits gehören zum Abo",
  "ai.credits.subscription_body": "Das Monatsabo enthält {credits} KI-Credits pro Monat, dazu Systeme jeder Größe, Exporte und den Plan. Jederzeit kündbar.",
  "ai.credits.subscribe": "Abonnieren",
  "ai.credits.taxes": "Steuern werden an der Kasse angezeigt.",
  "ai.credits.details": "So funktionieren Credits",
  "ai.credits.failed_requests": "Technische Fehler kosten keine Credits.",
  "ai.credits.buy": "Credits kaufen",
  "ai.credits.opening": "Kasse wird geöffnet…",
  "ai.credits.checkout_open": "Schließe deinen Kauf an der Kasse ab.",
  "ai.credits.ready": "Credits hinzugefügt. Du kannst weitermachen.",
  "ai.credits.continue": "Weiter",
  "ai.credits.waiting": "Zahlung erhalten. Guthaben wird aktualisiert…",
  "ai.credits.error": "Die Kasse konnte nicht geöffnet werden. Versuche es erneut.",
  "ai.credits.balance_error": "Das Guthaben konnte nicht aktualisiert werden. Versuche es erneut.",
  "ai.credits.unavailable": "Credit-Käufe sind vorübergehend nicht verfügbar.",
  "ai.credits.retry": "Erneut versuchen",
  "ai.credits.refresh": "Guthaben aktualisieren",
  "ai.credits.generation": "{remaining} Credits übrig. Höchstens {max} Credits pro Anfrage",
  "ai.credits.free": "Kostenlose KI-Credits mit einem Konto.",
  "ai.credits.sign_in": "Anmelden für kostenlose KI-Credits",
  "ai.credits.first_free": "Kostenlose KI-Credits mit einem Konto",
  "ai.credits.usage": "Nach Nutzung bezahlen · Höchstens {max} Credits pro Anfrage",
  "ai.denied.wall": "Melde dich an, um KI-Credits hinzuzufügen.",
  "ai.denied.quota": "Füge KI-Credits hinzu, um weitere Komponenten oder Systeme zu erstellen.",
  "ai.denied.rate_limited":
    "Das waren viele Entwürfe auf einmal. Warte kurz und versuch es noch einmal.",
  "ai.denied.capped":
    "Das Entwurfswerkzeug ist gerade ausgelastet. Alles andere auf dieser Seite funktioniert weiter.",
  "ai.denied.turnstile":
    "Wir konnten die Anfrage nicht verifizieren. Lade die Seite neu und versuch es noch einmal.",
  "ai.denied.invalid":
    "Beschreib die Anlage in ein, zwei Sätzen — die Beschreibung war leer oder zu lang.",

  // ── Share modal ────────────────────────────────────────
  "share.label": "Teilen",
  "share.title": "Dieses Projekt teilen",
  "share.description":
    "Erstelle pro Zugriffsstufe einen Link. Jeder mit dem Link kann ihn nutzen — halte Bearbeiter-Links privat.",
  "share.done": "Fertig",
  "share.save_first": "Speichere das Projekt zuerst, um es zu teilen.",
  "share.loading": "Lädt…",
  "share.role.viewer": "Betrachter",
  "share.role.viewer_sub": "Kann ansehen, nicht bearbeiten oder kommentieren.",
  "share.role.commenter": "Kommentator",
  "share.role.commenter_sub": "Kann ansehen und Kommentare hinterlassen.",
  "share.role.editor": "Bearbeiter",
  "share.role.editor_sub": "Kann ansehen, kommentieren und die Arbeitsfläche bearbeiten.",
  "share.create_link": "{label}-Link erstellen",
  "share.copy": "Link kopieren",
  "share.copied": "Kopiert",
  "share.show_qr": "QR-Code anzeigen",
  "share.hide_qr": "QR-Code ausblenden",
  "share.regenerate": "Link neu erzeugen (alter wird ungültig)",
  "share.revoke": "Link widerrufen",
  "share.qr_print_blurb":
    "Drucke diesen QR-Code aus und bewahre ihn an Bord auf — wer ihn scannt, kann den Schaltplan ansehen.",
  "share.qr_scan_caption": "Scannen, um den Schaltplan anzusehen",
  "share.print": "Drucken",
  "share.regenerate_short": "Neu erzeugen",
  "share.revoke_short": "Widerrufen",
  "share.access.label": "Direkter Zugriff",
  "share.access.sub":
    "Erzeuge einen privaten Link für eine bestimmte Person. Nicht in der Galerie gelistet.",

  // ── Community publish (in share modal) ─────────────────
  "share.publish.section": "Community",
  "share.publish.heading": "In der Community veröffentlichen",
  "share.publish.subtitle":
    "Hilf jemandem, der ein ähnliches Boot verkabelt. Veröffentlichte Designs erscheinen in der Community-Galerie und können als Ausgangspunkt geforkt werden — ein guter Weg, eine zweite Meinung zu bekommen, bevor du Kabel abschneidest.",
  "share.publish.toggle_on": "In der Community-Galerie gelistet",
  "share.publish.toggle_off": "Privat — nur mit Freigabe-Link sichtbar",
  "share.publish.segment_private": "Privat",
  "share.publish.segment_public": "In der Community",
  "share.publish.views_label": "Aufrufe · letzte 7 Tage",
  "share.publish.views_direct": "direkt",
  "share.publish.views_empty":
    "Sobald du den Link irgendwo geteilt hast, erscheinen Anzahl und Quellen hier.",
  "share.publish.forks_link": "{forks} deines Designs",
  "share.publish.forks_singular": "Fork",
  "share.publish.forks_plural": "Forks",
  "share.publish.public_link": "Öffentlicher Link",
  "share.publish.description_label": "Kurzbeschreibung (optional)",
  "share.publish.description_placeholder":
    "z. B. Segelyacht 38 ft Langfahrt — LiFePO4-Verbraucherbank, MPPT-Solar.",
  "share.publish.unpublish": "Aus Galerie nehmen",
  "share.publish.publish": "Veröffentlichen",
  "share.publish.saving": "Speichern…",
  "share.publish.anonymous_label": "Anonym veröffentlichen",
  "share.publish.anonymous_hint":
    "Namen verbergen. Der Build erscheint als Anonymous und die Share-Karte zeigt keine Byline.",

  // ── Community gallery ──────────────────────────────────
  "topbar.community": "Community",
  "topbar.community_title": "Von anderen veröffentlichte Projekte ansehen",
  "community.label": "Community",
  "community.eyebrow_vol": "Vol. 01",
  "community.count_label": "Builds",
  "community.title": "Builds aus der Praxis",
  "community.description":
    "Schaltpläne, die andere Crimp-Nutzer veröffentlicht haben. Öffne einen, um das Layout zu lesen, oder forke ihn als Ausgangspunkt für deinen eigenen Build.",
  "community.close": "Schließen",
  "community.loading": "Lade…",
  "community.empty":
    "Noch keine öffentlichen Projekte. Veröffentliche dein eigenes über das Teilen-Menü und sei der Erste.",
  "community.empty_headline": "Am Steg ist es ruhig.",
  "community.empty_subline":
    "Noch keine Builds veröffentlicht. Veröffentliche deinen über das Teilen-Menü und sei der Erste.",
  "community.empty_filtered":
    "Keine Builds passen zu diesem Filter. Probier einen anderen Typ.",
  "community.owner_title": "Builds von {name}",
  "community.owner_description":
    "Öffentliche Schaltpläne, die {name} mit der Community geteilt hat. Öffnen zum Lesen oder Forken als Ausgangspunkt.",
  "community.error": "Community-Projekte konnten nicht geladen werden: {message}",
  "community.fork_count": "{count} Forks",
  "community.comment_count": "{count} Kommentare",
  "community.published_on": "Veröffentlicht am {date}",
  "community.open": "Öffnen",
  "community.fork_of": "Geforkt von einem anderen Projekt",
  "community.by": "von {name}",
  "community.sort.label": "Sortieren",
  "community.sort.aria": "Builds sortieren",
  "community.sort.newest": "Neueste",
  "community.sort.likes": "Beliebteste",
  "community.sort.forks": "Meist geforkt",
  "community.sort.comments": "Meist diskutiert",
  "community.like.title_set": "Diesen Build liken",
  "community.like.title_unset": "Like entfernen",
  "community.activity_title": "Aktivität in letzter Zeit ({date})",
  "community.filter.label": "Filtern",
  "community.filter.aria": "Nach Bootstyp filtern",
  "community.filter.all": "Alle",

  // ── Public-view banner ─────────────────────────────────
  "public_banner.label": "Öffentlich — nur lesen",
  "public_banner.fork": "Zum Bearbeiten forken",
  "public_banner.fork_title": "Eine Kopie dieses Projekts auf deinem Konto speichern",
  "public_banner.sign_in_to_fork": "Zum Forken anmelden",
  "public_banner.forking": "Forke…",
  "public_banner.fork_failed": "Fork fehlgeschlagen: {message}",
  "public_banner.fork_name_prompt": "Benenne deinen Fork:",
  "public_banner.fork_suffix": "(Fork)",
  "public_banner.back_to_community": "Community durchsuchen",
  "public_banner.download_image": "Bild herunterladen",
  "public_banner.download_image_title":
    "Speichere den Schaltplan als PNG — gut für Gruppen, die Bildanhänge bevorzugen.",
  "public_banner.like_aria": "Diesen Build liken",
  "public_banner.like_title": "Diesen Build liken",
  "public_banner.unlike_title": "Like entfernen",
  "public_banner.share": "Teilen",
  "public_banner.share_title": "Diesen Build in einer Gruppe oder einem Forum teilen",
  "public_banner.forked_from": "nach",
  "public_banner.forked_from_title": "Das Original-Design von {name} öffnen",

  "auth_reason.fork": "Melde dich an, um dieses Projekt zu forken.",
  "auth_reason.like": "Melde dich an, um diesen Build zu liken.",

  // ── Lineage modal ──────────────────────────────────────
  "lineage.label": "Stammbaum",
  "lineage.title": "Auf deinem Design aufgebaut",
  "lineage.description":
    "Öffentliche Projekte, die als Fork dieses Builds entstanden sind. Öffne eines, um zu sehen, wie es sich entwickelt hat.",
  "lineage.close": "Schließen",
  "lineage.loading": "Lade…",
  "lineage.parent_label": "Geforkt von",
  "lineage.children_label": "Forks dieses Builds",
  "lineage.children_empty":
    "Noch keine öffentlichen Forks. Wenn jemand dein Design forkt und veröffentlicht, erscheint er hier.",
  "lineage.count": "{count} insgesamt",

  // ── Share composer ─────────────────────────────────────
  "share_composer.label": "Teilen",
  "share_composer.title": "Diesen Build teilen",
  "share_composer.description":
    "Fertiger Beitragstext für Foren, Facebook-Gruppen oder Reddit — plus Link und sauberes Bild.",
  "share_composer.close": "Schließen",
  "share_composer.copied": "Kopiert",
  "share_composer.copy_text": "Kopieren",
  "share_composer.post_text_label": "Beitragstext",
  "share_composer.downloading": "Bereite vor…",
  "share_composer.action_image": "Bild",
  "share_composer.action_image_sub": "Lade den Schaltplan als PNG herunter.",
  "share_composer.action_link": "Link",
  "share_composer.action_link_sub": "Kopiere die öffentliche URL.",
  "share_composer.action_text": "Beitragstext",
  "share_composer.action_text_sub": "Titel, Autor, Link — fertig zum Einfügen.",
  "share_composer.mod_hint":
    "Tipp — die meisten Boot- und Camper-Gruppen bevorzugen Bildanhänge gegenüber externen Links. Poste das Bild direkt und ergänze den Link in einem Kommentar.",
  "share_composer.compose_on": "Verfassen auf",

  // ── Project picker modal ───────────────────────────────
  "picker.label": "Projekte",
  "picker.title": "Deine Projekte",
  "picker.description": "Gespeichertes Projekt öffnen, umbenennen, duplizieren oder löschen.",
  "picker.close": "Schließen",
  "picker.new_project": "Neues Projekt",
  "picker.empty": "Noch keine gespeicherten Projekte. Klicke auf {strong}, um eines zu starten.",
  "picker.empty_strong": "Neues Projekt",
  "picker.open_badge": "geöffnet",
  "picker.delete_confirm": '"{name}" löschen? Das kann nicht rückgängig gemacht werden.',
  "picker.rename": "Umbenennen",
  "picker.duplicate": "Duplizieren",
  "picker.delete": "Löschen",
  "picker.local_heading": "Auf diesem Gerät",
  "picker.cloud_heading": "In der Cloud",
  "picker.local_empty": "Noch keine lokalen Projekte.",
  "picker.cloud_sign_in": "Melde dich an, um Cloud-Projekte zu sehen.",
  "picker.cloud_usage": "{used} von {limit} Cloud-Projekten",
  "picker.cloud_usage_full": "Alle Plätze sind belegt. Lösche ein Cloud-Projekt, das du nicht mehr brauchst, um einen freizugeben.",

  // ── Admin project picker (cross-user) ──────────────────
  "admin_picker.label": "Alle Projekte",
  "admin_picker.title": "Alle Projekte (Admin)",
  "admin_picker.description":
    "Projekte aller Nutzer durchsuchen. Wird schreibgeschützt geöffnet — Speichern erfordert weiterhin Eigentümerschaft.",
  "admin_picker.search_placeholder": "Nach Name, Besitzer oder E-Mail suchen…",
  "admin_picker.empty": "Es existieren noch keine Projekte.",
  "admin_picker.public_badge": "öffentlich",
  "admin_picker.owner_unknown": "Unbekannter Besitzer",

  // ── Cable length modal ─────────────────────────────────
  "cable_modal.eyebrow": "Neues Kabel",
  "cable_modal.distance_label": "Abstand von {from} nach {to}",
  "cable_modal.zone_a": "Zone A",
  "cable_modal.zone_b": "Zone B",
  "cable_modal.distance_placeholder": "z. B. 3",
  "cable_modal.metres": "Meter",
  "cable_modal.feet": "Fuß",
  "cable_modal.distance_saved":
    "Für diese Zonen gespeichert — Kabel wird mit Abstand + {slack} Reserve voreingestellt.",
  "cable_modal.distance_will_save":
    "Wir merken uns das für künftige Kabel zwischen diesen Zonen. Die Kabellänge wird mit Abstand + {slack} Reserve voreingestellt.",
  "cable_modal.length_label": "Kabellänge (einfacher Weg)",
  "cable_modal.auto_size_cable": "Kabel automatisch dimensionieren",
  "cable_modal.pick_manually": "Größe manuell wählen.",
  "cable_modal.add_fuse": "Sicherung hinzufügen",
  "cable_modal.negative_no_fuse": "Minusleiter — keine Sicherung.",
  "cable_modal.fuse_to_slot": "Setzt den Sicherungswert des Slots.",
  "cable_modal.inline_fuse_holder": "Inline-Sicherungshalter mit {rating} A.",
  "cable_modal.skip_fuse": "Sicherung an diesem Kabel überspringen.",
  "cable_modal.wire": "Leiter",
  "cable_modal.fuse": "Sicherung",
  "cable_modal.cable_amps": "Kabelstrom",
  "cable_modal.estimate": "{amp} gesch.",
  "cable_modal.no_fuse": "keine Sicherung",
  "cable_modal.no_known_load": "keine bekannte Last",
  "cable_modal.no_load_no_fuse":
    "Keine bekannte Last auf dieser Leitung — wird ohne Sicherung hinzugefügt.",
  "cable_modal.vdrop": "Spannungsabfall",
  "cable_modal.cancel": "Abbrechen",
  "cable_modal.add_cable_fuse": "Kabel + Sicherung hinzufügen",
  "cable_modal.add_cable": "Kabel hinzufügen",
  "cable_modal.fuse_explainer_negative":
    "Minus-/Masseleiter — Sicherungen schützen nur die Plusseite (ABYC).",
  "cable_modal.fuse_explainer_slot":
    "Kabel ist mit einer Sicherungsleiste verbunden — der Sicherungswert des Slots wird gesetzt.",
  "cable_modal.fuse_explainer_inline":
    "Der Sicherungswert wird vom Kabel selbst getragen.",

  // ── Inspector — system health ──────────────────────────
  "health.system": "System",
  "health.empty_title": "Zieh eine Batterie hinein, um zu starten",
  "health.empty_body":
    "Während du das System verkabelst, weist dieses Panel auf Auslegungsprobleme, Lücken in der Absicherung und andere prüfenswerte Punkte hin.",
  "health.empty_demo_lead": "Neu hier?",
  "health.empty_demo_button": "Demo-Projekt ansehen",
  "health.severity.critical": "Kritisch",
  "health.severity.warning": "Warnung",
  "health.severity.info": "Info",
  "health.severity_chip_title": "{count} {label}",
  "health.energy_budget": "Energiebudget",
  "health.battery_usable": "Batterie (nutzbar)",
  "health.reserve_hint": "Reserve {pct} %",
  "health.daily_use": "Täglicher Verbrauch",
  "health.daily_harvest": "Täglicher Ertrag",
  "health.harvest_hint": "Solar {solar} · Lichtmaschine {alternator} · Landstrom {shore} Wh",
  "health.net_daily": "Tagesbilanz",
  "health.net_now": "Netto jetzt",
  "health.autonomy": "Autonomie (ab voll)",
  "health.autonomy_hint":
    "Bei aktuellem Tagesverbrauch — wie lange bis zur Reserveschwelle.",
  "health.time_to_empty": "Zeit bis leer",
  "health.time_to_empty_hint":
    "Vom aktuellen SoC bis zur Reserve, wenn der jetzige Zustand anhält.",
  "health.time_to_full": "Zeit bis voll",
  "health.time_to_full_hint":
    "Vom aktuellen SoC bis voll, wenn der jetzige Zustand anhält.",
  "health.issues_all": "Probleme & Vorschläge",
  "health.issues_filter": "{label}",
  "health.fix_all": "Alle beheben ({count})",
  "health.fix_all_title": "{count} Ein-Klick-Korrekturen anwenden",
  "health.healthy": "Nichts zu melden — System wirkt gesund.",
  "health.no_at_severity": "Keine Probleme dieser Schwere.",
  "health.top_consumers": "Größte Verbraucher",
  "health.simulate_nudge_eyebrow": "Szenario",
  "health.simulate_nudge_body":
    "Passe Lasten, Sonne und Laufzeit an, um zu sehen, wie das System sich hält.",
  "health.simulate_nudge_cta": "Simulieren öffnen",
  "inspector.tab_edit": "Bearbeiten",
  "inspector.tabs_aria": "Inspector-Modus",
  "simulate.scenario": "Szenario",
  "simulate.reset": "Zurücksetzen",
  "simulate.load_on": "Ein",
  "simulate.load_off": "Aus",
  "simulate.reset_scenario_title": "Szenario auf Standard zurücksetzen",
  "simulate.sun_hours": "Spitzensonnen­stunden",
  "simulate.engine_hours": "Motorlauf",
  "simulate.shore_hours": "Landstrom",
  "simulate.reserve_floor": "Reserveschwelle",
  "simulate.preset_default": "Standard",
  "simulate.preset_custom": "Benutzerdefiniert",
  "simulate.no_saved_scenarios": "Noch keine gespeicherten Szenarien",
  "simulate.delete_saved": "Löschen",
  "simulate.save_current": "Aktuelles speichern als…",
  "simulate.save_placeholder": "z. B. Wochenende vor Anker",
  "simulate.reset_to_default": "Auf Standard zurücksetzen",
  "simulate.show_all": "Alle {count} anzeigen",
  "simulate.clear_overrides": "Zurücksetzen ({count})",
  "simulate.clear_overrides_title": "{count} Verbraucher-Overrides zurücksetzen",
  "simulate.harvest_none": "keine auf der Arbeitsfläche",
  "simulate.solar_conditions": "Sonnenverhältnisse",
  "simulate.solar_sunny": "Sonnig",
  "simulate.solar_cloudy": "Bewölkt",
  "simulate.solar_off": "Dunkel",
  "simulate.solar_mixed": "Gemischt",
  "simulate.close_sheet_aria": "Szenario-Panel schließen",
  "simulate.stop": "Simulation stoppen",
  "simulate.batteries": "Batterien",
  "simulate.show_on_canvas": "Auf der Arbeitsfläche zeigen",
  "simulate.combiner.combined": "Verbunden",
  "simulate.combiner.isolated": "Getrennt",
  "simulate.combiner.help": "Manueller Kontaktzustand; automatische Erkennung und Steuersignale werden nicht simuliert.",
  "simulate.orion.on": "Auto",
  "simulate.orion.forced": "Erzwungen",
  "simulate.orion.off": "Aus",
  "simulate.orion.status_running": "Motor erkannt",
  "simulate.orion.status_waiting": "Wartet auf den Motor",
  "simulate.orion.status_unresolved": "Motoranschluss ungeprüft",
  "simulate.orion.status_forced": "Motorerkennung deaktiviert",
  "simulate.ac.supplied": "AC-Quelle ein",
  "simulate.ac.off": "Keine aktive AC-Quelle",
  "simulate.ac.unverified": "AC-Versorgung ungeprüft",
  "simulate.ac.nominal": "Nennspannung {voltage} V",
  "simulate.ac.help": "Nur Quellen- und Kontaktzustand; Spannung, Strom und Schutzabschaltungen werden nicht simuliert.",
  "simulate.ac.main": "Hauptschalter",
  "simulate.ac.branch": "Stromkreis {number}",
  "simulate.ac.on_label": "Ein",
  "simulate.ac.off_label": "Aus",
  "simulate.ac.unknown_label": "Unbekannt",
  "simulate.ac.input": "Eingang: {status}",
  "simulate.ac.output": "Ausgang: {status}",
  "simulate.orion.help": "Auto folgt einer an beide Eingangsleiter angeschlossenen Lichtmaschine. Spannungsschwellen, Verzögerungen, externe Steuersignale und die Eingangsspannungssperre werden nicht simuliert.",
  "simulate.orion.forced_help": "Simuliert eine deaktivierte Motorabschalterkennung. Das Laden kann bei abgestelltem Motor Strom aus der Starterbatterie ziehen.",
  "health.fix_prefix": "Beheben · ",
  "health.show_on_canvas": "Auf der Arbeitsfläche zeigen →",
  "health.tip_drop_component":
    "Zieh eine Komponente aus der Palette auf die Arbeitsfläche, um zu starten.",
  "health.tip_wire":
    "Ziehe von einer Klemme zur anderen, um Komponenten zu verkabeln.",
  "health.tip_zone_intro":
    "Zieh eine {strong} aus der Palette, um Komponenten nach physischem Bereich zu gruppieren — Kabellängen werden aus den Zonen-Abständen abgeleitet.",
  "health.tip_zone_strong": "Zone",
  "health.tip_shortcuts":
    "{undo} Rückgängig · {fit} Ansicht anpassen · {del} Auswahl entfernen",

  // ── Inspector — component detail ───────────────────────
  "rv_panel.main": "Hauptschalter L1 + L2",
  "rv_panel.branches": "Stromkreise pro Außenleiter",
  "rv_panel.resize_help": "Vor dem Verkleinern die letzten Stromkreise trennen. Bestehende Verbindungen behalten ihre Stromkreisnummern.",
  "rv_panel.switch_help": "Hervorgehoben = Kontakt geschlossen. Der Hauptschalter trennt beide Außenleiter.",
  "rv_panel.limits": "Nur manuelle Kontakte. Automatische Auslösung, Fehlerstromschutz, Phasenbalance und AC-Lastfluss werden nicht simuliert. Die Busanschlüsse liegen hinter dem Hauptschalter; Verbraucher an die Stromkreisabgänge anschließen.",
  "inspector.custom": "Eigene",
  "inspector.custom_label": "Eigene Bezeichnung",
  "inspector.swap_part": "Bauteil",
  "inspector.swap_part_hint":
    "Gegen ein anderes Bauteil tauschen. Alle Kabel bleiben daran.",
  "inspector.swap_part_estimated":
    "Dieses Bauteil haben wir für dich gewählt. Tausche es gegen das, was du wirklich hast — alle Kabel bleiben daran.",
  "inspector.specs": "Daten",
  "inspector.reset_specs": "Zurücksetzen",
  "inspector.reset_specs_title": "Auf Katalogwerte zurücksetzen",
  "inspector.customize": "Anpassen",
  "inspector.customize_title":
    "Anschlüsse und Daten bearbeiten. Umkehrbar — verwende Zurücksetzen, um zum Katalog zurückzukehren.",
  "inspector.custom_callout.title_from": "Eigene Kopie von {name}",
  "inspector.custom_callout.title_scratch": "Eigene Komponente",
  "inspector.custom_callout.body_from":
    "Änderungen an Daten, Anschlüssen und Maßen bleiben auf dieser Instanz — andere Kopien von {name} bleiben unverändert.",
  "inspector.custom_callout.body_scratch":
    "Daten, Anschlüsse und Maße so anpassen, dass sie zu deinem realen Bauteil passen.",
  "inspector.custom_callout.revert": "Auf Katalog zurücksetzen",
  "inspector.reset_field": "Auf Katalog zurücksetzen",
  "inspector.identity": "Komponente",
  "inspector.identity.name": "Name",
  "inspector.identity.name_placeholder": "z. B. MD11C-Motorblock",
  "inspector.identity.category": "Kategorie",
  "inspector.identity.width": "Breite",
  "inspector.identity.height": "Höhe",
  "inspector.identity.photo": "Foto",
  "inspector.identity.photo_add": "Foto hinzufügen",
  "inspector.identity.photo_remove": "Entfernen",
  "inspector.identity.photo_failed": "Upload fehlgeschlagen — bitte erneut versuchen.",
  "inspector.identity.photo_signin": "Melde dich an, um dieser Komponente ein Foto hinzuzufügen.",
  "inspector.save_to_library": "In Bibliothek speichern",
  "inspector.save_to_library_title":
    "Diese eigene Komponente in deine persönliche Bibliothek auf diesem Gerät speichern — sie erscheint in der Palette zur Wiederverwendung.",
  "inspector.saved_to_library": "Gespeichert",
  "inspector.terminal.add": "Anschluss hinzufügen",
  "inspector.terminal.default_label": "T",
  "inspector.terminal.delete_title": "Anschluss entfernen",
  "inspector.terminal.delete_blocked":
    "Trenne zuerst {count} Kabel, um diesen Anschluss zu entfernen.",
  "inspector.terminal.label_placeholder": "Bezeichnung",
  "inspector.terminal.label_for": "Bezeichnung für Anschluss {terminal}",
  "inspector.terminal.polarity": "Polarität",
  "inspector.terminal.direction": "Richtung",
  "inspector.terminal.side": "Seite",
  "inspector.terminal.max_amp": "Anschlussgrenze (A)",
  "inspector.terminal.rating_help": "Stromgrenzen der Anschlüsse (A), getrennt von Eigenverbrauch und Batteriekapazität (Ah).",
  "inspector.terminal.offset": "Position",
  "inspector.terminal.color": "Farbe",
  "inspector.terminal.polarity.positive": "Plus",
  "inspector.terminal.polarity.negative": "Minus",
  "inspector.terminal.polarity.ground": "Masse",
  "inspector.terminal.polarity.signal": "Signal",
  "inspector.terminal.polarity.neutral": "Neutral",
  "inspector.terminal.direction.input": "Eingang",
  "inspector.terminal.direction.output": "Ausgang",
  "inspector.terminal.direction.bidi": "Beide",
  "inspector.terminal.side.left": "Links",
  "inspector.terminal.side.right": "Rechts",
  "inspector.terminal.side.top": "Oben",
  "inspector.terminal.side.bottom": "Unten",
  "inspector.terminal.expand": "Anschlussdetails anzeigen",
  "inspector.terminal.collapse": "Anschlussdetails ausblenden",
  "inspector.advanced": "Erweitert",
  "inspector.spec.capacity": "Kapazität",
  "inspector.spec.voltage": "Spannung",
  "inspector.spec.vmp": "Vmp",
  "inspector.spec.vmp_hint":
    "Vmp — Spannung am Maximum-Power-Point des Panels. Unterscheidet sich von der Systemspannung (V): ein 12-V-Panel hat typisch Vmp ≈ 18 V.",
  "inspector.spec.power": "Leistung",
  "inspector.spec.output": "Ausgang",
  "inspector.spec.imp": "Imp",
  "inspector.spec.imp_hint":
    "Imp — Strom am Maximum-Power-Point. Imp ≈ Leistung ÷ Vmp (z. B. 100 W ÷ 18 V ≈ 5,6 A).",
  "inspector.spec.continuous_draw": "Dauerstrom",
  "inspector.spec.peak_surge": "Einschaltspitze",
  "inspector.spec.peak_sun_hours": "Spitzensonnen­stunden",
  "inspector.spec.typical_charge": "Typischer Ladestrom",
  "inspector.solar_blurb":
    'Ein "12 V"-Solarpanel liefert tatsächlich rund 18 V am Maximum-Power-Point ({vmp}). Der Strom dort ist {imp}, nicht Leistung ÷ 12 V — deshalb gibt ein 100-W-Panel ~5,6 A, nicht 8,3 A. Der MPPT-Laderegler wandelt Vmp auf die Batteriespannung herunter.',
  "inspector.load_mode": "Verbrauchermodus",
  "inspector.energy_use": "Energieverbrauch",
  "inspector.always_on": "Dauerbetrieb (24/7)",
  "inspector.hours_per_day": "Stunden / Tag",
  "inspector.daily_energy": "Energie pro Tag",
  "inspector.draw_when_on": "Verbrauch bei \"An\"",
  "inspector.outputs": "Ausgänge (Sicherungsplätze)",
  "inspector.terminals": "Klemmen",
  "inspector.fuse": "Sicherung",
  "inspector.fuse_rating": "Nennwert",
  "inspector.breaker.title": "Leistungsschalter",
  "inspector.breaker.setup": "Als Leistungsschalter einrichten",
  "inspector.breaker.input": "Versorgungsseitiger Anschluss",
  "inspector.breaker.rating": "Schalternennstrom (A)",
  "inspector.breaker.voltage": "Maximale DC-Spannung (V, optional)",
  "inspector.breaker.voltage_short": "Maximale Spannung",
  "inspector.breaker.hint": "Den eingebauten Nennstrom verwenden. Auslösen wird in Simulieren manuell gesteuert.",
  "inspector.electrical.apply": "Einstellungen anwenden",
  "inspector.electrical.edit": "Einstellungen ändern",
  "inspector.dc_supply.title": "DC-Netzteil",
  "inspector.dc_supply.setup": "Als DC-Netzteil einrichten",
  "inspector.dc_supply.input": "Nenneingangsspannung (V)",
  "inspector.dc_supply.output": "Ausgangsspannung (V)",
  "inspector.dc_supply.current": "Ausgangsstromgrenze (A)",
  "inspector.dc_supply.efficiency": "Wirkungsgrad (%, optional)",
  "inspector.dc_supply.hint": "Die tatsächliche Ausgangseinstellung eingeben. Ohne Wirkungsgrad wird mit geschätzten 90 % gerechnet.",
  "inspector.dc_supply.range": "Ausgangsbereich (V, optional)",
  "inspector.dc_supply.range_display": "Ausgangsbereich",
  "inspector.dc_supply.range_min": "Min. (V)",
  "inspector.dc_supply.range_max": "Max. (V)",
  "inspector.dc_supply.range_hint": "Beide Grenzen eingeben und die Ausgangsspannung im Bereich halten.",
  "inspector.custom_fuse.setup": "Als DC-Sicherung einrichten",
  "inspector.custom_fuse.hint": "Typ und Nennstrom der eingebauten Sicherung eingeben. Die Anschlussbelastbarkeit ist kein Sicherungsnennstrom.",
  "inspector.custom_fuse.family": "Sicherungstyp",
  "inspector.custom_fuse.rating": "Sicherungsnennstrom (A)",
  "inspector.custom_fuse.apply": "Sicherungseinstellungen anwenden",
  "inspector.custom_fuse.title": "Sicherungsschutz",
  "inspector.custom_fuse.unconfigured": "Wenn dieses Bauteil eine Sicherung ist, Typ und Nennstrom angeben, damit Crimp den Schutz prüfen kann.",
  "inspector.custom_fuse.cancel": "Abbrechen",
  "inspector.custom_dc_fuse_block.title": "DC-Sicherungsblock",
  "inspector.custom_dc_fuse_block.setup": "Als DC-Sicherungsblock einrichten",
  "inspector.custom_dc_fuse_block.connections": "{input} mit {count} abgesicherten Ausgängen verbinden.",
  "inspector.custom_dc_fuse_block.ratings": "Unten jede eingebaute Sicherung angeben.",
  "inspector.custom_dc_fuse_block.slot_rating": "Sicherungsnennstrom für {label}",
  "inspector.shunt.bank": "Angeschlossene Batteriebank",
  "inspector.shunt.current": "Simulierter Strom",
  "inspector.shunt.discharging": "{amps} Entladung",
  "inspector.shunt.charging": "{amps} Ladung",
  "inspector.shunt.idle": "0 A",
  "inspector.shunt.unavailable": "Nicht verfügbar",
  "inspector.shunt.simulate_hint": "Zu Simulieren wechseln, um den Strom zu sehen.",
  "inspector.shunt.estimate": "Geschätzter Strom durch diesen Shunt.",
  "inspector.battery_fuse.title": "Sicherung am Batteriepol",
  "inspector.battery_fuse.blurb":
    "MRBF / MEGA / ANL / Class T direkt am Pluspol verschraubt. Jedes vom Pol abgehende Kabel übernimmt diesen Nennwert; ABYC E-11 §11.10 (Sicherung innerhalb 175 mm) ist automatisch erfüllt.",
  "inspector.battery_fuse.rating_label": "Nennwert",
  "inspector.battery_fuse.rating_none": "Keine Sicherung am Pol",
  "inspector.battery_fuse.type_label": "Typ (für AIC-Prüfung)",
  "inspector.battery_fuse.type_unknown": "Unbekannt",
  "inspector.rotate": "90° drehen",
  "inspector.rotate_title": "Körper 90° im Uhrzeigersinn drehen",
  "inspector.remove_from_zone": "Aus Zone entfernen",
  "inspector.remove_from_zone_title":
    "Komponente aus ihrer Zone lösen (sie bleibt an Ort und Stelle)",
  "inspector.delete_component": "Komponente löschen",

  // ── Inspector — zone detail ────────────────────────────
  "zone.eyebrow": "Zone",
  "zone.fallback_label": "Zone",
  "zone.label_field": "Bezeichnung",
  "zone.label_placeholder": "z. B. Motorraum",
  "zone.engine_bay": "Motorraum-Zone",
  "zone.engine_bay_hint":
    "Kabel, die vollständig in dieser Zone verlaufen, werden für 50 °C Umgebungstemperatur dimensioniert (ABYC E-11 Tabelle V, 105 °C-Kabel auf 0,82 derated).",
  "zone.distances": "Abstand zu Zonen",
  "zone.no_other": "Noch keine anderen Zonen.",
  "zone.distances_hint":
    "Legt die Länge neuer Kabel zwischen den beiden Zonen fest — und verschiebt die vorhandenen um dieselbe Differenz.",
  "zone.delete": "Zone löschen",
  "zone.size": "{w} × {h} px",

  // ── Inspector — junction detail ────────────────────────
  "junction.eyebrow": "Spleißpunkt",
  "junction.title": "Spleiß",
  "junction.body":
    "Ein Spleißpunkt ist eine Kabelverbindung — beliebig viele Kabel können hier zusammenlaufen. Zieh eine weitere Klemme darauf, um ein zusätzliches Kabel anzuschließen.",
  "junction.connections": "Verbindungen",
  "junction.cables_connected": "Angeschlossene Kabel",
  "junction.delete": "Spleißpunkt löschen",
  "junction.convert_to_busbar": "In Sammelschiene umwandeln",
  "junction.convert_pos": "+ Schiene",
  "junction.convert_pos_title": "Spleißpunkt durch eine Plus-Sammelschiene ersetzen.",
  "junction.convert_neg": "− Schiene",
  "junction.convert_neg_title": "Spleißpunkt durch eine Minus-Sammelschiene ersetzen.",
  "junction.convert_ground": "Masse",
  "junction.convert_ground_title":
    "Spleißpunkt durch eine Chassis-Massepunkt-Schiene ersetzen.",
  "junction.convert_fusebox": "Sicherungskasten",
  "junction.convert_fusebox_title":
    "Spleißpunkt durch einen Sicherungskasten ersetzen — eine Plus-Sammelschiene, die den Sicherungswert jedes Ausgangs am Bolzen anzeigt.",
  "junction.convert_mixed_warning":
    "Dieser Spleißpunkt verbindet sowohl Plus- als auch Minuskabel. Trenne eine Polarität, bevor du ihn in eine Sammelschiene umwandelst.",
  "inspector.busbar.title": "Sammelschiene",
  "inspector.busbar.studs": "Bolzen",
  "inspector.busbar.fewer": "Bolzen entfernen",
  "inspector.busbar.more": "Bolzen hinzufügen",
  "inspector.busbar.add_output": "Abgang hinzufügen",
  "inspector.busbar.convert_to_fusebox": "In Sicherungskasten umwandeln",
  "inspector.busbar.convert_to_fusebox_title":
    "Sammelschiene als Sicherungskasten darstellen — der Sicherungswert jedes angeschlossenen Kabels wird am Bolzen angezeigt.",
  "inspector.busbar.convert_to_busbar": "In Sammelschiene umwandeln",
  "inspector.busbar.convert_to_busbar_title":
    "Als einfache Sammelschiene ohne Sicherungspillen darstellen.",
  "inspector.busbar.convert_to_junction": "In Spleißpunkt umwandeln",
  "inspector.busbar.convert_to_junction_title":
    "Sammelschiene auf einen einzigen Spleißpunkt zusammenführen. Alle Kabel treffen sich an einem Punkt.",

  // ── Inspector — cable detail ───────────────────────────
  "cable.eyebrow": "Kabel",
  "cable.label": "Bezeichnung",
  "cable.label_placeholder": "z. B. C-12, Stb Hausbatt +",
  "cable.label_help":
    "Frei wählbares Etikett am Kabel — hilfreich, um den Plan dem realen Kabelbaum zuzuordnen.",
  "cable.label_color": "Etikettfarbe",
  "cable.label_color.kraft": "Kraftpapier",
  "cable.label_color.white": "Weiß",
  "cable.label_color.yellow": "Gelb",
  "cable.label_color.red": "Rot",
  "cable.label_color.blue": "Blau",
  "cable.label_color.green": "Grün",
  "cable.label_color.slate": "Schiefer",
  "cable.length": "Länge",
  "cable.calculated": "Berechnet",
  "cable.peak_current": "Spitzenstrom",
  "cable.continuous_current": "Dauerstrom",
  "cable.nominal_voltage": "Nennspannung",
  "cable.voltage_drop": "Spannungsabfall",
  "cable.wire_size": "Kabelquerschnitt",
  "cable.wire_ampacity": "Kabel-Strombelastbarkeit",
  "cable.suggested_fuse": "Empfohlene Sicherung",
  "cable.sizing_cranking":
    "Ausgelegt für {amp} kurzzeitigen Startstrom, mit einem Spannungsabfallziel von 2 % (Anlasserkreis).",
  "cable.sizing_general": "Ausgelegt für {amp} Dauer bei {pct} % Spannungsabfall.",
  "cable.sizing_general_at_v":
    "Ausgelegt für {amp} Dauer bei {volts} V, {pct} % Spannungsabfall-Budget.",
  "cable.sizing_ac":
    "Die Strombelastbarkeitsprüfung verwendet den AC-Klemmen-Nennstrom von {amp}. Stromkreis-Nennstrom, Installation und Schutz prüfen.",
  "cable.sizing_ac_unknown": "Der AC-Nennstrom fehlt. Stromkreisstrom, Kabelquerschnitt und Schutz prüfen.",
  "cable.ac_rating": "Verwendeter AC-Nennstrom",
  "cable.ac_rating_missing": "Nicht angegeben",
  "cable.ac_voltage_drop_unavailable": "Für AC nicht berechnet",
  "cable.ac_protection_label": "AC-Schutz",
  "cable.ac_protection": "Schutz des AC-Stromkreises prüfen",
  "cable.no_fuse_recommendation": "Noch keine — keine bekannte Last auf dieser Leitung",
  "cable.signal_fuse_unverified": "Signalleitungen benötigen einen komponentenspezifischen Schutz. Wählen Sie den Wert nach Herstelleranleitung; die Strombelastbarkeit der Leitung allein genügt nicht.",
  "cable.starting_fuse_unverified": "Für einen angeschlossenen Anlasser fehlt der Startstrom oder ist null. Wählen Sie einen dokumentierten Sicherungswert.",
  "cable.choose_fuse_rating": "Wert nach Komponentenhandbuch wählen",
  "cable.needs_review": "Prüfung erforderlich",
  "cable.terminal_limit_hint": "Die automatisch gewählte Sicherung überschreitet den Nennstrom der Klemme von {rating} A.",
  "cable.smartshunt_fuse_hint": "SmartShunt Vbatt+ verwendet die Hersteller-Zuleitung mit einer trägen 1-A-Sicherung. Prüfen Sie die mitgelieferte Leitung oder das angegebene Temperatursensor-Zubehör.",
  "cable.manufacturer_breaker_hint": "Prüfen Sie den {rating}-A-Motorschutzschalter des Herstellers und seine Auslösekennlinie, bevor Sie diesen Wert verwenden.",
  "cable.add_inline_fuse_optional": "Batteriepolschutz: {rating} A. Ein separater Abzweigschutz hätte {load} A; prüfen Sie, ob die Komponente einen benötigt.",
  "cable.suggested_fuse_opt": "Vorschlag ({rating} A)",
  "cable.protect_to_wire":
    "Keine bekannte Last auf dieser Leitung — eine Sicherung hier wird nach dem Leiter bemessen ({rating} A).",
  "cable.warn_undersized": "⚠ Leiter ist für den geschätzten Strom unterdimensioniert.",
  "cable.warn_vdrop":
    "⚠ Spannungsabfall überschreitet das {pct} %-Budget dieses Stromkreises — dickeren Leiter oder kürzere Strecke wählen.",
  "cable.warn_fuse_trips":
    "⚠ Sicherung löst aus — Komponenten auf dieser Strecke ziehen {load} A Dauer durch eine {rating}-A-Sicherung.",
  "cable.warn_wire_underrated_load":
    "⚠ Leiter zu dünn für nachgelagerte Last — Strecke führt {load} A Dauer, Strombelastbarkeit ist {ampacity} A.",
  "cable.upgrade_to": "Auf {mm2} mm² · AWG {awg} aufrüsten",
  "cable.worst_case": "Worst Case über alle Simulate-Zustände",
  "cable.continuous": "Dauer",
  "cable.suggested_wire": "Empfohlener Leiter",
  "cable.triggered_by": "Ausgelöst durch — {scenarios}",
  "cable.add_fuse": "Sicherung hinzufügen",
  "cable.add_inline_fuse": "Inline-Sicherung hinzufügen",
  "cable.add_inline_fuse_hint":
    "Batterie-MRBF ({rating} A) ist zu groß, um diesen Leiter allein zu schützen. {load} A Inline-Sicherung nahe der Last hinzufügen.",
  "cable.fuse_from_slot":
    "Sicherungswert kommt vom Slot der Sicherungsleiste am Endpunkt dieses Kabels.",
  "cable.protected_by": "Kabel ist durch eine {rating}-A-Sicherung nahe der Quelle geschützt.",
  "cable.mirrors_battery_mrbf":
    "Diese {rating} A entsprechen der Batterie-MRBF — dieselbe Sicherung, nicht zwei. Auf {load} A senken, um diesen Leiter wirklich zu schützen.",
  "cable.protect_with": "Schütze dieses Kabel mit der empfohlenen {rating}-A-Sicherung.",
  "cable.toggle_negative": "Minus-/Massekabel werden nie abgesichert (ABYC).",
  "cable.toggle_slot":
    "Die Sicherung dieses Kabels wird vom verbundenen Slot der Sicherungsleiste bestimmt.",
  "cable.series_link": "Bewusste Reihenverbindung",
  "cable.series_link.on_description":
    "Als Reihenbrücke markiert (12 V → 24 V-Batterien oder Panele in Reihe). Die Polaritätsprüfung ist für dieses Kabel deaktiviert; alle anderen Diagnosen gelten weiterhin.",
  "cable.series_link.off_description":
    "Nur bei gleichartigen Zellen verwenden (zwei 12 V-Batterien → 24 V-Bank oder Solarpanele in Reihe für höhere Voc am MPPT). Sonst bleibt + → − ein Kurzschluss.",
  "cable.manual_override": "Manuelle Vorgabe",
  "cable.wire_size_label": "Kabelquerschnitt",
  "cable.fuse_rating_label": "Sicherungswert",
  "cable.fuse_family_recommendation_unavailable": "Kein automatisch vorgeschlagener Nennstrom passt zur gewählten Sicherungsfamilie und zum berechneten Bedarf. Prüfen Sie das Schutzkonzept und wählen Sie ein geeignetes Gerät oder eine passende Familie.",
  "cable.fuse_type_label": "Sicherungstyp",
  "cable.vdrop_class_label": "Spannungsfall-Klasse",
  "cable.vdrop_class.auto": "Auto ({pct} % Abfall)",
  "cable.vdrop_class.auto_plain": "Auto",
  "cable.vdrop_class.critical": "Kritisch (3 % Abfall)",
  "cable.vdrop_class.non_critical": "Unkritisch (10 % Abfall)",
  "cable.vdrop_class.help":
    "ABYC E-11.14.2.6: 3 % für Verteiler-Hauptzuleitungen, Bilgenpumpen, Elektronik und Positionslaternen, 10 % für allgemeine Beleuchtung. An jedem Kabel eines Strangs setzbar — es stuft den ganzen Stromkreis um.",
  "cable.auto_fuse_type_opt": "Auto",
  "cable.auto_fuse_type_opt_inherited": "Auto ({type})",
  "cable.auto_wire_opt": "Auto ({mm2} mm²)",
  "cable.auto_fuse_opt": "Auto ({rating} A)",
  "cable.reset_layout": "Neu verlegen",
  "cable.reset_layout_title": "Dieses Kabel sauber neu verlegen (R)",
  "cable.delete": "Kabel löschen",

  // ── Inspector — multi detail ───────────────────────────
  "multi.eyebrow": "Mehrfachauswahl",
  "multi.items": "{count} Objekte",
  "multi.components": "Komponenten",
  "multi.cables": "Kabel",
  "multi.add_fuse": "Sicherung hinzufügen",
  "multi.add_fuse_each": "Sicherung an jedes Kabel hinzufügen",
  "multi.protected_one": "{count} Kabel mit empfohlener Sicherung geschützt.",
  "multi.protected_many": "{count} Kabel mit empfohlener Sicherung geschützt.",
  "multi.protect_one": "Schütze {count} Kabel mit dem empfohlenen Sicherungswert.",
  "multi.protect_many": "Schütze {count} Kabel mit dem empfohlenen Sicherungswert.",
  "multi.delete": "Auswahl löschen",

  // ── Comments ───────────────────────────────────────────
  "comments.title": "Kommentare",
  "comments.empty": "Noch keine Kommentare.",
  "comments.read_only":
    "Du siehst dies im Lesemodus. Bitte den Eigentümer um einen Kommentar- oder Bearbeitungslink.",
  "comments.your_name": "Dein Name",
  "comments.placeholder_node": "Frage zu dieser Komponente oder Zone…",
  "comments.placeholder_cable": "Frage zu diesem Kabel…",
  "comments.post": "Senden",
  "comments.resolve": "Erledigt",
  "comments.name_required": "Trag deinen Namen ein, damit andere wissen, wer kommentiert hat.",
  "comments.close": "Schließen",
  "comments.popover_aria": "Kommentare",
  "comments.popover_title_node": "Notizen zu dieser Komponente",
  "comments.popover_title_cable": "Notizen zu diesem Strang",
  "comments.popover_first_node":
    "Erste Notiz an dieser Komponente? Stell eine Frage oder zeig, was du ändern würdest.",
  "comments.popover_first_cable":
    "Erste Notiz an diesem Strang? Hinterfrage die Sicherung, den Querschnitt, die Führung.",
  "comments.popover_signin_to_post":
    "Melde dich an, um eine Notiz an genau diese Stelle des Builds zu hängen.",
  "comments.popover_kbd_hint": "⌘+↵ zum Senden",
  "comments.resolved_toggle": "{count} {label}",
  "comments.resolved_singular": "erledigte Notiz",
  "comments.resolved_plural": "erledigte Notizen",
  "comments.resolved_date": "erledigt {date}",
  "comments.pin": "Diese Notiz anheften",
  "comments.unpin": "Lösen",
  "comments.pinned_eyebrow": "Vom Autor angeheftet",

  // ── Feedback button ────────────────────────────────────
  "feedback.btn": "Feedback",
  "feedback.btn_title":
    "Feedback senden — Bug melden, Idee teilen oder etwas anderes",
  "feedback.type.bug": "Bug",
  "feedback.type.idea": "Idee",
  "feedback.type.other": "Entwickler kontaktieren",
  "feedback.type_aria": "Feedback-Typ",
  "feedback.placeholder.bug": "Was ist schiefgelaufen?",
  "feedback.placeholder.idea": "Was fehlt? Was würdest du gerne sehen?",
  "feedback.placeholder.other": "Schreib dem Entwickler eine Nachricht…",
  "feedback.heading.bug": "Bug melden",
  "feedback.heading.idea": "Idee teilen",
  "feedback.heading.other": "Entwickler kontaktieren",
  "feedback.picker.heading": "Was hast du im Sinn?",
  "feedback.picker.bug_desc": "Wir hängen dein System an, damit wir es reproduzieren können",
  "feedback.picker.idea_desc": "Geht direkt ans Team, damit wir es einplanen können",
  "feedback.picker.other_desc": "Private E-Mail an den Entwickler",
  "feedback.attached_blurb":
    "Eine Session-Aufzeichnung und dein aktuelles System werden automatisch angehängt.",
  "feedback.other_blurb":
    "Wird privat an den Entwickler gesendet. Du erhältst keine automatische Antwort — hinterlasse unten deine E-Mail, falls du eine möchtest.",
  "feedback.contact.email_placeholder": "E-Mail (optional)",
  "feedback.contact.name_placeholder": "Name (optional)",
  "feedback.contact.optional_blurb":
    "Hinterlasse deine E-Mail-Adresse, falls du eine Antwort möchtest, wenn das gelöst ist.",
  "feedback.region_banner": "Ziehen, um einen Bereich auszuwählen",
  "feedback.esc_cancel": "zum Abbrechen",
  "feedback.pick_banner": "Klicke auf ein Element, um es anzuhängen",
  "feedback.remove_screenshot": "Screenshot entfernen",
  "feedback.remove_item": "Element entfernen",
  "feedback.select_item": "Element auswählen",
  "feedback.add_screenshot": "Screenshot hinzufügen",
  "feedback.cancel": "Abbrechen",
  "feedback.send": "Senden",
  "feedback.sending": "Senden…",
  "feedback.fallback_component": "Komponente",
  "feedback.fallback_zone": "Zone",
  "feedback.fallback_junction": "Spleißpunkt",
  "feedback.cable_label": "Kabel: {from} → {to}",

  // ── Canvas (minimap) ───────────────────────────────────
  "canvas.minimap_aria": "System-Minikarte",

  // ── Diagnostics — fallback labels ──────────────────────
  "diag.label.zone": "Zone",
  "diag.label.junction": "Spleißpunkt",
  "diag.label.component": "Komponente",
  "diag.label.load": "Verbraucher",

  // ── Diagnostics — AC, controls and PV circuits ─────────
  "diag.control-coil-voltage-mismatch.fix": "Verwenden Sie die vorgeschriebene Spulenversorgung oder ein Relais mit der richtigen Nennspulenspannung. Prüfen Sie beide Spulenleitungen und den Steuerkreis.",
  "diag.control-coil-voltage-mismatch.message": "Die Anschlüsse 85 und 86 von {label} können dieselbe angegebene Quelle mit {actual} V erreichen. Dies liegt außerhalb der geprüften Nennspannungsklasse der Spule von {rated} V. Betriebsspannungstoleranz und Transienten müssen weiterhin anhand der Herstellerangaben geprüft werden.",
  "diag.control-coil-voltage-mismatch.title": "Versorgungsspannung der Relaisspule weicht ab",
  "diag.control-contact-voltage-unverified.fix": "Verwenden Sie Kontakte, die für die tatsächliche Versorgungsspannung und den geschalteten Strom ausgelegt sind. Prüfen Sie alle vorgesehenen Stellungen und die Installationsgrenzen des Herstellers.",
  "diag.control-contact-voltage-unverified.message": "{label} ist mit einer angegebenen Gleichspannungsquelle von {actual} V verbunden. Die geprüfte Kontaktbemessung beträgt {rated} V DC. Die Quelle liegt außerhalb der geprüften Nennspannungsklasse oder Höchstspannung.",
  "diag.control-contact-voltage-unverified.title": "Spannung des Schaltkontakts prüfen",
  "diag.control-state-coverage-unverified.fix": "Alle zulässigen Kombinationen der Kontaktstellungen prüfen. Fehlende weitere Warnungen bestätigen nicht, dass ungeprüfte Kombinationen verifiziert wurden.",
  "diag.control-state-coverage-unverified.message": "Diese Zeichnung überschreitet die Grenze von 64 Kombinationen für die gemeinsame Prüfung der Kontaktstellungen. Fehlerprüfungen für alternative Stellungen sind unvollständig.",
  "diag.control-state-coverage-unverified.title": "Kombinationen der Kontaktstellungen prüfen",
  "diag.ac-source-neutral-shared.title": "AC-Quellen teilen einen Neutralleiterpfad",
  "diag.ac-source-neutral-shared.message": "Neutralleiter unterschiedlicher AC-Quellen sind verbunden. Die Zeichnung belegt nicht, ob Neutralleiterumschaltung und Erdverbindungen diese Anordnung erlauben.",
  "diag.ac-source-neutral-shared.fix": "Quellentrennung und Neutralleiterumschaltung in jedem Versorgungsmodus anhand der Anleitungen für Quellen und Umschalter prüfen.",
  "diag.control-contact-overload.title": "Schaltstellung überlastet Kontakt oder Kabel",
  "diag.control-contact-overload.message": "{label}, Stellung {position}: Der vollständige Stromkreis führt {current} A durch einen Kontakt oder ein angeschlossenes Kabel mit {maxCurrent} A Nennwert. Diese Stellung wird auch geprüft, wenn sie aktuell inaktiv ist.",
  "diag.control-contact-overload.fix": "Geeigneten Schütz oder Schalter und ausreichend belastbares Kabel verwenden. Dauerstrom, Anlaufstrom und induktive Lastanforderungen prüfen.",
  "diag.ac-conductor-mismatch.title": "AC-Leiter sind vertauscht oder verbunden",
  "diag.ac-conductor-mismatch.message": "Ein verdrahteter oder wählbarer Pfad verbindet die Phase mit Neutralleiter, Schutzleiter oder einer anderen Phase.",
  "diag.ac-conductor-mismatch.fix": "Den markierten Pfad verfolgen und L-, N- und PE-Anschlüsse vor dem Einschalten korrigieren.",
  "diag.ac-sources-paralleled.title": "AC-Quellenausgänge sind miteinander verbunden",
  "diag.ac-sources-paralleled.message": "Ein verdrahteter oder wählbarer Phasenpfad verbindet mehrere AC-Ausgänge. Die Zeichnung belegt keine Synchronisierung oder freigegebenen Parallelbetrieb.",
  "diag.ac-sources-paralleled.fix": "Den vorgesehenen Quellenumschalter verwenden und Umgehungen entfernen oder vor dem Anschluss ein vom Hersteller freigegebenes Parallelsystem dokumentieren.",
  "diag.ac-source-pair-mismatch.title": "AC-Phase und Neutralleiter stammen aus verschiedenen Quellen",
  "diag.ac-source-pair-mismatch.message": "L und N für {label} erreichen nicht denselben AC-Quellenanschluss.",
  "diag.ac-source-pair-mismatch.fix": "Phase und Neutralleiter jeder Versorgung gemeinsam durch den vorgesehenen Quellenumschalter und die Schutzeinrichtungen führen.",
  "diag.ac-circuit-incomplete.title": "AC-Versorgungsleiter sind unvollständig",
  "diag.ac-circuit-incomplete.message": "Die L- und N-Pfade für {label} bilden keine vollständige Verbindung zu einer AC-Quelle.",
  "diag.ac-circuit-incomplete.fix": "L und N zur selben Quelle vervollständigen. Der Schutzleiter darf den Neutralleiter nicht ersetzen.",
  "diag.ac-earth-port-ambiguous.title": "Schutzleiteranschluss nicht eindeutig",
  "diag.ac-earth-port-ambiguous.message": "Im Schaltplan ist nicht erkennbar, welcher Schutzleiteranschluss an {label} zum Wechselstromkreis mit dem Neutralleiter {port} gehört.",
  "diag.ac-earth-port-ambiguous.fix": "Prüfen Sie den Geräteanschlussplan und kennzeichnen Sie den Schutzleiteranschluss für diesen Wechselstromkreis.",
  "diag.ac-source-earth-missing.title": "Schutzleiter erreicht die AC-Quelle nicht",
  "diag.ac-source-earth-missing.message": "Im Schaltplan fehlt ein durchgängiger Schutzleiter von {label} ({loadTerminal}) zur versorgenden Wechselstromquelle {source} ({sourceTerminal}).",
  "diag.ac-source-earth-missing.fix": "Den PE-Anschluss der Quelle über das Erdungssystem herstellen. Eine Verbindung zu DC-Minus allein belegt keine Landstrom-Erdverbindung.",
  "diag.ac-protection-missing.title": "AC-Überstromschutz fehlt oder wird umgangen",
  "diag.ac-protection-missing.message": "Eine Quelle kann {label} versorgen, ohne einen erkannten AC-Überstromschutzpfad zu durchlaufen.",
  "diag.ac-protection-missing.fix": "Passenden AC-Leitungsschutz ergänzen oder prüfen und Umgehungen entfernen. Eingetragene DC-Sicherungswerte und GFCI-Geräte belegen keinen AC-Überstromschutz.",
  "diag.ac-protection-exceeds-rating.title": "AC-Schutz überschreitet den Gerätenennstrom",
  "diag.ac-protection-exceeds-rating.message": "{label} ist für {rating} A ausgelegt, aber in einem Versorgungspfad fehlt ein erkannter Überstromschutz mit höchstens diesem Wert.",
  "diag.ac-protection-exceeds-rating.fix": "Nennwerte der installierten Geräte und Schutzschalter in jedem möglichen Versorgungspfad prüfen.",
  "diag.ac-load-exceeds-rating.title": "Angegebene AC-Lasten überschreiten einen Nennstrom",
  "diag.ac-load-exceeds-rating.message": "Die angeschlossene Eingangsleistung erfordert mindestens {current} A bei Nennspannung, mehr als der Nennstrom von {rating} A für Stromkreis oder Gerät.",
  "diag.ac-load-exceeds-rating.fix": "Gleichzeitige Lasten reduzieren oder den Stromkreis neu auslegen. Leistungsfaktor und Einschaltstrom können den Strom über diese Untergrenze erhöhen.",
  "diag.ac-voltage-mismatch.title": "AC-Nennspannungen stimmen nicht überein",
  "diag.ac-voltage-mismatch.message": "Für {label} sind {expected} V angegeben, eine angeschlossene oder wählbare Quelle gibt jedoch {actual} V an.",
  "diag.ac-voltage-mismatch.fix": "Tatsächlichen Eingangsspannungsbereich und Quellenkonfiguration vor dem Anschluss prüfen.",
  "diag.ac-neutral-earth-link-unverified.title": "Gezeichnete Neutralleiter-Erdverbindung prüfen",
  "diag.ac-neutral-earth-link-unverified.message": "Ein Kabel verbindet AC-Neutralleiter und Schutzleiter. Die richtige Position hängt von Quelle, Umschaltung und internen Erdungsrelais ab.",
  "diag.ac-neutral-earth-link-unverified.fix": "Die Verbindung in jedem Betriebsmodus anhand der Herstelleranleitung prüfen. Keine N/PE-Verbindung auf bloßen Verdacht ergänzen oder entfernen.",
  "diag.ac-profile-unverified.title": "Bearbeitete AC-Ausrüstung muss geprüft werden",
  "diag.ac-profile-unverified.message": "Elektrische Änderungen an {label} entsprechen nicht mehr dem Schutz- und Anschlussprofil im Katalog.",
  "diag.ac-profile-unverified.fix": "Geänderte Kontakte, Anschlüsse und Nennwerte anhand der installierten Ausrüstung prüfen. Der Schutz des ursprünglichen Profils darf nicht vorausgesetzt werden.",
  "diag.ac-protection-reversed.title": "AC-Schutzeinrichtung wird von der Lastseite gespeist",
  "diag.ac-protection-reversed.message": "Eine Quelle erreicht einen geschützten Ausgang von {label}, während der LINE-Eingang keine eigene Quelleneinspeisung hat.",
  "diag.ac-protection-reversed.fix": "Die Versorgung gemäß Herstelleranleitung an LINE und nachgeschaltete Leitungen an die vorgesehenen LOAD- oder Steckdosenanschlüsse anschließen.",
  "diag.ac-leakage-bypassed.title": "AC-Fehlerstromschutz wird umgangen",
  "diag.ac-leakage-bypassed.message": "Phase oder Neutralleiter können einen geschützten Ausgang von {label} erreichen, ohne dessen Schutzkontakte zu durchlaufen.",
  "diag.ac-leakage-bypassed.fix": "L und N verfolgen und die unbeabsichtigte Umgehung entfernen. Den Schutzleiter durchgängig erhalten.",
  "diag.ac-leakage-unverified.title": "AC-Fehlerstromschutz prüfen",
  "diag.ac-leakage-unverified.message": "Die Versorgungspfade zu {label} belegen keinen erkannten Fehlerstromschutz für L und N.",
  "diag.ac-leakage-unverified.fix": "Erforderlichen RCD-, ELCI- oder GFCI-Typ, Empfindlichkeit und Einbauposition prüfen. Ein gewöhnlicher Leitungsschutzschalter belegt keinen Fehlerstromschutz.",
  "diag.ac-state-coverage-unverified.title": "AC-Schaltkombinationen müssen geprüft werden",
  "diag.ac-state-coverage-unverified.message": "Diese Zeichnung enthält mehr Schaltkombinationen, als die AC-Prüfung vollständig untersuchen kann.",
  "diag.ac-state-coverage-unverified.fix": "Jede zulässige Quellenkombination und Verriegelung prüfen. Das Fehlen weiterer Befunde bestätigt nicht die ungeprüften Kombinationen.",
  "diag.ac-design-unverified.title": "Weitere AC-Installationsprüfungen erforderlich",
  "diag.ac-design-unverified.message": "Die Zeichnung prüft Leiterverbindungen und angegebene Nennwerte; vollständiger AC-Schutz und Installationssicherheit werden nicht bestätigt.",
  "diag.ac-design-unverified.fix": "Installierte Nennwerte, Kabelverlegung, Fehlerstrom, Schutzkoordination, Fehlerstromschutz und N/PE-Verbindungen in jedem Versorgungsmodus prüfen. Leistungsfaktor, Einschaltstrom und automatische Auslösungen werden nicht simuliert.",
  "diag.control-relay-manual.title": "Relaisstellung wird manuell gewählt",
  "diag.control-relay-manual.message": "{label}: Die Kontaktstellung wird manuell gewählt und folgt nicht der Spulenverdrahtung. Spulenstrom, Betätigung und Schalttransienten werden nicht berechnet. Die Auslegung des angeschlossenen DC-Stromkreises bleibt vorläufig; automatische Kabel- und Sicherungsmengen werden nicht ausgegeben.",
  "diag.control-relay-manual.fix": "Spulenkreis und jede vorgesehene Kontaktstellung anhand der Anleitungen für Relais und Last prüfen. Gemeinsame Zuleitungen, Rückleitungen, alle Zweige und deren Schutz überprüfen.",
  "diag.control-coil-incomplete.title": "Relaisspule ist unvollständig angeschlossen",
  "diag.control-coil-incomplete.message": "Bei {label} ist nur einer der beiden Spulenanschlüsse verbunden. Die gezeichnete Steuerung zeigt keinen vollständigen Spulenkreis.",
  "diag.control-coil-incomplete.fix": "Spulenzuleitung und Rückleiter einzeichnen. Spannung, Polarität und Absicherung anhand der Relaisanleitung prüfen.",
  "diag.control-contact-bypass.title": "Externe Verdrahtung überbrückt einen Kontakt",
  "diag.control-contact-bypass.message": "Bei {label} besteht außerhalb des Bauteils ein leitender Pfad vom gemeinsamen zum geschalteten Anschluss. Der Kontakt kann diesen Pfad möglicherweise nicht trennen.",
  "diag.control-contact-bypass.fix": "Markierten Stromkreis prüfen und unbeabsichtigte Brücken oder zusätzliche Einspeisungen entfernen.",
  "diag.control-model-unverified.title": "Geänderte Kontaktanordnung prüfen",
  "diag.control-model-unverified.message": "{label} entspricht nicht mehr dem geprüften Kontaktschema. Die beibehaltene Katalog-ID bestätigt weder Kontakttrennung noch Funktion. Die Auslegung des angeschlossenen DC-Stromkreises bleibt vorläufig; automatische Kabel- und Sicherungsmengen werden nicht ausgegeben.",
  "diag.control-model-unverified.fix": "Jeden Kontakt, Spulenanschluss und jede Schalterstellung in der bearbeiteten Definition prüfen. Danach den angeschlossenen DC-Stromkreis, gemeinsame Zuleitungen, Rückleitungen und Schutz in jeder vorgesehenen Stellung prüfen.",
  "diag.control-state-sizing-unverified.title": "Jede Schalterstellung prüfen",
  "diag.control-state-sizing-unverified.message": "{label}: Stromschätzungen folgen einer ausgewählten Kontaktstellung und bestätigen keine Auslegung über alle Stellungen. Die Auslegung des angeschlossenen DC-Stromkreises bleibt vorläufig; automatische Kabel- und Sicherungsmengen werden nicht ausgegeben. Die Federrückstellung wird nicht zeitlich simuliert.",
  "diag.control-state-sizing-unverified.fix": "Den gesamten angeschlossenen DC-Stromkreis in jeder vorgesehenen Stellung einschließlich gedrücktem Taster prüfen. Gemeinsame Zuleitungen, Rückleitungen, alle Zweige und deren Schutz überprüfen.",
  "diag.pv-string-protection-missing.title": "PV-Strangschutz ist nicht eingezeichnet",
  "diag.pv-string-protection-missing.message": "{label}, Strang {string}: Ein Pfad vom Solarmodul erreicht den Sammler ohne deklarierte einzelne gPV-Sicherung. Eine gemeinsame Ausgangssicherung schützt die Stränge nicht einzeln.",
  "diag.pv-string-protection-missing.fix": "Erforderlichen Strangschutz anhand von Modul-Isc, maximaler Seriensicherung und Rückstrom paralleler Stränge prüfen und das nötige einzelne Schutzgerät einzeichnen.",
  "diag.pv-string-protection-bypass.title": "Verdrahtung überbrückt die Strangsicherung",
  "diag.pv-string-protection-bypass.message": "{label}, Strang {string}: Ein paralleler leitender Pfad umgeht die deklarierte Strangsicherung.",
  "diag.pv-string-protection-bypass.fix": "Unbeabsichtigte Überbrückung entfernen. Jede erforderliche Strangsicherung muss vor der gemeinsamen Plusschiene in Reihe liegen.",
  "diag.pv-string-fuse-unrated.title": "PV-Sicherungswahl ist unvollständig oder widersprüchlich",
  "diag.pv-string-fuse-unrated.message": "{label}, Strang {string}: Dem Halter fehlt ein passender gültiger gPV-Wert, er überschreitet 30 A oder widerspricht einer angeschlossenen Sicherungsangabe.",
  "diag.pv-string-fuse-unrated.fix": "Kompatible gPV-Sicherung separat angeben. Nennstrom mit Modul und Halter abgleichen und widersprüchliche Angaben korrigieren.",
  "diag.pv-combiner-rating-unverified.title": "Nennwerte des PV-Sammlers prüfen",
  "diag.pv-combiner-rating-unverified.message": "{label} zeigt eine allgemeine Leiteranordnung. Spannungs- und Stromgrenzen von Gehäuse, Schienen und Anschlüssen sowie Modul-Isc, kalte Voc und genaue Sicherungskoordination sind nicht verifiziert.",
  "diag.pv-combiner-rating-unverified.fix": "Tatsächlichen Sammler und Schutzprodukte wählen und deren Grenzen für diese PV-Anlage prüfen. Sicherungshalter sind keine Lasttrennschalter.",
  "diag.pv-string-return-missing.title": "PV-Strangrückleiter fehlt",
  "diag.pv-string-return-missing.message": "{label}, Strang {string}: Der Pluseingang ist verbunden, der zugehörige Minuseingang nicht.",
  "diag.pv-string-return-missing.fix": "Vollständigen Plus- und Minuskreis des Strangs mit richtiger Polarität einzeichnen.",
  "diag.pv-combiner-model-unverified.title": "Geänderte PV-Sammlertopologie prüfen",
  "diag.pv-combiner-model-unverified.message": "{label} entspricht nicht mehr der geprüften Anordnung mit getrennten Plus- und Minusschienen.",
  "diag.pv-combiner-model-unverified.fix": "Geänderte Anschlüsse und interne Verbindungen prüfen, bevor PV- oder Schutzberechnungen verwendet werden.",
  "diag.pv-holder-voltage-exceeded.title": "PV-Strang überschreitet die Halterspannung",
  "diag.pv-holder-voltage-exceeded.message": "{label}, Strang {string}: Der gezeichnete Serienstrang hat {voltage} V deklarierte STC-Voc und überschreitet die {maxVoltage} V DC des Halters bereits vor der Kältekorrektur.",
  "diag.pv-holder-voltage-exceeded.fix": "Ausreichend spannungsfeste Bauteile wählen und die maximale kältekorrigierte Strangspannung prüfen.",

  // ── Diagnostics — topology ─────────────────────────────
  "diag.unfused-positive-run.title": "Ungesicherter Plusleiter zu {label}",
  "diag.unfused-positive-run.message":
    "{label} ist ohne Sicherung im Pfad an einen Batterie+ angeschlossen. Ein Kurzschluss hier kann Kabel schmelzen, bevor irgendetwas auslöst.",
  "diag.unfused-positive-run.fix":
    "Setze nahe der Batterie eine Sicherung in den Plusleiter.",

  "diag.fuse-too-far-from-battery.title": "Sicherungsabstand prüfen: {label}",
  "diag.fuse-too-far-from-battery.message": "Der Plan zeigt {distanceMm} mm bis zur ersten Sicherung. Crimp prüft standardmäßig 175 mm; Installationsausnahmen werden nicht berücksichtigt.",
  "diag.fuse-too-far-from-battery.fix": "Tatsächliche Sicherungsposition und ungesicherte Kabellänge prüfen.",

  "diag.battery-missing-terminal-fuse.title": "Batterieschutz prüfen: {label}",
  "diag.battery-missing-terminal-fuse.message": "Der Plan zeigt eine Batterieleitung über 175 mm ohne Sicherung am Pluspol.",
  "diag.battery-missing-terminal-fuse.fix": "Ungesicherte Länge prüfen und geeigneten Schutz nahe der Batterie vorsehen.",

  "diag.fuse-aic-inadequate.title":
    "Abschaltvermögen der {type}-Sicherung liegt unter dem modellierten Fehlerstrom",
  "diag.fuse-aic-inadequate.message":
    "Das Modell schätzt den Batterie-Kurzschlussstrom auf ~{faultA} A; für diese {type}-Sicherung ist ein Abschaltvermögen von {aic} A hinterlegt. Übersteigt der Fehlerstrom diesen Wert, kann die Sicherung möglicherweise nicht sicher abschalten. Prüfe den tatsächlichen Batterie-Kurzschlussstrom und die Herstellerangabe bei deiner Systemspannung.",
  "diag.fuse-aic-inadequate.fix":
    "Verwende eine Sicherung mit ausreichendem DC-Abschaltvermögen bei der Systemspannung und setze sie möglichst nah an den Batterie-Pluspol. Prüfe die Daten des konkreten Produkts; das Abschaltvermögen kann mit steigender Spannung abnehmen.",
  "diag.fuse-aic-inadequate.title_covered":
    "Koordination der {type}-Sicherung mit vorgeschaltetem Schutz prüfen",
  "diag.fuse-aic-inadequate.message_covered":
    "Das Modell schätzt den Bank-Kurzschlussstrom auf ~{faultA} A, über dem Abschaltvermögen dieser {type}-Sicherung von {aic} A. Auf jedem modellierten Versorgungspfad wurde vorgeschalteter Schutz gefunden; eines der Geräte ist eine {upstreamRating} A {upstreamType} mit einem modellierten Abschaltvermögen von {upstreamAic} A. Dies bestätigt weder die eingebauten Sicherungswerte noch den tatsächlichen Fehlerstrom oder eine geprüfte Reihenkombination. Koordination und Durchlassenergie müssen anhand der Herstellerdaten geprüft werden.",
  "diag.fuse-aic-inadequate.fix_covered":
    "Prüfe den tatsächlichen Bank-Kurzschlussstrom und alle eingebauten Sicherungswerte. Prüfe die Herstellerdaten für die genaue Kombination aus vorgeschalteter {upstreamType} und nachgeschaltetem Schutz bei deiner Systemspannung oder verwende nachgeschalteten Schutz mit eigenständig ausreichendem Abschaltvermögen.",

  "diag.fuse-rating-unavailable.title":
    "Nennstrom der {type}-Sicherung prüfen: {rating} A",
  "diag.fuse-rating-unavailable.message_below":
    "Der angegebene Nennstrom von {rating} A liegt außerhalb des Crimp-Referenzbereichs für {type} ({min}–{max} A). Prüfen Sie das genaue Schutzgerät und seine Eignung für diesen Stromkreis.",
  "diag.fuse-rating-unavailable.message_above":
    "Der angegebene Nennstrom von {rating} A liegt außerhalb des Crimp-Referenzbereichs für {type} ({min}–{max} A). Prüfen Sie das genaue Schutzgerät und seine Eignung für diesen Stromkreis.",
  "diag.fuse-rating-unavailable.fix_below":
    "Wählen Sie ein geprüftes Schutzgerät oder eine geeignete Familie für diesen Stromkreis. Erhöhen Sie den Nennstrom nur nach Prüfung der Leiterbelastbarkeit und des Geräteschutzes.",
  "diag.fuse-rating-unavailable.fix_above":
    "Wählen Sie ein geprüftes Schutzgerät oder eine geeignete Familie für diesen Stromkreis. Erhöhen Sie den Nennstrom nur nach Prüfung der Leiterbelastbarkeit und des Geräteschutzes.",

  "diag.battery-chemistry-unknown.title": "{label} hat keine Chemie gesetzt",
  "diag.battery-chemistry-unknown.message":
    "{label} deklariert weder eine Chemie noch einen Kurzschlussstrom, also sind Fehlerstrom und Ladeakzeptanz unbekannt — nicht null. Die Chemie wird für die Ladeakzeptanz benötigt. Der Fehlerstrom erfordert separate Quellen- oder Bankdaten; die Chemie allein reicht nicht aus.",
  "diag.battery-chemistry-unknown.fix":
    "Gib {label} in seiner Definition eine Chemie — oder einen deklarierten Kurzschlussstrom und maximalen Ladestrom.",

  "diag.fuse-type-unspecified.title":
    "{count} abgesicherte Leitungen ohne Sicherungstyp",
  "diag.fuse-type-unspecified.message":
    "Diese Bank liefert im Kurzschluss bis zu ~{faultA} A. In dieser Größenordnung entscheidet der *Typ* der Sicherung, ob sie auslöst oder verschweißt — die schwächste gängige Familie bricht nur {weakestAic} A. Diese Leitungen haben einen Wert, aber keinen Typ, deshalb greifen weder die Prüfung des Abschaltvermögens noch die der Lieferbarkeit.",
  "diag.fuse-type-unspecified.fix":
    "Wähle je Leitung eine Familie: Class T oder MRBF direkt am Batteriepol, ANL oder MEGA als Inline-Halter, und einen Flachsicherungsblock für kleine Abgänge hinter einer ausreichenden Hauptsicherung.",

  "diag.fix.add_fuse_near_battery": "Sicherung nahe der Batterie ergänzen",
  "diag.fix.upgrade_fuse_to": "Sicherung auf {rating} A erhöhen",
  "diag.fix.upgrade_wire_to": "Auf {mm2} mm² aufrüsten",
  "diag.fix.delete_duplicate_one": "Duplikat löschen",
  "diag.fix.delete_duplicate_many": "{count} Duplikate löschen",

  "diag.load-floating.title": "{label} hat keinen Versorgungspfad",
  "diag.load-floating.message":
    "In der Zeichnung ist kein Pfad von {label} zu einer der beiden Batterieklemmen dargestellt.",
  "diag.load-floating.fix":
    "Die tatsächliche Plusversorgung und Rückleitung zur selben Batterie einschließlich eingebauter Sicherungen darstellen.",

  "diag.load-via-floating-converter.title":
    "{label} hängt von {upstream} ab, das nicht mit einer Batterie verbunden ist",
  "diag.load-via-floating-converter.message":
    "{label} ist mit {upstream} verbunden, aber {upstream} hat keinen Pfad zur Batterie — {label} kann darüber noch nicht eingeschaltet werden.",
  "diag.load-via-floating-converter.fix":
    "Verbinde die Eingangsseite von {upstream} über eine Sicherung mit Batterie+ und der zugehörigen Batterie−-Schiene.",

  "diag.load-no-positive.title": "{label} hat keine Plusversorgung",
  "diag.load-no-positive.message":
    "Der Minus von {label} ist verbunden, aber es führt kein Pfad zu einem Batterie+.",
  "diag.load-no-positive.fix":
    "Verkable den Plus des Verbrauchers über eine Sicherung zur Plusschiene der Batterie.",

  "diag.load-no-return.title": "{label} hat keinen Rückleiter",
  "diag.load-no-return.message":
    "Die Versorgung erreicht {label}, aber der Rückweg zu dieser Quelle fehlt in der Zeichnung.",
  "diag.load-no-return.ground_message": "{label} ist mit einem Massesymbol verbunden, aber dieses Netz hat keinen Rückleiter zur Quelle, die den Verbraucher versorgt.",
  "diag.load-no-return.ground_fix": "Massesymbole sind miteinander verbunden. Zeichne den tatsächlichen Rückleiter dieses Netzes zum Rückleiteranschluss der zugehörigen Versorgungsquelle.",
  "diag.load-no-return.fix":
    "Den tatsächlichen Rückweg vom Verbraucher zum Rückleiteranschluss der zugehörigen Versorgungsquelle darstellen.",

  "diag.battery-bms-missing.title": "{label} benötigt ein externes BMS",
  "diag.battery-bms-missing.message": "Dieses Batteriemodell benötigt ein kompatibles externes BMS. In der Zeichnung ist kein kompatibler Controller an seine Batterieversorgung angeschlossen.",
  "diag.battery-bms-missing.fix": "Das vom Hersteller freigegebene BMS ergänzen und seine erforderliche batterieseitige Versorgung sowie die Batteriedatenkette anschließen.",
  "diag.battery-bms-incompatible.title": "Batterie und BMS sind inkompatibel",
  "diag.battery-bms-incompatible.message": "{bms} ist an {label} angeschlossen, seine angegebene Batteriekompatibilität schließt dieses Modell jedoch aus.",
  "diag.battery-bms-incompatible.fix": "Das für diese Batteriefamilie vorgesehene BMS verwenden; Smart- und NG-Controller sind nicht austauschbar.",
  "diag.bms-disconnect-bypassed.title": "BMS-Abschaltung ist überbrückt",
  "diag.bms-disconnect-bypassed.message": "Ein Leiterpfad verbindet Batterie- und Systemanschlüsse an {label} vorbei; seine eingebaute Abschaltung kann diesen Pfad nicht trennen.",
  "diag.bms-disconnect-bypassed.fix": "Die Umgehung entfernen und das geschützte System durch den vorgesehenen BMS-Systemanschluss führen.",
  "diag.bms-control-unverified.title": "BMS-Abschaltung prüfen",
  "diag.bms-control-unverified.message": "Die Stromverkabelung zeigt nicht, ob das BMS Laden und Verbraucher abschalten kann.",
  "diag.bms-control-unverified.fix": "Messleitungen und Abschaltsteuerung anhand der BMS-Anleitung prüfen.",

  // BMS, service isolation and AC protection checks.
  "diag.ac-source-neutral-floating.title": "Neutralleiter der AC-Quelle ist potentialfrei",
  "diag.ac-source-neutral-floating.message": "{source} hat im dokumentierten Werkszustand einen ungeerdeten Neutralleiter und versorgt {label}; eine quellenseitige N/PE-Verbindung ist nicht eingezeichnet. Der Fehlerstromschutz muss für diese Installation geprüft werden.",
  "diag.ac-source-neutral-floating.fix": "Quelle, Neutralleiterumschaltung, PE-Durchgängigkeit und Fehlerstromschutz in jedem Versorgungsmodus gemeinsam nach Herstelleranleitung prüfen lassen. Keine N/PE-Verbindung auf bloßen Verdacht ergänzen.",
  "diag.ac-neutral-bond-unverified.title": "AC-Neutralleiter und Erdung prüfen",
  "diag.ac-neutral-bond-unverified.message": "Die Zeichnung kann die N/PE-Anordnung für {label}, versorgt durch {source}, einschließlich interner Verbindungen und Quellenumschaltung nicht verifizieren.",
  "diag.ac-neutral-bond-unverified.fix": "Quelle, Neutralleiterumschaltung, PE-Durchgängigkeit und Fehlerstromschutz in jedem Versorgungsmodus gemeinsam nach Herstelleranleitung prüfen lassen. Keine N/PE-Verbindung auf bloßen Verdacht ergänzen.",
  "diag.pv-isolation-missing.title": "Solartrennung fehlt",
  "diag.pv-isolation-missing.message": "{label} bleibt mit den Modulen verbunden, wenn die eingezeichneten Solartrennschalter öffnen.",
  "diag.pv-isolation-missing.fix": "Geeigneten DC-Solartrennschalter vorsehen und Umgehungsverbindungen entfernen.",
  "diag.pv-isolation-unverified.title": "Solartrennung prüfen",
  "diag.pv-isolation-unverified.message": "Crimp kann die Trennung aller Solarzuleitungen zu {label} nicht verifizieren.",
  "diag.pv-isolation-unverified.fix": "Regleranleitung sowie Verdrahtung und Bemessungswerte des Trennschalters prüfen.",
  "diag.inverter-dc-isolation-unverified.title": "Wechselrichter-Trennung prüfen",
  "diag.inverter-dc-isolation-unverified.message": "Crimp kann nicht bestätigen, dass {label} von jeder DC-Quelle getrennt werden kann.",
  "diag.inverter-dc-isolation-unverified.fix": "Trennverfahren in der Wechselrichteranleitung prüfen.",
  "diag.ac-neutral-bond-unverified.message_source": "Außen- und Neutralleiter zur Versorgung von {label} lassen sich nicht zu einer gemeinsamen modellierten AC-Quelle zurückverfolgen. Quelle, N/PE-Anordnung und Fehlerstromschutz bleiben ungeprüft.",
  "diag.ac-neutral-bond-unverified.message_relay": "{source} verwendet ein internes Erdungsrelais. Konfiguration, PE-Durchgängigkeit und Verhalten bei Landstrom-/Generatorumschaltung sind nicht dargestellt; der Schutz von {label} bleibt ungeprüft.",

  "diag.missing-ground.title": "Erdungspfad fehlt: {label}",
  "diag.missing-ground.message": "{terminal} erreicht im Plan keinen Erdungsbezugspunkt.",
  "diag.missing-ground.fix": "Erdungspfad gemäß Geräteanleitung vervollständigen.",

  "diag.parallel-battery-imbalance.title":
    "Parallele Batterien: asymmetrische Kabellängen",
  "diag.parallel-battery-imbalance.message":
    "{labels} teilen sich eine Plusschiene, ihre Kabel unterscheiden sich aber um mehr als 20 % ({min} m vs. {max} m). Die kürzere Strecke übernimmt überproportional viel Lade- und Entladestrom.",
  "diag.parallel-battery-imbalance.fix":
    "Gleiche die Kabellängen von jeder Batterie zur Schiene an, oder nutze die Diagonalbus-Verkabelung (Smartgauge / 'Method 2').",

  "diag.parallel-battery-chemistry-mismatch.title":
    "Parallele Batterien: gemischte Chemien",
  "diag.parallel-battery-chemistry-mismatch.message":
    "{labels} hängen am selben Bus, nutzen aber unterschiedliche Chemien (z. B. LiFePO4 und Blei). Die Chemien ruhen bei unterschiedlichen Leerlaufspannungen und folgen unterschiedlichen Lade-/Entladekurven, sodass eine Bank ständig Strom in die andere drückt — die Bleibank verschleißt schnell, und die LFP-Zellen sehen Ladeprofile, für die sie nicht ausgelegt sind.",
  "diag.parallel-battery-chemistry-mismatch.fix":
    "LiFePO4 nicht mit Blei in derselben Bank parallelisieren. Trenne sie auf isolierte Schienen, verbunden über DC-DC-Lader oder Batteriekombinierer.",

  "diag.reversed-polarity.title": "{aLabel} + an {bLabel} − verbunden",
  "diag.reversed-polarity.message":
    "Dieses Kabel verbindet eine Plusklemme direkt mit einer Minus- oder Masseklemme. Spannung anzulegen ist ein direkter Kurzschluss — Kabel verdampfen, bevor irgendeine Sicherung reagieren kann.",
  "diag.reversed-polarity.fix":
    "Trenne das Kabel. Prüfe die Polarität an beiden Enden, bevor du wieder anschließt. Wenn das ein bewusster Reihenstrang ist (12 V → 24 V-Bank, oder Panele in Serie für höhere Voc), markiere das Kabel als Reihenverbindung, um die Warnung zu deaktivieren.",
  "diag.reversed-polarity.fix-action": "Als Reihenverbindung markieren",

  "diag.ac-onto-dc-rail.title": "AC an DC angeschlossen: {acLabel} → {dcLabel}",
  "diag.ac-onto-dc-rail.message": "Der Plan verbindet einen aktiven AC-Leiter mit einem DC-Anschluss. Stromschlag oder Brand sind möglich.",
  "diag.ac-onto-dc-rail.fix": "AC-DC-Verbindung entfernen. Eigene Anschlussdefinitionen prüfen; erforderlichen Schutzleiter angeschlossen lassen.",

  "diag.charge-on-crank-terminal.title": "{farLabel} hängt an der Anlasserklemme des Motors",
  "diag.charge-on-crank-terminal.message":
    "Dieses Kabel speist {farLabel} von der Anlasserklemme (Starter) des Motors, die Anlassstrom führt. Ladegeräte gehören an den Alt-B+-Ausgang der Lichtmaschine — an der Anlasserklemme wird das Kabel für die Anlassspitze dimensioniert und zeigt bei laufendem Motor keinen Lichtmaschinenstrom.",
  "diag.charge-on-crank-terminal.fix":
    "Verlege das Kabel an den Alt-B+-Ausgang des Motors.",
  "diag.charge-on-crank-terminal.fix-action": "Zu Alt B+ verschieben",

  "diag.suspicious-series-link.title":
    "Reihenverbindung von {aLabel} zu {bLabel} sieht falsch aus",
  "diag.suspicious-series-link.message":
    "Dieses Kabel ist als Reihenverbindung markiert, aber die Enden sind nicht beide Batterien oder beide Solarpanele. Reihenbrücken sind nur sinnvoll zwischen gleichartigen Zellen (12 V → 24 V-Bank, Panele in Reihe für höhere Voc am MPPT). Sonst bleibt + → − ein Kurzschluss.",
  "diag.suspicious-series-link.fix":
    "Entferne die Reihenmarkierung und prüfe die Polarität an beiden Enden.",
  "diag.suspicious-series-link.fix-action": "Reihenverbindung aufheben",

  "diag.voltage-mismatch.title":
    "{label} ist für {declared} ausgelegt, hängt aber an einer {actual}-V-Leitung",
  "diag.voltage-mismatch.message":
    "Diese Komponente ist nur für {declared} angegeben, aber das Kabel landet auf einer {actual}-V-Domäne. Ein 12-V-Gerät an 48 V wird sofort zerstört; ein 48-V-Gerät an 12 V startet nicht. Schließe es über einen passenden Step-Up/Step-Down-Wandler an die richtige Schiene.",
  "diag.voltage-mismatch.fix":
    "Verlege die Komponente an die passende Schiene oder setze einen DC-DC-Wandler zwischen die beiden Spannungen.",

  "diag.cross-voltage-bond.title":
    "{fromLabel} ({fromV} V) direkt mit {toLabel} ({toV} V) verbunden",
  "diag.cross-voltage-bond.message":
    "Dieses Kabel verbindet zwei verschiedene Spannungsdomänen ohne Wandler dazwischen. Jedes Gerät auf der Niederspannungsseite sieht die höhere Spannung — typischerweise zerstört es sich, sobald das System eingeschaltet wird.",
  "diag.cross-voltage-bond.fix":
    "Lösche das Kabel. Um die beiden Schienen zum Laden zu koppeln, setze einen passenden DC-DC-Lader dazwischen.",

  "diag.high-voltage-rail.title":
    "Batterieschiene bei {actual} V überschreitet die 60-V-DC-Berührungssicherheitsgrenze",
  "diag.high-voltage-rail.message":
    "Die Nennspannung dieser Schiene ({actual} V) liegt über der IEC 60364 / ABYC-Grenze für Extra-Niederspannung von {limit} V DC. Standard-Marinekabel und die meisten Verteilerblöcke sind über diesem Wert nicht für Berührung freigegeben — freiliegende Leiter werden zur Stromschlaggefahr, und die Installation erfordert besondere Isolierung, Trennung und Verriegelung. Wenn du absichtlich eine Hochspannungsbank baust, ist das ein Hinweis; wenn du versehentlich zu viele Zellen in Reihe geschaltet hast, entferne welche.",
  "diag.high-voltage-rail.fix":
    "Reduziere die Reihenzahl, sodass die Bank bei oder unter 60 V bleibt (4× 12 V LFP → 51,2 V ist der typische 48-V-Designpunkt), oder behandle dies als HV-Installation mit HV-zugelassenem Kabel, Isolierung und Trenneinrichtungen durchgehend.",

  "diag.mismatched-series-stack.title":
    "In Reihe geschaltete Batterien haben unterschiedliche Spannungen ({fromV} V + {toV} V)",
  "diag.mismatched-series-stack.message":
    "Diese Reihenverbindung verbindet eine {fromV}-V-Batterie mit einer {toV}-V-Batterie. Reihengeschaltete Zellen müssen die gleiche Nennspannung haben — die kleinere Zelle entlädt sich in die größere jede Runde, sinkt unter ihren sicheren Boden und gerät schließlich in dauerhafte Polaritätsumkehr. ABYC E-11 §11.10 und alle Batteriehersteller fordern abgestimmte Zellen in jedem Reihenstrang.",
  "diag.mismatched-series-stack.fix":
    "Ersetze eine der Batterien, sodass beide die gleiche Nennspannung haben, oder entferne die Reihenverbindung und behandle jede Bank als separate Domäne (mit eigenem DC-DC, falls eine Brücke benötigt wird).",

  "diag.custom-component-implausible-amps.surge-below-draw.title":
    "{label}: Einschaltspitze liegt unter dem Dauerstrom",
  "diag.custom-component-implausible-amps.surge-below-draw.message":
    "{label} gibt {ampSurge} A Einschaltspitze bei {ampDraw} A Dauerstrom an. Die Spitze ist der kurze Einschaltstrom zusätzlich zum Dauerstrom und kann daher nicht der kleinere der beiden Werte sein — eines der Felder enthält den falschen Wert. So wie es jetzt ist, ändert der Spitzenwert nichts: Sicherungs- und Überlastprüfungen rechnen mit dem höheren Wert, also {ampDraw} A.",
  "diag.custom-component-implausible-amps.surge-below-draw.fix":
    "Sieh im Datenblatt nach und trage den Betriebsstrom als Dauerstrom und den Anlaufstrom als Einschaltspitze ein. Falls die beiden vertauscht wurden, tausche sie zurück.",

  "diag.custom-component-implausible-amps.engine-surge.title":
    "{label}: {ampSurge} A Einschaltspitze bei nur {ampDraw} A Dauerstrom",
  "diag.custom-component-implausible-amps.engine-surge.message":
    "Bei einem Motor ist der Dauerstrom das, was der Anlasser während des Durchdrehens zieht, und die Einschaltspitze der kurze Stromstoß darüber hinaus — bei echten Motoren liegen beide etwa um den Faktor 2 auseinander, hier sind es {ratio}×. {ampDraw} A sieht nach einem Zünd- oder Betriebsstrom aus, damit beschreibt nichts an dieser Komponente den anhaltenden Anlassstrom — und der Anlassstrom bestimmt die Größe des Anlasserkabels.",
  "diag.custom-component-implausible-amps.engine-surge.fix":
    "Trage den Anlassstrom des Motors als Dauerstrom ein — kleine Schiffsdiesel ziehen beim Anlassen rund 100–250 A — und lass die Einschaltspitze für den anfänglichen Stromstoß.",

  "diag.custom-component-ai-estimated.title":
    "{count} Komponenten mit vom Assistenten geschätzten Daten",
  "diag.custom-component-ai-estimated.message":
    "{fieldCount} Angaben sind KI-Schätzungen. Sie beeinflussen Kabelquerschnitte, Sicherungen und die Energiebilanz.",
  "diag.custom-component-ai-estimated.fix":
    "Mit Beheben nach Herstellerunterlagen suchen und die geschätzten Angaben prüfen. Unbestätigte Werte bleiben markiert.",

  "diag.ai-estimated-lengths.title_one":
    "Eine angenommene Leitungslänge bestimmt einen Querschnitt",
  "diag.ai-estimated-lengths.title_many":
    "{count} angenommene Leitungslängen bestimmen einen Querschnitt",
  "diag.ai-estimated-lengths.message_one":
    "Der Assistent hat {total} Leitungslängen gewählt, weil deine Beschreibung sie nicht enthielt. Bei dieser hat die Länge den Querschnitt bestimmt — ist sie falsch, ist das Kabel unterdimensioniert. Die übrigen sind so kurz, dass kein plausibler Fehler etwas ändert.",
  "diag.ai-estimated-lengths.message_many":
    "Der Assistent hat {total} Leitungslängen gewählt, weil deine Beschreibung sie nicht enthielt. Bei diesen {count} hat die Länge den Querschnitt bestimmt — ist eine falsch, ist dieses Kabel unterdimensioniert. Die übrigen sind so kurz, dass kein plausibler Fehler etwas ändert.",
  "diag.ai-estimated-lengths.fix_one":
    "Miss diesen Weg am Boot aus und trage die echte Länge ein. Folge dem Weg, den das Kabel wirklich nimmt, nicht der Luftlinie.",
  "diag.ai-estimated-lengths.fix_many":
    "Miss diese Wege am Boot aus und trage die echten Längen ein. Folge dem Weg, den jedes Kabel wirklich nimmt, nicht der Luftlinie.",
  "diag.ai-estimated-parts.title_one": "Ein Bauteil wurde für dich gewählt",
  "diag.ai-estimated-parts.title_many": "{count} Bauteile wurden für dich gewählt",
  "diag.ai-estimated-parts.message_one":
    "Du hast dieses nicht benannt, also hat der Assistent ein passendes Katalogbauteil gewählt. Die Daten stammen aus dem Katalog, nicht vom Assistenten — sie beschreiben aber das gewählte Bauteil, und das ist vielleicht nicht das, was du kaufst.",
  "diag.ai-estimated-parts.message_many":
    "Du hast diese nicht benannt, also hat der Assistent passende Katalogbauteile gewählt. Die Daten stammen aus dem Katalog, nicht vom Assistenten — sie beschreiben aber die gewählten Bauteile, und das sind vielleicht nicht die, die du kaufst.",
  "diag.ai-estimated-parts.fix_one":
    "Wähle es aus und tausche es unter Bauteil gegen das, was du wirklich hast. Alle Kabel bleiben daran.",
  "diag.ai-estimated-parts.fix_many":
    "Wähle jedes aus und tausche es unter Bauteil gegen das, was du wirklich hast. Alle Kabel bleiben daran.",

  "diag.dc-breaker-exceeds-wire.title": "{rating}-A-Schutzschalter über Kabelbelastbarkeit",
  "diag.dc-breaker-exceeds-wire.message": "Das Kabel mit {mm2} mm² ist für {ampacity} A ausgelegt.",
  "diag.dc-breaker-exceeds-wire.fix": "Größeren Leiter oder einen geeigneten Schutz mit geringerem Nennstrom wählen.",
  "diag.dc-breaker-unverified.title": "Schutzschalter prüfen: {label}",
  "diag.dc-breaker-unverified.message": "Ausschaltvermögen und maximale Betriebsspannung sind nicht verifiziert.",
  "diag.dc-breaker-unverified.fix": "DC-Ausschaltvermögen mit Batteriekurzschlussstrom und Höchstspannung vergleichen. Einbauort und Auslöseverhalten prüfen.",
  "diag.dc-breaker-reversed.title": "Schutzschalteranschlüsse tauschen: {label}",
  "diag.dc-breaker-reversed.message": "Die Batterie ist mit der Lastseite des Schutzschalters verbunden.",
  "diag.dc-breaker-reversed.fix": "Batterieplus an BAT LINE und Motorplus an AUX LOAD anschließen.",
  "diag.dc-breaker-voltage.title": "Spannungsgrenze überschritten: {label}",
  "diag.dc-breaker-voltage.message": "Die {voltage}-V-Bank überschreitet die angegebene DC-Grenze von {maximum} V.",
  "diag.dc-breaker-voltage.fix": "Einen Schutzschalter für die maximale Betriebsspannung verwenden.",
  "diag.dc-breaker-interrupt-capacity.title": "Ausschaltvermögen überschritten: {label}",
  "diag.dc-breaker-interrupt-capacity.message": "Der angegebene Kurzschlussstrom beträgt {fault} A; das Ausschaltvermögen beträgt {rating} A.",
  "diag.dc-breaker-interrupt-capacity.fix": "Schutz mit ausreichendem DC-Ausschaltvermögen für diese Quelle wählen.",
  "diag.fuse-exceeds-wire.title": "{rating}-A-Sicherung über Kabelbelastbarkeit",
  "diag.fuse-exceeds-wire.message": "Die zur Planung verwendete Strombelastbarkeit des Kabels beträgt {ampacity} A, weniger als die {rating}-A-Sicherung. Kabeldaten und Überlastschutz prüfen.",
  "diag.fuse-exceeds-wire.fix": "Geeignete kleinere Sicherung oder größeren Kabelquerschnitt verwenden.",

  "diag.lynx-fuse-sum-exceeds-rating.title":
    "Lynx-Abzweigsicherungen ergeben {sum} A — über dem Grenzwert von {limit} A",
  "diag.lynx-fuse-sum-exceeds-rating.message":
    "Die Sicherungen der an diese Lynx-Kette angeschlossenen Stromkreise ergeben {sum} A, aber {module} begrenzt die gesamte Kette auf {limit} A. Victron verlangt, dass die Sicherungssumme höchstens dem Nennstrom des schwächsten Lynx-Moduls entspricht.",
  "diag.lynx-fuse-sum-exceeds-rating.fix":
    "Reduziere die Abzweigsicherungen oder verteile Stromkreise auf separat gespeiste Verteilungsketten, bis die Summe innerhalb der Modulgrenze bleibt.",

  "diag.inline-fuse-needed.title":
    "Inline-Sicherung erforderlich — Batterie-MRBF ({rating} A) schützt {mm2} mm²-Leiter nicht",
  "diag.inline-fuse-needed.message":
    "Die {rating}-A-Polsicherung der Batterie überschreitet die Dauerstrombelastbarkeit dieses Kabels von {ampacity} A. Ergänze den Abzweigschutz am Einspeisepunkt des dünneren Leiters.",
  "diag.inline-fuse-needed.fix":
    "Setze eine zur Last passende Sicherung am Einspeisepunkt dieses dünneren Leiters ein oder vergrößere den Leiter passend zum MRBF-Nennstrom.",

  "diag.duplicate-cable.title": "{count} Kabel zwischen denselben Klemmen",
  "diag.duplicate-cable.message":
    "Zwei oder mehr Kabel verbinden exakt dasselbe Klemmenpaar. Fast immer ein Überrest aus dem Bearbeiten — die Duplikate tragen keinen zusätzlichen Strom und verschmutzen nur die Arbeitsfläche.",
  "diag.duplicate-cable.fix": "Lösche die doppelten Kabel.",

  "diag.mismatched-cable-lengths.title":
    "Kabel zwischen {aLabel} und {bLabel} haben unterschiedliche Längen",
  "diag.mismatched-cable-lengths.message":
    "Mehrere Kabel laufen zwischen {aLabel} und {bLabel} mit Längen {min} m und {max} m. Kabel zwischen demselben Komponentenpaar nehmen meist denselben Weg, daher ist ein so großer Unterschied normalerweise ein Eingabefehler — und für die Spannungsabfall-Rechnung sollte die längere Länge zählen.",
  "diag.mismatched-cable-lengths.fix":
    "Setze beide Kabel auf die tatsächliche Streckenlänge. Wenn sie wirklich unterschiedliche Wege gehen, lass sie wie sie sind.",

  "diag.unfused-charger-output.title": "Ungesichertes {terminal}-Kabel: {label}",
  "diag.unfused-charger-output.message": "Das Kabel an {terminal} erreicht Batterieplus ohne Sicherung. Ein Kurzschluss kann das Kabel überhitzen.",
  "diag.unfused-charger-output.fix": "Geeigneten Schutz nahe der Batterie in diesem Leiter vorsehen.",

  "diag.unfused-source-output.title": "Ungesicherter Plus-Ausgang von {label}",
  "diag.unfused-source-output.message":
    "Der Plus-Ausgang von {label} kann ohne Sicherung im Pfad einen Batterie+ erreichen. Ein Kurzschluss auf diesem Kabel jagt den vollen Kurzschlussstrom der Batterie hindurch — ABYC E-11 §11.10.1.1 fordert eine Sicherung innerhalb von 7 in (178 mm) der Batterie an jedem mit ihr verbundenen Leiter.",
  "diag.unfused-fuse-block-feed.title": "Ungesicherte Zuleitung zu {label}",
  "diag.unfused-fuse-block-feed.message":
    "Das Kabel, das den Eingang von {label} aus der Batterie speist, hat keine Sicherung im Pfad. Die Slot-Sicherungen am Block schützen jeden Abgang — aber nicht das Zuleitungskabel selbst. Scheuert es irgendwo am Chassis, jagt die Batterie ihren vollen Kurzschlussstrom hindurch (ABYC E-11 §11.10.1.1).",
  "diag.unfused-fuse-block-feed.fix":
    "Setze innerhalb von 7 in (178 mm) der Batterie eine Hauptsicherung (MRBF oder Class-T) am Zuleitungskabel.",
  "diag.unfused-source-output.fix":
    "Setze nahe der Batterie eine Sicherung an diesem Leiter (MRBF oder Class-T für Hochstromquellen).",

  "diag.charger-input-output-loop.title":
    "Eingang und Ausgang von {chargerLabel} teilen sich {battLabel}",
  "diag.charger-input-output-loop.message":
    "Der Plus-Ausgang von {chargerLabel} und ein Plus-Eingang erreichen beide {battLabel}. Das Ladegerät versucht, dieselbe Bank zu laden, aus der es gespeist wird — es verweigert den Betrieb oder fährt nur Verluste durch sich selbst. Der Ausgang (BATT+) gehört auf eine separate Hilfs-/Hausbatterie.",
  "diag.charger-input-output-loop.fix":
    "Verlege den BATT+-Ausgang von {chargerLabel} auf die Hilfs-/Hausbatterie, nicht auf die Quellbank.",

  "diag.charger-output-on-cranking-bus.title":
    "Ausgang von {chargerLabel} hängt am Anlasserkreis",
  "diag.charger-output-on-cranking-bus.message":
    "Der BATT+-Ausgang von {chargerLabel} erreicht die Anlasser-/Lichtmaschinen-Klemme von {engineLabel} — die Schiene der Startbatterie. Der Ausgang eines DC-DC-Ladegeräts gehört auf eine separate Hilfs-/Hausbatterie, nicht auf dieselbe Klemme, von der sein Eingang gespeist wird. So verkabelt versucht das Ladegerät, die Bank aufzuladen, aus der es gespeist wird.",
  "diag.charger-output-on-cranking-bus.fix":
    "Verlege den BATT+-Ausgang von {chargerLabel} auf eine eigene Hausbatterie. Lass den Lichtmaschinen-/Startseite-Eingang an der Motorklemme.",

  "diag.dcdc-fed-without-battery-buffer.title":
    "{chargerLabel} wird direkt von {altLabel} gespeist — keine Batterie auf der Schiene",
  "diag.dcdc-fed-without-battery-buffer.message":
    "Der Plus-Eingang von {chargerLabel} erreicht {altLabel}, ohne dass eine Batterie auf derselben Schiene sitzt. Der nahezu null-Impedanz-Eingang des DC-DC hat nichts, was die Spannung klemmt, und der Regler der Lichtmaschine hat nichts, was Lastabwurfspitzen abfängt — rechne mit gegrilltem Regler oder DC-DC beim ersten Motorstart ohne aufgeschaltete Startbatterie.",
  "diag.dcdc-fed-without-battery-buffer.fix":
    "Schließe das Lichtmaschinen-B+ zuerst an die Startbatterie an, und nimm dann den Eingang von {chargerLabel} vom Plus der Startbatterie ab (oder von einer Schiene, an der die Startbatterie hängt).",

  "diag.fuse-block-slot-unrated.title_one":
    "{label}: Slot {slots} hat keinen Sicherungswert",
  "diag.fuse-block-slot-unrated.title_many":
    "{label}: Slots {slots} haben keinen Sicherungswert",
  "diag.fuse-block-slot-unrated.message_one":
    "{slots} an {label} trägt Last, hat aber keinen Sicherungswert. Bis ein Wert vergeben ist, gibt es keinen Überstromschutz — das nachgelagerte Kabel kann schmelzen, bevor irgendetwas auslöst.",
  "diag.fuse-block-slot-unrated.message_many":
    "{slots} an {label} tragen Last, haben aber keinen Sicherungswert. Bis Werte vergeben sind, gibt es keinen Überstromschutz — die nachgelagerten Kabel können schmelzen, bevor irgendetwas auslöst.",
  "diag.fuse-block-slot-unrated.fix":
    "Öffne die Sicherungsleiste im Inspector und vergib für jeden belegten Slot einen Sicherungswert, dimensioniert nach dem dünnsten nachgelagerten Kabel.",

  "diag.charger-return-floating.title": "Rückleitung fehlt: {label}",
  "diag.charger-return-floating.message": "Im Plan fehlt die vollständige Rückleitung von {negLabels} zur Zielbatteriebank.",
  "diag.charger-return-floating.message_dead_end": "Im Plan fehlt die vollständige Rückleitung von {negLabels} zur Zielbatteriebank.",
  "diag.charger-return-floating.fix": "{negLabels} mit der Minusschiene der Zielbank verbinden.",

  "diag.source-return-floating.title": "Rückleitung fehlt: {label}",
  "diag.source-return-floating.message": "Die Plusleitung ist angeschlossen, aber eine Rückleitung zur selben Batteriebank ist nicht erkennbar.",
  "diag.source-return-floating.fix": "Minus- oder Gehäuserückleitung zu dieser Bank prüfen.",

  "diag.monitor-power-unverified.title": "Monitorversorgung prüfen: {label}",
  "diag.monitor-power-unverified.message_missing": "Im Plan fehlt die vollständige Versorgung über Vbatt+ und Minus. AUX ersetzt Vbatt+ nicht.",
  "diag.monitor-power-unverified.message_voltage": "{label} benötigt 6,5–70 V DC. Die Versorgungsspannung ist unbekannt oder liegt außerhalb dieses Bereichs.",
  "diag.monitor-power-unverified.message_protection": "Vbatt+ hat einen Versorgungspfad ohne angegebene Sicherung bis 1 A. Mitgelieferte abgesicherte Leitung prüfen.",
  "diag.monitor-power-unverified.fix": "Versorgung, Rückleitung und abgesicherte Leitung anhand der Monitoranleitung prüfen.",
  "diag.shunt-bypass.title": "Strom umgeht {label}",
  "diag.shunt-bypass.message": "{equipment} hat einen Rückleiter zur überwachten Batterie, der nicht durch {label} führt. Der Monitor kann diesen Lade- oder Entladestrom übersehen und einen falschen Ladezustand anzeigen.",
  "diag.shunt-bypass.fix": "Zur Überwachung der gesamten Batteriebank diese Rückleiter an die Seite {terminal} von {label} anschließen und parallele Rückleiter um den Shunt entfernen.",

  "diag.sense-terminal-as-bus.title":
    "{label}: {terminal} ist eine Messleitung, keine Sammelschiene",
  "diag.sense-terminal-as-bus.message":
    "{count} Kabel liegen an {terminal} von {label}. Eine Messklemme ist für {maxAmp} A ausgelegt und misst nur Spannung — sie kann weder Strom führen noch verteilen, also ist alles, was darüber läuft, undimensioniert und ungeschützt.",
  "diag.sense-terminal-as-bus.message_bonds":
    "{terminal} von {label} ist die einzige Verbindung zwischen {batteries}. Diese Bänke sind über eine {maxAmp}-A-Messleitung verbunden, die jeden Ausgleichsstrom zwischen ihnen führt — ungesichert und nie dafür ausgelegt.",
  "diag.sense-terminal-as-bus.fix":
    "Gib der Messklemme eine einzige Leitung zur gemessenen Batterie (am Pol mit 1 A abgesichert) und lege alle anderen Kabel auf eine Sammelschiene oder den Batteriepol.",

  "diag.terminal-overloaded.title_provisional": "Anschlussstrom prüfen: {label} {terminal}",
  "diag.terminal-overloaded.message_provisional": "Geschätzter Strom: {load} A bei {maxAmp} A Bemessungsstrom. Das unvollständige Modell kann eine Überlast nicht bestätigen.",
  "diag.terminal-overloaded.fix_verify": "Stromkreis vervollständigen und Betriebsstrom prüfen, dann mit dem Anschlusswert vergleichen.",
  "diag.terminal-overloaded.title": "Anschlusswert überschritten: {label} {terminal}",
  "diag.terminal-overloaded.message": "Berechneter Dauerstrom: {load} A, über dem Anschlusswert von {maxAmp} A. Die Verbindung kann überhitzen.",
  "diag.terminal-overloaded.fix": "Komponente für mindestens {load} A verwenden oder Last reduzieren.",

  "diag.fuse-exceeds-terminal.title": "Sicherung zu groß für {label}: {terminal}",
  "diag.fuse-exceeds-terminal.message": "Dieser Anschluss ist für {maxAmp} A ausgelegt, aber mit {fuse} A abgesichert.",
  "diag.fuse-exceeds-terminal.fix": "Schutz bis {maxAmp} A oder eine für die größere Sicherung geeignete Komponente verwenden.",

  "diag.cable-dangling-terminal.title": "Ein Kabelende hat keinen Anschluss",
  "diag.cable-dangling-terminal.message": "{label} hat keinen Anschluss {terminal}. Dieses Kabel kann keinen Stromkreis schließen.",
  "diag.cable-dangling-terminal.fix": "Mit einem vorhandenen Anschluss verbinden, den Anschluss wiederherstellen oder das Kabel entfernen.",
  "diag.cable-dangling-terminal.title_missing_node": "Ein Kabelende hat keine Komponente",
  "diag.cable-dangling-terminal.message_missing_node": "Ein Ende dieses Kabels fehlt. Es kann keinen Stromkreis schließen.",
  "diag.cable-dangling-terminal.fix_missing_node": "Verbinde das lose Ende oder entferne das Kabel.",
  "diag.cable-dangling-terminal.action_remove": "Defektes Kabel entfernen",

  "diag.pv-input-incomplete.title": "Unvollständiger PV-Stromkreis an {label}",
  "diag.pv-input-incomplete.message": "Der angeschlossene PV-Eingang von {label} hat keinen vollständigen Plus- und Rückleiterpfad durch die Solarmodule. Eine fehlende Verbindung oder falsche Polarität verhindert das Solarladen.",
  "diag.pv-input-incomplete.fix": "Plus- und Rückleiter zwischen den Solarmodulen und diesem PV-Eingang vollständig anschließen, einschließlich aller Serienverbindungen.",
  "diag.pv-string-fuse-exceeds-module.title": "PV-Strangsicherung mit {fuse} A überschreitet die Modulgrenze von {max} A",
  "diag.pv-string-fuse-exceeds-module.message": "Dieser Strang hat eine Sicherung mit {fuse} A, aber {label} erlaubt höchstens {max} A als Strangsicherung. Die Sicherung erfüllt die angegebene Überstromschutzgrenze des Moduls nicht.",
  "diag.pv-string-fuse-exceeds-module.fix": "Eine für PV geeignete Strangsicherung mit höchstens {max} A verwenden und auch den erforderlichen Betriebsstrom prüfen. Einzelne Strangsicherungen getrennt von der gemeinsamen Sammelleitung betrachten.",

  "diag.mppt-over-voltage.title":
    "{label}: PV-Eingang überschreitet max. {max} V",
  "diag.mppt-over-voltage.message":
    "Der längste Serienstring an {label} erreicht {voc} V Voc bei STC über {panels} Module, klettert auf {vocCold} V bei −25 °C Betriebstemperatur (NEC 690.7 ×1,20) — über der zulässigen PV-Eingangsspannung von {max} V. Das Überschreiten der angegebenen Eingangsgrenze kann den Regler beschädigen; prüfe die Kalt-Voc anhand der tatsächlichen Moduldaten und der niedrigsten Installationstemperatur.",
  "diag.mppt-over-voltage.fix":
    "Verdrahte die Module parallel statt seriell, entferne ein Modul aus dem String oder wähle einen MPPT mit höherer PV-Spannungstoleranz (Kalt-Voc ≤ {max} V).",

  // ── Diagnostics — sizing ───────────────────────────────
  "diag.combined-battery-discharge.title": "Angeschlossener Bedarf übersteigt die Batterie-Entladewerte",
  "diag.combined-battery-discharge.message": "Der bekannte angeschlossene Bedarf beträgt mindestens {draw} A und überschreitet die aus angegebenen Entladewerten ermittelte Grenze von {limit} A.",
  "diag.combined-battery-discharge.fix": "Den gleichzeitigen Bedarf reduzieren oder die Entladefähigkeit der Batteriebank prüfen und erhöhen.",
  "diag.combined-battery-discharge.detail_message": "Geprüft wird der gleichzeitige Dauerstrombedarf von {count} bekannten Verbrauchern mit vollständigen Batteriestromkreisen. Anlaufstrom bleibt ausgeschlossen; optionale Ladung wird nicht abgezogen. Berechneter DC-DC-Eingangsstrom erfordert vollständige Stromkreise und angegebene Spannungen; ohne angegebenen Wirkungsgrad gilt eine verlustfreie Untergrenze. Der Bedarf über den gesamten Serienstrang zählt in jedem benötigten Abschnitt; Lasten an Zwischenabgriffen kommen bei den versorgenden Batterien hinzu. Parallele Nennwerte bilden eine Gesamtgrenze ohne Annahmen zur gleichen oder kapazitätsabhängigen Stromaufteilung.",
  "diag.combined-battery-discharge.detail_fix": "Die Dauerentladewerte und Betriebsbedingungen der installierten Batterien und des BMS prüfen. Unbekannte Lasten, unvollständige Stromkreise und fehlende Nennwerte werden durch diese Prüfung nicht bestätigt. Vor einer Parallelschaltung Stromaufteilung und Herstellerfreigabe prüfen. Dies ist eine Auslegungsberechnung, kein gemessener Strom.",

  "diag.inverter-exceeds-battery-c-rate.title_unverified": "Entladefähigkeit der Batterie für {label} prüfen",
  "diag.inverter-exceeds-battery-c-rate.message_unverified": "Mindestens ein Batterie-Entladewert ist nicht verfügbar. Crimp kann daher die Dauerstromfähigkeit der Bank für {label} nicht bestätigen. Der berechnete Bedarf beträgt bis zu {draw} A.",
  "diag.inverter-exceeds-battery-c-rate.fix_unverified": "Die Dauerentladewerte der angeschlossenen Batterien, etwaige BMS-Grenzen und die Leitungswege vor der Geräteauswahl prüfen.",

  "diag.inverter-exceeds-battery-c-rate.title":
    "{label} überschreitet den angegebenen Batterie-Entladewert",
  "diag.inverter-exceeds-battery-c-rate.message":
    "Der berechnete Bedarf von {label} beträgt bis zu {draw} A und überschreitet die angegebene Dauer-Entladegrenze der Bank von {bank} A. Die Werte der installierten Batterie und eines etwaigen BMS prüfen.",
  "diag.inverter-exceeds-battery-c-rate.fix":
    "Angeschlossene Batteriepfade und Nennwerte prüfen. Den Bedarf reduzieren oder Geräte mit ausreichender Entladefähigkeit verwenden; die Herstellerfreigabe für parallele Batterien beachten.",

  "diag.dcdc-overloads-alternator.title": "Lichtmaschinenlast prüfen: {pct} %",
  "diag.dcdc-overloads-alternator.message": "DC-DC-Bedarf: {draw} A bei {rated} A Nennstrom der Lichtmaschine. Im Leerlauf kann weniger Strom verfügbar sein.",
  "diag.dcdc-overloads-alternator.fix": "Dauerstrom im Leerlauf prüfen und DC-DC-Grenze entsprechend einstellen.",

  "diag.charge-exceeds-acceptance.title_recommended": "Empfohlenen Ladestrom prüfen",
  "diag.charge-exceeds-acceptance.message_recommended": "Quellen können bis zu {charge} A liefern, mehr als die für dieses Batteriemodell empfohlenen {accept} A Ladestrom.",
  "diag.charge-exceeds-acceptance.fix_recommended": "Ladeeinstellungen anhand der Empfehlung sowie der Batterie- und BMS-Grenzen prüfen.",
  "diag.charge-exceeds-acceptance.detail_message_recommended": "Der Hersteller empfiehlt {accept} A für dieses Batteriemodell im Katalog. Das ist eine Empfehlung und kein maximaler Ladestrom. Quellen können bis zu {charge} A liefern; der tatsächliche Strom hängt von Einstellungen und gleichzeitiger Nutzung ab.",
  "diag.charge-exceeds-acceptance.detail_fix_recommended": "Installiertes Batteriemodell, maximalen Ladestrom, BMS-Grenzen und Betriebsbedingungen prüfen. Beim Einstellen von Ladegeräten und Lichtmaschinenreglern auch die Ladeempfehlung beachten.",
  "diag.charge-exceeds-acceptance.title_planning": "Planungswert für Ladestrom prüfen",
  "diag.charge-exceeds-acceptance.message_planning": "Quellen können bis zu {charge} A liefern, mehr als Crimps Planungswert von {accept} A für diese Batterie.",
  "diag.charge-exceeds-acceptance.fix_planning": "Daten der genauen Batterie und des BMS vor dem Festlegen der Ladeeinstellungen prüfen.",
  "diag.charge-exceeds-acceptance.detail_message_planning": "Crimp behält {accept} A als konservativen Planungswert für diese Katalogbatterie bei. Das ist weder ein verifizierter Herstellerhöchstwert noch eine Herstellerempfehlung. Quellen können bis zu {charge} A liefern; tatsächliche Gerätegrenzen prüfen.",
  "diag.charge-exceeds-acceptance.detail_fix_planning": "Installiertes Modell und Revision bestimmen, dann Ladestromangabe, BMS-Grenzen und Betriebsbedingungen prüfen. Ein Planungswert legt den zulässigen Ladestrom nicht fest.",
  "diag.charge-exceeds-acceptance.title": "Batterieladestrom prüfen",
  "diag.charge-exceeds-acceptance.title_current": "{charge} A Ladekapazität: Batteriegrenze prüfen",
  "diag.charge-exceeds-acceptance.message_specified": "Quellen können bis zu {charge} A liefern; der Plan verwendet {accept} A als Ladegrenze. Angabe und Ladeeinstellungen prüfen.",
  "diag.charge-exceeds-acceptance.fix_specified": "Verifizierte Batterie- und BMS-Grenzen einhalten und die angegebene Grenze prüfen.",
  "diag.charge-exceeds-acceptance.message": "Quellen können bis zu {charge} A liefern, über der geschätzten Batteriegrenze von {accept} A. Tatsächliche Batteriespezifikation prüfen.",
  "diag.charge-exceeds-acceptance.fix_lead": "Maximalen Ladestrom des Herstellers prüfen und Ladegeräte einstellen.",
  "diag.charge-exceeds-acceptance.fix_lfp": "Ladegrenzen von Batterie und BMS prüfen, dann Ladegeräte einstellen.",

  "diag.manufacturer-breaker-unverified.title": "Vorgeschriebenen Schutzschalter prüfen: {label}",
  "diag.manufacturer-breaker-unverified.message": "Der Hersteller nennt einen {rating}-A-Schutzschalter. Eine gleich große Schmelzsicherung ist nicht automatisch gleichwertig.",
  "diag.manufacturer-breaker-unverified.fix": "Genauen Schutzschalter und Auslösekennlinie in der Anleitung prüfen.",
  "diag.manufacturer-breaker-rating.title": "Schutzschalterwert prüfen: {label}",
  "diag.manufacturer-breaker-rating.message": "Der Plan zeigt {rating} A Schutz; der Hersteller nennt einen {required}-A-Schutzschalter.",
  "diag.manufacturer-breaker-rating.fix": "Schutzschalter in jedem Versorgungspfad prüfen. Erforderlichen Zuleitungsschutz beibehalten.",
  "diag.manufacturer-supplied-lead-protection.title": "Schutz der mitgelieferten Leitung prüfen: {label}",
  "diag.manufacturer-supplied-lead-protection.message": "Die mitgelieferte {mm2}-mm²-Leitung ist für {ampacity} A ausgelegt, unter der eingezeichneten {rating}-A-Sicherung.",
  "diag.manufacturer-supplied-lead-protection.fix": "Leitung nach Anleitung schützen. Eine dickere Verlängerung erhöht ihre Belastbarkeit nicht.",
  "cable.manufacturer_fridge_fuse_hint": "Das CRX-Handbuch nennt {rating} A bei der angeschlossenen Versorgungsspannung. Prüfen Sie die genaue Kühlschrankvariante und den Sicherungstyp.",
  "cable.manufacturer_wire_ampacity_hint": "Das gewählte Kabel trägt den Hersteller-Sicherungswert nicht. Prüfen Sie Kabel und Schutz vor der Sicherungsauswahl.",
  "cable.manufacturer_installation_unverified": "Prüfen Sie die Verkabelungsanforderungen des Herstellers. Der Kabelquerschnitt bleibt vorläufig.",
  "diag.manufacturer-installation-unverified.title": "{label}: Herstellerverkabelung prüfen",
  "diag.manufacturer-installation-unverified.message": "Die Herstelleranforderung an das Kabel ist für diesen Stromkreis nicht geklärt.",
  "diag.manufacturer-installation-unverified.identity": "Dieses Teil behält eine CRX-Katalogkennung, aber die ursprüngliche Modellidentität ist nicht belegt. Prüfen Sie sein eigenes Installationshandbuch vor numerischen Kabel- oder Sicherungsempfehlungen.",
  "diag.manufacturer-installation-unverified.route": "Die gesamte Hin- und Rückleitung zwischen Quelle und Kühlschrank ist ungeklärt oder hat alternative Wege. Prüfen Sie die Kabelanforderung des Herstellers.",
  "diag.manufacturer-installation-unverified.voltage": "Die Versorgungsspannung des Kühlschranks ist unbekannt oder außerhalb der unterstützten 12/24-V-Klassen. Prüfen Sie die Installationsanforderungen.",
  "diag.manufacturer-installation-unverified.24v-chart": "Die 24-V-Installation erfordert das Kabeldiagramm des Herstellers. Die Sicherungsempfehlung von 7,5 A bestätigt keinen Kabelquerschnitt.",
  "diag.manufacturer-installation-unverified.length": "Die gesamte 12-V-Hin- oder Rückleitung überschreitet die unterstützten 8 m des Diagramms. Klären Sie den Querschnitt mit dem Hersteller.",
  "diag.manufacturer-wire-minimum.ip43": "Das gewählte Kabel mit {mm2} mm² unterschreitet die veröffentlichte Mindestgröße {minimumLabel} für {label}.",
  "diag.manufacturer-installation-unverified.ip43": "Diese IP43-Installation liegt außerhalb der geprüften Kabelvorgaben. Automatische Kabel- und Sicherungsempfehlungen bleiben vorläufig.",
  "diag.manufacturer-installation-unverified.ip43-identity": "Die bearbeiteten Ladegerätdaten bestätigen das geprüfte IP43-Modell und seine Ausgänge nicht. Aus der kopierten Katalogkennung wird kein Mindestkabelquerschnitt abgeleitet.",
  "diag.manufacturer-installation-unverified.ip43-route": "Eine vollständige Verbindung zu derselben 12-V-Batteriequelle und deren angegebene Spannungen sind nicht bestätigt. Abzweige, Batterien in Reihe oder zusätzliche Wege verhindern die Prüfung anhand der Kabeltabelle des Herstellers.",
  "diag.manufacturer-installation-unverified.ip43-length": "Die längere einfache Leitung überschreitet die veröffentlichte Kabeltabelle: 3 m beim 12/50 oder 4,5 m am Hauptausgang des 12/30. Ein größerer Querschnitt allein bestätigt die Installation nicht.",
  "diag.manufacturer-installation-unverified.ip43-terminal": "Das Kabel direkt an einer Hauptklemme des Ladegeräts überschreitet deren angegebene Kapazität von 16 mm². Einen zugelassenen Anschluss prüfen; keinen zu großen Leiter in die Klemme zwängen.",
  "diag.manufacturer-installation-unverified.ip43-fix": "Genaue Ladegerätversion, vollständige Plus- und Minusleitungslängen, Klemmenkapazität und Kabelverlegung anhand des Handbuchs prüfen.",
  "diag.manufacturer-installation-unverified.parallel": "Das Handbuch verlangt parallele DC-Kabel. Crimp kann diese Anordnung nicht prüfen; automatische Kabel- und Sicherungsempfehlungen bleiben vorläufig.",
  "diag.manufacturer-installation-unverified.parallel-fix": "Prüfen Sie beide DC-Leitungen anhand der Installationstabelle des genauen Modells.",
  "diag.manufacturer-installation-unverified.fix": "Prüfen Sie genaue CRX-Variante, Versorgungsspannung und vollständige Kabellängen anhand des Installationshandbuchs.",
  "diag.manufacturer-fuse-minimum.title": "{label}: Sicherung unter Herstelleranforderung",
  "diag.manufacturer-fuse-minimum.message": "Eine angegebene Versorgungssicherung mit {rating} A unterschreitet die dokumentierte Anforderung von {minimum} A für {label}.",
  "diag.manufacturer-fuse-minimum.fix": "Prüfen Sie das genaue Modellhandbuch und das gewählte Kabel vor Schutzänderungen. Ein höherer Wert bestätigt weder den geeigneten Sicherungstyp noch das Ausschaltvermögen.",
  "diag.manufacturer-wire-minimum.title": "{label}: Kabel unter Herstellerminimum",
  "diag.manufacturer-wire-minimum.message": "Diese Leitung mit {mm2} mm² unterschreitet den Mindestquerschnitt von {minimum} mm² für {label}.",
  "diag.manufacturer-wire-minimum.fix": "Wähle mindestens den Herstellerquerschnitt und prüfe Strombelastbarkeit und Spannungsabfall der gesamten Leitung.",
  "diag.manufacturer-fuse-range.title": "{label}: Sicherung außerhalb des Herstellerbereichs",
  "diag.manufacturer-fuse-range.message": "Die {rating}-A-Sicherung liegt außerhalb des für {label} vorgeschriebenen Batteriesicherungsbereichs von {min}–{max} A.",
  "diag.manufacturer-fuse-range.fix": "Verwende eine Sicherung im Herstellerbereich und prüfe, ob die Leitung deren Nennstrom tragen kann.",

  "diag.charger-cumulative-vdrop.title": "{label}: {pct} % Ladespannungsabfall",
  "diag.charger-cumulative-vdrop.title_verify": "Ladespannungsabfall prüfen: {label}",
  "diag.charger-cumulative-vdrop.message_manufacturer": "Geschätzter Leitungsverlust: {drop} V. Dieses SmartSolar-Modell erlaubt höchstens {allowance} % Verlust im Batteriekabel.",
  "diag.charger-cumulative-vdrop.message_target": "Geschätzter Leitungsverlust: {drop} V, über dem Planungsziel von {allowance} %. Dies ist keine Herstellergrenze.",
  "diag.charger-cumulative-vdrop.message_verify": "Geschätzt werden {drop} V bei einer Grenze von {allowance} %. Die Stromaufteilung ist ungeprüft; der tatsächliche Verlust kann abweichen.",
  "diag.charger-cumulative-vdrop.fix": "Strom und Kabellängen prüfen. Querschnitte bei Bedarf vergrößern oder Leitungen kürzen.",
  "diag.cranking-vdrop-unverified.title": "Starterkabel prüfen",
  "diag.cranking-vdrop-unverified.message": "Der geschätzte Spannungsabfall beim Start beträgt {pct} % und liegt über Crimps konservativem Planungsziel.",
  "diag.cranking-vdrop-unverified.fix": "Startstrom, Kabellängen und Querschnitte anhand der Motoranleitung prüfen.",

  "diag.pv-cumulative-vdrop.title": "{label}: {pct}% Spannungsfall im Solarkreis",
  "diag.pv-cumulative-vdrop.message": "Die vollständige Solarzuleitung und Rückleitung verlieren bei Auslegungsstrom {drop} V und überschreiten das gewählte Planungsziel von {allowance}%.",
  "diag.pv-cumulative-vdrop.fix": "Verkürzen Sie die Solarleitungen oder erhöhen Sie die Leiterquerschnitte; prüfen Sie die Installationsanleitungen von Modul und Regler.",
  "diag.cumulative-vdrop.title":
    "{label}: {pct} % Auslegungs-Spannungsabfall Batterie → Verbraucher",
  "diag.cumulative-vdrop.message":
    "Der summierte Auslegungs-Spannungsabfall für {label} beträgt {drop} V und überschreitet die zulässigen {allowance}%. Die maximalen Leiterströme müssen nicht gleichzeitig auftreten; dies ist eine Auslegungsrechnung, keine Vorhersage des tatsächlichen Spannungsverlusts.",
  "diag.cumulative-vdrop.title_lower_bound": "{label}: mindestens {pct} % Auslegungs-Spannungsabfall Batterie → Verbraucher",
  "diag.cumulative-vdrop.message_lower_bound":
    "Der berechenbare Teil des Auslegungs-Spannungsabfalls für {label} beträgt {drop} V und überschreitet bereits die zulässigen {allowance}%. Ungewisse Leiterbeiträge fehlen, daher ist die vollständige Auslegungssumme mindestens so hoch. Die Maxima je Leiter müssen nicht gleichzeitig auftreten; dies ist keine Vorhersage des tatsächlichen Spannungsverlusts.",
  "diag.cumulative-vdrop.title_provisional": "{label}: geschätzten Auslegungs-Spannungsabfall von {pct} % prüfen",
  "diag.cumulative-vdrop.message_provisional":
    "Die vorläufige Schätzung des Auslegungs-Spannungsabfalls für {label} beträgt {drop} V, über den zulässigen {allowance}%. Ungeprüfte Stromwerte gehen in diese Schätzung ein; sie belegt weder den tatsächlichen Spannungsverlust noch eine garantierte Untergrenze.",
  "diag.cumulative-vdrop.fix_provisional":
    "Prüfe den vollständigen Hin- und Rückleiterkreis, die Ein- und Ausgangswerte der Geräte sowie die Stromaufteilung. Berechne den Spannungsabfall erneut, bevor du dich auf eine automatische Leiterauswahl verlässt.",
  "diag.cumulative-vdrop.fix":
    "Sicherung und Verbraucher näher an die Batterie verlegen oder die Stammleitung um eine Größe vergrößern.",
  "diag.cumulative-vdrop.group_title": "Auslegungs-Spannungsabfall Batterie → Verbraucher",
  "diag.cumulative-vdrop.short_title": "{label}: {pct} %",
  "diag.cumulative-vdrop.short_title_lower_bound": "{label}: mindestens {pct} %",
  "diag.cumulative-vdrop.short_title_provisional": "{label}: geschätzte {pct} % prüfen",
  "health.group_count": "{count} Verbraucher",
  "health.group_expand": "{count} anzeigen",
  "health.group_collapse": "Ausblenden",

  "diag.fuse-trips-under-load.title":
    "{rating}-A-Sicherung wird auslösen — Strecke führt {load} A",
  "diag.fuse-trips-under-load.message":
    "Komponenten auf dieser Strecke ziehen {load} A Dauer, das Kabel ist aber mit {rating} A abgesichert. Ein zusätzliches Hochstromgerät stromabwärts (Anlasser, Wechselrichter…) hebt jede Sicherung auf seinem Pfad mit an.",
  "diag.fuse-trips-under-load.fix":
    "Sicherung auf mindestens {target} A erhöhen und prüfen, ob die Strombelastbarkeit des Leiters reicht.",
  "diag.fuse-trips-under-load.title_verify":
    "{rating}-A-Sicherung bei {load} A Auslegungsstrom prüfen",
  "diag.fuse-trips-under-load.message_starting":
    "Die modellierten {load} A enthalten kurzzeitigen Anlasserstrom. Daraus folgt nicht, dass die {rating}-A-Sicherung beim Start auslöst. Prüfe die Lastdauer und die Zeit-Strom-Kennlinie der eingebauten Sicherung.",
  "diag.fuse-trips-under-load.message_provisional":
    "Die Stromschätzung von {load} A ist vorläufig. Sie belegt weder den tatsächlichen Dauerstrom noch ein sicheres Auslösen der {rating}-A-Sicherung. Prüfe Stromkreis, Stromaufteilung und Lastdauer, bevor der Schutz geändert wird.",
  "diag.fuse-trips-under-load.fix_verify":
    "Prüfe tatsächliche Lastdauer, Zeit-Strom-Kennlinie der eingebauten Sicherung und Strombelastbarkeit des Leiters. Bestätige Nennstrom und Einbauort vor dem Austausch.",

  "diag.wire-undersized-for-load.title":
    "Leiter zu dünn — benötigt ≥ {required} A Strombelastbarkeit, hat {ampacity} A",
  "diag.wire-undersized-for-load.title_provisional":
    "Leiterquerschnitt prüfen — Stromschätzung ist unvollständig",
  "diag.wire-undersized-for-load.message_provisional":
    "Die vorläufige Schätzung von {load} A ergibt {required} A Auslegungs-Strombelastbarkeit und liegt über den {ampacity} A dieses Leiters. Die Stromberechnung ist unvollständig und keine geprüfte Obergrenze. Bestätige Strompfade, Lastdauer und Einbaubedingungen vor der Auswahl von Leiter oder Schutzorgan.",
  "diag.wire-undersized-for-load.fix_verify":
    "Vervollständige das Strommodell und prüfe Leiter-Strombelastbarkeit, Spannungsabfall und Überstromschutz. Eine automatische Querschnittsänderung kann die ausreichende Auslegung des Stromkreises nicht bestätigen.",
  "diag.wire-undersized-for-load.message":
    "Die modellierte Last von {load} A erfordert {required} A Leiter-Strombelastbarkeit einschließlich der zutreffenden Reserve für Dauerlasten. Kurzzeitiger Anlasserstrom wird getrennt von Dauerstrom behandelt. Dieses {mm2} mm²-Kabel ist für {ampacity} A ausgelegt und liegt damit unter der Anforderung. Prüfe Leiterquerschnitt, Installationsbedingungen und Überstromschutz.",
  "diag.wire-undersized-for-load.fix":
    "Auf eine Leitung mit ≥ {required} A Strombelastbarkeit aufrüsten oder eine Sicherung ≤ {ampacity} A einsetzen.",
  "diag.wire-undersized-for-load.fix_wire":
    "Wähle einen Leiter, der unter den Einbaubedingungen mindestens {required} A tragen kann. Prüfe den Spannungsabfall im gesamten Stromkreis.",
  "diag.wire-undersized-for-load.fix_manual":
    "Keine automatische Leiterauswahl erfüllt die Auslegungsanforderung. Reduziere oder teile die Last auf, oder prüfe eine geeignete Leiteranordnung mit mindestens {required} A Strombelastbarkeit und zulässigem Spannungsabfall im Stromkreis.",

  // ── Diagnostics — energy ───────────────────────────────
  "diag.no-battery.title": "Keine Batterie im System",
  "diag.no-battery.message":
    "Füge eine Batterie hinzu, damit wir Laufzeit, Autonomie und Ladezeit abschätzen können.",
  "diag.battery-capacity-unknown.title": "Batteriekapazität fehlt",
  "diag.battery-capacity-unknown.message":
    "Batteriesymbole sind vorhanden, ihre Definitionen geben jedoch keine nutzbare Kapazität an. Laufzeit, Autonomie und Ladezeit lassen sich aus diesen Daten nicht abschätzen.",
  "diag.battery-capacity-unknown.fix":
    "Trage für jede echte Batterie die Nennkapazität in Ah ein. Stelle einen Wandlerausgang als Wandler dar, statt einer Ersatzquelle eine erfundene Batteriekapazität zuzuweisen.",

  "diag.no-charging.title": "Keine Ladequelle",
  "diag.no-charging.message":
    "Die aktiven Verbraucher benötigen ~{watts} W ohne Ladequelle. Bei diesem Verbrauch erreicht die Batterie ihre Reserve in {hours} Stunden.",
  "diag.no-charging.fix":
    "Füge ein Solarpanel + MPPT, ein DC-DC-Ladegerät oder ein Landstrom-Ladegerät hinzu.",

  // ── Tools menu ─────────────────────────────────────────
  "tools.title": "Werkzeuge",
  "tools.bom": "Stückliste",
  "tools.auto_layout": "Auswahl anordnen",
  "tools.redraw_cables": "Alle Kabel neu zeichnen",
  "tools.freeze_build": "Aufbau einfrieren…",
  "tools.unfreeze_build": "Aufbau auftauen…",
  "tools.section.build_state": "Aufbau-Status",
  "tools.section.build_state_hint":
    "Fixiert automatisch dimensionierte Querschnitte und Sicherungen, damit spätere Änderungen einen festgelegten Aufbau nicht still verändern.",

  // ── Freeze / unfreeze build ────────────────────────────
  "freeze_build.freeze_title": "Aufbau einfrieren",
  "freeze_build.unfreeze_title": "Aufbau auftauen",
  "freeze_build.freeze_description":
    "Friert die aktuell automatisch berechnete Querschnitts- und Sicherungsgröße jedes Kabels ein, damit spätere Änderungen sie nicht still verändern. Verwende dies, wenn der Aufbau für Einkauf oder Installation festgelegt wird.",
  "freeze_build.unfreeze_description":
    "Hebt die Querschnittssperre jedes Kabels auf, sodass die Querschnitte wieder live der Auto-Empfehlung folgen.",
  "freeze_build.freeze_bullet_wire":
    "Jeder Kabelquerschnitt wird auf den aktuell angezeigten Wert fixiert.",
  "freeze_build.freeze_bullet_fuse":
    "Bereits abgesicherte Kabel werden auf die aktuelle Empfehlung aktualisiert. Unabgesicherte Kabel (Minusleiter, Anlasserkreise) bleiben unverändert.",
  "freeze_build.freeze_bullet_undo":
    "Reversibel mit Rückgängig oder über den Befehl „Aufbau auftauen“.",
  "freeze_build.unfreeze_bullet_wire":
    "Jede Querschnittssperre wird gelöscht. Querschnitte folgen wieder der Live-Auto-Dimensionierung.",
  "freeze_build.unfreeze_bullet_fuse":
    "Sicherungswerte bleiben unangetastet — das Auftauen entfernt keinen Schutz.",
  "freeze_build.unfreeze_bullet_undo": "Reversibel mit Rückgängig.",
  "freeze_build.preview_label": "Was sich ändert",
  "freeze_build.preview_wires":
    "{count} Kabelquerschnitt(e) werden gesperrt.",
  "freeze_build.preview_fuses":
    "{count} Sicherung(en) werden auf die aktuelle Empfehlung aktualisiert.",
  "freeze_build.preview_cleared":
    "{count} Kabel werden entsperrt und folgen wieder der Auto-Dimensionierung.",
  "freeze_build.already_frozen":
    "Alle Kabel entsprechen bereits der aktuellen Empfehlung — nichts zu ändern.",
  "freeze_build.nothing_to_unfreeze":
    "Keine Kabel haben eine gesperrte Querschnittsgröße — nichts aufzutauen.",
  "freeze_build.no_cables": "Dieses Projekt enthält noch keine Kabel.",
  "freeze_build.cancel": "Abbrechen",
  "freeze_build.freeze_confirm": "Aufbau einfrieren",
  "freeze_build.unfreeze_confirm": "Auftauen",

  // ── Druckfähiger Elektroplan ───────────────────────────
  "blueprint.label": "Crimp Lifetime",
  "blueprint.title": "Druckfähiger Elektroplan",
  "blueprint.description":
    "Ein druckfertiger Elektroplan mit Kabelreferenzen, einfachen Kabellängen, Leiterquerschnitten, zugewiesenen Sicherungen und Kabelliste.",
  "blueprint.print_hint": "A3 quer · Im Browser drucken oder als PDF speichern",
  "blueprint.close": "Schließen",
  "blueprint.print": "Drucken / als PDF speichern",
  "blueprint.preview.label": "Kostenlose Vorschau",
  "blueprint.preview.title": "Vollständigen Elektroplan freischalten",
  "blueprint.preview.description":
    "Der vollständige Schaltplan und die ersten {count} Zeilen jeder Tabelle sind enthalten. Hol dir Crimp Lifetime, um alle Zeilen zu sehen und zu drucken oder als PDF zu speichern.",
  "blueprint.preview.cta": "Vollständigen Plan erhalten",
  "blueprint.preview.footer_hint":
    "Vorschau · vollständiger Schaltplan und Beispielzeilen",
  "blueprint.preview.more_rows": "{count} weitere mit Crimp Lifetime",
  "blueprint.sheet.schematic": "Elektroplan",
  "blueprint.sheet.schedule": "Kabelliste",
  "blueprint.sheet.bom": "Stückliste",
  "blueprint.empty": "Füge Komponenten und Kabel hinzu, um einen Plan zu erstellen.",
  "blueprint.no_cables": "Keine Kabel vorhanden.",
  "blueprint.legend.cable": "Kabelreferenz",
  "blueprint.legend.schedule": "Vollständige Kabelangaben stehen auf Blatt 2",
  "blueprint.legend.length": "Einfache Kabellänge",
  "blueprint.legend.wire": "Leiterquerschnitt",
  "blueprint.legend.fuse": "Zugewiesene Sicherung",
  "blueprint.verify_badge": "VOR DER INSTALLATION PRÜFEN",
  "blueprint.cable_schedule": "Kabelliste",
  "blueprint.warnings.title": "Zu prüfende Punkte",
  "blueprint.warning.invalid_length": "{refs}: Länge fehlt oder ist ungültig.",
  "blueprint.warning.unresolved_endpoint":
    "{refs}: Ein Endpunkt oder Anschluss konnte nicht aufgelöst werden.",
  "blueprint.warning.unresolved_sizing":
    "{refs}: Last konnte nicht aufgelöst werden; Leiterquerschnitt prüfen.",
  "blueprint.warning.unprotected_positive":
    "{refs}: Der Plusleiter hat keine zugewiesene Sicherung.",
  "blueprint.warning.duplicate_split_protection":
    "{refs}: Dieselbe zugewiesene Sicherung erscheint auf beiden Seiten einer Verbindungsstelle; prüfe, ob eine oder zwei physische Sicherungen vorgesehen sind.",
  "blueprint.warning.duplicate_user_reference":
    "{refs}: Die Kabelkennzeichnung „{label}“ ist doppelt; eindeutige Drucksuffixe wurden ergänzt.",
  "blueprint.notes.title": "Installationshinweise",
  "blueprint.notes.length":
    "Die Längen sind einfache Projekt-Kabellängen; Zuschläge für Zuschnitt und Reserve sind nicht enthalten.",
  "blueprint.notes.fuse":
    "Sicherungswerte zeigen nur den zugewiesenen Schutz. Sicherungstyp, Halter und physischer Einbauort werden nicht modelliert.",
  "blueprint.notes.generated_refs":
    "Generierte Kabelreferenzen können sich bei Änderungen der Topologie ändern. Vergib eine eigene Kabelbezeichnung für eine dauerhafte Installations-ID.",
  "blueprint.bom.title": "Stückliste",
  "blueprint.bom.polarity": "Polarität",
  "blueprint.polarity.positive": "Plus",
  "blueprint.polarity.negative": "Minus / Rückleiter",
  "blueprint.polarity.signal": "Signal",
  "blueprint.polarity.unknown": "Unbekannt",
  "blueprint.bom.cable_summary":
    "{count} Kabel · {length} gesamte erfasste Länge",
  "blueprint.bom.unresolved":
    "Nicht in den Kabel-Beschaffungsgruppen enthalten (Dimensionierung ungeklärt): {count}",
  "blueprint.bom.fuse_ambiguous":
    "Die physische Anzahl für {refs} ist unklar; vor der Bestellung prüfen.",
  "blueprint.document_type": "ELEKTROPLAN",
  "blueprint.generated": "Erstellt",
  "blueprint.units": "Einheiten",
  "blueprint.size_target": "Ziel für Spannungsabfall",
  "blueprint.col.ref": "Ref",
  "blueprint.col.from": "Von",
  "blueprint.col.to": "Nach",
  "blueprint.col.length": "Länge",
  "blueprint.col.conductor": "Leiter",
  "blueprint.col.current": "Auslegungsstrom",
  "blueprint.col.fuse": "Zugewiesene Sicherung",
  "blueprint.col.vdrop": "Spannungsabfall",
  "blueprint.col.notes": "Hinweise",
  "blueprint.generated_ref": "Generierte Referenz",
  "blueprint.value.verify": "PRÜFEN",
  "blueprint.value.manual": "manuell",
  "blueprint.value.continuous": "Dauerstrom",
  "blueprint.value.peak": "Spitze",
  "blueprint.value.unfused": "Keine Sicherung zugewiesen",
  "blueprint.value.suggested": "Empfehlung: {rating} A",

  "professional_feature.title": "Professional-Funktion",
  "professional_feature.description": "Stückliste und JSON-Export sind in Professional enthalten.",
  "professional_feature.action": "Professional ansehen",

  // ── Bill of materials ──────────────────────────────────
  "bom.label": "Werkzeuge",
  "bom.title": "Stückliste",
  "bom.unverified_cables": "Die Dimensionierung von {count} Kabeln muss geprüft werden. Automatische Querschnitte dieser Kabel fehlen in der Liste; manuell eingetragene Querschnitte sind als zu prüfende Angaben gekennzeichnet.",
  "bom.description":
    "Alles in deinem System, zusammengefasst — Komponenten, Kabellängen je Größe und Sicherungswerte.",
  "bom.empty":
    "Zieh Komponenten auf die Arbeitsfläche und verkable sie, um hier eine Stückliste zu sehen.",
  "bom.section.components": "Komponenten",
  "bom.section.cables": "Kabel",
  "bom.section.fuses": "Sicherungen",
  "bom.col.item": "Artikel",
  "bom.col.qty": "Anzahl",
  "bom.col.size": "Größe",
  "bom.col.length": "Gesamtlänge",
  "bom.col.count": "Anzahl",
  "bom.col.rating": "Nennstrom",
  "bom.cables.summary": "{count} Kabel · {length} insgesamt",
  "bom.fuses.none": "Noch keine Sicherungen an den Kabeln.",
  "bom.cables.none": "Noch keine Kabel.",
  "bom.blueprint_hint":
    "Brauchst du auch Schaltplan und Kabelliste? Wähle Projekt → Druckbarer Elektroplan.",
  "bom.copy": "Kopieren",
  "bom.copied": "Kopiert",
  "bom.copy_failed": "Kopieren fehlgeschlagen",
  "bom.print": "Drucken / PDF speichern",
  "bom.close": "Schließen",

  "inspector.settings.section": "Einstellungen",
  "inspector.charge_limit.label": "Ladestromgrenze",
  "inspector.charge_limit.hint": "Grenze für die Simulation. Die Kabelauslegung verwendet den Nennstrom von {amps} A.",
  "inspector.charge_limit.reset": "Nennstrom verwenden",
  "inspector.charge_limit.range": "Einen Wert von 0 bis {amps} A eingeben.",
  "setting.orion.jumperDisabled.label": "Jumper deaktiviert (aus bei Batterieschalter-Stellung Combined)",
  "setting.orion.jumperDisabled.help":
    "Setze diese Option, wenn der Fernschalt-Eingang des Orion über einen Hilfskontakt am Blue Sea 6011 oder einem ähnlichen Zweikreis-Schalter gebrückt ist, sodass das DC-DC-Ladegerät bei COMBINE-Stellung abgeschaltet wird. Unterdrückt die Eingangs-/Ausgangs-Schleifenwarnung, die sonst ausgelöst würde, weil beide Seiten in dieser Stellung dieselbe Batterie sehen.",
};

export const DICTIONARY: Record<Locale, Record<TranslationKey, string>> = {
  en: EN,
  sv: SV,
  de: DE,
};

export function interpolate(
  template: string,
  vars: Record<string, string | number> | undefined,
  locale: Locale = "en",
): string {
  const rendered = !vars ? template : template.replace(/\{(\w+)\}/g, (_m, k) =>
    k in vars ? String(vars[k]) : `{${k}}`,
  );
  return resolveDiagnosticLabels(rendered, key => DICTIONARY[locale][key]);
}
